import type { AdminLocale, BilingualLabel } from './index';

/**
 * Admin copy.
 *
 * Every string is a `{ ar, en }` pair in one place — no hard-coded text in a
 * component (ADR-015). Kept as a flat, typed object rather than JSON so a
 * missing key is a compile error rather than a dot-path rendered on screen.
 */

const M = {
  // ─── Chrome ───────────────────────────────────────────────────────────────
  appName: { ar: 'لوحة التحكم', en: 'Admin' },
  breadcrumb: { ar: 'موقعك في لوحة التحكم', en: 'Where you are' },
  // The menu's sections (lib/nav.ts).
  navSales: { ar: 'المبيعات', en: 'Sales' },
  navCatalog: { ar: 'الكتالوج', en: 'Catalog' },
  navCare: { ar: 'خدمة العملاء', en: 'Customer care' },
  navMarketing: { ar: 'التسويق', en: 'Marketing' },
  navStorefront: { ar: 'واجهة المتجر', en: 'Storefront' },
  navSetup: { ar: 'الإعدادات', en: 'Settings' },
  navGeneral: { ar: 'عام', en: 'General' },
  collapseMenu: { ar: 'طيّ القائمة', en: 'Collapse menu' },
  expandMenu: { ar: 'توسيع القائمة', en: 'Expand menu' },
  openMenu: { ar: 'فتح القائمة', en: 'Open menu' },
  signOut: { ar: 'تسجيل الخروج', en: 'Sign out' },
  toggleLanguage: { ar: 'تغيير اللغة', en: 'Change language' },
  themeLight: { ar: 'الوضع الفاتح', en: 'Light mode' },
  themeDark: { ar: 'الوضع الداكن', en: 'Dark mode' },
  themeSystem: { ar: 'حسب النظام', en: 'Match system' },

  // ─── The page kit and the settings area ───────────────────────────────────
  navOnlineStore: { ar: 'المتجر الإلكتروني', en: 'Online store' },
  navAnalytics: { ar: 'التحليلات', en: 'Analytics' },
  searchPages: { ar: 'ابحث في الصفحات…', en: 'Search pages…' },
  searchPagesEmpty: { ar: 'لا صفحة بهذا الاسم', en: 'No page by that name' },
  searchPagesHint: { ar: '↑ ↓ للتنقل · Enter للفتح · Esc للإغلاق', en: '↑ ↓ to move · Enter to open · Esc to close' },
  // The shell's own reads (docs/07-Admin/Admin-App.md).
  viewStore: { ar: 'عرض المتجر', en: 'View store' },
  storeClosed: { ar: 'مغلق', en: 'Closed' },
  needsAttention: { ar: 'بانتظارك', en: 'Needs attention' },
  allCaughtUp: { ar: 'لا شيء بانتظارك الآن.', en: 'Nothing is waiting on you.' },
  attentionOrdersToFulfil: { ar: 'طلبات للتجهيز', en: 'Orders to fulfil' },
  attentionOrdersUnpaid: { ar: 'طلبات بانتظار الدفع', en: 'Orders awaiting payment' },
  attentionReturns: { ar: 'مرتجعات بانتظار قرار أو استرداد', en: 'Returns to decide or refund' },
  attentionTickets: { ar: 'تذاكر دعم مفتوحة', en: 'Open support tickets' },
  attentionReviews: { ar: 'تقييمات بانتظار المراجعة', en: 'Reviews to moderate' },
  attentionQuestions: { ar: 'أسئلة بانتظار الإجابة', en: 'Questions to answer' },
  attentionTrade: { ar: 'طلبات حسابات تجارية', en: 'Trade applications' },
  attentionFollowUps: { ar: 'متابعات حان موعدها', en: 'Follow-ups due' },
  attentionLowStock: { ar: 'أصناف منخفضة المخزون', en: 'Variants low on stock' },
  attentionMessagesFailed: { ar: 'رسائل تعذّر إرسالها اليوم', en: 'Messages that failed today' },
  searchKindPages: { ar: 'الصفحات', en: 'Pages' },
  searchKindOrders: { ar: 'الطلبات', en: 'Orders' },
  searchKindProducts: { ar: 'المنتجات', en: 'Products' },
  searchKindCustomers: { ar: 'العملاء', en: 'Customers' },
  searchEverything: { ar: 'ابحث عن صفحة أو طلب أو منتج أو عميل…', en: 'Search pages, orders, products, customers…' },
  notFoundTitle: { ar: 'هذه الصفحة غير موجودة', en: 'This page does not exist' },
  notFoundBody: {
    ar: 'ربما نُقلت أو كُتب عنوانها خطأ. ابحث عنها بـ ⌘K، أو عُد إلى النظرة العامة.',
    en: 'It may have moved, or the address was mistyped. Find it with ⌘K, or go back to the overview.',
  },
  goToOverview: { ar: 'إلى النظرة العامة', en: 'Go to the overview' },
  crashTitle: { ar: 'تعذّر عرض هذه الصفحة', en: 'This page could not be shown' },
  crashBody: {
    ar: 'حدث خطأ غير متوقع أثناء عرضها. لم يُفقد شيء محفوظ — أعد تحميلها، وإن تكرر فأخبر الدعم بما كنت تفعله.',
    en: 'Something unexpected went wrong drawing it. Nothing saved was lost — reload it, and if it happens again tell support what you were doing.',
  },
  reloadPage: { ar: 'إعادة تحميل', en: 'Reload' },
  unsavedChanges: { ar: 'تغييرات غير محفوظة', en: 'Unsaved changes' },
  discard: { ar: 'تجاهل', en: 'Discard' },
  back: { ar: 'رجوع', en: 'Back' },
  orderView_all: { ar: 'الكل', en: 'All' },
  orderView_toFulfil: { ar: 'للتجهيز', en: 'To fulfil' },
  orderView_unpaid: { ar: 'غير مدفوعة', en: 'Unpaid' },
  orderView_onTheWay: { ar: 'في الطريق', en: 'On the way' },
  orderView_delivered: { ar: 'تم التوصيل', en: 'Delivered' },
  orderView_closed: { ar: 'ملغاة ومستردة', en: 'Cancelled and refunded' },
  allSettings: { ar: 'كل الإعدادات', en: 'All settings' },
  settingsHomeSubtitle: {
    ar: 'كل ما يضبط المتجر في مكان واحد، مجمّعًا حسب موضوعه',
    en: 'Everything that sets the store up, in one place, grouped by what it is for',
  },
  settingsGroupStore: { ar: 'المتجر', en: 'Store' },
  settingsGroupSelling: { ar: 'البيع', en: 'Selling' },
  settingsGroupCommunication: { ar: 'التواصل', en: 'Communication' },
  settingsGroupApps: { ar: 'التطبيقات والربط', en: 'Apps and integrations' },
  settingsGroupTeam: { ar: 'الفريق والأمان', en: 'Team and security' },
  settingsCheckout: { ar: 'الطلبات والدفع', en: 'Orders and checkout' },
  settingsCustomers: { ar: 'العملاء والحسابات', en: 'Customers and accounts' },
  settingsCatalog: { ar: 'عرض المنتجات', en: 'Product listing' },
  settingsShipping: { ar: 'الشحن والتوصيل', en: 'Shipping and delivery' },
  settingsTaxes: { ar: 'الضرائب', en: 'Taxes' },
  settingsNotifications: { ar: 'الإشعارات والبريد', en: 'Notifications' },
  settingsGeneralHint: { ar: 'الاسم والتواصل والبيانات النظامية والفاتورة', en: 'Name, contact, legal details and the invoice' },
  settingsCheckoutHint: { ar: 'ترقيم الطلبات وما يقبله المتجر والمخزون والإرجاع', en: 'Order numbering, what the store accepts, stock and returns' },
  settingsCustomersHint: { ar: 'تسجيل الدخول والتذاكر والتجّار والتقييمات', en: 'Sign-in, tickets, trade accounts and reviews' },
  settingsCatalogHint: { ar: 'عدد المنتجات في الصفحة وترتيبها ودليل الكميات', en: 'Products per page, their order, and the quantity guide' },
  settingsFeaturesHint: { ar: 'تشغيل خصائص المتجر الاختيارية وإيقافها', en: 'Switch the store’s optional features on and off' },
  settingsPaymentsHint: { ar: 'بوابات الدفع والدفع عند الاستلام', en: 'Payment gateways and cash on delivery' },
  settingsShippingHint: { ar: 'المناطق والأسعار وتقويم التوصيل', en: 'Zones, rates and the delivery calendar' },
  settingsLocations: { ar: 'المستودعات والفروع', en: 'Warehouses and branches' },
  settingsCustomCode: { ar: 'أكوادك الخاصة', en: 'Your own code' },
  settingsCustomCodeHint: { ar: 'CSS وسكربتات في صفحات المتجر', en: 'CSS and scripts on the storefront' },
  settingsLocationsHint: { ar: 'أماكن المخزون والمناطق التي يخدمها كل منها', en: 'Where stock is kept, and the zones each one serves' },
  settingsTaxesHint: { ar: 'نسبة الضريبة وكيف تظهر على الأسعار', en: 'The VAT rate and how prices show it' },
  settingsNotificationsHint: { ar: 'قوالب البريد والمرسِل', en: 'Email templates and who they come from' },
  settingsMessagingHint: { ar: 'الرسائل النصية ورموز التحقق', en: 'Text messages and sign-in codes' },
  whatsappHint: { ar: 'تحديثات الطلبات على واتساب عبر Meta', en: 'Order updates on WhatsApp through Meta' },
  settingsMessageLogHint: { ar: 'كل رسالة أُرسلت، وإعادة إرسال ما فشل', en: 'Every message sent, and resending what failed' },
  settingsIntegrationsHint: { ar: 'التحليلات والرسائل وشركات الشحن', en: 'Analytics, messaging and couriers' },
  settingsAiHint: { ar: 'المساعد الذي يكتب المسودات', en: 'The assistant that drafts for you' },
  settingsApiKeysHint: { ar: 'مفاتيح لأنظمة خارجية بصلاحيات محددة', en: 'Scoped keys for outside systems' },
  settingsWebhooksHint: { ar: 'أحداث المتجر إلى أنظمتك، أو إلى Slack وTelegram', en: 'The store’s events to your systems, or to Slack and Telegram' },
  settingsTeamHint: { ar: 'الموظفون ومجموعات الصلاحيات', en: 'Staff and permission groups' },
  settingsAuditHint: { ar: 'من غيّر ماذا ومتى', en: 'Who changed what, and when' },
  hidden: { ar: 'مخفي', en: 'Hidden' },
  advancedEmpty: { ar: 'كل إعدادات المتجر لها نموذج — لا شيء هنا', en: 'Every setting has a form of its own — nothing here' },
  settingsAdvancedHint: { ar: 'إعدادات لم يُصمَّم لها نموذج بعد', en: 'Settings without a form of their own yet' },

  // ─── Sections ─────────────────────────────────────────────────────────────
  overview: { ar: 'نظرة عامة', en: 'Overview' },
  orders: { ar: 'الطلبات', en: 'Orders' },
  products: { ar: 'المنتجات', en: 'Products' },
  inventory: { ar: 'المخزون', en: 'Inventory' },
  discounts: { ar: 'الخصومات', en: 'Discounts' },
  customers: { ar: 'العملاء', en: 'Customers' },
  returns: { ar: 'المرتجعات', en: 'Returns' },
  reviews: { ar: 'التقييمات', en: 'Reviews' },
  questions: { ar: 'أسئلة المنتجات', en: 'Questions' },
  settings: { ar: 'الإعدادات', en: 'Settings' },
  auditLog: { ar: 'سجل التدقيق', en: 'Audit log' },

  // ─── Overview ─────────────────────────────────────────────────────────────
  overviewSubtitle: { ar: 'ملخص أداء المتجر والمبيعات', en: 'Store and sales performance' },
  greetingMorning: { ar: 'صباح الخير، {name}', en: 'Good morning, {name}' },
  greetingAfternoon: { ar: 'نهارك سعيد، {name}', en: 'Good afternoon, {name}' },
  greetingEvening: { ar: 'مساء الخير، {name}', en: 'Good evening, {name}' },
  revenue: { ar: 'الإيرادات', en: 'Revenue' },
  orderCount: { ar: 'عدد الطلبات', en: 'Orders' },
  averageOrderValue: { ar: 'متوسط قيمة الطلب', en: 'Average order value' },
  taxCollected: { ar: 'ضريبة محصّلة', en: 'VAT collected' },
  newCustomers: { ar: 'عملاء جدد', en: 'New customers' },
  awaitingFulfilment: { ar: 'بانتظار الشحن', en: 'Awaiting fulfilment' },
  openReturns: { ar: 'مرتجعات مفتوحة', en: 'Open returns' },
  lowStockCount: { ar: 'أصناف منخفضة', en: 'Low stock items' },
  actionQueue: { ar: 'يحتاج إجراءً', en: 'Needs action' },
  actionQueueHint: { ar: 'طلبات مدفوعة بانتظار الشحن، الأقدم أولًا', en: 'Paid orders waiting to ship, oldest first' },
  queueEmpty: { ar: 'لا توجد طلبات بانتظار الشحن', en: 'No orders waiting to ship' },
  queueEmptyHint: { ar: 'كل شيء تم تجهيزه. 🎉', en: 'Everything is handled. 🎉' },
  lowStock: { ar: 'مخزون منخفض', en: 'Low stock' },
  lowStockHint: { ar: 'أصناف تحتاج إعادة طلب', en: 'Items that need reordering' },
  lowStockEmpty: { ar: 'المخزون بحالة جيدة', en: 'Stock levels are healthy' },
  recentOrders: { ar: 'أحدث الطلبات', en: 'Recent orders' },
  unit: { ar: 'وحدة', en: 'units' },
  outOfStock: { ar: 'نفد المخزون', en: 'Out of stock' },
  inStock: { ar: 'متوفر', en: 'In stock' },
  last30Days: { ar: 'آخر ٣٠ يومًا', en: 'Last 30 days' },
  today: { ar: 'اليوم', en: 'Today' },
  last7Days: { ar: 'آخر ٧ أيام', en: 'Last 7 days' },
  last90Days: { ar: 'آخر ٩٠ يومًا', en: 'Last 90 days' },
  overviewPeriod: { ar: 'الفترة', en: 'Period' },
  vsPrevious: { ar: '{change} عن الفترة السابقة', en: '{change} vs the previous period' },
  newVsPrevious: { ar: 'لا شيء في الفترة السابقة', en: 'None in the previous period' },
  sameAsPrevious: { ar: 'مثل الفترة السابقة', en: 'Same as the previous period' },
  salesTrend: { ar: 'المبيعات اليومية', en: 'Sales by day' },
  salesTrendHint: { ar: 'إيراد كل يوم شاملًا الضريبة — مرّر المؤشر لترى اليوم', en: 'Each day’s revenue, VAT included — point at a day to read it' },
  noSalesInPeriod: { ar: 'لا مبيعات في هذه الفترة', en: 'No sales in this period' },
  topSellers: { ar: 'الأكثر مبيعًا', en: 'Best sellers' },
  topSellersHint: { ar: 'حسب عدد القطع المباعة في الفترة', en: 'By units sold in the period' },
  noTopSellers: { ar: 'لم يُبع شيء في هذه الفترة', en: 'Nothing sold in this period' },
  unitsSoldCount: { ar: '{count} قطعة', en: '{count} sold' },
  ordersWord: { ar: 'طلبات', en: 'orders' },
  queueOldestFirst: { ar: 'الأقدم انتظارًا أولًا', en: 'Longest waiting first' },
  viewAll: { ar: 'عرض الكل', en: 'View all' },

  // ─── Orders ───────────────────────────────────────────────────────────────
  ordersSubtitle: { ar: 'متابعة الطلبات وتحديث حالتها', en: 'Track and fulfil orders' },
  orderNumber: { ar: 'رقم الطلب', en: 'Order' },
  customer: { ar: 'العميل', en: 'Customer' },
  status: { ar: 'الحالة', en: 'Status' },
  total: { ar: 'القيمة', en: 'Total' },
  placed: { ar: 'التاريخ', en: 'Placed' },
  searchOrders: { ar: 'ابحث برقم الطلب أو البريد أو الاسم', en: 'Search by order number, email, or name' },
  allStatuses: { ar: 'كل الحالات', en: 'All statuses' },
  loadMore: { ar: 'عرض المزيد', en: 'Load more' },
  clearFilters: { ar: 'مسح التصفية', en: 'Clear filters' },
  noOrders: { ar: 'لا توجد طلبات', en: 'No orders here' },
  noOrdersFiltered: { ar: 'لا نتائج مطابقة. جرّب مسح التصفية.', en: 'Nothing matches those filters.' },
  noOrdersYet: { ar: 'ستظهر الطلبات هنا فور استلامها.', en: 'Orders appear here as customers place them.' },
  backToOrders: { ar: 'رجوع للطلبات', en: 'Back to orders' },
  downloadInvoice: { ar: 'تحميل الفاتورة', en: 'Download invoice' },
  // Shipping labels (ADR-068).
  printShippingLabel: { ar: 'طباعة ملصق الشحن', en: 'Print shipping label' },
  // Who has the order (ADR-074).
  assignedToNobody: { ar: 'لا أحد', en: 'Nobody' },
  assignedToMe: { ar: 'مُسند إليّ', en: 'Assigned to me' },
  assignToMe: { ar: 'أسنده إليّ', en: 'Assign to me' },
  assignSelected: { ar: 'إسناد', en: 'Assign' },
  assignSelectedTitle: { ar: 'إسناد {count} طلبات', en: 'Assign {count} orders' },
  assignSelectedHint: { ar: 'لا يتغيّر شيء آخر — لا الحالة ولا ما يُبلَّغ به العميل', en: 'Nothing else changes — not the status, not what the customer is told' },
  timelineAssignedTo: { ar: 'إلى {name}', en: 'To {name}' },
  timelineUnassigned: { ar: 'لم يعد مُسندًا لأحد', en: 'Now with nobody' },
  timelineAssignedFrom: { ar: 'كان مع {name}', en: 'Was with {name}' },
  auditAction_order_assigned: { ar: 'إسناد طلب', en: 'Order assigned' },
  printShippingLabels: { ar: 'ملصقات الشحن', en: 'Shipping labels' },
  labelsAtMost: { ar: '{count} ملصقًا كحد أقصى في المرة', en: 'At most {count} labels at a time' },
  // The invoice register and credit notes (ADR-067).
  invoices: { ar: 'الفواتير', en: 'Invoices' },
  invoicesSubtitle: {
    ar: 'كل فاتورة وإشعار دائن أصدره المتجر — للمحاسب وإقرار الضريبة',
    en: 'Every invoice and credit note the store has issued — for the accountant and the VAT return',
  },
  exportInvoices: { ar: 'تصدير', en: 'Export' },
  exportInvoicesHint: { ar: 'ملف CSV بالتصفية الحالية — الإشعارات الدائنة بالسالب', en: 'A CSV of the current filter — credit notes as negatives' },
  invoicedTotal: { ar: 'إجمالي الفواتير', en: 'Invoiced' },
  documentsCount: { ar: '{count} فاتورة', en: '{count} invoices' },
  creditedTotal: { ar: 'إجمالي الإشعارات الدائنة', en: 'Credited' },
  creditNotesCount: { ar: '{count} إشعار دائن', en: '{count} credit notes' },
  netInvoiced: { ar: 'الصافي', en: 'Net' },
  netInvoicedHint: { ar: 'الفواتير ناقص الإشعارات الدائنة، شامل الضريبة', en: 'Invoiced less credited, VAT included' },
  netVat: { ar: 'صافي الضريبة', en: 'Net VAT' },
  netVatHint: { ar: '{invoiced} في الفواتير، {credited} مستردة', en: '{invoiced} invoiced, {credited} credited' },
  invoice: { ar: 'فاتورة', en: 'Invoice' },
  creditNote: { ar: 'إشعار دائن', en: 'Credit note' },
  creditsInvoice: { ar: 'عن الفاتورة {number}', en: 'Against {number}' },
  issuedFrom: { ar: 'صدرت من', en: 'Issued from' },
  issuedTo: { ar: 'إلى', en: 'Issued to' },
  issuedFromChip: { ar: 'من {date}', en: 'From {date}' },
  issuedToChip: { ar: 'إلى {date}', en: 'To {date}' },
  searchInvoices: { ar: 'ابحث برقم الفاتورة أو الطلب أو اسم المنشأة', en: 'Search by document or order number, or the business' },
  documentKind: { ar: 'النوع', en: 'Type' },
  noInvoices: { ar: 'لا فواتير هنا', en: 'No invoices here' },
  noInvoicesHint: {
    ar: 'تصدر الفاتورة عند استلام المبلغ أو عند طباعتها، والإشعار الدائن عند الاسترداد أو الإلغاء.',
    en: 'An invoice is issued when the money comes in or when it is first printed; a credit note when money goes back or the order is cancelled.',
  },
  documentNumber: { ar: 'الرقم', en: 'Number' },
  issuedAt: { ar: 'تاريخ الإصدار', en: 'Issued' },
  buyer: { ar: 'المشتري', en: 'Buyer' },
  totalInclVat: { ar: 'الإجمالي شامل الضريبة', en: 'Total incl. VAT' },
  vat: { ar: 'الضريبة', en: 'VAT' },
  openDocument: { ar: 'فتح المستند', en: 'Open the document' },
  vatNumberShort: { ar: 'ر.ض {number}', en: 'VAT {number}' },
  orderDocuments: { ar: 'الفاتورة والإشعارات', en: 'Invoice and credit notes' },
  items: { ar: 'المنتجات', en: 'Items' },
  timeline: { ar: 'السجل الزمني', en: 'Timeline' },
  // The order timeline's vocabulary, matching ORDER_EVENT_TYPES in the contract.
  'event.order.placed': { ar: 'أُنشئ الطلب', en: 'Order placed' },
  'event.order.status_changed': { ar: 'تغيّرت حالة الطلب', en: 'Status changed' },
  'event.payment.succeeded': { ar: 'نجحت عملية الدفع', en: 'Payment succeeded' },
  'event.payment.from_balance': { ar: 'دُفع من رصيد العميل', en: 'Paid from store credit' },
  'event.payment.failed': { ar: 'فشلت عملية الدفع', en: 'Payment failed' },
  'event.payment.retried': { ar: 'إعادة محاولة الدفع ببطاقة أخرى', en: 'Payment tried again' },
  'event.payment.refunded': { ar: 'رُدّ المبلغ', en: 'Refund issued' },
  'event.return.requested': { ar: 'طُلب إرجاع', en: 'Return requested' },
  'event.return.status_changed': { ar: 'تغيّرت حالة الإرجاع', en: 'Return status changed' },
  'event.email.sent': { ar: 'أُرسل بريد', en: 'Email sent' },
  'event.order.claimed': { ar: 'أُضيف إلى حساب العميل', en: "Joined the customer's account" },
  'event.payment.refund_failed': { ar: 'رفض مزوّد الدفع الاسترداد', en: 'Refund refused by the provider' },
  'event.payment.refund_unconfirmed': { ar: 'لم يؤكّد مزوّد الدفع الاسترداد', en: 'Refund not confirmed by the provider' },
  'event.order.edited': { ar: 'عُدّل الطلب', en: 'Order changed' },
  'event.order.assigned': { ar: 'إسناد الطلب', en: 'Order assigned' },
  'event.digital.delivered': { ar: 'تسليم الملفات والأكواد', en: 'Files and codes handed over' },
  'event.courier.sent': { ar: 'أُرسل الطلب إلى منصة الشحن', en: 'Sent to the shipping platform' },
  'event.courier.failed': { ar: 'تعذّر إرسال الطلب إلى منصة الشحن', en: 'Could not send to the shipping platform' },
  'event.courier.update': { ar: 'تحديث من منصة الشحن', en: 'Shipping platform update' },
  'event.courier.cancelled': { ar: 'أُلغي الطلب لدى منصة الشحن', en: 'Cancelled at the shipping platform' },
  'event.courier.cancel_failed': { ar: 'تعذّر إلغاء الطلب لدى منصة الشحن', en: 'Could not cancel at the shipping platform' },
  'event.courier.book_failed': { ar: 'رفضت شركة الشحن حجز الشحنة', en: 'The courier would not book the parcel' },
  'event.return.pickup': { ar: 'استلام المرتجع من العميل', en: 'Return pickup' },
  returnPickup: { ar: 'استلام المرتجع من العميل', en: 'Collecting it from the customer' },
  returnPickupNone: { ar: 'لم يُحجز مندوب بعد.', en: 'No courier booked yet.' },
  returnPickupBook: { ar: 'احجز الاستلام', en: 'Book pickup' },
  returnPickupRetry: { ar: 'أعد محاولة الحجز', en: 'Try booking again' },
  returnPickupLabel: { ar: 'البوليصة', en: 'Label' },
  returnPickupArrivedHint: { ar: 'وصل إلى المستودع — افحصه ثم سجّل الاستلام.', en: 'It has reached the warehouse — inspect it, then mark it received.' },
  returnShippingFeeLine: { ar: 'رسوم شحن المرتجع على العميل: {amount} — تُخصم من المبلغ المسترد.', en: 'Return shipping the customer pays: {amount} — taken off the refund.' },
  returnPickup_BOOKED: { ar: 'محجوز', en: 'Booked' },
  returnPickup_PICKED_UP: { ar: 'استلمه المندوب', en: 'Picked up' },
  returnPickup_IN_TRANSIT: { ar: 'في الطريق إلينا', en: 'On its way back' },
  returnPickup_ARRIVED: { ar: 'وصل المستودع', en: 'At the warehouse' },
  returnPickup_FAILED: { ar: 'تعذّر الحجز', en: 'Could not book' },
  returnPickup_CANCELLED: { ar: 'أُلغي', en: 'Cancelled' },
  courierBooked: { ar: 'حُجزت الشحنة', en: 'Booked' },
  courierToBook: { ar: 'تُنشأ الشحنة من المنصة', en: 'Shipment to be created on the provider' },
  shipWithOrder: { ar: 'الشحن عبر', en: 'Ships with' },
  courierStatus_new: { ar: 'وصل إلى المنصة', en: 'Received by the provider' },
  courierStatus_processing: { ar: 'جارٍ تجهيز الشحنة', en: 'Shipment being prepared' },
  courierStatus_pickedUp: { ar: 'استلمها المندوب', en: 'Picked up' },
  courierStatus_inTransit: { ar: 'في الطريق', en: 'In transit' },
  courierStatus_outForDelivery: { ar: 'خرجت للتوصيل', en: 'Out for delivery' },
  courierStatus_delivered: { ar: 'تم التسليم', en: 'Delivered' },
  courierStatus_failedAttempt: { ar: 'تعذّر التسليم', en: 'Delivery attempt failed' },
  courierStatus_returned: { ar: 'مرتجعة', en: 'Returned' },
  courierStatus_cancelled: { ar: 'ملغاة', en: 'Cancelled' },
  'event.digital.short': { ar: 'نفدت الأكواد', en: 'Out of codes' },
  timelineDigitalLines: { ar: 'عدد المنتجات: {count}', en: '{count} line(s)' },
  timelineDigitalShort: { ar: '{product} — ينقصه {missing} كود، أضفها ثم اضغط «سلّم الآن»', en: '{product} — {missing} code(s) short; add them, then Deliver now' },
  timelineEditedItems: { ar: '{from} ← {to}', en: '{from} → {to}' },
  timelineEditedFields: { ar: 'تغيّر: {fields}', en: 'Changed: {fields}' },
  timelinePlacedByStaff: { ar: 'أنشأه الفريق', en: 'Placed by the team' },
  timelinePaidOutside: { ar: 'دُفع خارج المتجر ({method}) · {amount}', en: 'Paid outside the store ({method}) · {amount}' },
  editField_email: { ar: 'البريد', en: 'email' },
  editField_phone: { ar: 'الجوال', en: 'phone' },
  editField_address: { ar: 'العنوان', en: 'address' },
  editField_note: { ar: 'ملاحظة العميل', en: 'customer note' },
  editField_billing: { ar: 'الفوترة', en: 'billing' },
  // ─── Orders staff place and change (ADR-062) ──────────────────────────────
  newOrder: { ar: 'طلب جديد', en: 'New order' },
  newOrderSubtitle: {
    ar: 'طلب بالهاتف أو واتساب نيابةً عن عميل — بالأسعار والعروض نفسها التي يراها في المتجر',
    en: 'A phone or WhatsApp order on a customer’s behalf — at the prices and offers they would see in the store',
  },
  discardDraft: { ar: 'تجاهل المسودة', en: 'Discard draft' },
  newOrderCustomerHint: {
    ar: 'عميل بحساب تُسعّر له أسعاره (قائمة التاجر) ويظهر الطلب في حسابه — أو بلا حساب',
    en: 'A customer with an account is priced at their own prices (a trade list) and sees the order in their account — or none',
  },
  customerAccount: { ar: 'حساب العميل', en: 'Customer account' },
  guestNoAccount: { ar: 'بلا حساب (زائر)', en: 'No account (guest)' },
  newOrderEmailHint: { ar: 'يصله التأكيد عليه', en: 'Where the confirmation goes' },
  saveToAddressBook: { ar: 'حفظ العنوان في دفتر عناوين العميل', en: 'Save the address to the customer’s address book' },
  free: { ar: 'مجاني', en: 'Free' },
  deliveryNeedsAddress: { ar: 'تظهر طرق التوصيل عند اكتمال العنوان والمنتجات.', en: 'Delivery options appear once the address and items are in.' },
  payCod: { ar: 'الدفع عند الاستلام', en: 'Cash on delivery' },
  payCodHint: { ar: 'يُحصَّل النقد عند التسليم', en: 'Cash is collected at the door' },
  payManual: { ar: 'مدفوع خارج المتجر', en: 'Paid outside the store' },
  payWallet: { ar: 'رصيد المتجر', en: 'Store credit' },
  giftCards: { ar: 'بطاقات الهدايا', en: 'Gift cards' },
  affiliates: { ar: 'المسوّقون', en: 'Affiliates' },
  customerGroups: { ar: 'مجموعات العملاء', en: 'Customer groups' },
  customerGroupsSubtitle: {
    ar: 'مجموعات تُبنى بقواعد وتُحدَّث كل ليلة — لتصفية العملاء وحملة تسويقية وكوبون خاص',
    en: 'Groups built by rules and refreshed nightly — to filter customers, target a campaign, keep a coupon for them',
  },
  customerGroup: { ar: 'المجموعة', en: 'Group' },
  customerGroupAdd: { ar: 'إضافة مجموعة', en: 'Add group' },
  customerGroupsSearch: { ar: 'اسم المجموعة', en: 'Group name' },
  customerGroupsEmpty: { ar: 'لا توجد مجموعات بعد', en: 'No groups yet' },
  customerGroupsEmptyHint: {
    ar: 'مثل: كبار العملاء (أنفقوا أكثر من 2,000)، أو من لم يطلب منذ ستة أشهر.',
    en: 'Such as big spenders (over 2,000 spent), or those who have not ordered for six months.',
  },
  customerGroupRules: { ar: 'القواعد', en: 'Rules' },
  customerGroupMembers: { ar: 'الأعضاء', en: 'Members' },
  customerGroupRefreshed: { ar: 'آخر تحديث', en: 'Last refreshed' },
  customerGroupRefresh: { ar: 'تحديث الآن', en: 'Refresh now' },
  customerGroupEditHint: { ar: 'يُحدَّث الأعضاء عند الحفظ ثم كل ليلة', en: 'Members are found on saving, then every night' },
  customerGroupRulesHint: { ar: 'العضو يحقق كل قاعدة مُدخلة؛ اترك الحقل فارغًا لتجاهله.', en: 'A member meets every rule given; leave a field empty to skip it.' },
  customerGroupBadNumber: { ar: 'أدخل أرقامًا صحيحة في الحقول الرقمية.', en: 'Enter whole numbers in the number fields.' },
  customerGroupPreview: { ar: 'تضم الآن {count} عميلًا.', en: '{count} customers would be in it now.' },
  // ─── WhatsApp through Meta (ADR-105) ──────────────────────────────────────
  whatsapp: { ar: 'واتساب', en: 'WhatsApp' },
  whatsappSubtitle: {
    ar: 'تحديثات الطلبات تصل العميل على واتساب عبر حسابك في Meta، بقوالب تجهزها المنصة ويوافق عليها Meta',
    en: 'Order updates reach customers on WhatsApp through your own Meta account, with templates the platform prepares and Meta approves',
  },
  whatsappNotConnected: { ar: 'واتساب غير مربوط بعد', en: 'WhatsApp is not connected yet' },
  whatsappConnectHint: {
    ar: 'أدخل مفاتيح حسابك في Meta في صفحة الربط ثم فعّله: معرّف رقم الهاتف، ومعرّف حساب واتساب للأعمال، ورمز الوصول الدائم، وسر التطبيق.',
    en: 'Enter your Meta account’s keys in Integrations and switch it on: the phone number ID, the WhatsApp Business Account ID, a permanent access token and the app secret.',
  },
  whatsappOpenIntegrations: { ar: 'فتح الربط', en: 'Open integrations' },
  whatsappConnected: { ar: 'مربوط', en: 'Connected' },
  whatsappNumber: { ar: 'الرقم', en: 'Number' },
  whatsappVerifiedName: { ar: 'الاسم الظاهر للعملاء', en: 'Name customers see' },
  whatsappQuality: { ar: 'جودة الرقم لدى Meta', en: 'Quality rating' },
  whatsappMetaError: { ar: 'لم يُجب Meta: {error}', en: 'Meta did not answer: {error}' },
  whatsappWebhook: { ar: 'الاستلامات من Meta', en: 'Receipts from Meta' },
  whatsappWebhookHint: {
    ar: 'في تطبيقك على Meta: واتساب ← الإعدادات ← Webhook. الصق العنوان ورمز التحقق، ثم اشترك في messages و message_template_status_update — هكذا يصلنا ما وصل وما قُرئ وموافقة القوالب.',
    en: 'In your Meta app: WhatsApp → Configuration → Webhook. Paste the address and the verify token, then subscribe to messages and message_template_status_update — that is how delivered, read and template approvals reach the store.',
  },
  whatsappWebhookUrl: { ar: 'عنوان الاستقبال', en: 'Callback URL' },
  whatsappVerifyToken: { ar: 'رمز التحقق', en: 'Verify token' },
  whatsappNoApiUrl: { ar: 'اضبط API_PUBLIC_URL على الخادم ليظهر العنوان.', en: 'Set API_PUBLIC_URL on the server to see the address.' },
  whatsappUpdates: { ar: 'التحديثات التي تُرسل', en: 'Updates that go' },
  whatsappUpdatesHint: {
    ar: 'تصل فقط لمن وافق عند الدفع على تحديثات واتساب، وفقط بعد موافقة Meta على قالبها.',
    en: 'Only to shoppers who ticked the WhatsApp box at checkout, and only once Meta has approved the template.',
  },
  whatsappLanguage: { ar: 'لغة الرسائل', en: 'Language they are sent in' },
  whatsappEvent_orderConfirmed: { ar: 'تأكيد الطلب', en: 'Order confirmed' },
  whatsappEvent_orderShipped: { ar: 'الطلب في الطريق', en: 'On its way' },
  whatsappEvent_orderReadyForPickup: { ar: 'جاهز للاستلام', en: 'Ready to collect' },
  whatsappEvent_orderDelivered: { ar: 'تم التسليم', en: 'Delivered' },
  whatsappEvent_orderCancelled: { ar: 'أُلغي الطلب', en: 'Cancelled' },
  whatsappEvent_orderRefunded: { ar: 'استرداد مبلغ', en: 'Refunded' },
  whatsappEvent_cartReminder: { ar: 'تذكير بالسلة', en: 'Cart reminder' },
  whatsappEvent_cartOffer: { ar: 'تذكير بالسلة مع كود', en: 'Cart reminder with a code' },
  whatsappCategory_UTILITY: { ar: 'خدمية', en: 'Utility' },
  whatsappCategory_MARKETING: { ar: 'تسويقية', en: 'Marketing' },
  whatsappCartReminders: { ar: 'تذكير السلات المتروكة على واتساب', en: 'Abandoned-cart reminders on WhatsApp' },
  whatsappCartRemindersHint: {
    ar: 'مع أول تذكير بالسلة، لعميل أعطى رقم واتساب عند الدفع ووافق على الرسائل التسويقية — وبكود السلة إن وُجد. ردّ العميل بـ«إيقاف» يوقف كل الرسائل التسويقية.',
    en: 'With a cart’s first reminder, to a customer who gave a WhatsApp number at checkout and agreed to marketing — with the cart’s code when it has one. A customer replying STOP stops all marketing.',
  },
  reminderByWhatsApp: { ar: 'واتساب', en: 'WhatsApp' },
  whatsappWindowOpen: { ar: 'يصل ردّك على واتساب فورًا حتى {time}', en: 'Your reply reaches WhatsApp at once until {time}' },
  whatsappWindowClosed: {
    ar: 'مرّت 24 ساعة على آخر رسالة من العميل: يُحفظ ردّك ويصله حين يكتب مجددًا، ونخبره مرة أن لديه ردًّا.',
    en: 'More than 24 hours since the customer last wrote: your reply waits and goes when they write again — they are told once that an answer is there.',
  },
  whatsappReplyWaiting: { ar: 'بانتظار رسالة العميل', en: 'Waiting for the customer to write' },
  whatsappInbox: { ar: 'رسائل العملاء على واتساب تصل إلى خدمة العملاء', en: 'Customers’ WhatsApp messages arrive in Support' },
  whatsappInboxHint: {
    ar: 'كل رسالة تفتح تذكرة أو تكمل المفتوحة لنفس الرقم، والردّ من خدمة العملاء يعود على واتساب. الصور وملفات PDF تُحفظ مع التذكرة.',
    en: 'Each message opens a ticket or continues the number’s open one, and replies from Support go back on WhatsApp. Pictures and PDFs are kept with the ticket.',
  },
  whatsappEvent_supportFollowUp: { ar: 'ردّ الدعم بانتظارك', en: 'A support reply is waiting' },
  reminderByWhatsAppHint: { ar: 'لمن أعطى رقم واتساب عند الدفع ووافق على التسويق', en: 'If they gave a WhatsApp number and agreed to marketing' },
  campaignWhatsAppTemplate: { ar: 'القالب', en: 'Template' },
  campaignWhatsAppTemplateHint: {
    ar: 'أحد قوالبك التسويقية المعتمدة في WhatsApp Manager — {{1}} يُملأ بالاسم الأول للعميل.',
    en: 'One of your approved marketing templates from WhatsApp Manager — {{1}} becomes the customer’s first name.',
  },
  campaignWhatsAppNone: {
    ar: 'لا قوالب تسويقية في حسابك بعد. أنشئ قالبًا في WhatsApp Manager وانتظر موافقة Meta، ثم عد هنا.',
    en: 'No marketing templates on your account yet. Create one in WhatsApp Manager, wait for Meta’s approval, then come back.',
  },
  campaignWhatsAppAudience: {
    ar: 'تصل لمن وافق على الرسائل التسويقية وأعطى رقم واتساب عند الدفع.',
    en: 'Reaches customers who agreed to marketing and gave a WhatsApp number at checkout.',
  },
  campaignTestWhatsAppHint: { ar: 'يصل القالب على واتساب لهذا الرقم، باسم «Test».', en: 'The template arrives on WhatsApp at this number, addressed to “Test”.' },
  campaignWhatsAppResults: { ar: 'وصلت {delivered} · قُرئت {read}', en: 'Delivered {delivered} · read {read}' },
  whatsappTemplates: { ar: 'القوالب', en: 'Templates' },
  whatsappTemplatesHint: {
    ar: 'تجهزها المنصة بالعربية والإنجليزية. أرسلها لمراجعة Meta مرة واحدة — تصل الموافقة عادةً خلال دقائق، وقد تمتد إلى يوم.',
    en: 'Prepared by the platform in Arabic and English. Send them to Meta for review once — approval usually takes minutes, sometimes a day.',
  },
  whatsappSubmit: { ar: 'إرسال إلى Meta للمراجعة', en: 'Submit to Meta' },
  whatsappRefresh: { ar: 'تحديث الحالة', en: 'Refresh status' },
  whatsappStatus_NOT_SUBMITTED: { ar: 'لم يُرسل', en: 'Not submitted' },
  whatsappStatus_PENDING: { ar: 'قيد المراجعة', en: 'In review' },
  whatsappStatus_APPROVED: { ar: 'معتمد', en: 'Approved' },
  whatsappStatus_REJECTED: { ar: 'مرفوض', en: 'Rejected' },
  whatsappStatus_PAUSED: { ar: 'موقوف مؤقتًا', en: 'Paused' },
  whatsappStatus_DISABLED: { ar: 'معطّل', en: 'Disabled' },
  whatsappTest: { ar: 'رسالة تجربة', en: 'Test message' },
  whatsappTestHint: {
    ar: 'يرسل قالب Meta الجاهز hello_world إلى رقم تكتبه — يثبت أن المفاتيح والرقم يعملان.',
    en: 'Sends Meta’s ready-made hello_world template to a number you type — proof the keys and the number work.',
  },
  whatsappTestSend: { ar: 'إرسال', en: 'Send' },
  whatsappTestSent: { ar: 'أُرسلت. تجدها في سجل الرسائل.', en: 'Sent. It is in the message log.' },
  whatsappTestFailed: { ar: 'لم تُرسل: {error}', en: 'Not sent: {error}' },
  whatsappDelivered: { ar: 'وصلت', en: 'Delivered' },
  whatsappRead: { ar: 'قُرئت', en: 'Read' },
  // ─── A simple CRM (ADR-104) ────────────────────────────────────────────────
  followUps: { ar: 'المتابعات', en: 'Follow-ups' },
  followUpsSubtitle: {
    ar: 'ما يجب فعله بعد ذلك، ومن يفعله، ومتى — ويصله تذكير بالبريد حين يحين موعده',
    en: 'What must happen next, who does it and when — they get a mail when it falls due',
  },
  followUpsMine: { ar: 'متابعاتي', en: 'Mine' },
  followUpsEveryone: { ar: 'الكل', en: 'Everyone' },
  followUpsDone: { ar: 'المنجزة', en: 'Done' },
  followUpsSearch: { ar: 'ابحث بالعنوان أو العميل أو رقم الطلب', en: 'Search by title, customer or order number' },
  followUpsEmpty: { ar: 'لا متابعات هنا', en: 'No follow-ups here' },
  followUpsEmptyHint: {
    ar: 'أضف متابعة من صفحة عميل أو من هنا: ما المطلوب، ومن يفعله، ومتى.',
    en: 'Add one from a customer’s page or here: what is to be done, by whom, and when.',
  },
  followUpAdd: { ar: 'إضافة متابعة', en: 'Add follow-up' },
  followUpEdit: { ar: 'تعديل المتابعة', en: 'Edit follow-up' },
  followUpTitle: { ar: 'ما المطلوب؟', en: 'What is to be done?' },
  followUpTitlePlaceholder: { ar: 'مثل: الاتصال بخصوص طلبها القادم', en: 'Such as: call about her next order' },
  followUpNote: { ar: 'تفاصيل', en: 'Details' },
  followUpDue: { ar: 'الموعد', en: 'Due' },
  followUpAssignee: { ar: 'المكلَّف', en: 'Who does it' },
  followUpNobody: { ar: 'لم يُكلَّف أحد', en: 'Nobody yet' },
  followUpOrder: { ar: 'الطلب', en: 'Order' },
  followUpOrderNone: { ar: 'بلا طلب', en: 'No order' },
  followUpAbout: { ar: 'بخصوص', en: 'About' },
  followUpMarkDone: { ar: 'تم', en: 'Mark done' },
  followUpReopen: { ar: 'إعادة فتح', en: 'Reopen' },
  followUpOverdue: { ar: 'متأخرة', en: 'Overdue' },
  followUpOpen: { ar: 'مفتوحة', en: 'Open' },
  followUpDoneBy: { ar: 'أنجزها {name} في {date}', en: 'Done by {name} on {date}' },
  followUpCreatedBy: { ar: 'أضافها {name}', en: 'Added by {name}' },
  followUpDeleteTitle: { ar: 'حذف المتابعة؟', en: 'Delete this follow-up?' },
  followUpDeleteBody: { ar: 'لا يحذفها إلا من أضافها أو المالك. لتسجيل أنها انتهت اضغط «تم» بدلًا من الحذف.', en: 'Only whoever added it, or the owner, can delete it. To record that it is finished, mark it done instead.' },
  followUpsCustomerHint: { ar: 'المفتوحة لهذا العميل', en: 'Open ones for this customer' },
  customerTimeline: { ar: 'سجل العميل', en: 'Timeline' },
  timelineHint: { ar: 'كل ما جرى معه، الأحدث أولًا', en: 'Everything with them, newest first' },
  timelineEmpty: { ar: 'لا شيء بعد', en: 'Nothing yet' },
  timelineJoined: { ar: 'فتح حسابًا', en: 'Opened an account' },
  timelineOrder: { ar: 'طلب {number}', en: 'Order {number}' },
  timelineReturn: { ar: 'طلب إرجاع {number}', en: 'Return {number}' },
  timelineTicket: { ar: 'محادثة دعم {number}', en: 'Support conversation {number}' },
  timelineReview: { ar: 'قيّم {product}', en: 'Reviewed {product}' },
  timelineNote: { ar: 'ملاحظة من {name}', en: 'Note from {name}' },
  timelineFollowUp: { ar: 'متابعة أضافها {name}', en: 'Follow-up added by {name}' },
  timelineMessage: { ar: 'رسالة: {name}', en: 'Message: {name}' },
  timelineOpened: { ar: 'فُتحت', en: 'Opened' },
  timelineClicked: { ar: 'ضغط رابطًا', en: 'Clicked' },
  timelineFailed: { ar: 'لم تصل', en: 'Not delivered' },
  customersToCall: { ar: 'عملاء يستحقون اتصالًا', en: 'Customers to call' },
  customersToCallHint: {
    ar: 'مهددون بالفقد، الأعلى إنفاقًا أولًا — من له متابعة مفتوحة لا يظهر هنا',
    en: 'At risk, biggest spenders first — anyone with an open follow-up is left out',
  },
  customersToCallEmpty: { ar: 'لا أحد الآن — لا عميل مهدد بالفقد بلا متابعة', en: 'Nobody right now — every customer at risk is being followed up' },
  // ─── Each person's overview, and the summary by email (ADR-109) ─────────────
  customize: { ar: 'تخصيص', en: 'Customize' },
  customizeOverview: { ar: 'تخصيص الصفحة الرئيسية', en: 'Customize your overview' },
  customizeOverviewHint: { ar: 'لك وحدك — لا يتغيّر شيء عند زملائك', en: 'Yours only — nothing changes for your colleagues' },
  overviewSections: { ar: 'الأقسام', en: 'Sections' },
  overviewSectionsHint: {
    ar: 'اسحب لترتيبها، وأخفِ ما لا تحتاجه. قسمان جانبيان متتاليان يتشاركان صفًا.',
    en: 'Drag to order them, and hide what you do not need. Two side sections in a row share it.',
  },
  showOnOverview: { ar: 'إظهار في الصفحة الرئيسية', en: 'Show on the overview' },
  resetToDefault: { ar: 'استعادة الترتيب الأصلي', en: 'Back to the default' },
  overviewFigures: { ar: 'الأرقام الرئيسية', en: 'Headline figures' },
  overviewFiguresHint: { ar: 'المبيعات والطلبات والعملاء الجدد، وما ينتظر', en: 'Sales, orders, new customers, and what waits' },
  recentOrdersHint: { ar: 'آخر ما وصل من طلبات', en: 'The latest orders in' },
  overviewVisits: { ar: 'الزيارات', en: 'Visits' },
  overviewVisitsHint: { ar: 'مسار الشراء باختصار: كم زيارة وكم منها وصل كل خطوة', en: 'The funnel in brief: how many came, and how far they went' },
  myFollowUps: { ar: 'متابعاتي', en: 'My follow-ups' },
  myFollowUpsHint: { ar: 'المفتوحة المكلَّف بها، الأقرب موعدًا أولًا', en: 'The open ones given to you, soonest first' },
  myFollowUpsEmpty: { ar: 'لا متابعات مفتوحة لك', en: 'No open follow-ups for you' },
  reportDigest: { ar: 'ملخص المتجر بالبريد', en: 'The store summary by email' },
  reportDigestHint: {
    ar: 'أرقام الفترة مقارنةً بما قبلها، وما ينتظر، والأكثر مبيعًا — الساعة 8 صباحًا. الأسبوعي صباح الأحد عن الأسبوع الماضي، والشهري أول الشهر.',
    en: 'The period’s figures against the one before, what waits, and the best sellers — at 8 in the morning. Weekly on Sunday for the week just ended, monthly on the 1st.',
  },
  reportDigestOff: { ar: 'لا', en: 'Off' },
  reportDigestDaily: { ar: 'يومي', en: 'Daily' },
  reportDigestWeekly: { ar: 'أسبوعي', en: 'Weekly' },
  reportDigestMonthly: { ar: 'شهري', en: 'Monthly' },
  reportDigestTo: { ar: 'يصل إلى {email}.', en: 'It goes to {email}.' },
  reportDigestLast: { ar: 'آخر ملخص عن فترة انتهت {date}.', en: 'The last one covered a period ending {date}.' },
  reportDigestFirst: { ar: 'يصلك الأول خلال ساعة من الحفظ، عن آخر فترة اكتملت.', en: 'The first arrives within the hour of saving, for the latest finished period.' },
  // ─── What customers are worth (ADR-109) ───────────────────────────────────
  reportCustomerValue: { ar: 'قيمة العملاء', en: 'Customer value' },
  reportCustomerValueHint: {
    ar: 'العملاء الذين كان أول طلب لهم في الفترة، وكل ما أنفقوه منذ ذلك',
    en: 'The customers whose first order fell in the period, and everything they have spent since',
  },
  customerValueCustomers: { ar: 'عملاء جدد مشترون', en: 'New buying customers' },
  customerValueAverage: { ar: 'متوسط قيمة العميل', en: 'Average customer value' },
  customerValueOrders: { ar: 'طلبات لكل عميل', en: 'Orders per customer' },
  customerValueRepeat: { ar: 'اشتروا مرة أخرى', en: 'Bought again' },
  customerValueBySource: { ar: 'حسب مصدر أول طلب', en: 'By the source of their first order' },
  customerValueBySourceHint: { ar: 'أيّ القنوات تجلب عملاء يستمرون في الشراء', en: 'Which channels bring customers who keep buying' },
  customerValueByStage: { ar: 'حسب مرحلتهم الآن', en: 'By the stage they are in now' },
  customerValueLifetime: { ar: 'إنفاقهم منذ أول طلب', en: 'Spent since their first order' },
  customerValueStagesByMonth: { ar: 'المراحل شهرًا بشهر', en: 'Stages month by month' },
  customerValueStagesByMonthHint: {
    ar: 'العملاء المشترون في كل مرحلة في نهاية كل شهر، آخر 12 شهرًا — لا يتبع الفترة المختارة. تُحسب من الطلبات كما هي اليوم وبإعدادات المراحل الحالية.',
    en: 'Buying customers in each stage at the end of each month, the last 12 — not the chosen period. Worked out from orders as they stand today, under today’s stage settings.',
  },
  customerValueSoFar: { ar: 'حتى الآن', en: 'so far' },
  customerValueNotDrawn: { ar: 'لم يشتروا بعد — لا تظهر في الأعمدة', en: 'not bought yet — not drawn' },
  lastOrderDaysAgo: { ar: 'آخر طلب قبل {days} يومًا', en: 'Last order {days} days ago' },
  // ─── Customer stages and RFM (ADR-103) ────────────────────────────────────
  customerStage: { ar: 'المرحلة', en: 'Stage' },
  customerStagesTitle: { ar: 'العملاء حسب المرحلة', en: 'Customers by stage' },
  customerStagesHint: {
    ar: 'من طلباتهم المُباعة وحدود المراحل في الإعدادات — اضغط مرحلة لعرض عملائها',
    en: 'From their sold orders and the stage settings — press a stage to list its customers',
  },
  stageProspect: { ar: 'مهتم', en: 'Prospect' },
  stageNew: { ar: 'جديد', en: 'New' },
  stageActive: { ar: 'نشط', en: 'Active' },
  stageLoyal: { ar: 'وفيّ', en: 'Loyal' },
  stageAtRisk: { ar: 'مهدد بالفقد', en: 'At risk' },
  stageLost: { ar: 'مفقود', en: 'Lost' },
  stageProspectHint: { ar: 'لم يشترِ بعد', en: 'Has not bought yet' },
  stageNewHint: { ar: 'طلب واحد حديث', en: 'One recent order' },
  stageActiveHint: { ar: 'أكثر من طلب، وآخرها حديث', en: 'More than one order, the last recent' },
  stageLoyalHint: { ar: 'طلبات كثيرة وآخرها حديث', en: 'Many orders, the last recent' },
  stageAtRiskHint: { ar: 'لم يطلب منذ مدة — حان وقت التواصل', en: 'Quiet for a while — time to reach out' },
  stageLostHint: { ar: 'لم يطلب منذ زمن طويل', en: 'Has not ordered in a long time' },
  rfmTitle: { ar: 'مقارنةً ببقية المشترين', en: 'Against your other buyers' },
  rfmHint: { ar: '5 = ضمن أفضل 20% من مشتريك، 1 = ضمن أدنى 20%', en: '5 = in your best 20% of buyers, 1 = in the lowest 20%' },
  rfmRecency: { ar: 'حداثة الشراء', en: 'Bought recently' },
  rfmFrequency: { ar: 'عدد الطلبات', en: 'Orders' },
  rfmMonetary: { ar: 'الإنفاق', en: 'Spend' },
  rfmNone: { ar: 'يُقارن بعد أول طلب', en: 'Compared once they buy' },
  ruleStages: { ar: 'المرحلة: {stages}', en: 'stage: {stages}' },
  ruleStagesLabel: { ar: 'في أي من هذه المراحل', en: 'In any of these stages' },
  ruleBoughtProduct: { ar: 'اشترى منتجًا', en: 'bought a product' },
  ruleConsentLabel: { ar: 'الموافقة على الرسائل التسويقية', en: 'Agreed to marketing' },
  ruleConsentYes: { ar: 'وافق على التسويق', en: 'agreed to marketing' },
  ruleConsentNo: { ar: 'لم يوافق على التسويق', en: 'not agreed to marketing' },
  ruleClickedWithin: { ar: 'ضغط رابطًا في رسالة خلال {days} يومًا', en: 'clicked a mail in {days} days' },
  ruleClickedWithinLabel: { ar: 'ضغط رابطًا في رسالة تسويقية خلال (أيام)', en: 'Clicked a marketing mail within (days)' },
  campaignStagesHint: { ar: 'مرحلتهم يوم الإرسال، لا يوم كتابة الحملة', en: 'Their stage on the day it sends, not the day it was written' },
  customerGroupDeleteTitle: { ar: 'حذف المجموعة؟', en: 'Delete this group?' },
  customerGroupDeleteBody: {
    ar: 'الكوبون المخصص لها يصبح متاحًا للجميع، والحملة المستهدفة لها لا تصل لأحد.',
    en: 'A coupon kept for it becomes anyone’s, and a campaign aimed at it reaches nobody.',
  },
  ruleMinOrdersLabel: { ar: 'طلبات على الأقل', en: 'Orders, at least' },
  ruleMaxOrdersLabel: { ar: 'طلبات على الأكثر', en: 'Orders, at most' },
  ruleMinSpentLabel: { ar: 'إنفاق على الأقل (ريال)', en: 'Spent, at least (SAR)' },
  ruleMaxSpentLabel: { ar: 'إنفاق على الأكثر (ريال)', en: 'Spent, at most (SAR)' },
  ruleLastOrderWithinLabel: { ar: 'طلب خلال (أيام)', en: 'Ordered within (days)' },
  ruleLapsedLabel: { ar: 'لم يطلب منذ (أيام)', en: 'No order for (days)' },
  ruleLapsedHint: { ar: 'ممن اشتروا من قبل', en: 'Among those who bought before' },
  ruleRegisteredWithinLabel: { ar: 'سجّل خلال (أيام)', en: 'Registered within (days)' },
  ruleMinOrders: { ar: '{count}+ طلبات', en: '{count}+ orders' },
  ruleMaxOrders: { ar: 'حتى {count} طلبات', en: 'up to {count} orders' },
  ruleMinSpent: { ar: 'أنفق {amount}+', en: 'spent {amount}+' },
  ruleMaxSpent: { ar: 'أنفق حتى {amount}', en: 'spent up to {amount}' },
  ruleLastOrderWithin: { ar: 'طلب خلال {days} يومًا', en: 'ordered in {days} days' },
  ruleLapsed: { ar: 'لا طلب منذ {days} يومًا', en: 'no order for {days} days' },
  ruleRegisteredWithin: { ar: 'سجّل خلال {days} يومًا', en: 'joined in {days} days' },
  ruleBoughtCategory: { ar: 'اشترى من تصنيف', en: 'bought from a category' },
  campaignGroupHint: { ar: 'أعضاء مجموعة عملاء', en: 'Members of a customer group' },
  discountGroupHint: { ar: 'لأعضاء هذه المجموعة فقط', en: 'Only this group’s members may use it' },
  discountAnyCustomer: { ar: 'أي عميل', en: 'Anyone' },
  customerGroupsOf: { ar: 'المجموعات', en: 'Groups' },
  affiliatesSubtitle: {
    ar: 'من يجلب الطلبات برابطه أو بكوبونه، وما يستحقه، وما دُفع له',
    en: 'Who brings orders with their link or coupon, what they are owed, and what was paid',
  },
  affiliate: { ar: 'المسوّق', en: 'Affiliate' },
  affiliateAdd: { ar: 'إضافة مسوّق', en: 'Add affiliate' },
  affiliateEditHint: {
    ar: 'تُحسب العمولة على ما دُفع للمنتجات، وتُستحق عند التسليم',
    en: 'The commission is on what was paid for the goods, and is owed once delivered',
  },
  affiliatesSearch: { ar: 'اسم أو بريد أو رمز', en: 'Name, email or code' },
  affiliatesEmpty: { ar: 'لا يوجد مسوّقون بعد', en: 'No affiliates yet' },
  affiliatesEmptyHint: {
    ar: 'أضف مسوّقًا ليشارك رابطه أو كوبونه، وتظهر هنا الطلبات التي جلبها وعمولته.',
    en: 'Add one to share their link or coupon; the orders they bring and their commission show here.',
  },
  affiliateStatus_ACTIVE: { ar: 'نشط', en: 'Active' },
  affiliateStatus_PAUSED: { ar: 'موقوف', en: 'Paused' },
  affiliatePausedHint: { ar: 'لا تُنسب إليه طلبات جديدة', en: 'No new orders are credited to them' },
  affiliateCommission: { ar: 'العمولة', en: 'Commission' },
  affiliateCommissionPercent: { ar: 'العمولة (%)', en: 'Commission (%)' },
  affiliateCommissionHint: { ar: 'على الطلبات من الآن', en: 'On orders from now on' },
  affiliateVisits: { ar: 'الزيارات', en: 'Visits' },
  affiliateSales: { ar: 'المبيعات', en: 'Sales' },
  affiliateOwed: { ar: 'المستحق', en: 'Owed now' },
  affiliateOrder: { ar: 'الطلب', en: 'Order' },
  affiliateNone: { ar: 'لا يوجد', en: 'None' },
  affiliateCode: { ar: 'الرمز', en: 'Code' },
  affiliateCodeHint: { ar: 'يظهر في الرابط: ?ref=الرمز', en: 'In the link: ?ref=code' },
  affiliateCoupon: { ar: 'كوبونه', en: 'Their coupon' },
  affiliateCouponHint: { ar: 'الطلب بهذا الكوبون يُنسب إليه', en: 'An order using it is theirs' },
  affiliateAccount: { ar: 'حسابه في المتجر', en: 'Their account' },
  affiliateAccountHint: { ar: 'يرى منه رابطه وطلباته وعمولته', en: 'Where they see their link, orders and commission' },
  affiliateLink: { ar: 'رابط المسوّق', en: 'Their link' },
  affiliatePendingPaid: { ar: 'بانتظار التسليم {pending} · دُفع {paid}', en: '{pending} waiting for delivery · {paid} paid' },
  affiliatePay: { ar: 'تسجيل دفعة', en: 'Record payout' },
  affiliatePayConfirm: { ar: 'تسجيل دفع {amount}', en: 'Record {amount} paid' },
  affiliateCommissions: { ar: 'العمولات', en: 'Commissions' },
  affiliateNoCommissions: { ar: 'لم يجلب طلبات بعد.', en: 'No orders brought yet.' },
  affiliatePayouts: { ar: 'الدفعات', en: 'Payouts' },
  commissionStatus_PENDING: { ar: 'بانتظار التسليم', en: 'Waiting' },
  commissionStatus_APPROVED: { ar: 'مستحقة', en: 'Owed' },
  commissionStatus_PAID: { ar: 'مدفوعة', en: 'Paid' },
  commissionStatus_VOID: { ar: 'ملغاة', en: 'Void' },
  giftCardsSubtitle: {
    ar: 'رموز بقيمة يضيفها العميل إلى رصيده ويدفع بها',
    en: 'Codes with a value a customer adds to their balance and pays with',
  },
  giftCardsIssue: { ar: 'إصدار بطاقات', en: 'Issue gift cards' },
  giftCardsIssueHint: {
    ar: 'بطاقة واحدة أو حتى 100 بالقيمة نفسها. تُعرض الرموز كاملة مرة واحدة بعد الإصدار.',
    en: 'One card, or up to 100 alike. Their codes are shown whole once, right after issuing.',
  },
  giftCardsIssuedTitle: { ar: 'أُصدرت {count} بطاقة', en: '{count} issued' },
  giftCardsIssuedHint: {
    ar: 'انسخ الرموز الآن — لن تظهر كاملة لغيرك بعد ذلك.',
    en: 'Copy the codes now — they are shown whole to nobody else after this.',
  },
  giftCardsCopyAll: { ar: 'نسخ كل الرموز', en: 'Copy all codes' },
  giftCardsActive: { ar: 'بطاقات صالحة', en: 'Cards still valid' },
  giftCardsOutstanding: { ar: 'قيمتها غير المستخدمة', en: 'Owed on them' },
  giftCardsOutstandingHint: { ar: 'التزام على المتجر حتى تُستخدم', en: 'A liability until they are redeemed' },
  giftCardsAll: { ar: 'الكل', en: 'All' },
  giftCardsSearch: { ar: 'آخر 4 أحرف من الرمز، أو بريد أو اسم', en: 'Last four of the code, or an email or name' },
  giftCardsEmpty: { ar: 'لا توجد بطاقات هدايا', en: 'No gift cards yet' },
  giftCardsEmptyHint: {
    ar: 'أصدر بطاقة لعميل أو لمسابقة أو لتطبعها في المعرض.',
    en: 'Issue one for a customer, a giveaway, or to print for the showroom.',
  },
  giftCardStatus_ACTIVE: { ar: 'صالحة', en: 'Active' },
  giftCardStatus_REDEEMED: { ar: 'مستخدمة', en: 'Redeemed' },
  giftCardStatus_VOID: { ar: 'ملغاة', en: 'Void' },
  giftCardCode: { ar: 'الرمز', en: 'Code' },
  giftCardValue: { ar: 'القيمة', en: 'Value' },
  giftCardValueSar: { ar: 'القيمة (ريال)', en: 'Value (SAR)' },
  giftCardValueHint: { ar: 'من 1 إلى 10,000 للبطاقة', en: '1 to 10,000 a card' },
  giftCardQuantity: { ar: 'العدد', en: 'How many' },
  giftCardQuantityHint: { ar: 'حتى 100 بالقيمة نفسها', en: 'Up to 100 alike' },
  giftCardExpires: { ar: 'آخر يوم للاستخدام', en: 'Last day to redeem' },
  giftCardExpiresHint: { ar: 'اتركه فارغًا لبطاقة لا تنتهي', en: 'Leave empty for a card that never ends' },
  giftCardNeverExpires: { ar: 'لا تنتهي', en: 'Never ends' },
  giftCardRecipientName: { ar: 'اسم المُهدى إليه', en: 'Who it is for' },
  giftCardRecipientEmail: { ar: 'بريده', en: 'Their email' },
  giftCardRecipientEmailHint: { ar: 'يُرسل إليه الرمز مع الرسالة — لبطاقة واحدة', en: 'Mailed the code with the message — one card only' },
  giftCardMessage: { ar: 'الرسالة', en: 'Message' },
  giftCardFor: { ar: 'لمن', en: 'For' },
  giftCardIssued: { ar: 'أُصدرت', en: 'Issued' },
  giftCardRedeemed: { ar: 'استُخدمت', en: 'Redeemed' },
  giftCardVoided: { ar: 'أُلغيت', en: 'Voided' },
  giftCardVoid: { ar: 'إلغاء البطاقة', en: 'Void card' },
  giftCardVoidReason: { ar: 'سبب الإلغاء', en: 'Why it is voided' },
  giftCardVoidHint: { ar: 'تتوقف البطاقة عن العمل فورًا', en: 'The card stops working at once' },
  giftCardResend: { ar: 'إعادة الإرسال', en: 'Send again' },
  giftCardSent: { ar: 'أُرسلت البطاقة إلى المُهدى إليه.', en: 'The card was mailed to its recipient.' },
  storeCredit: { ar: 'الرصيد', en: 'Store credit' },
  storeCreditHint: {
    ar: 'من المبالغ المستردة والاسترداد النقدي، ويُخصم أولًا عند الدفع',
    en: 'From refunds and cashback; spent first at checkout',
  },
  walletAdjust: { ar: 'تعديل الرصيد', en: 'Change balance' },
  walletAdd: { ar: 'إضافة رصيد', en: 'Add credit' },
  walletTake: { ar: 'خصم رصيد', en: 'Take credit' },
  walletNoteHint: { ar: 'يظهر في سجل الرصيد بجانب الحركة', en: 'Kept on the ledger beside the movement' },
  walletNoMovements: { ar: 'لا توجد حركات على الرصيد بعد.', en: 'No movements yet.' },
  walletKind_ADJUSTMENT: { ar: 'تعديل من المتجر', en: 'Changed by the store' },
  walletKind_REFUND: { ar: 'استرداد', en: 'Refund' },
  walletKind_CASHBACK: { ar: 'استرداد نقدي', en: 'Cashback' },
  walletKind_CASHBACK_REVERSED: { ar: 'استرجاع الاسترداد النقدي', en: 'Cashback taken back' },
  walletKind_REFERRAL: { ar: 'شكر على دعوة صديق', en: 'Thanks for inviting a friend' },
  discountFirstOrderOnly: { ar: 'للطلب الأول فقط', en: 'First order only' },
  discountFirstOrderOnlyHint: { ar: 'لمن لم يشترِ من قبل — بالحساب أو بالبريد', en: 'For someone who has not bought before — by account or email' },
  walletKind_REFERRAL_REVERSED: { ar: 'استرجاع شكر الدعوة', en: 'Invitation thanks taken back' },
  walletKind_GIFT_CARD: { ar: 'بطاقة هدية', en: 'Gift card' },
  walletKind_POINTS: { ar: 'استبدال نقاط', en: 'Points exchanged' },
  loyaltyPoints: { ar: 'نقاط الولاء', en: 'Loyalty points' },
  loyaltyWorth: { ar: 'تساوي {amount} رصيدًا', en: 'Worth {amount} in store credit' },
  loyaltyAdjust: { ar: 'تعديل النقاط', en: 'Change points' },
  loyaltyAdd: { ar: 'إضافة نقاط', en: 'Add points' },
  loyaltyTake: { ar: 'خصم نقاط', en: 'Take points' },
  loyaltyPointsLabel: { ar: 'النقاط', en: 'Points' },
  loyaltyKind_ORDER: { ar: 'طلب مُسلَّم', en: 'Delivered order' },
  loyaltyKind_ORDER_REVERSED: { ar: 'استرجاع نقاط طلب', en: 'Order refunded' },
  loyaltyKind_REVIEW: { ar: 'تقييم منشور', en: 'Published review' },
  loyaltyKind_REVIEW_REVERSED: { ar: 'سحب تقييم', en: 'Review withdrawn' },
  loyaltyKind_REFERRAL: { ar: 'شكر على دعوة صديق', en: 'Thanks for inviting a friend' },
  loyaltyKind_BIRTHDAY: { ar: 'هدية عيد الميلاد', en: 'Birthday gift' },
  tierSpend: { ar: 'إنفاق 12 شهرًا', en: 'Spend in 12 months' },
  tierRate: { ar: 'النسبة ٪', en: 'Rate %' },
  tierAdd: { ar: 'إضافة مستوى', en: 'Add a tier' },
  loyaltyTierLine: { ar: 'المستوى: {tier} — يكسب {rate}٪', en: 'Tier: {tier} — earns {rate}%' },
  loyaltyNextTier: { ar: '{amount} أخرى في 12 شهرًا تبلغ «{tier}»', en: '{amount} more in 12 months reaches {tier}' },
  customerBirthday: { ar: 'عيد الميلاد', en: 'Birthday' },
  loyaltyKind_REFERRAL_REVERSED: { ar: 'استرجاع شكر الدعوة', en: 'Invitation thanks taken back' },
  loyaltyKind_CONVERTED: { ar: 'استُبدلت برصيد', en: 'Exchanged for credit' },
  loyaltyKind_ADJUSTMENT: { ar: 'تعديل من المتجر', en: 'Changed by the store' },
  walletKind_SPEND: { ar: 'دُفع في طلب', en: 'Spent on an order' },
  walletKind_SPEND_RETURNED: { ar: 'أُعيد من طلب', en: 'Returned from an order' },
  payManualHint: { ar: 'تحويل بنكي أو نقدًا في المعرض — يبدأ الطلب مدفوعًا', en: 'Bank transfer or cash at the showroom — the order starts paid' },
  payManualNeedsAdmin: { ar: 'يحتاج صلاحية إدارة الطلبات', en: 'Needs the orders:admin permission' },
  payLink: { ar: 'رابط دفع', en: 'Payment link' },
  payLinkHint: { ar: 'يدفع العميل بالبطاقة من رابط ترسله له', en: 'The customer pays by card from a link you send' },
  payLinkUnavailable: { ar: 'يحتاج مزوّد دفع إلكتروني مفعّلًا', en: 'Needs an online payment provider switched on' },
  payLinkAfter: { ar: 'يظهر رابط الدفع في صفحة الطلب لترسله للعميل.', en: 'The payment link shows on the order, ready to send to the customer.' },
  receivedBy: { ar: 'كيف وصل المبلغ', en: 'How it was paid' },
  receivedReferenceHint: { ar: 'رقم التحويل أو رقم الإيصال', en: 'The transfer reference or receipt number' },
  notesAndBilling: { ar: 'الملاحظات والفوترة', en: 'Notes and billing' },
  customerNoteHint: { ar: 'يراها العميل أيضًا — هدية، ملاحظة للخياط', en: 'The customer sees it too — a gift message, a note for the tailor' },
  staffNoteOnOrder: { ar: 'ملاحظة للفريق', en: 'Note for the team' },
  billABusiness: { ar: 'فاتورة باسم منشأة', en: 'Bill a business' },
  sendConfirmation: { ar: 'إرسال التأكيد للعميل', en: 'Send the customer a confirmation' },
  sendConfirmationHint: { ar: 'بريد، ورسالة نصية إن كانت مفعّلة', en: 'Email, and a text where texts are on' },
  orderSummary: { ar: 'ملخص الطلب', en: 'Order summary' },
  discountCode: { ar: 'كوبون الخصم', en: 'Discount code' },
  placeOrder: { ar: 'إنشاء الطلب', en: 'Place order' },
  placeOrderFor: { ar: 'إنشاء الطلب — {amount}', en: 'Place order — {amount}' },
  district: { ar: 'الحي', en: 'District' },
  postalCode: { ar: 'الرمز البريدي', en: 'Postal code' },
  shortCode: { ar: 'العنوان الوطني المختصر', en: 'National address short code' },
  directions: { ar: 'إرشادات للمندوب', en: 'Directions for the courier' },
  unitPrice: { ar: 'سعر الوحدة', en: 'Unit price' },
  priceKept: { ar: 'بسعر الشراء', en: 'price as bought' },
  noLinesYet: { ar: 'لم تُضف منتجات بعد.', en: 'No items yet.' },
  addProduct: { ar: 'إضافة منتج…', en: 'Add a product…' },
  searchSkuOrName: { ar: 'ابحث بالاسم أو رمز المنتج أو الباركود', en: 'Search by name, SKU or barcode' },
  editDetails: { ar: 'تعديل البيانات', en: 'Edit details' },
  editDetailsTitle: { ar: 'تعديل بيانات الطلب', en: 'Edit the order’s details' },
  editDetailsBody: {
    ar: 'التواصل والملاحظة والفوترة — دون تغيير المبلغ. يُسجَّل في السجل.',
    en: 'Contact, note and billing — never the amount. Written on the timeline.',
  },
  editItems: { ar: 'تعديل المنتجات', en: 'Edit items' },
  editItemsTitle: { ar: 'تعديل منتجات الطلب', en: 'Edit the order’s items' },
  editItemsBody: {
    ar: 'لطلب الدفع عند الاستلام قبل شحنه: تبقى أسعار المنتجات المشتراة، وتُسعَّر الجديدة الآن، ويتغيّر المبلغ المطلوب تحصيله ويُبلَّغ العميل.',
    en: 'For a cash order before it ships: lines already bought keep their price, new ones are priced now, the cash to collect follows, and the customer is told.',
  },
  editItemsLocked: {
    ar: 'لا تتغير المنتجات بعد الشحن أو بعد الدفع أو إصدار الفاتورة — استخدم الاسترداد أو ألغِ الطلب وأنشئ آخر.',
    en: 'Items cannot change once the order has shipped, been paid or invoiced — refund, or cancel it and place another.',
  },
  editReason: { ar: 'السبب', en: 'Why' },
  newTotal: { ar: 'الإجمالي الجديد', en: 'New total' },
  collectMore: { ar: 'تحصيل {amount} إضافية', en: '{amount} more to collect' },
  collectLess: { ar: 'تحصيل {amount} أقل', en: '{amount} less to collect' },
  removeBilling: { ar: 'بلا فوترة منفصلة', en: 'No separate billing' },
  paymentLink: { ar: 'رابط الدفع', en: 'Payment link' },
  copyLink: { ar: 'نسخ الرابط', en: 'Copy link' },
  cancelOrder: { ar: 'إلغاء الطلب', en: 'Cancel order' },
  cancelOrderHint: {
    ar: 'لا يُحذف طلب: الإلغاء يعيد المخزون والكوبون ويُبقيه في السجلات برقمه.',
    en: 'An order is never deleted: cancelling returns the stock and the coupon, and keeps it in the books with its number.',
  },

  // What the timeline says about each moment, beside its name (lib/order-timeline.ts).
  timelineCashCollected: { ar: 'تحصيل نقدي عند التسليم · {amount}', en: 'Cash collected on handover · {amount}' },
  timelinePaidByHand: { ar: 'سُجّل مدفوعًا يدويًا · {amount}', en: 'Recorded as paid by hand · {amount}' },
  timelineRefund: { ar: 'استُرد {amount}', en: '{amount} refunded' },
  timelineRefundFor: { ar: 'عن {number}', en: 'for {number}' },
  timelineSettledBy: { ar: 'دُفع {method}', en: 'paid back by {method}' },
  timelineCarrier: { ar: '{carrier} · {number}', en: '{carrier} · {number}' },
  timelineRestocked: { ar: 'أُعيد المخزون', en: 'stock put back' },
  timelineTo: { ar: 'إلى {to}', en: 'to {to}' },
  actorSystem: { ar: 'النظام', en: 'System' },
  actorWebhook: { ar: 'مزوّد الدفع', en: 'Payment provider' },
  actorGuest: { ar: 'العميل (زائر)', en: 'Customer (guest)' },
  actorUnknown: { ar: 'حساب محذوف', en: 'Erased account' },
  // The customer messages, by what they say (the email templates' keys).
  'mail.order.placed.cod': { ar: 'تأكيد الطلب', en: 'Order confirmation' },
  'mail.order.placed.unpaid': { ar: 'بانتظار الدفع', en: 'Awaiting payment' },
  'mail.order.paid': { ar: 'تم استلام الدفع', en: 'Payment received' },
  'mail.order.shipped': { ar: 'إشعار الشحن', en: 'Shipping notice' },
  'mail.order.updated': { ar: 'تعديل الطلب', en: 'Order changed' },
  'mail.order.ready_for_pickup': { ar: 'جاهز للاستلام', en: 'Ready to collect' },
  'mail.order.delivered': { ar: 'تم التوصيل', en: 'Delivered' },
  'mail.order.cancelled': { ar: 'إلغاء الطلب', en: 'Cancellation' },
  'mail.order.refunded': { ar: 'رد المبلغ', en: 'Refund' },
  'mail.return.approved': { ar: 'قبول الإرجاع', en: 'Return approved' },
  'mail.return.rejected': { ar: 'رفض الإرجاع', en: 'Return declined' },
  'mail.return.refunded': { ar: 'رد مبلغ الإرجاع', en: 'Return refunded' },
  codFee: { ar: 'رسوم الدفع عند الاستلام', en: 'Cash-on-delivery fee' },
  linePaid: { ar: 'المدفوع بعد الخصومات {amount}', en: 'Paid {amount} after offers' },
  paymentStatus: { ar: 'حالة الدفع', en: 'Payment status' },
  paymentState_PENDING: { ar: 'بانتظار الدفع', en: 'Awaiting payment' },
  paymentState_PENDING_COD: { ar: 'لم يُحصَّل بعد', en: 'Cash not collected yet' },
  paymentState_SUCCEEDED: { ar: 'مدفوع', en: 'Paid' },
  paymentState_SUCCEEDED_COD: { ar: 'حُصِّل نقدًا', en: 'Cash collected' },
  paymentState_PARTIALLY_REFUNDED: { ar: 'مسترد جزئيًا', en: 'Partly refunded' },
  paymentState_REFUNDED: { ar: 'مسترد', en: 'Refunded' },
  paymentState_FAILED: { ar: 'فشل', en: 'Failed' },
  paymentState_CANCELLED: { ar: 'ملغى — لم يُدفع شيء', en: 'Cancelled — no money moved' },
  codCollectsOnDelivery: {
    ar: 'يُسجَّل التحصيل تلقائيًا عند تحديد الطلب «تم التوصيل» أو «تم الاستلام».',
    en: 'Collection is recorded when the order is marked delivered or collected.',
  },
  orderReturns: { ar: 'المرتجعات', en: 'Returns' },
  refundOrder: { ar: 'استرداد مبلغ', en: 'Refund' },
  refundOrderTitle: { ar: 'استرداد مبلغ من هذا الطلب', en: 'Refund money on this order' },
  refundOrderBody: {
    ar: 'دون إرجاع: لفتة لطيفة، أو تعديل سعر، أو شحن تأخر. يعود المبلغ عبر مزوّد الدفع؛ والدفع عند الاستلام يُرد يدويًا.',
    en: 'Without a return: goodwill, a price adjusted, a late delivery. It goes back through the payment provider; a cash order is paid back by hand.',
  },
  refundAmount: { ar: 'المبلغ (ريال)', en: 'Amount (SAR)' },
  refundLeft: { ar: 'المتبقي القابل للاسترداد {amount}', en: 'Up to {amount} is left to refund' },
  refundWhy: { ar: 'السبب', en: 'Why' },
  refundMethodLabel: { ar: 'كيف أُعيد المبلغ', en: 'How the money went back' },
  refundMethodBANK_TRANSFER: { ar: 'تحويل بنكي', en: 'Bank transfer' },
  refundMethodCASH: { ar: 'نقدًا', en: 'Cash' },
  refundMethodOTHER: { ar: 'طريقة أخرى', en: 'Other' },
  refundReference: { ar: 'المرجع', en: 'Reference' },
  refundReferenceHint: {
    ar: 'رقم التحويل، أو آخر أرقام الآيبان، أو رقم الإيصال',
    en: 'The transfer reference, the last digits of the IBAN, a receipt number',
  },
  refundRestockAll: { ar: 'إعادة كل المنتجات للبيع', en: 'Put every line back on sale' },
  refundRestockAllHint: {
    ar: 'للاسترداد الكامل فقط، وفقط إن عادت البضاعة وهي صالحة للبيع',
    en: 'A whole refund only — and only if the goods are back and fit to sell',
  },
  confirmRefundAmount: { ar: 'استرداد {amount}', en: 'Refund {amount}' },
  carrierChoose: { ar: 'اختر شركة الشحن', en: 'Choose a carrier' },
  carrierOther: { ar: 'شركة أخرى…', en: 'Another carrier…' },
  carrierName: { ar: 'اسم شركة الشحن', en: 'Carrier name' },
  trackingUrlAuto: { ar: 'يُبنى من صفحة تتبع شركة الشحن', en: 'Built from the carrier’s tracking page' },
  colItems: { ar: 'المنتجات', en: 'Items' },
  colPayment: { ar: 'الدفع', en: 'Payment' },
  colDelivery: { ar: 'التسليم', en: 'Delivery' },
  deliveryShip: { ar: 'توصيل', en: 'Delivery' },
  noEmail: { ar: 'بلا بريد — سجّل برقم الجوال', en: 'No email — signed up with a mobile' },
  reachSplit: {
    ar: '({accounts} حسابًا، {subscribers} مشتركًا في النشرة)',
    en: '({accounts} accounts, {subscribers} newsletter subscribers)',
  },
  exportCustomerData: { ar: 'تصدير بياناته', en: 'Export their data' },
  eraseCustomer: { ar: 'حذف العميل', en: 'Erase customer' },
  eraseCustomerTitle: { ar: 'حذف هذا العميل نهائيًا؟', en: 'Erase this customer for good?' },
  eraseCustomerBody: {
    ar: 'بطلب منه فقط. تبقى طلباته وإرجاعاته لحسابات المتجر دون اسمه وبريده وجواله وعنوانه، وتبقى الفواتير كما صدرت. يُحذف الحساب وكل ما سواه. يُرفض إن كان له طلب في الطريق أو إرجاع مفتوح.',
    en: 'Only on their request. Their orders and returns stay for the books without their name, email, phone or address, and invoices stay as issued. The account and everything else is deleted. Refused while an order is on its way or a return is open.',
  },
  nextStep: { ar: 'الخطوة التالية', en: 'Next step' },
  nothingFurther: { ar: 'لا يوجد إجراء إضافي على هذا الطلب.', en: 'Nothing further to do on this order.' },
  markAs: { ar: 'تحديد كـ', en: 'Mark as' },
  subtotal: { ar: 'المجموع الفرعي', en: 'Subtotal' },
  discount: { ar: 'الخصم', en: 'Discount' },
  shipping: { ar: 'الشحن', en: 'Shipping' },
  vatIncluded: { ar: 'شامل {amount} ضريبة القيمة المضافة', en: 'Includes {amount} VAT' },
  deliveryAddress: { ar: 'عنوان التوصيل', en: 'Delivery address' },
  paymentMethod: { ar: 'طريقة الدفع', en: 'Payment method' },
  paymentCod: { ar: 'الدفع عند الاستلام', en: 'Cash on delivery' },
  paymentCard: { ar: 'بطاقة', en: 'Card' },
  shipment: { ar: 'الشحنة', en: 'Shipment' },
  carrier: { ar: 'شركة الشحن', en: 'Carrier' },
  trackingNumber: { ar: 'رقم التتبع', en: 'Tracking number' },
  trackingUrl: { ar: 'رابط التتبع', en: 'Tracking URL' },
  optional: { ar: 'اختياري', en: 'Optional' },
  reason: { ar: 'السبب', en: 'Reason' },
  shipDialogTitle: { ar: 'شحن هذا الطلب', en: 'Ship this order' },
  shipDialogBody: { ar: 'تُرسل بيانات التتبع للعميل وتظهر في صفحة طلبه.', en: 'Tracking details go to the customer and appear on their order.' },
  cancelDialogTitle: { ar: 'إلغاء هذا الطلب', en: 'Cancel this order' },
  cancelDialogBody: { ar: 'الإلغاء يعيد المخزون ويحرّر أي كوبون مستخدم.', en: 'Cancelling returns the stock and releases any coupon used.' },
  confirmShipment: { ar: 'تأكيد الشحن', en: 'Confirm shipment' },
  confirmCancellation: { ar: 'تأكيد الإلغاء', en: 'Confirm cancellation' },
  markPaidDialogTitle: { ar: 'تسجيل الطلب مدفوعًا', en: 'Mark this order paid' },
  markPaidDialogBody: {
    ar: 'لدفعة استُلمت خارج بوابة الدفع — تحويل مثلًا. اذكر كيف وصلت، فيبقى ذلك في سجل التدقيق.',
    en: 'For a payment taken outside the gateway — a transfer, say. Say how it arrived; the audit log keeps it.',
  },
  confirmMarkPaid: { ar: 'تسجيله مدفوعًا', en: 'Mark paid' },
  confirmStatusTitle: { ar: 'تأكيد تغيير الحالة', en: 'Confirm status change' },
  confirmStatusBody: { ar: 'يُسجَّل هذا على الطلب ويُبلَّغ العميل.', en: 'This is recorded on the order and the customer is notified.' },
  reasonHint: { ar: 'يُسجَّل في السجل الزمني للطلب', en: 'Recorded on the order timeline' },

  // ─── Working an order (ADR-059) ───────────────────────────────────────────
  'event.order.note': { ar: 'ملاحظة', en: 'Note' },
  'event.order.address_changed': { ar: 'صُحّح عنوان الشحن', en: 'Shipping address corrected' },
  'event.email.failed': { ar: 'تعذّر إرسال بريد', en: 'Email failed' },
  'event.sms.sent': { ar: 'أُرسلت رسالة نصية', en: 'Text message sent' },
  changeStatus: { ar: 'تغيير الحالة', en: 'Change status' },
  changeStatusTitle: { ar: 'تغيير حالة الطلب', en: 'Change the order status' },
  changeStatusBody: {
    ar: 'تظهر هنا الحالات المسموح بها من حالة الطلب الآن فقط.',
    en: 'Only the moves the order can make from where it is now.',
  },
  newStatus: { ar: 'الحالة الجديدة', en: 'New status' },
  statusNote: { ar: 'ملاحظة على التغيير', en: 'Note on this change' },
  statusNoteHint: {
    ar: 'اختيارية. تبقى في سجل الطلب للفريق، ولا يراها العميل.',
    en: 'Optional. Kept on the timeline for the team; the customer never sees it.',
  },
  statusNoteRequiredHint: {
    ar: 'مطلوبة هنا — اذكر السبب، فيبقى في السجل وسجل التدقيق.',
    en: 'Required here — say why; the timeline and the audit log keep it.',
  },
  notifyCustomer: { ar: 'إبلاغ العميل', en: 'Tell the customer' },
  notifyCustomerHint: {
    ar: 'يصله البريد والرسالة النصية الخاصة بهذه الحالة.',
    en: 'They get the email and text that go with this status.',
  },
  noCustomerMessage: {
    ar: 'لا يُرسل للعميل شيء عند هذه الحالة.',
    en: 'This status sends the customer nothing.',
  },
  restockItems: { ar: 'إعادة المنتجات إلى المخزون', en: 'Put the items back in stock' },
  restockItemsHint: {
    ar: 'ألغِ التحديد إن كانت تالفة أو مفقودة.',
    en: 'Untick it when they are damaged or gone.',
  },
  saveStatus: { ar: 'حفظ الحالة', en: 'Save status' },
  progress_placed: { ar: 'تم الطلب', en: 'Placed' },
  progress_paid: { ar: 'الدفع', en: 'Paid' },
  progress_paid_cod: { ar: 'التأكيد', en: 'Confirmed' },
  progress_processing: { ar: 'التجهيز', en: 'Preparing' },
  progress_sent: { ar: 'الشحن', en: 'Shipped' },
  progress_sent_pickup: { ar: 'جاهز للاستلام', en: 'Ready to collect' },
  progress_delivered: { ar: 'التسليم', en: 'Delivered' },
  progress_delivered_pickup: { ar: 'الاستلام', en: 'Collected' },
  orderNotePlaceholder: { ar: 'اكتب ملاحظة للفريق…', en: 'Write a note for the team…' },
  orderNotesStaffOnly: { ar: 'لا يرى العميل الملاحظات', en: 'Only staff see notes' },
  eventFromTo: { ar: '{from} ← {to}', en: '{from} → {to}' },
  eventNotified: { ar: 'أُبلغ العميل', en: 'Customer notified' },
  eventNotNotified: { ar: 'دون إبلاغ العميل', en: 'Customer not notified' },
  eventRestocked: { ar: 'أُعيد المخزون', en: 'Stock returned' },
  eventNotRestocked: { ar: 'لم يُعد المخزون', en: 'Stock kept out' },
  eventTo: { ar: 'إلى {to}', en: 'To {to}' },
  actor_system: { ar: 'النظام', en: 'System' },
  actor_webhook: { ar: 'بوابة الدفع', en: 'Payment provider' },
  actor_guest: { ar: 'العميل', en: 'Customer' },
  actorCustomer: { ar: 'العميل', en: 'Customer' },
  actorFormerStaff: { ar: 'عضو سابق في الفريق', en: 'Former team member' },
  customerOrderCount: { ar: '{count} طلبات', en: '{count} orders' },
  customerFirstOrder: { ar: 'أول طلب', en: 'First order' },
  customerSpent: { ar: 'أنفق {amount}', en: '{amount} spent' },
  viewCustomer: { ar: 'عرض العميل', en: 'View customer' },
  guestCustomer: { ar: 'ضيف', en: 'Guest' },
  contactWhatsapp: { ar: 'واتساب', en: 'WhatsApp' },
  contactCall: { ar: 'اتصال', en: 'Call' },
  contactEmail: { ar: 'مراسلة', en: 'Email' },
  copied: { ar: 'نُسخ', en: 'Copied' },
  copyAddress: { ar: 'نسخ العنوان', en: 'Copy address' },
  editAddress: { ar: 'تعديل العنوان', en: 'Edit address' },
  editAddressTitle: { ar: 'تصحيح عنوان الشحن', en: 'Correct the shipping address' },
  editAddressBody: {
    ar: 'قبل خروج الطلب فقط. تبقى المدينة كما هي لأنها حدّدت سعر الشحن، ويُحفظ العنوان القديم في السجل.',
    en: 'Before the order leaves. The city stays — it priced the delivery — and the old address is kept on the timeline.',
  },
  addressCityFixed: { ar: 'المدينة: {city}، {region}', en: 'City: {city}, {region}' },
  addressPhone: { ar: 'الجوال', en: 'Mobile' },
  addressLine1: { ar: 'الشارع والمبنى', en: 'Street and building' },
  addressLine2: { ar: 'تفاصيل إضافية', en: 'More detail' },
  addressDistrict: { ar: 'الحي', en: 'District' },
  addressShortCode: { ar: 'الرمز المختصر', en: 'Short code' },
  addressShortCodeHint: { ar: 'أربعة أحرف وأربعة أرقام، مثل RRRD2929', en: 'Four letters and four digits, like RRRD2929' },
  addressDirections: { ar: 'إرشادات للمندوب', en: 'Directions for the courier' },
  addressPostalCode: { ar: 'الرمز البريدي', en: 'Postal code' },
  paymentStatus_PENDING: { ar: 'بانتظار التأكيد', en: 'Pending' },
  paymentStatus_SUCCEEDED: { ar: 'ناجح', en: 'Succeeded' },
  paymentStatus_FAILED: { ar: 'فشل', en: 'Failed' },
  paymentStatus_REFUNDED: { ar: 'مُسترد', en: 'Refunded' },
  paymentStatus_PARTIALLY_REFUNDED: { ar: 'مُسترد جزئيًا', en: 'Partly refunded' },
  paymentProvider: { ar: 'مزوّد الدفع', en: 'Provider' },
  paymentReference: { ar: 'مرجع العملية', en: 'Reference' },
  paymentRefunded: { ar: 'المبلغ المسترد', en: 'Refunded' },
  paymentFee: { ar: 'رسوم الدفع عند الاستلام', en: 'Cash on delivery fee' },
  promotionSavings: { ar: 'العروض', en: 'Offers' },
  moreActions: { ar: 'إجراءات أخرى', en: 'More actions' },
  resendNotification: { ar: 'إعادة إرسال: {name}', en: 'Send again: {name}' },
  'notify_order.placed': { ar: 'تأكيد الطلب', en: 'Order confirmation' },
  'notify_order.paid': { ar: 'تأكيد الدفع', en: 'Payment confirmation' },
  'notify_order.shipped': { ar: 'إشعار الشحن', en: 'Shipping notice' },
  'notify_order.ready_for_pickup': { ar: 'جاهز للاستلام', en: 'Ready for pickup' },
  'notify_order.cancelled': { ar: 'إشعار الإلغاء', en: 'Cancellation notice' },
  'notify_order.digital': { ar: 'الملفات والأكواد', en: 'Downloads and codes' },
  // Things that arrive by link (ADR-077).
  productKind: { ar: 'ما الذي يصل للمشتري', en: 'What the buyer receives' },
  productKindChanged: {
    ar: 'احفظ لتغيير النوع — يُحسب المخزون بالطريقة الجديدة من بعدها',
    en: 'Save to change it — stock is counted the new way from then on',
  },
  kindPhysical: { ar: 'منتج يُشحن', en: 'A parcel' },
  kindPhysicalHint: { ar: 'عنوان وشركة شحن', en: 'An address and a courier' },
  kindDownload: { ar: 'ملفات للتنزيل', en: 'Files to download' },
  kindDownloadHint: { ar: 'روابط تصل فور الدفع، دون مخزون', en: 'Links sent on payment; no stock to count' },
  kindCode: { ar: 'كود أو بطاقة', en: 'A code or card' },
  kindCodeHint: { ar: 'كود لكل قطعة من مخزون الأكواد', en: 'One code per unit, from its pool' },
  kindGiftCard: { ar: 'بطاقة هدية المتجر', en: 'The store’s gift card' },
  kindGiftCardHint: {
    ar: 'تُصدر بطاقة لكل قطعة عند الدفع بقيمة سعرها؛ تُشترى وحدها ودون ضريبة',
    en: 'A card per unit, issued on payment and worth its price; bought on its own, without VAT',
  },
  giftCardProductHint: {
    ar: 'لا ملفات ولا أكواد هنا: تُصدر بطاقة لكل قطعة عند الدفع، بقيمة سعر خيارها. اجعل كل قيمة خيارًا (100، 200، 500…). تظهر البطاقات في صفحة بطاقات الهدايا.',
    en: 'Nothing to upload or paste: a card is issued per unit on payment, worth its variant’s price. Make each amount a variant (100, 200, 500…). The cards appear under Gift cards.',
  },
  tabDigital: { ar: 'التسليم الرقمي', en: 'Digital delivery' },
  digitalSaveKindFirst: { ar: 'احفظ نوع المنتج أولًا، ثم أضف الملفات أو الأكواد.', en: 'Save what the product is first, then add its files or codes.' },
  notCounted: { ar: 'غير محسوب', en: 'Not counted' },
  codesLeft: { ar: '{count} كود', en: '{count} codes' },
  digitalStockCodeHint: { ar: 'المخزون هو الأكواد المتبقية — أضفها من تبويب التسليم الرقمي', en: 'Stock is the codes left — add them on the Digital delivery tab' },
  digitalStockDownloadHint: { ar: 'الملفات لا تنفد', en: 'Files never run out' },
  digitalFiles: { ar: 'الملفات', en: 'Files' },
  digitalFilesHint: {
    ar: 'يصل المشتري رابط تنزيل لكل ملف بعد الدفع — خاصة، لا تظهر في المتجر',
    en: 'The buyer gets a download link for each after payment — private, never on the store',
  },
  digitalUpload: { ar: 'رفع ملف', en: 'Upload a file' },
  digitalNoFiles: { ar: 'لا ملفات بعد', en: 'No files yet' },
  digitalNoFilesHint: { ar: 'PDF أو ZIP أو MP3 أو MP4 أو صورة، حتى 100 ميغابايت.', en: 'PDF, ZIP, MP3, MP4 or an image, up to 100 MB.' },
  digitalFileName: { ar: 'اسم الملف', en: 'File name' },
  digitalFileSize: { ar: 'الحجم', en: 'Size' },
  digitalFileAdded: { ar: 'أُضيف', en: 'Added' },
  digitalActions: { ar: 'إجراءات', en: 'Actions' },
  digitalRename: { ar: 'إعادة التسمية', en: 'Rename' },
  digitalRenameHint: { ar: 'الاسم الذي يراه المشتري ويحفظ به الملف', en: 'The name the buyer sees and saves it under' },
  digitalDeleteFileTitle: { ar: 'حذف الملف؟', en: 'Delete this file?' },
  digitalDeleteFileBody: {
    ar: 'يختفي لكل من اشتراه، وتتوقف الروابط المرسلة. ارفع نسخة جديدة قبل حذف القديمة.',
    en: 'It goes for everyone who bought it, and links already sent stop working. Upload the new version before deleting the old.',
  },
  digitalCodes: { ar: 'الأكواد', en: 'Codes' },
  digitalCodesHint: {
    ar: 'لكل خيار مخزون أكواده — يصل كود لكل قطعة بعد الدفع، والمخزون هو ما تبقى',
    en: 'Each variant has its own pool — one code per unit goes out on payment, and stock is what is left',
  },
  digitalPool: { ar: 'المخزون', en: 'Pool' },
  digitalAvailable: { ar: 'متاحة', en: 'Available' },
  digitalDelivered: { ar: 'مُسلّمة', en: 'Delivered' },
  digitalEmpty: { ar: 'نفدت', en: 'None left' },
  digitalViewCodes: { ar: 'عرض الأكواد', en: 'View codes' },
  digitalAddCodes: { ar: 'إضافة أكواد', en: 'Add codes' },
  digitalAddCodesHint: { ar: 'الصقها كما وصلتك من المورّد، كودًا في كل سطر', en: 'Paste them as the supplier sent them, one a line' },
  digitalAddCount: { ar: 'إضافة {count}', en: 'Add {count}' },
  digitalAvailableNow: { ar: 'المتاح الآن: {count}', en: '{count} available now' },
  digitalCodesLines: { ar: 'الأكواد', en: 'Codes' },
  digitalCodesLinesHint: { ar: 'المكرر وما سبق إضافته يُتجاهل — حتى 5,000 في المرة', en: 'Repeats and codes already there are skipped — up to 5,000 at a time' },
  digitalAddedResult: { ar: 'أُضيف {added} — وتُجوهل {duplicates} مكرر', en: '{added} added — {duplicates} repeat(s) skipped' },
  digitalCodesOf: { ar: 'أكواد {sku}', en: 'Codes · {sku}' },
  digitalSearchCodes: { ar: 'ابحث في الأكواد', en: 'Search codes' },
  digitalNoAvailable: { ar: 'لا أكواد متاحة', en: 'No codes available' },
  digitalNoDelivered: { ar: 'لم يُسلَّم كود بعد', en: 'No codes delivered yet' },
  digitalDeleteCodeTitle: { ar: 'حذف الكود؟', en: 'Delete this code?' },
  digitalDeleteCodeBody: { ar: 'يخرج من المخزون ولن يُسلَّم لأحد.', en: 'It leaves the pool and goes to nobody.' },
  deliverNow: { ar: 'سلّم الآن', en: 'Deliver now' },
  // Selling what has not arrived yet (ADR-079).
  preorder: { ar: 'الطلب المسبق', en: 'Pre-order' },
  preorderHint: {
    ar: 'يُباع بعد نفاد المخزون، ويُشحن عند وصوله — المخزون بالسالب هو ما يُدين به المتجر',
    en: 'Keeps selling after it runs out, and ships when it arrives — stock below zero is what the store owes',
  },
  preorderOn: { ar: 'اقبل الطلبات المسبقة', en: 'Take pre-orders' },
  preorderShipsOn: { ar: 'يُشحن ابتداءً من', en: 'Ships from' },
  preorderShipsOnHint: { ar: 'يراه العميل على المنتج والسلة والطلب', en: 'The shopper sees it on the product, the basket and the order' },
  preorderLimit: { ar: 'الحد لكل خيار', en: 'Limit per variant' },
  preorderLimitHint: { ar: 'أكثر عدد يُطلب مسبقًا — فارغ: بلا حد', en: 'The most that can be owed — empty: no limit' },
  preorderDateRequired: { ar: 'حدّد تاريخ الشحن للطلب المسبق', en: 'Give the pre-order a ship date' },
  preorderBadge: { ar: 'طلب مسبق', en: 'Pre-order' },
  preorderLine: { ar: 'طلب مسبق · يُشحن من {date}', en: 'Pre-order · ships from {date}' },
  owedUnits: { ar: 'مستحق {count}', en: '{count} owed' },
  deliverNowHint: {
    ar: 'يسلّم ما ينتظر من أكواد ويعيد إرسال الملفات والأكواد للعميل',
    en: 'Hands over any codes still waiting, and mails the downloads and codes to the customer again',
  },
  digitalSentAgain: { ar: 'أُرسلت الملفات والأكواد للعميل', en: 'The downloads and codes went to the customer' },
  digitalHandedOver: { ar: 'سُلّم {when}', en: 'Handed over {when}' },
  digitalWaiting: { ar: 'بانتظار الدفع أو الأكواد', en: 'Waiting for payment or codes' },
  digitalLinksUntil: { ar: 'الروابط حتى {date}', en: 'Links work until {date}' },
  digitalDownloads: { ar: 'نُزّل {used} من {total}', en: 'Downloaded {used} of {total}' },
  digitalNoShipping: { ar: 'لا شحن', en: 'Nothing ships' },
  digitalNoShippingHint: { ar: 'كل ما في الطلب يصل برابط أو كود بعد الدفع.', en: 'Everything on it arrives by link or code after payment.' },
  printPackingSlip: { ar: 'طباعة إيصال التجهيز', en: 'Print packing slip' },
  packingSlip: { ar: 'إيصال التجهيز', en: 'Packing slip' },
  packingSlips: { ar: 'إيصالات التجهيز', en: 'Packing slips' },
  packingSlipShipTo: { ar: 'الشحن إلى', en: 'Ship to' },
  packingSlipQty: { ar: 'الكمية', en: 'Qty' },
  packingSlipPacked: { ar: 'جُهّز', en: 'Packed' },
  print: { ar: 'طباعة', en: 'Print' },
  trackShipment: { ar: 'تتبّع', en: 'Track' },
  itemCustomerInputs: { ar: 'ما كتبه العميل', en: 'What the customer entered' },
  orderItemCount: { ar: '{count} قطعة', en: '{count} items' },
  orderItemOne: { ar: 'قطعة واحدة', en: '1 item' },
  bulkMoveTitleOne: { ar: 'تحديد طلب واحد كـ{status}', en: 'Mark 1 order as {status}' },
  bulkDoneOne: { ar: 'حُدّث طلب واحد', en: '1 order updated' },
  selectAllOrders: { ar: 'تحديد كل الطلبات الظاهرة', en: 'Select every order shown' },
  selectOrder: { ar: 'تحديد الطلب {number}', en: 'Select order {number}' },
  selectedCount: { ar: '{count} محدد', en: '{count} selected' },
  bulkNoCommonMove: { ar: 'لا حالة تصلح لكل الطلبات المحددة', en: 'No move fits every selected order' },
  bulkMoveTitle: { ar: 'تحديد {count} طلبات كـ{status}', en: 'Mark {count} orders as {status}' },
  bulkMoveBody: {
    ar: 'يُطبَّق على كل طلب بقواعده؛ وما يرفضه النظام يبقى كما هو ويُذكر لك.',
    en: 'Each order keeps its own rules; any the system refuses stay as they are and are listed.',
  },
  bulkResult: { ar: 'حُدّث {done}، وتعذّر {failed}', en: '{done} updated, {failed} refused' },
  bulkDone: { ar: 'حُدّثت {done} طلبات', en: '{done} orders updated' },
  exportOrders: { ar: 'تصدير', en: 'Export' },
  exportOrdersHint: { ar: 'الطلبات المطابقة للتصفية الحالية، ملف CSV', en: 'The orders matching this filter, as CSV' },
  paymentColumn: { ar: 'الدفع', en: 'Payment' },
  deliveryColumn: { ar: 'التوصيل', en: 'Delivery' },
  unpaidCod: { ar: 'عند الاستلام', en: 'On delivery' },
  sortOrders: { ar: 'الترتيب', en: 'Sort' },
  sortNewestFirst: { ar: 'الأحدث أولًا', en: 'Newest first' },
  sortOldestFirst: { ar: 'الأقدم أولًا', en: 'Oldest first' },
  shipmentStatus_PENDING: { ar: 'بانتظار الاستلام من المندوب', en: 'Waiting for the courier' },
  shipmentStatus_IN_TRANSIT: { ar: 'في الطريق', en: 'In transit' },
  shipmentStatus_DELIVERED: { ar: 'تم التسليم', en: 'Delivered' },
  shipmentStatus_FAILED: { ar: 'تعذّر التسليم', en: 'Delivery failed' },

  // ─── Products ─────────────────────────────────────────────────────────────
  // The list: its order, its selection, and a row's own actions.
  productsSort: { ar: 'الترتيب', en: 'Sort' },
  productSort_newest: { ar: 'الأحدث أولًا', en: 'Newest first' },
  productSort_bestselling: { ar: 'الأكثر مبيعًا', en: 'Best-selling' },
  productSort_rating: { ar: 'الأعلى تقييمًا', en: 'Best-rated' },
  productSort_price_asc: { ar: 'السعر: من الأقل', en: 'Price: low to high' },
  productSort_price_desc: { ar: 'السعر: من الأعلى', en: 'Price: high to low' },
  productSort_name_asc: { ar: 'الاسم أبجديًا', en: 'Name: A to Z' },
  productsNoMatch: { ar: 'لا منتج يطابق هذه التصفية.', en: 'No product matches these filters.' },
  productRatingTitle: { ar: '{stars} من ٥، من {count} تقييم', en: '{stars} out of 5, from {count} reviews' },
  productNotRated: { ar: 'لا تقييمات بعد', en: 'No reviews yet' },
  productsSelectAll: { ar: 'تحديد كل المنتجات الظاهرة', en: 'Select every product shown' },
  productsSelectOne: { ar: 'تحديد {name}', en: 'Select {name}' },
  productMove_ACTIVE: { ar: 'نشر', en: 'Publish' },
  productMove_DRAFT: { ar: 'إعادة إلى المسودة', en: 'Move to draft' },
  productMove_ARCHIVED: { ar: 'أرشفة', en: 'Archive' },
  productsBulkArchiveTitle: { ar: 'أرشفة {count} منتج؟', en: 'Archive {count} products?' },
  productsBulkArchiveBody: {
    ar: 'تختفي من المتجر وتبقى في الطلبات السابقة. لا يُحذف شيء، ويمكن نشرها من جديد.',
    en: 'They disappear from the store but stay on past orders. Nothing is deleted, and they can be published again.',
  },
  productsBulkDone: { ar: 'حُدّث {done} منتج', en: '{done} products updated' },
  productsViewOnStore: { ar: 'عرضه في المتجر', en: 'View on store' },
  productsSubtitle: { ar: 'إدارة الأصناف والأسعار والمخزون', en: 'Manage items, prices, and stock' },
  newProduct: { ar: 'منتج جديد', en: 'New product' },
  editProduct: { ar: 'تعديل المنتج', en: 'Edit product' },
  searchProducts: { ar: 'ابحث بالاسم أو رمز المنتج', en: 'Search by name or SKU' },
  allCategories: { ar: 'كل الأقسام', en: 'All categories' },
  category: { ar: 'القسم', en: 'Category' },
  noCategory: { ar: 'بدون قسم', en: 'No category' },
  price: { ar: 'السعر', en: 'Price' },
  basePrice: { ar: 'السعر الأساسي', en: 'Base price' },
  basePriceHint: { ar: 'بالريال، شامل الضريبة. تستخدمه المتغيرات ما لم يكن لها سعر خاص.', en: 'In riyals, VAT-inclusive. Variants use it unless they set their own.' },
  stock: { ar: 'المخزون', en: 'Stock' },
  sku: { ar: 'رمز المنتج', en: 'SKU' },
  variants: { ar: 'المتغيرات', en: 'Variants' },
  nameEn: { ar: 'الاسم (إنجليزي)', en: 'Name (English)' },
  nameAr: { ar: 'الاسم (عربي)', en: 'Name (Arabic)' },
  descriptionEn: { ar: 'الوصف (إنجليزي)', en: 'Description (English)' },
  descriptionAr: { ar: 'الوصف (عربي)', en: 'Description (Arabic)' },
  bilingualHint: { ar: 'المتجر ثنائي اللغة — أدخل الاسمين ليظهر المنتج بشكل صحيح للجميع.', en: 'The store is bilingual — enter both names so every shopper sees a real one.' },
  productStatus: { ar: 'حالة المنتج', en: 'Status' },
  statusDRAFT: { ar: 'مسودة', en: 'Draft' },
  statusACTIVE: { ar: 'منشور', en: 'Active' },
  statusARCHIVED: { ar: 'مؤرشف', en: 'Archived' },
  archive: { ar: 'أرشفة', en: 'Archive' },
  archiveTitle: { ar: 'أرشفة هذا المنتج؟', en: 'Archive this product?' },
  archiveBody: { ar: 'يختفي من المتجر ويبقى في الطلبات السابقة. لا يُحذف نهائيًا.', en: 'It disappears from the store but stays on past orders. Nothing is deleted.' },
  noProducts: { ar: 'لا توجد منتجات', en: 'No products yet' },
  orderPinOnMap: { ar: 'الموقع على الخريطة', en: 'Pin on the map' },
  productGrid: { ar: 'تعديل جماعي', en: 'Edit in a grid' },
  productGridSubtitle: {
    ar: 'عدّل منتجات كثيرة كجدول بيانات: صف لكل نوع، وخانات المنتج في صفه الأول.',
    en: 'Many products at once, like a spreadsheet: a row for each variant, the product’s own cells on its first row.',
  },
  productGridHint: {
    ar: 'الأسعار بالريال. سعر النوع الفارغ يتبع سعر المنتج. تغيير المخزون جرد للإجمالي.',
    en: 'Prices in riyals. An empty variant price follows the product’s. A stock change is a recount of the total.',
  },
  gridBasePrice: { ar: 'سعر المنتج', en: 'Product price' },
  gridVariantPrice: { ar: 'سعر النوع', en: 'Variant price' },
  gridMpn: { ar: 'رقم القطعة (MPN)', en: 'MPN' },
  gridWeight: { ar: 'الوزن (غ)', en: 'Weight (g)' },
  gridOpenEditor: { ar: 'فتح المنتج', en: 'Open the product' },
  gridDiscard: { ar: 'تجاهل التعديلات', en: 'Discard changes' },
  gridSaveCount: { ar: 'حفظ {count}', en: 'Save {count}' },
  gridSaved: { ar: 'حُفظ {products} منتج و{variants} نوع.', en: 'Saved {products} products and {variants} variants.' },
  gridRefused: {
    ar: 'لم يُحفظ {count} صف — السبب بجانب كل صف، وحُفظ الباقي.',
    en: '{count} rows were not saved — the reason is beside each; the rest were.',
  },
  gridInvalid: { ar: 'بعض الخانات غير صالحة — صحّحها قبل الحفظ.', en: 'Some cells are not valid — fix them to save.' },
  noProductsHint: { ar: 'أضف أول منتج ليظهر في المتجر.', en: 'Add your first product to see it in the store.' },
  addVariant: { ar: 'إضافة متغير', en: 'Add variant' },
  variantHint: { ar: 'كل منتج يحتاج متغيرًا واحدًا على الأقل — هو ما يُشترى فعليًا.', en: 'Every product needs at least one variant — that is what a shopper actually buys.' },
  optionsHint: { ar: 'خيارات مثل: color=brown; size=M', en: 'Options like: color=brown; size=M' },
  optionLabels: { ar: 'أسماء الخيارات وألوانها', en: 'Option names and swatches' },
  optionLabelsHint: {
    ar: 'ما يقرؤه المتسوّق لكل رمز في المتغيّرات، بالعربية والإنجليزية، ولون كل خيار لون. الترتيب هنا هو ترتيب العرض.',
    en: 'What a shopper reads for each variant code, in both languages, and the chip for each colour. This order is the order shown.',
  },
  optionLabel: { ar: 'الاسم المعروض', en: 'Shown as' },
  swatch: { ar: 'لون العيّنة', en: 'Swatch' },
  soldBy: { ar: 'يُباع', en: 'Sold by' },
  customerNote: { ar: 'ملاحظة العميل', en: 'Customer’s note' },
  delivery: { ar: 'التوصيل', en: 'Delivery' },
  deliveryMethod: { ar: 'طريقة التوصيل', en: 'Delivery method' },
  shopperActivity: { ar: 'السلال والقوائم المحفوظة', en: 'Carts & saved lists' },
  shopperActivitySubtitle: { ar: 'ما يحمله المتسوقون الآن: السلال، وقوائم الأمنيات والمقارنة', en: 'What shoppers are holding now: carts, wishlists and compare lists' },
  cartsTab: { ar: 'السلال', en: 'Carts' },
  wishlistsTab: { ar: 'قوائم الأمنيات', en: 'Wishlists' },
  compareTab: { ar: 'المقارنة', en: 'Compare' },
  openCarts: { ar: 'سلال نشطة', en: 'Open carts' },
  cartsHolding: { ar: 'تحمل {amount}', en: 'Holding {amount}' },
  abandonedCarts: { ar: 'سلال متروكة', en: 'Abandoned carts' },
  abandonedAfter: { ar: 'بلا حركة {hours} ساعة أو أكثر', en: 'Idle {hours} hours or more' },
  abandonedValue: { ar: 'في السلال المتروكة', en: 'Left in abandoned carts' },
  atTodaysPrices: { ar: 'بأسعار اليوم', en: 'At today’s prices' },
  recoveryMailsSent: { ar: 'رسائل الاسترجاع', en: 'Recovery mails sent' },
  recoveryMailsHint: { ar: 'إلى أصحاب الحسابات', en: 'To account holders' },
  abandoned: { ar: 'متروكة', en: 'Abandoned' },
  cartOpen: { ar: 'نشطة', en: 'Open' },
  searchCarts: { ar: 'ابحث باسم العميل أو بريده أو بمنتج في السلة', en: 'Search by customer, email, or a product in the cart' },
  cartState: { ar: 'الحالة', en: 'State' },
  cartWho: { ar: 'صاحب السلة', en: 'Whose cart' },
  noCarts: { ar: 'لا توجد سلال', en: 'No carts' },
  noCartsHint: { ar: 'تظهر هنا كل سلة فيها منتجات — لعميل أو لزائر', en: 'Every cart with something in it shows here — an account’s or a guest’s' },
  cartValue: { ar: 'قيمة السلة', en: 'Cart value' },
  lastActivity: { ar: 'آخر نشاط', en: 'Last activity' },
  listSeparator: { ar: '، ', en: ', ' },
  unitsCount: { ar: '{count} قطعة', en: '{count} units' },
  recoveryMailed: { ar: 'أُرسلت رسالة الاسترجاع', en: 'Recovery mail sent' },
  guestCart: { ar: 'سلة زائر', en: 'A guest’s cart' },
  cartSubtitle: { ar: 'منذ {since} · آخر نشاط {last}', en: 'Since {since} · last active {last}' },
  guestCartHint: { ar: 'زائر بلا حساب — لا بريد لمراسلته', en: 'A guest without an account — no email to write to' },
  lineNotOnSale: { ar: 'لم يعد معروضًا للبيع', en: 'No longer on sale' },
  lineShortOfStock: { ar: 'المتوفر {stock} فقط', en: 'Only {stock} in stock' },
  cartValueHint: { ar: 'بأسعار التجزئة اليوم؛ لا تُطبَّق أسعار التجار الخاصة', en: 'At today’s retail prices; a trade customer’s own prices are not applied' },
  recoveryMailedOn: { ar: 'أُرسلت رسالة الاسترجاع في {date}', en: 'Recovery mail sent on {date}' },
  noWishlists: { ar: 'لم يحفظ أحد منتجًا بعد', en: 'Nothing on wishlists yet' },
  noCompareLists: { ar: 'لا شيء في قوائم المقارنة', en: 'Nothing on compare lists yet' },
  savedListsHint: { ar: 'يظهر هنا كل منتج حفظه عميل أو زائر', en: 'Every product a customer or a guest saved shows here' },
  savedBy: { ar: 'حفظه', en: 'Saved by' },
  priceToday: { ar: 'السعر اليوم', en: 'Price today' },
  priceDropFor: { ar: 'انخفض سعره لـ', en: 'Price drop for' },
  lastSaved: { ar: 'آخر حفظ', en: 'Last saved' },
  accountsAndGuests: { ar: '{accounts} حساب · {guests} زائر', en: '{accounts} accounts · {guests} guests' },
  saversWishlist: { ar: 'من أضافه إلى قائمة الأمنيات', en: 'Who added it to their wishlist' },
  saversCompare: { ar: 'من أضافه إلى المقارنة', en: 'Who added it to compare' },
  guestsCountedNotNamed: { ar: 'و{count} زائر بلا حساب — يُعدّون ولا يُسمَّون', en: 'And {count} guests without an account — counted, never named' },
  savedAtPrice: { ar: 'حفظه بسعر {amount}', en: 'Saved at {amount}' },
  sendReminder: { ar: 'تذكير العميل بسلته', en: 'Remind them about their cart' },
  sendReminderHint: { ar: 'رسالة السلة المتروكة بصياغة المتجر — مرة يوميًا على الأكثر', en: 'The store’s abandoned-cart message — once a day at most' },
  reminderByEmail: { ar: 'بالبريد', en: 'By email' },
  reminderBySms: { ar: 'برسالة نصية', en: 'By text' },
  reminderBySmsHint: { ar: 'إلى رقمه الموثّق', en: 'To their verified number' },
  remindedRecently: { ar: 'أُرسل تذكير {when} — يمكن الإرسال مجددًا بعد يوم', en: 'A reminder went {when} — another can go after a day' },
  sendReminderButton: { ar: 'أرسل التذكير', en: 'Send the reminder' },
  reminderUndelivered: {
    ar: 'لم تصل الرسالة — سجل الرسائل يبيّن السبب. لن تُرسل مرة أخرى قبل يوم.',
    en: 'The message did not go through — the message log says why. It will not be sent again for a day.',
  },
  cartCouponApplied: { ar: 'آخر كود خصم طبّقه:', en: 'Last code applied:' },
  usageCartsHolding: { ar: 'في السلال الآن', en: 'In carts now' },
  usageCartsHoldingHint: { ar: 'طبّقوه ولم يطلبوا بعد', en: 'Applied it, not ordered yet' },
  customerTags: { ar: 'الوسوم', en: 'Tags' },
  addTagFilter: { ar: 'أضف وسمًا…', en: 'Add a tag…' },
  removeTag: { ar: 'إزالة الوسم {tag}', en: 'Remove the tag {tag}' },
  addTagPlaceholder: { ar: 'اكتب وسمًا ثم Enter', en: 'Type a tag, then Enter' },
  customerTagsHint: { ar: 'كلمات المتجر عن العميل — VIP، جملة، «اتصل قبل التوصيل». حتى ٢٠ وسمًا', en: 'The store’s own words for them — VIP, wholesale, “call before delivery”. Up to 20' },
  exportCustomers: { ar: 'تصدير', en: 'Export' },
  exportCustomersHint: { ar: 'العملاء المعروضون حسب التصفية، كملف CSV', en: 'The customers this filter shows, as a CSV file' },
  usageButton: { ar: 'الاستخدام', en: 'Usage' },
  usageOf: { ar: 'ماذا فعل {name}', en: 'What {name} did' },
  couponUsageBody: { ar: 'كل طلب استُخدم فيه الكود: المبيع والمسترد والمتروك', en: 'Every order that used the code: sold, refunded and abandoned' },
  offerUsageBody: { ar: 'كل طلب طُبّق عليه العرض: المبيع والمسترد والمتروك', en: 'Every order the offer applied to: sold, refunded and abandoned' },
  usageOrders: { ar: 'الطلبات', en: 'Orders' },
  usageOrdersSplit: { ar: '{sold} مبيع · {refunded} مسترد · {abandoned} متروك', en: '{sold} sold · {refunded} refunded · {abandoned} abandoned' },
  usageCustomers: { ar: 'العملاء', en: 'Customers' },
  usageGuestOrders: { ar: 'منها {count} طلب زائر', en: '{count} guest orders among them' },
  usageDiscountGiven: { ar: 'الخصم الممنوح', en: 'Discount given' },
  usageOnSold: { ar: 'على الطلبات المبيعة', en: 'On the sold orders' },
  averageOrder: { ar: 'متوسط الطلب', en: 'Average order' },
  usageLeft: { ar: 'الاستخدامات المتبقية', en: 'Uses left' },
  usageNoLimit: { ar: 'بلا حد', en: 'No limit' },
  usageOfMax: { ar: 'من {max}', en: 'Of {max}' },
  allTab: { ar: 'الكل', en: 'All' },
  usageSold: { ar: 'مبيعة', en: 'Sold' },
  usageRefunded: { ar: 'مستردة', en: 'Refunded' },
  usageNoOrders: { ar: 'لا توجد طلبات هنا', en: 'No orders here' },
  editDetailsAddressElsewhere: {
    ar: 'يُصحَّح العنوان من بطاقة العنوان في الطلب، وتبقى المدينة التي سُعِّر عليها التوصيل.',
    en: 'The address is corrected from the order’s address card, which keeps the city delivery was priced for.',
  },
  pickupAtStore: { ar: 'استلام من المتجر', en: 'Collected from the store' },
  promisedDates: { ar: 'الموعد الموعود', en: 'Promised for' },
  collected: { ar: 'تم الاستلام', en: 'Collected' },
  billedTo: { ar: 'الفاتورة باسم', en: 'Billed to' },
  vatNumber: { ar: 'الرقم الضريبي', en: 'VAT number' },
  films: { ar: 'الأفلام', en: 'Films' },
  filmsHint: {
    ar: 'تُعرض بعد الصور، ولا يُحمَّل منها إلا الصورة الافتتاحية حتى يفتحها المتسوّق',
    en: 'Shown after the photographs; nothing but the poster loads until a shopper opens one',
  },
  addFilm: { ar: 'إضافة فيلم', en: 'Add a film' },
  noFilms: { ar: 'لا أفلام لهذا المنتج', en: 'No films for this product' },
  untitledFilm: { ar: 'فيلم بلا عنوان', en: 'Untitled film' },
  filmFile: { ar: 'ملف الفيلم', en: 'Film file' },
  filmFileHint: { ar: 'MP4 أو WebM حتى ٥٠ ميغابايت', en: 'MP4 or WebM, up to 50 MB' },
  filmPoster: { ar: 'الصورة الافتتاحية', en: 'Poster' },
  filmPosterHint: { ar: 'اختيارية — بدونها تظهر الصورة المصغّرة فارغة', en: 'Optional — without one the thumbnail is a plain tile' },
  filmLabel: { ar: 'ماذا يُظهر الفيلم', en: 'What the film shows' },
  filmSeconds: { ar: 'المدة بالثواني', en: 'Length in seconds' },
  uploadFilm: { ar: 'رفع الفيلم', en: 'Upload film' },
  alsoListedIn: { ar: 'يظهر أيضًا في', en: 'Also listed in' },
  alsoListedInHint: {
    ar: 'تصنيفات أخرى يظهر فيها المنتج. التصنيف الأساسي أعلاه هو مسار التنقّل.',
    en: 'Other categories this appears in. The category above is its breadcrumb.',
  },
  addCategory: { ar: 'أضف تصنيفًا…', en: 'Add a category…' },
  removeFrom: { ar: 'إزالة من {name}', en: 'Remove from {name}' },
  orderProducts: { ar: 'ترتيب المنتجات', en: 'Order products' },
  orderProductsHint: {
    ar: 'ترتيب «المميّز» لهذا التصنيف: ما يراه المتسوّق أولًا عند اختيار الترتيب المميّز.',
    en: 'This category’s “featured” order — what shoppers see first when they sort by featured.',
  },
  noProductsListed: { ar: 'لا منتجات في هذا التصنيف بعد', en: 'No products are listed in this category yet' },
  saveOrder: { ar: 'حفظ الترتيب', en: 'Save order' },
  soldByHint: {
    ar: 'بالقطعة، أو بكمية من وحدة: قماش بالمتر، قهوة بالكيلو. يظهر للمتسوّق سعر الوحدة ووفر العبوة الأكبر.',
    en: 'By the piece, or by an amount of a unit: cloth by the metre, coffee by the kilo. Shoppers see the unit price and what a bigger pack saves.',
  },
  soldByPiece: { ar: 'بالقطعة', en: 'By the piece' },
  unit_m: { ar: 'متر', en: 'm' },
  unit_kg: { ar: 'كغ', en: 'kg' },
  unit_l: { ar: 'لتر', en: 'L' },
  measureHolds: { ar: 'الكمية في الواحدة ({unit})', en: 'Holds ({unit})' },
  measureHoldsHint: { ar: 'مثلًا 1 للمتر الواحد، 25 للفّة ٢٥ مترًا، 0.5 لنصف متر', en: 'e.g. 1 for a metre, 25 for a 25 m roll, 0.5 for half a metre' },
  variantPriceHint: { ar: 'اتركه فارغًا لاستخدام السعر الأساسي', en: 'Leave empty to use the base price' },
  created: { ar: 'تم إنشاء المنتج', en: 'Product created' },

  // ─── Categories, variants, images ─────────────────────────────────────────
  categoriesLoadFailed: { ar: 'تعذّر تحميل الأقسام.', en: 'Could not load the categories.' },
  categorySearch: { ar: 'ابحث في الأقسام بالاسم أو المعرّف', en: 'Search categories by name or slug' },
  categoryNoMatch: { ar: 'لا قسم يطابق «{search}».', en: 'No category matches “{search}”.' },
  categoryShowAgain: { ar: 'إظهار مجددًا', en: 'Show again' },
  categoryProductCount: { ar: '{count} منتج', en: '{count} products' },
  categoryProductCountHint: {
    ar: 'المنتجات المنشورة المدرجة في هذا القسم نفسه — أقسامه الفرعية تُحسب وحدها.',
    en: 'Published products listed in this category itself — its subcategories count their own.',
  },
  // The header menu (pages/navigation.tsx).
  menuLoadFailed: { ar: 'تعذّر تحميل القائمة.', en: 'Could not load the menu.' },
  menuSaveFailed: { ar: 'تعذّر حفظ القائمة.', en: 'Could not save the menu.' },
  menuPageNotWritten: { ar: '{page} — لم تُكتب بعد', en: '{page} — not written yet' },
  categories: { ar: 'الأقسام', en: 'Categories' },
  categoriesSubtitle: { ar: 'شجرة الأقسام كما يتصفحها العميل', en: 'The tree a shopper navigates' },
  newCategory: { ar: 'قسم جديد', en: 'New category' },
  editCategory: { ar: 'تعديل القسم', en: 'Edit category' },
  categoryName: { ar: 'اسم القسم', en: 'Category name' },
  slug: { ar: 'المعرّف النصي', en: 'Slug' },
  slugHint: { ar: 'يظهر في الرابط — يُولَّد تلقائيًا إن تُرك فارغًا', en: 'Appears in the URL — generated if left empty' },
  parentCategory: { ar: 'القسم الأعلى', en: 'Parent category' },
  topLevel: { ar: '— قسم رئيسي —', en: '— Top level —' },
  deactivate: { ar: 'إيقاف', en: 'Deactivate' },
  deactivateCategoryTitle: { ar: 'إيقاف هذا القسم؟', en: 'Deactivate this category?' },
  deactivateCategoryBody: { ar: 'يختفي من المتجر وتبقى منتجاته وطلباته السابقة. لا يُحذف.', en: 'It disappears from the store; its products and past orders stay. Nothing is deleted.' },
  noCategories: { ar: 'لا توجد أقسام', en: 'No categories yet' },
  noCategoriesHint: { ar: 'أنشئ قسمًا لتنظيم المنتجات في المتجر.', en: 'Create one to organise products in the store.' },
  defaultVariant: { ar: 'افتراضي', en: 'Default' },
  removeVariantTitle: { ar: 'حذف هذا المتغير؟', en: 'Remove this variant?' },
  removeVariantBody: { ar: 'لا يمكن حذف آخر متغير — أرشف المنتج بدلًا من ذلك.', en: 'The last variant cannot be removed — archive the product instead.' },
  variantStockHint: { ar: 'تعديل المخزون هنا يُسجَّل في حركة المخزون تلقائيًا', en: 'Editing stock here records a stock movement automatically' },
  variantStockMovement: { ar: 'يُسجَّل كحركة جرد', en: 'Recorded as a recount movement' },
  images: { ar: 'الصور', en: 'Images' },
  imagesHint: { ar: 'الصورة الأولى هي التي تظهر في قوائم المنتجات', en: 'The first image is the one listings show' },
  uploadImage: { ar: 'رفع صورة', en: 'Upload image' },
  noImages: { ar: 'لا توجد صور لهذا المنتج', en: 'This product has no images yet' },

  // ─── Inventory ────────────────────────────────────────────────────────────
  thresholdStoreSetting: { ar: 'حسب إعداد المتجر', en: 'Store setting' },
  movementsPick: { ar: 'اختر صنفًا لترى سجل حركته', en: 'Pick an item to see its history' },
  lowStockShown: { ar: 'يظهر {shown} صنفًا', en: 'Showing {shown}' },
  lowStockCapped: {
    ar: 'تظهر أقل {shown} صنفًا مخزونًا فقط — ابحث برمز أو اختر قسمًا لترى غيرها.',
    en: 'Only the {shown} lowest are listed — search by SKU or pick a category to see the rest.',
  },
  movementTypeIN: { ar: 'وارد', en: 'In' },
  movementTypeOUT: { ar: 'صادر', en: 'Out' },
  movementTypeADJUST: { ar: 'تعديل', en: 'Adjustment' },
  movementReason_order_placed: { ar: 'طلب جديد', en: 'Order placed' },
  movementReason_order_cancelled: { ar: 'إلغاء طلب', en: 'Order cancelled' },
  movementReason_return_restock: { ar: 'مرتجع عاد إلى المخزون', en: 'Return put back on the shelf' },
  movementReason_recount: { ar: 'جرد', en: 'Recount' },
  movementReason_damage: { ar: 'تلف', en: 'Damaged' },
  movementReason_loss: { ar: 'فقد', en: 'Lost' },
  movementReason_supplier_delivery: { ar: 'توريد من المورّد', en: 'Supplier delivery' },
  movementReason_import: { ar: 'استيراد', en: 'Import' },
  movementReason_transfer: { ar: 'نقل بين المواقع', en: 'Moved between locations' },
  transferStock: { ar: 'نقل المخزون', en: 'Move stock' },
  transferStockBody: {
    ar: 'من موقع إلى آخر. يُسجَّل خروجًا من الأول ودخولًا إلى الثاني، ولا يتغير إجمالي المتجر.',
    en: 'From one location to another — out of one and into the other on the ledger. The store’s total does not change.',
  },
  transferStockSubmit: { ar: 'نقل', en: 'Move' },
  transferFrom: { ar: 'من', en: 'From' },
  transferTo: { ar: 'إلى', en: 'To' },
  transferAvailable: { ar: 'المتاح هناك: {count}', en: '{count} there to move' },
  recountWhere: { ar: 'مكان الجرد', en: 'Where you counted' },
  recountWhereTotal: { ar: 'المتجر كله (الإجمالي)', en: 'The whole store (the total)' },
  recountWhereTotalHint: {
    ar: 'الفرق يُحسب على الموقع الرئيسي؛ ما في الفروع يبقى كما هو.',
    en: 'The main location takes the difference; what the branches hold stays as it is.',
  },
  recountReason: { ar: 'سبب التغيير', en: 'Why the number changed' },
  recountReasonHint_recount: { ar: 'العدد على الرف يختلف عن النظام', en: 'The shelf count differs from the system' },
  recountReasonHint_supplier_delivery: { ar: 'وصلت شحنة — أدخل العدد بعد رصّها', en: 'A delivery arrived — enter the count once it is shelved' },
  recountReasonHint_damage: { ar: 'قطع تالفة لم تعد صالحة للبيع', en: 'Units damaged and no longer sellable' },
  recountReasonHint_loss: { ar: 'قطع مفقودة أو مسروقة', en: 'Units missing or stolen' },
  recountOnHand: { ar: 'في النظام الآن: {count}', en: 'On record now: {count}' },
  recountDifference: { ar: 'الفرق الذي سيُسجَّل: {delta}', en: 'Difference to record: {delta}' },
  recountNoChange: { ar: 'لا فرق — لن تُسجَّل حركة', en: 'No difference — nothing will be recorded' },
  inventorySubtitle: { ar: 'الأصناف المنخفضة وجرد المخزون', en: 'Low stock and stock counts' },
  recount: { ar: 'جرد', en: 'Recount' },
  recountTitle: { ar: 'جرد المخزون', en: 'Recount stock' },
  recountBody: { ar: 'أدخل العدد الذي عددته على الرف، لا الفرق.', en: 'Enter the number you counted on the shelf, not the difference.' },
  countedStock: { ar: 'العدد الفعلي', en: 'Counted stock' },
  movements: { ar: 'حركة المخزون', en: 'Stock movements' },
  movementsHint: { ar: 'كل تغيير وسببه، الأحدث أولًا', en: 'Every change and its reason, newest first' },
  noMovements: { ar: 'لا توجد حركات مسجلة', en: 'No movements recorded' },
  note: { ar: 'ملاحظة', en: 'Note' },
  threshold: { ar: 'الحد', en: 'Threshold' },
  noLowStock: { ar: 'لا توجد أصناف تحت الحد', en: 'Nothing below the threshold' },

  // ─── Discounts ────────────────────────────────────────────────────────────
  discountSwitchOn: { ar: 'تفعيل مجددًا', en: 'Switch on again' },
  discountViewAll: { ar: 'كل الأكواد', en: 'All codes' },
  discountViewLive: { ar: 'السارية الآن', en: 'In effect now' },
  noDiscountsFiltered: { ar: 'لا توجد أكواد تطابق هذا.', en: 'No codes match this.' },
  editDiscount: { ar: 'تعديل الكود', en: 'Edit code' },
  discountDates: { ar: 'المدة', en: 'Dates' },
  discountUsedOf: { ar: '{used} من {max}', en: '{used} of {max}' },
  discountUsedNoLimit: { ar: '{used} · بلا حد', en: '{used} · no limit' },
  discountPerCustomer: { ar: '{count} لكل عميل', en: '{count} per customer' },
  discountMinOrder: { ar: 'للطلبات من {amount}', en: 'On orders from {amount}' },
  discountUsedSoFar: { ar: 'استُخدم حتى الآن', en: 'Used so far' },
  discountMinOrderHint: { ar: 'بالريال — اختياري', en: 'In riyals — optional' },
  discountPerCustomerHint: { ar: 'كم مرة يستخدمه العميل الواحد — اتركه فارغًا بلا حد', en: 'How often one customer may use it — empty for no limit' },
  discountStartsHint: { ar: 'من بداية هذا اليوم — اتركه فارغًا ليبدأ فورًا', en: 'From the start of that day — empty to start now' },
  discountEndsHint: { ar: 'حتى نهاية هذا اليوم — اتركه فارغًا بلا نهاية', en: 'Until the end of that day — empty for no end' },
  mktAmountError: { ar: 'اكتب مبلغًا بالريال، مثل ٢٥ أو ١٢٫٥٠', en: 'Write an amount in riyals, like 25 or 12.50' },
  mktWholeError: { ar: 'اكتب رقمًا صحيحًا، مثل ١ أو ٥٠', en: 'Write a whole number, like 1 or 50' },
  mktDateError: { ar: 'تاريخ غير صالح', en: 'Not a valid date' },
  mktEndBeforeStart: { ar: 'ينتهي قبل أن يبدأ', en: 'It ends before it starts' },
  mktTooShort: { ar: 'حرفان على الأقل', en: 'At least two characters' },
  mktFrom: { ar: 'من {date}', en: 'From {date}' },
  mktUntil: { ar: 'حتى {date}', en: 'Until {date}' },
  mktCheckFields: { ar: 'راجع الحقول المعلّمة بالأحمر.', en: 'Check the fields marked in red.' },
  discountsSubtitle: { ar: 'أكواد الخصم وحالتها', en: 'Promo codes and their state' },
  newDiscount: { ar: 'كود جديد', en: 'New code' },
  searchDiscounts: { ar: 'ابحث بالكود', en: 'Search by code' },
  code: { ar: 'الكود', en: 'Code' },
  codeImmutable: { ar: 'لا يمكن تغيير الكود بعد إنشائه — قد يكون مطبوعًا بالفعل.', en: 'A code cannot be changed once created — it may already be printed.' },
  discountType: { ar: 'النوع', en: 'Type' },
  typePERCENTAGE: { ar: 'نسبة مئوية', en: 'Percentage' },
  typeFIXED: { ar: 'مبلغ ثابت', en: 'Fixed amount' },
  typeFREE_SHIPPING: { ar: 'شحن مجاني', en: 'Free shipping' },
  value: { ar: 'القيمة', en: 'Value' },
  valuePercentHint: { ar: 'نسبة صحيحة من ١ إلى ١٠٠', en: 'A whole percentage from 1 to 100' },
  valueFixedHint: { ar: 'المبلغ بالريال', en: 'The amount in riyals' },
  minOrderTotal: { ar: 'الحد الأدنى للطلب', en: 'Minimum order' },
  maxUses: { ar: 'أقصى عدد استخدامات', en: 'Usage limit' },
  maxUsesHint: { ar: 'اتركه فارغًا لعدد غير محدود', en: 'Leave empty for unlimited' },
  maxUsesPerUser: { ar: 'الحد لكل عميل', en: 'Per-customer limit' },
  usage: { ar: 'الاستخدام', en: 'Usage' },
  startsAt: { ar: 'يبدأ في', en: 'Starts' },
  endsAt: { ar: 'ينتهي في', en: 'Ends' },
  stateactive: { ar: 'فعّال', en: 'Active' },
  statescheduled: { ar: 'مجدول', en: 'Scheduled' },
  stateexpired: { ar: 'منتهي', en: 'Expired' },
  stateexhausted: { ar: 'نفد', en: 'Used up' },
  statedisabled: { ar: 'موقوف', en: 'Switched off' },
  disable: { ar: 'إيقاف', en: 'Switch off' },
  disableTitle: { ar: 'إيقاف هذا الكود؟', en: 'Switch off this code?' },
  disableBody: { ar: 'يتوقف عن العمل فورًا ويبقى في سجل الطلبات السابقة. لا يُحذف.', en: 'It stops working immediately and stays on past orders. Nothing is deleted.' },
  noDiscounts: { ar: 'لا توجد أكواد خصم', en: 'No discount codes yet' },
  noDiscountsHint: { ar: 'أنشئ كودًا ليستخدمه العملاء عند الدفع.', en: 'Create a code for shoppers to use at checkout.' },
  unlimited: { ar: 'غير محدود', en: 'Unlimited' },

  // ─── Customers ────────────────────────────────────────────────────────────
  filterAccount: { ar: 'حالة الحساب', en: 'Account' },
  accountActive: { ar: 'نشط', en: 'Active' },
  customersSubtitle: { ar: 'دليل العملاء وإجمالي إنفاقهم', en: 'Directory and lifetime spend' },
  searchCustomers: { ar: 'ابحث بالاسم أو البريد أو الجوال', en: 'Search by name, email or mobile' },
  orderCountShort: { ar: 'الطلبات', en: 'Orders' },
  totalSpent: { ar: 'إجمالي الإنفاق', en: 'Lifetime spend' },
  lastOrder: { ar: 'آخر طلب', en: 'Last order' },
  joined: { ar: 'تاريخ التسجيل', en: 'Joined' },
  noCustomers: { ar: 'لا يوجد عملاء بعد', en: 'No customers yet' },
  noCustomersHint: { ar: 'يظهر العميل هنا بعد إنشاء حسابه.', en: 'Customers appear here once they create an account.' },
  spendNote: { ar: 'الإنفاق لا يشمل الطلبات الملغاة', en: 'Spend excludes cancelled orders' },
  inactive: { ar: 'موقوف', en: 'Inactive' },

  // ─── Returns ──────────────────────────────────────────────────────────────
  returnQtyOf: { ar: 'الكمية {count} من {ordered}', en: 'Qty {count} of {ordered}' },
  returnQty: { ar: 'الكمية {count}', en: 'Qty {count}' },
  returnItemSection: { ar: 'الصنف المُرجَع', en: 'Item returned' },
  returnWhySection: { ar: 'ما طلبه العميل', en: 'What the customer asked' },
  returnHandlingSection: { ar: 'ما فعله المتجر', en: 'What the store did' },
  returnMoneySection: { ar: 'المبلغ', en: 'Money' },
  returnOrderSection: { ar: 'الطلب والعميل', en: 'Order and customer' },
  returnResolutionLabel: { ar: 'الحل المطلوب', en: 'Asked for' },
  returnResolution_REFUND: { ar: 'استرداد المبلغ', en: 'A refund' },
  returnResolution_REPLACEMENT: { ar: 'استبدال', en: 'A replacement' },
  returnResolution_STORE_CREDIT: { ar: 'رصيد في المتجر', en: 'Store credit' },
  returnNoCustomerNote: { ar: 'لم يكتب العميل شيئًا.', en: 'The customer wrote nothing.' },
  returnRequestedOn: { ar: 'تاريخ الطلب', en: 'Requested' },
  returnNothingYet: { ar: 'لم يُتخذ إجراء بعد.', en: 'Nothing done yet.' },
  returnDisposition: { ar: 'مصير الصنف', en: 'What happened to it' },
  returnDispositionHint: {
    ar: 'ما حدث للصنف بعد فحصه — يُحفظ لتقارير المرتجعات.',
    en: 'What physically happened to the item once inspected — kept for returns reporting.',
  },
  disposition_RESTOCKED: { ar: 'صالح للبيع — يعود للمخزون', en: 'Fit to sell — back in stock' },
  disposition_REFURBISH: { ar: 'يحتاج إلى إصلاح وتجديد', en: 'Needs refurbishing' },
  disposition_RETURNED_TO_VENDOR: { ar: 'يُعاد إلى المورّد', en: 'Back to the supplier' },
  disposition_SCRAPPED: { ar: 'تالف — يُتلف', en: 'Damaged — scrapped' },
  returnRestockedUnits: { ar: 'أُعيد {count} إلى المخزون', en: '{count} put back on sale' },
  returnNotRestocked: { ar: 'لم يُعد إلى المخزون', en: 'Not put back on sale' },
  restockUnitsHint: {
    ar: 'يُضاف {count} إلى المخزون الآن، ويُسجَّل حركةً في المخزون.',
    en: 'Adds {count} to stock now, recorded as a stock movement.',
  },
  returnStaffNoteHint: {
    ar: 'للفريق فقط ولا يراها العميل — وتحل محل الملاحظة السابقة.',
    en: 'For the team only, never the customer — and it replaces the note already there.',
  },
  approveTitle: { ar: 'الموافقة على الإرجاع', en: 'Approve this return' },
  approveBody: {
    ar: 'توافق على استرجاع الصنف. لا يُسترد أي مبلغ في هذه الخطوة.',
    en: 'Agrees to take the item back. No money moves at this step.',
  },
  rejectBody: {
    ar: 'يُغلق المرتجع دون استرداد، ويُحفظ السبب لتقارير المرتجعات المرفوضة.',
    en: 'Closes the return without a refund. The reason is kept for rejected-returns reporting.',
  },
  receiveBody: {
    ar: 'سجّل أن الصنف وصل وفُحص، وما حدث له بعد الفحص.',
    en: 'Record that the item is back and inspected, and what became of it.',
  },
  refundSheetTitle: { ar: 'استرداد مبلغ المرتجع', en: 'Refund this return' },
  refundAmountLabel: { ar: 'المبلغ المُعاد (ريال)', en: 'Amount to give back (SAR)' },
  refundAmountHint: {
    ar: 'حتى {max}. المبلغ كاملًا ما لم يُحتجز جزء منه.',
    en: 'Up to {max}. The full amount, unless part of it is kept.',
  },
  refundAmountInvalid: { ar: 'اكتب المبلغ بالريال، مثل 149.50', en: 'Type the amount in riyals, like 149.50' },
  refundAmountZero: { ar: 'يجب أن يكون المبلغ أكبر من صفر', en: 'The amount must be more than zero' },
  refundAmountOver: { ar: 'أكثر مما يمكن استرداده ({max})', en: 'More than can be refunded ({max})' },
  refundCappedByPayment: {
    ar: 'تبقّى {left} فقط على عملية الدفع بعد استردادات سابقة.',
    en: 'Only {left} is left on the payment after earlier refunds.',
  },
  refundNothingLeft: {
    ar: 'لم يتبقَّ شيء على عملية الدفع لاسترداده.',
    en: 'Nothing is left on the payment to refund.',
  },
  refundProvider: { ar: 'مزوّد الدفع', en: 'Provider' },
  refundToCard: {
    ar: 'سيُعاد {amount} إلى البطاقة التي دُفع بها، عبر {provider}. لا يمكن التراجع عن ذلك.',
    en: '{amount} goes back to the card it was paid with, through {provider}. This cannot be undone.',
  },
  refundToCod: {
    ar: 'دُفع هذا الطلب عند الاستلام، فلا يمر المبلغ عبر مزوّد دفع: يُسجَّل {amount} مستردًا وتعيده أنت إلى العميل بنفسك. لا يمكن التراجع عن ذلك.',
    en: 'This order was paid cash on delivery, so no provider moves the money: {amount} is recorded as refunded and you pay it back to the customer yourself. This cannot be undone.',
  },
  refundToCredit: {
    ar: 'يُضاف {amount} إلى رصيد العميل في المتجر، ويصرفه في طلب قادم. لا يمر المبلغ عبر مزوّد دفع.',
    en: '{amount} is added to the customer’s store credit, to spend on a later order. No provider moves any money.',
  },
  refundDestination: { ar: 'إلى أين يُعاد المبلغ', en: 'Where it goes' },
  refundToPayment: { ar: 'بطريقة الدفع نفسها', en: 'Back the way it was paid' },
  refundToCreditLabel: { ar: 'إلى رصيد العميل', en: 'To store credit' },
  refundToCreditHint: {
    ar: 'فوري، ويُصرف عند الدفع في الطلب القادم',
    en: 'At once, and spent at checkout next time',
  },
  refundToHand: {
    ar: 'دُفع هذا الطلب خارج المتجر، فلا يمر المبلغ عبر مزوّد دفع: يُسجَّل {amount} مستردًا وتعيده أنت إلى العميل بنفسك. لا يمكن التراجع عن ذلك.',
    en: 'This order was paid outside the store, so no provider moves the money: {amount} is recorded as refunded and you pay it back to the customer yourself. This cannot be undone.',
  },
  refundToUnknown: {
    ar: 'سيُعاد {amount} عبر مزوّد الدفع الذي استلم المبلغ، وإن كان الدفع عند الاستلام فيُسجَّل لتسدده بنفسك. لا يمكن التراجع عن ذلك.',
    en: '{amount} goes back through the provider that took the payment — or, for cash on delivery, is recorded for you to pay back yourself. This cannot be undone.',
  },
  refundPartial: { ar: 'استرداد جزئي — قيمة المرتجع كاملة {full}.', en: 'A partial refund — the return is worth {full} in all.' },
  refundConfirmTitle: { ar: 'استرداد {amount}؟', en: 'Refund {amount}?' },
  refundConfirm: { ar: 'استرداد {amount}', en: 'Refund {amount}' },
  refundWithoutItem: { ar: 'استرداد دون استلام الصنف', en: 'Refund without the item' },
  refundWithoutItemHint: {
    ar: 'للمبالغ الصغيرة: يحتفظ العميل بالصنف ويُسترد له المبلغ.',
    en: 'For small amounts: the customer keeps the item and is refunded.',
  },
  returnPaymentHidden: {
    ar: 'تفاصيل الدفع تظهر لمن يملك صلاحية عرض الطلبات.',
    en: 'The payment shows to those who may read orders.',
  },
  returnsSubtitle: { ar: 'طلبات الإرجاع والمبالغ المستردة', en: 'Return requests and refunds' },
  returnNumber: { ar: 'رقم الإرجاع', en: 'Return' },
  product: { ar: 'المنتج', en: 'Product' },
  quantity: { ar: 'الكمية', en: 'Qty' },
  location: { ar: 'الموقع', en: 'Location' },
  locationAdd: { ar: 'إضافة موقع', en: 'Add location' },
  locationsSearch: { ar: 'ابحث باسم الموقع', en: 'Search by name' },
  locationsEmpty: { ar: 'لا مواقع بعد', en: 'No locations yet' },
  locationKindWAREHOUSE: { ar: 'مستودع', en: 'Warehouse' },
  locationKindBRANCH: { ar: 'فرع', en: 'Branch' },
  locationAddress: { ar: 'العنوان', en: 'Address' },
  locationCovers: { ar: 'يخدم أولًا', en: 'Serves first' },
  locationCoversHint: {
    ar: 'طلبات هذه المناطق تُجهَّز منه أولًا. بلا مناطق: يخدم كل مكان بعد المواقع المخصصة.',
    en: 'Orders to these zones are taken from here first. None: it serves everywhere, after the locations that cover the zone.',
  },
  locationEverywhere: { ar: 'كل المناطق', en: 'Everywhere' },
  locationUnits: { ar: 'الوحدات', en: 'Units' },
  locationMain: { ar: 'الرئيسي', en: 'Main' },
  locationMainHint: {
    ar: 'الموقع الرئيسي يحمل كل ما لم يُعدّ في موقع آخر، ولا يُحذف ولا يُوقف.',
    en: 'The main location holds whatever no other location counts; it cannot be deleted or switched off.',
  },
  locationEditHint: {
    ar: 'يبدأ فارغًا: اجرد فيه أو انقل إليه مخزونًا من شاشة المخزون.',
    en: 'It starts empty: count stock there, or move some to it, from the inventory screen.',
  },
  locationInactive: { ar: 'موقوف', en: 'Off' },
  locationOrder: { ar: 'الأولوية', en: 'Preference' },
  locationOrderHint: { ar: 'الأقل أولًا، حين يخدم أكثر من موقع المنطقة نفسها', en: 'Lower first, when more than one serves a zone' },
  locationActive: { ar: 'مفعّل', en: 'In use' },
  locationActiveHint: {
    ar: 'الموقوف لا يُعرض للاستلام ولا يُفضَّل للتوصيل، ومخزونه يُستعمل أخيرًا.',
    en: 'Switched off, it is not offered for pickup or preferred for delivery; its stock is used last.',
  },
  locationDeleteTitle: { ar: 'حذف الموقع؟', en: 'Delete this location?' },
  locationDeleteBody: {
    ar: 'لا يُحذف إلا موقع فارغ لم تُجهَّز منه طلبات؛ وإلا فأوقفه. طرق الاستلام المرتبطة به لن تسمّي فرعًا.',
    en: 'Only an empty location no order was sent from can go — otherwise switch it off. Pickup methods naming it will name no branch.',
  },
  pickupLocation: { ar: 'فرع الاستلام', en: 'Collected from' },
  pickupLocationHint: {
    ar: 'تُعرض الطريقة فقط حين يحمل الفرع كل ما في السلة.',
    en: 'Offered only when this branch holds everything in the basket.',
  },
  pickupLocationNone: { ar: 'بلا فرع محدد', en: 'No branch named' },
  returnReason: { ar: 'السبب', en: 'Reason' },
  amount: { ar: 'المبلغ', en: 'Amount' },
  refunded: { ar: 'المسترد', en: 'Refunded' },
  approve: { ar: 'موافقة', en: 'Approve' },
  reject: { ar: 'رفض', en: 'Reject' },
  receive: { ar: 'استلام', en: 'Mark received' },
  refund: { ar: 'استرداد المبلغ', en: 'Refund' },
  restock: { ar: 'إعادة للمخزون', en: 'Put back on sale' },
  dispositionLabel: { ar: 'ما مصير القطعة؟', en: 'What happens to it' },
  dispositionPick: { ar: 'اختر', en: 'Choose one' },
  dispositionRESTOCKED: { ar: 'تعود للبيع', en: 'Back on sale' },
  dispositionSCRAPPED: { ar: 'تُتلف', en: 'Written off' },
  dispositionRETURNED_TO_VENDOR: { ar: 'تُعاد للمورد', en: 'Sent back to the supplier' },
  dispositionREFURBISH: { ar: 'تُصلح', en: 'To be repaired' },
  dispositionHint: {
    ar: '«تعود للبيع» وحدها تعيدها للمخزون — اخترها فقط إن كانت صالحة للبيع.',
    en: 'Only “Back on sale” puts it back in stock — choose it only if it is fit to sell.',
  },
  restockSuggested: { ar: 'يعيد المتجر عادةً مرتجعات كهذه للبيع.', en: 'The store usually puts returns like this back on sale.' },
  returnDetail: { ar: 'تفاصيل الإرجاع', en: 'Return details' },
  returnCustomerWrote: { ar: 'ما كتبه العميل', en: 'What the customer wrote' },
  returnRequested: { ar: 'طُلب', en: 'Requested' },
  returnApprovedOn: { ar: 'قُبل', en: 'Approved' },
  returnReceivedOn: { ar: 'استُلم', en: 'Received' },
  returnRejectedOn: { ar: 'رُفض', en: 'Rejected' },
  returnRefundedOn: { ar: 'استُرد', en: 'Refunded' },
  returnWorth: { ar: 'قيمة الإرجاع', en: 'Worth' },
  returnWorthHint: {
    ar: 'ما دُفع فعلًا للقطعة بعد حصتها من العروض والكوبون',
    en: 'What was actually paid for it, after its share of the offers and coupon',
  },
  openOrder: { ar: 'فتح الطلب', en: 'Open the order' },
  refundReturnTitle: { ar: 'استرداد مبلغ الإرجاع', en: 'Refund this return' },
  refundUpTo: { ar: 'حتى {amount}', en: 'Up to {amount}' },
  settledBy: { ar: 'رُد {method}', en: 'Paid back by {method}' },
  restockHint: { ar: 'أعد الصنف للمخزون فقط إذا كان صالحًا للبيع — وإلا بِعناه مرتين.', en: 'Only put it back if it is fit to sell — otherwise we sell it twice.' },
  receiveTitle: { ar: 'استلام المرتجع', en: 'Receive this return' },
  rejectTitle: { ar: 'رفض المرتجع', en: 'Reject this return' },
  rejectReasonLabel: { ar: 'سبب الرفض', en: 'Rejection reason' },
  refundTitle: { ar: 'استرداد المبلغ؟', en: 'Refund this return?' },
  ownerOnlyRefund: { ar: 'الاسترداد متاح لمالك المتجر فقط.', en: 'Only the store owner can issue a refund.' },
  staffNote: { ar: 'ملاحظة داخلية', en: 'Internal note' },
  returnStatusREQUESTED: { ar: 'مطلوب', en: 'Requested' },
  returnStatusAPPROVED: { ar: 'موافق عليه', en: 'Approved' },
  returnStatusREJECTED: { ar: 'مرفوض', en: 'Rejected' },
  returnStatusRECEIVED: { ar: 'تم الاستلام', en: 'Received' },
  returnStatusREFUNDED: { ar: 'تم الاسترداد', en: 'Refunded' },
  returnStatusCANCELLED: { ar: 'ملغي', en: 'Cancelled' },
  reasonDEFECTIVE: { ar: 'تالف', en: 'Defective' },
  reasonWRONG_ITEM: { ar: 'صنف خاطئ', en: 'Wrong item' },
  reasonNOT_AS_DESCRIBED: { ar: 'مخالف للوصف', en: 'Not as described' },
  reasonDAMAGED_IN_TRANSIT: { ar: 'تضرر أثناء الشحن', en: 'Damaged in transit' },
  reasonNO_LONGER_NEEDED: { ar: 'لم تعد هناك حاجة', en: 'No longer needed' },
  reasonOTHER: { ar: 'أخرى', en: 'Other' },
  rejectOUTSIDE_WINDOW: { ar: 'خارج مدة الإرجاع', en: 'Outside the return window' },
  rejectNOT_DEFECTIVE: { ar: 'غير تالف', en: 'Not defective' },
  rejectMISSING_PARTS: { ar: 'أجزاء ناقصة', en: 'Missing parts' },
  rejectUSED_ITEM: { ar: 'صنف مستعمل', en: 'Item was used' },
  rejectFRAUD_SUSPECTED: { ar: 'اشتباه احتيال', en: 'Suspected fraud' },
  rejectOTHER: { ar: 'أخرى', en: 'Other' },
  noReturns: { ar: 'لا توجد مرتجعات', en: 'No returns' },
  noReturnsHint: { ar: 'تظهر طلبات الإرجاع هنا فور استلامها.', en: 'Return requests appear here as customers open them.' },

  // ─── Questions (R20, ADR-047) ─────────────────────────────────────────────
  questionHideTitle: { ar: 'إخفاء هذا السؤال؟', en: 'Hide this question?' },
  questionHideBodyAnswered: {
    ar: 'يُزال السؤال وإجابته من صفحة المنتج. لا يُحذف شيء: يبقى في تبويب «مخفي» ويمكن نشره مجددًا.',
    en: 'The question and its answer come off the product page. Nothing is deleted: it stays under Hidden and can be published again.',
  },
  questionHideBodyWaiting: {
    ar: 'يخرج من قائمة الانتظار دون إجابة، ولا يُرسل شيء إلى السائل. يبقى في تبويب «مخفي»، وإن أجبت عنه لاحقًا يُنشر.',
    en: 'It leaves the waiting queue unanswered, and nothing is sent to the asker. It stays under Hidden; answering it later publishes it.',
  },
  questionsSubtitle: {
    ar: 'أسئلة العملاء عن المنتجات — الإجابة عنها تنشرها',
    en: "Customers' questions about products — answering one publishes it",
  },
  searchQuestionsHint: { ar: 'نص السؤال أو الإجابة', en: 'The question or the answer' },
  questionsNote: {
    ar: 'لا يظهر سؤال للزوار قبل الإجابة عنه. يرى السائل سؤاله وهو ينتظر، ويصله بالبريد أول إجابة إن كان لحسابه بريد.',
    en: 'No question is public until it is answered. The asker sees theirs while it waits, and is emailed the first answer when their account has an email.',
  },
  noQuestions: { ar: 'لا توجد أسئلة هنا', en: 'No questions here' },
  noQuestionsHint: { ar: 'لا سؤال بانتظار إجابة. 🎉', en: 'Nothing is waiting for an answer. 🎉' },
  questionWaiting: { ar: 'بانتظار الإجابة', en: 'Waiting' },
  questionPublished: { ar: 'منشور', en: 'Published' },
  questionHidden: { ar: 'مخفي', en: 'Hidden' },
  customerWithoutName: { ar: 'عميل بلا اسم', en: 'A customer with no name' },
  storeAnswer: { ar: 'إجابة المتجر', en: "The store's answer" },
  storeAnswerHint: {
    ar: 'تُنشر مع السؤال مؤرخة. اكتبها بلغة السؤال؛ أول إجابة تصل إلى السائل بالبريد.',
    en: "Published with the question, dated. Write it in the question's language; the first answer is emailed to the asker.",
  },
  publishAnswer: { ar: 'انشر الإجابة', en: 'Publish the answer' },
  editAnswer: { ar: 'عدّل الإجابة', en: 'Edit the answer' },
  publishAgain: { ar: 'انشر مجددًا', en: 'Publish again' },
  hideQuestion: { ar: 'إخفاء', en: 'Hide' },

  // ─── Reviews ──────────────────────────────────────────────────────────────
  reviewsSelectAll: { ar: 'تحديد كل التقييمات الظاهرة', en: 'Select every review shown' },
  reviewSelect: { ar: 'تحديد تقييم {name}', en: 'Select the review by {name}' },
  reviewsPublishSelected: { ar: 'نشر المحدد', en: 'Publish selected' },
  reviewsRejectSelected: { ar: 'رفض المحدد', en: 'Reject selected' },
  reviewsPublishTitle: { ar: 'نشر التقييمات المحددة ({count})؟', en: 'Publish the selected reviews ({count})?' },
  reviewsPublishBody: {
    ar: 'تظهر في صفحات منتجاتها، ويُعاد حساب تقييم كل منتج منها. يمكنك رفض أيٍّ منها لاحقًا.',
    en: "They appear on their products' pages, and each product's rating is worked out again. You can reject any of them later.",
  },
  reviewsRejectTitle: { ar: 'رفض التقييمات المحددة ({count})؟', en: 'Reject the selected reviews ({count})?' },
  reviewsRejectBody: {
    ar: 'تختفي من المتجر ولا تُحتسب في تقييم منتجاتها، لكنها لا تُحذف: تبقى في تبويب «مرفوض» ويمكن نشرها لاحقًا.',
    en: "They leave the store and stop counting towards their products' ratings, but nothing is deleted: they stay under Rejected and can be published later.",
  },
  reviewsBulkDone: { ar: 'تم تحديث التقييمات ({done})', en: 'Reviews updated: {done}' },
  reviewRemoveReplyTitle: { ar: 'حذف ردّ المتجر؟', en: "Remove the store's reply?" },
  reviewRemoveReplyBody: {
    ar: 'يختفي الرد من تحت التقييم في المتجر. يبقى التقييم نفسه كما هو، ويمكنك كتابة رد جديد متى شئت.',
    en: 'The reply disappears from under the review in the store. The review itself stays as it is, and you can write a new reply at any time.',
  },
  // Support (ADR-028) — the queue and the thread.
  ticketAssignToMe: { ar: 'أسندها إليّ', en: 'Assign to me' },
  ticketDraftFailed: { ar: 'تعذّرت كتابة مسودة الرد.', en: 'Could not draft a reply.' },
  ticketOpenedAgo: { ar: 'فُتحت {age}', en: 'opened {age}' },
  reviewsSubtitle: { ar: 'مراجعة التقييمات قبل نشرها', en: 'Moderate reviews before they publish' },
  pendingQueue: { ar: 'بانتظار المراجعة', en: 'Pending' },
  approved: { ar: 'منشور', en: 'Approved' },
  rejected: { ar: 'مرفوض', en: 'Rejected' },
  allReviews: { ar: 'الكل', en: 'All' },
  rating: { ar: 'التقييم', en: 'Rating' },
  verifiedPurchase: { ar: 'شراء موثّق', en: 'Verified purchase' },
  helpfulVotes: { ar: 'وجده {count} مفيدًا', en: '{count} found it helpful' },
  storeReply: { ar: 'ردّ المتجر', en: "The store's reply" },
  storeReplyHint: {
    ar: 'يظهر للعموم تحت التقييم، مع تاريخه.',
    en: 'Public, under the review, with its date.',
  },
  replyAsStore: { ar: 'ردّ باسم المتجر', en: 'Reply as the store' },
  choiceCode: { ar: 'الرمز', en: 'Code' },
  choiceArabic: { ar: 'بالعربية', en: 'Arabic' },
  choiceEnglish: { ar: 'بالإنجليزية', en: 'English' },
  editReply: { ar: 'تعديل الرد', en: 'Edit reply' },
  removeReply: { ar: 'حذف الرد', en: 'Remove reply' },
  author: { ar: 'الكاتب', en: 'Author' },
  publish: { ar: 'نشر', en: 'Publish' },
  noReviews: { ar: 'لا توجد تقييمات هنا', en: 'Nothing to moderate' },
  noReviewsHint: { ar: 'لا شيء بانتظار المراجعة. 🎉', en: 'The queue is empty. 🎉' },
  moderationNote: { ar: 'التقييم لا يظهر في المتجر حتى يُنشر.', en: 'A review is invisible in the store until it is published.' },

  // ─── Settings ─────────────────────────────────────────────────────────────
  hexInvalid: { ar: 'اكتب اللون بصيغة #RRGGBB، مثل ‎#2F3A8C', en: 'Write the colour as #RRGGBB, like #2F3A8C' },
  // Feature switches (features.flags): a name and what it changes, per known flag.
  flag_reviews: { ar: 'تقييمات المنتجات', en: 'Product reviews' },
  flag_reviewsHint: { ar: 'يكتب المشترون تقييمات تظهر بعد مراجعتها.', en: 'Buyers write reviews, shown once moderated.' },
  flag_wishlist: { ar: 'قائمة المفضلة', en: 'Wishlist' },
  flag_wishlistHint: { ar: 'يحفظ المتسوّق منتجات ليعود إليها.', en: 'Shoppers save products to come back to.' },
  flag_bulkImport: { ar: 'الاستيراد من ملف', en: 'Import from a file' },
  flag_bulkImportHint: { ar: 'رفع المنتجات والأسعار من CSV أو Excel.', en: 'Upload products and prices from CSV or Excel.' },
  flag_guestCheckout: { ar: 'الشراء كزائر', en: 'Guest checkout' },
  flag_guestCheckoutHint: { ar: 'إتمام الطلب دون إنشاء حساب.', en: 'Checking out without an account.' },
  flag_cashOnDelivery: { ar: 'الدفع عند الاستلام', en: 'Cash on delivery' },
  flag_cashOnDeliveryHint: { ar: 'يظهر خيار الدفع نقدًا للمندوب.', en: 'Offers paying the courier in cash.' },
  flag_cardPayment: { ar: 'الدفع بالبطاقة', en: 'Card payment' },
  flag_cardPaymentHint: { ar: 'يتطلّب بوابة دفع مفعّلة في إعدادات الدفع.', en: 'Needs a payment gateway switched on under Payments.' },
  flag_newsletter: { ar: 'النشرة البريدية', en: 'Newsletter' },
  flag_newsletterHint: { ar: 'خانة الاشتراك في أسفل المتجر.', en: 'The sign-up box at the foot of the store.' },
  flag_looks: { ar: 'تسوّق الإطلالة', en: 'Shop the look' },
  flag_looksHint: { ar: 'إطلالات كاملة تُضاف إلى السلة بخطوة.', en: 'Whole looks, added to the cart in one step.' },
  flag_questions: { ar: 'أسئلة المنتجات', en: 'Product questions' },
  flag_questionsHint: { ar: 'يسأل المتسوّق عن منتج ويجيب المتجر علنًا.', en: 'Shoppers ask about a product; the store answers in public.' },
  flag_buyNow: { ar: 'اشترِ الآن', en: 'Buy now' },
  flag_buyNowHint: {
    ar: 'زر بجانب «أضف إلى السلة» ينقل المنتج وحده إلى إتمام الطلب، وتبقى السلة كما هي.',
    en: 'A button beside “Add to cart” that takes that item alone to checkout, leaving the cart as it is.',
  },
  noFeatureFlags: { ar: 'لا توجد ميزات قابلة للتبديل', en: 'No features to switch' },
  settingsSubtitle: { ar: 'ما يجعل هذا المتجر مختلفًا عن غيره', en: 'What makes this store different from another' },
  storeIdentity: { ar: 'هوية المتجر', en: 'Store identity' },
  storeName: { ar: 'اسم المتجر', en: 'Store name' },
  supportEmail: { ar: 'بريد الدعم', en: 'Support email' },
  storePhone: { ar: 'رقم التواصل', en: 'Contact phone' },
  taxSettings: { ar: 'الضريبة', en: 'Tax' },
  taxRate: { ar: 'نسبة الضريبة', en: 'Tax rate' },
  taxRateHint: { ar: 'بنقاط الأساس: ١٥٠٠ تعني ١٥٪. الأسعار شاملة الضريبة.', en: 'In basis points: 1500 means 15%. Prices are tax-inclusive.' },
  featureFlags: { ar: 'الميزات', en: 'Features' },
  featureFlagsHint: { ar: 'إيقاف ميزة يخفيها من المتجر فورًا.', en: 'Switching a feature off hides it from the storefront immediately.' },
  homeLayout: { ar: 'تكوين الصفحة الرئيسية', en: 'Home page layout' },
  homeLayoutHint: { ar: 'أضف أقسام الصفحة الرئيسية ورتّبها وحرّرها. لا يتغير شيء في المتجر حتى تحفظ.', en: 'Add, arrange and edit the sections of your home page. Nothing changes on the store until you save.' },
  // The home page's sections (ADR-056).
  addSection: { ar: 'إضافة قسم', en: 'Add section' },
  addSectionBelow: { ar: 'إضافة قسم أسفله', en: 'Add section below' },
  addSectionTitle: { ar: 'إضافة قسم', en: 'Add a section' },
  addSectionAtEnd: { ar: 'يُضاف في آخر الصفحة', en: 'It goes at the end of the page' },
  addSectionAfter: { ar: 'يُضاف بعد «{name}»', en: 'It goes after “{name}”' },
  sectionOnePerPage: { ar: 'واحد فقط في الصفحة', en: 'Only one per page' },
  sectionAtMost: { ar: '{count} على الأكثر في الصفحة', en: 'At most {count} per page' },
  sectionNeedsFlag: { ar: 'فعّل «{flag}» من الإعدادات ← الميزات أولًا', en: 'Turn on “{flag}” in Settings → Features first' },
  sectionsFull: { ar: 'الصفحة تتسع لـ {max} قسمًا على الأكثر', en: 'A page holds at most {max} sections' },
  sectionsCount: { ar: '{count} من {max} قسمًا', en: '{count} of {max} sections' },
  sectionsEmpty: { ar: 'لا أقسام في الصفحة الرئيسية بعد', en: 'Your home page has no sections yet' },
  sectionsEmptyHint: { ar: 'ابدأ ببانر أو منتجات أو أقسام.', en: 'Start with a banner, products or categories.' },
  sectionShow: { ar: 'إظهار في المتجر', en: 'Show on the store' },
  sectionHide: { ar: 'إخفاء من المتجر', en: 'Hide from the store' },
  sectionActions: { ar: 'إجراءات القسم', en: 'Section actions' },
  sectionRemoved: { ar: 'حُذف «{name}»', en: '“{name}” removed' },
  sectionTheme: { ar: 'من القالب', en: 'Theme' },
  sectionThemeNote: {
    ar: 'يرسم قالبُ متجرك هذا القسم، ويُضبط محتواه مع المطوّر. يمكنك هنا نقله وإخفاؤه وجدولته وحذفه.',
    en: 'Your theme draws this section, and its content is set with your developer. Here you can move, hide, schedule or remove it.',
  },
  sectionNeedsAttention: { ar: 'يحتاج مراجعة', en: 'Needs attention' },
  sectionProblemsTitle: { ar: 'قبل الحفظ', en: 'Before you save' },
  sectionsWithProblems: { ar: 'أقسام تحتاج مراجعة: {count}', en: 'Sections that need attention: {count}' },
  sectionContent: { ar: 'المحتوى', en: 'Content' },
  sectionDisplay: { ar: 'العرض', en: 'Display' },
  sectionDisplayHint: { ar: 'أين ومتى يظهر هذا القسم', en: 'Where and when this section shows' },
  sectionVisibility: { ar: 'يظهر على', en: 'Show on' },
  visibility_all: { ar: 'كل الشاشات', en: 'Every screen' },
  visibility_mobile: { ar: 'الجوال فقط', en: 'Phones only' },
  visibility_desktop: { ar: 'الحاسوب فقط', en: 'Computers only' },
  sectionSchedule: { ar: 'الجدولة', en: 'Schedule' },
  sectionScheduleHint: {
    ar: 'اتركهما فارغين ليظهر حتى تخفيه. الأوقات بتوقيت جهازك.',
    en: 'Leave both empty to show it until you hide it. Times are your computer’s.',
  },
  scheduleStarts: { ar: 'يبدأ', en: 'From' },
  scheduleEnds: { ar: 'ينتهي', en: 'Until' },
  scheduleUpcoming: { ar: 'يبدأ {date}', en: 'Starts {date}' },
  scheduleRunning: { ar: 'حتى {date}', en: 'Until {date}' },
  scheduleEnded: { ar: 'انتهى', en: 'Ended' },
  sectionSelectPrompt: { ar: 'اختر قسمًا لتحريره', en: 'Choose a section to edit it' },
  sectionSelectPromptHint: { ar: 'أو أضف قسمًا جديدًا من أسفل القائمة.', en: 'Or add a new one from the foot of the list.' },
  sectionItemsCount: { ar: '{count} × {item}', en: '{count} × {item}' },
  sectionItemEmpty: { ar: 'فارغة', en: 'Empty' },
  sectionItemsFull: { ar: '{max} على الأكثر', en: 'At most {max}' },
  sectionBack: { ar: 'كل الأقسام', en: 'All sections' },
  previewHome: { ar: 'معاينة', en: 'Preview' },
  previewHomeHint: { ar: 'يفتح الصفحة كما ستبدو بعد الحفظ، في نافذة جديدة', en: 'Opens the page as it will look once saved, in a new tab' },
  previewNeedsUrl: {
    ar: 'المعاينة تحتاج STOREFRONT_URL في إعدادات الـ API.',
    en: 'The preview needs STOREFRONT_URL set on the API.',
  },
  previewFailed: { ar: 'تعذّر فتح المعاينة.', en: 'Could not open the preview.' },
  replaceImage: { ar: 'استبدال', en: 'Replace' },
  uploadingImage: { ar: 'جارٍ الرفع…', en: 'Uploading…' },
  imageUploadHint: { ar: 'JPEG أو PNG أو WebP أو AVIF، حتى ٥ ميجابايت', en: 'JPEG, PNG, WebP or AVIF, up to 5 MB' },
  videoUploadHint: { ar: 'MP4 أو WebM، حتى ٥٠ ميجابايت', en: 'MP4 or WebM, up to 50 MB' },
  themeSectionsUnavailable: {
    ar: 'أقسام القالب معروضة للقراءة فقط: {reason}',
    en: 'Your theme’s sections are read-only for now: {reason}',
  },
  themeSections_unconfigured: {
    ar: 'اضبط STOREFRONT_URL في إعدادات الـ API ليقرأ وصف أقسام القالب.',
    en: 'set STOREFRONT_URL on the API so it can read what your theme describes.',
  },
  themeSections_unreachable: { ar: 'تعذّر الوصول إلى واجهة المتجر.', en: 'the storefront could not be reached.' },
  themeSections_invalid: { ar: 'وصف أقسام القالب غير صالح — أبلغ المطوّر.', en: 'the theme’s description is not valid — tell your developer.' },
  themeSections_none: { ar: 'القالب لا يصف أقسامه بعد — تُضبط مع المطوّر.', en: 'the theme does not describe them yet — they are set with your developer.' },
  themeSectionsLent: {
    ar: 'تعذّر الوصول إلى واجهة المتجر — أقسام القالب معروضة كما قُرئت آخر مرة.',
    en: 'The storefront could not be reached — your theme’s sections are shown as last read.',
  },
  tryAgain: { ar: 'إعادة المحاولة', en: 'Try again' },
  linkNone: { ar: 'بلا رابط', en: 'No link' },
  linkKind_category: { ar: 'قسم', en: 'A category' },
  linkKind_product: { ar: 'منتج', en: 'A product' },
  linkKind_page: { ar: 'صفحة', en: 'A page' },
  linkKind_products: { ar: 'كل المنتجات', en: 'All products' },
  linkKind_custom: { ar: 'عنوان', en: 'An address' },
  linkCustomHint: { ar: 'مسار في المتجر يبدأ بـ / أو عنوان يبدأ بـ https://', en: 'A path on the store starting with /, or an address starting https://' },
  chooseProduct: { ar: 'اختر منتجًا', en: 'Choose a product' },
  categoryGone: { ar: 'لم يعد موجودًا', en: 'No longer exists' },
  numberRange: { ar: 'من {min} إلى {max}', en: 'From {min} to {max}' },
  blockEnabled: { ar: 'مفعّل', en: 'Enabled' },
  moveUp: { ar: 'تقديم', en: 'Move up' },
  moveDown: { ar: 'تأخير', en: 'Move down' },
  maxPerOrder: { ar: 'الحد في الطلب الواحد', en: 'Most per order' },
  maxPerOrderHint: {
    ar: 'لعينة مجانية أو إصدار محدود. فارغ = بلا حد خاص',
    en: 'For a free sample or a limited run. Empty = no limit of its own',
  },
  limitPerOrder: { ar: 'الحد {count} في الطلب', en: 'Limit {count} per order' },
  blockhero: { ar: 'الواجهة الرئيسية', en: 'Hero' },
  blockbanner: { ar: 'شريط إعلاني', en: 'Banner' },
  blockcategories: { ar: 'الأقسام', en: 'Categories' },
  'blockfeatured-collection': { ar: 'مجموعة مختارة', en: 'Featured collection' },
  'blockproduct-rail': { ar: 'شريط منتجات', en: 'Product rail' },
  'blockcontent-block': { ar: 'نص تعريفي', en: 'Content block' },
  contentPages: { ar: 'صفحات المحتوى', en: 'Content pages' },
  // Redirects (ADR-073).
  redirects: { ar: 'تحويل الروابط', en: 'Redirects' },
  redirectsSubtitle: { ar: 'روابط متجرك القديمة وأين تذهب الآن — تحفظ الزوار وترتيب البحث عند الانتقال من منصة أخرى', en: 'Your old links and where they go now — keeps visitors and search rankings when you move from another platform' },
  addRedirect: { ar: 'تحويل جديد', en: 'New redirect' },
  editRedirect: { ar: 'تعديل التحويل', en: 'Edit redirect' },
  searchRedirects: { ar: 'ابحث بالرابط القديم أو الجديد', en: 'Search by the old or new link' },
  noRedirects: { ar: 'لا تحويلات بعد', en: 'No redirects yet' },
  noRedirectsHint: { ar: 'انتقلت من سلة أو غيرها؟ الصق روابطك القديمة وروابطها الجديدة دفعة واحدة', en: 'Moved from Salla or elsewhere? Paste your old links and their new ones in one go' },
  redirectFrom: { ar: 'الرابط القديم', en: 'Old link' },
  redirectFromHint: { ar: 'مسار مثل /ar/محفظة/p123 — أو الرابط كاملًا، ونأخذ مساره', en: 'A path like /ar/wallet/p123 — or the whole link, and we keep its path' },
  redirectTo: { ar: 'يذهب إلى', en: 'Goes to' },
  redirectToHint: { ar: 'مسار في متجرك يبدأ بـ / أو رابط يبدأ بـ https://', en: 'A path of your store starting with /, or an https:// link' },
  redirectKind: { ar: 'النوع', en: 'Kind' },
  redirectPermanent: { ar: 'دائم ٣٠١', en: 'Permanent 301' },
  redirectTemporary: { ar: 'مؤقت ٣٠٢', en: 'Temporary 302' },
  redirectPermanentLabel: { ar: 'تحويل دائم', en: 'Permanent' },
  redirectPermanentHint: { ar: 'تنقل محركات البحث ترتيب الرابط القديم إلى الجديد — اختر المؤقت لعرض لفترة', en: 'Search engines move the old link’s ranking to the new one — choose temporary for a campaign' },
  redirectHits: { ar: 'الزيارات', en: 'Visits' },
  redirectEditorHint: { ar: 'خطوة واحدة دائمًا: لا يحوّل رابط إلى نفسه ولا إلى تحويل آخر', en: 'Always one hop: a link never sends to itself or to another redirect' },
  redirectsBulk: { ar: 'لصق عدة روابط', en: 'Paste many' },
  redirectsBulkHint: { ar: 'سطر لكل رابط: القديم، ثم الجديد — حتى ٥٠٠ سطر', en: 'A line each: the old link, then the new — up to 500 lines' },
  redirectsBulkLines: { ar: 'الروابط', en: 'Links' },
  redirectsBulkLinesHint: { ar: 'افصل بينهما بفاصلة أو مسافة. الموجود يُحدَّث، والمرفوض يُذكر سببه.', en: 'Separate them with a comma or a space. One already there is updated; one refused says why.' },
  redirectsBulkAdd: { ar: 'أضف', en: 'Add' },
  redirectsBulkResult: { ar: 'أُضيف {added}، وحُدّث {updated}، ورُفض {refused}', en: '{added} added, {updated} updated, {refused} refused' },
  redirectsBulkLine: { ar: 'السطر {line}', en: 'Line {line}' },
  deleteRedirectTitle: { ar: 'حذف هذا التحويل؟', en: 'Delete this redirect?' },
  deleteRedirectBody: { ar: 'يعود الرابط القديم صفحة غير موجودة.', en: 'The old link becomes a page not found again.' },
  auditKind_redirect: { ar: 'تحويل رابط', en: 'Redirect' },
  auditAction_redirect_create: { ar: 'إضافة تحويل', en: 'Redirect added' },
  auditAction_redirect_update: { ar: 'تعديل تحويل', en: 'Redirect changed' },
  auditAction_redirect_delete: { ar: 'حذف تحويل', en: 'Redirect deleted' },
  auditAction_redirect_bulk: { ar: 'لصق تحويلات', en: 'Redirects pasted' },
  contentPagesHint: { ar: 'اكتب صفحات متجرك بالعربي والإنجليزي، وانشرها متى اكتملت، واضبط كيف تظهر في Google وعند مشاركة رابطها.', en: 'Write your store’s pages in Arabic and English, publish them when they are ready, and set how they look in Google and when shared.' },
  pageTitle: { ar: 'عنوان الصفحة', en: 'Page title' },
  pageBody: { ar: 'المحتوى', en: 'Content' },
  pageEmpty: { ar: 'غير مكتوبة', en: 'Not written' },
  // The pages editor (ADR-058).
  pageNew: { ar: 'صفحة جديدة', en: 'New page' },
  pageNewHint: { ar: 'صفحة خاصة بمتجرك: دليل المقاسات، العناية بالأقمشة، الجملة…', en: 'A page of your own: a size guide, fabric care, wholesale…' },
  pageNewDraftNote: { ar: 'تبدأ الصفحة مسودة لا يراها العملاء، وتنشرها متى اكتملت.', en: 'It starts as a draft shoppers cannot see; publish it when it is ready.' },
  pageCreate: { ar: 'إنشاء الصفحة', en: 'Create page' },
  pageTitleRequired: { ar: 'اكتب عنوانًا بإحدى اللغتين على الأقل.', en: 'Give it a title in at least one language.' },
  pageTitlePlaceholder: { ar: 'مثال: سياسة الشحن', en: 'For example: Shipping policy' },
  pageUntitled: { ar: 'بلا عنوان', en: 'Untitled' },
  pageAddress: { ar: 'رابط الصفحة', en: 'Page address' },
  pageAddressHint: { ar: 'العنوان ثابت: تغييره يكسر الروابط إليها من محركات البحث والرسائل.', en: 'The address stays fixed: changing it would break links to it from search engines and messages.' },
  pageAddressNewHint: { ar: 'حروف إنجليزية صغيرة وأرقام وشرطات. لا يتغير بعد الإنشاء.', en: 'Lower-case letters, digits and dashes. It cannot change after the page is created.' },
  pageSlugRequired: { ar: 'اكتب رابطًا للصفحة.', en: 'Give the page an address.' },
  pageSlugInvalid: { ar: 'حروف إنجليزية صغيرة وأرقام وشرطات فقط، حتى ٦٠ حرفًا.', en: 'Lower-case letters, digits and dashes only, up to 60.' },
  pageSlugTaken: { ar: 'هناك صفحة بهذا الرابط.', en: 'A page already has this address.' },
  pageSearch: { ar: 'ابحث في الصفحات', en: 'Search pages' },
  pageChoose: { ar: 'الصفحة', en: 'Page' },
  pageSearchEmpty: { ar: 'لا صفحة تطابق البحث.', en: 'No page matches.' },
  pageGroupHelp: { ar: 'صفحات المساعدة', en: 'Help pages' },
  pageGroupLegal: { ar: 'الصفحات النظامية', en: 'Legal' },
  pageGroupOwn: { ar: 'صفحاتك', en: 'Your pages' },
  pageCountPublished: { ar: '{count} منشورة', en: '{count} published' },
  pageCountDraft: { ar: '{count} مسودة', en: '{count} draft' },
  pageCountEmpty: { ar: '{count} غير مكتوبة', en: '{count} not written' },
  pageStatus: { ar: 'حالة الصفحة', en: 'Page status' },
  pageStatusPublished: { ar: 'منشورة', en: 'Published' },
  pageStatusDraft: { ar: 'مسودة', en: 'Draft' },
  pagePartlyWritten: { ar: 'منشورة بلغة واحدة', en: 'Live in one language' },
  pageWritten: { ar: 'مكتوبة', en: 'Written' },
  pageEdited: { ar: 'تعديلات غير محفوظة', en: 'Unsaved changes' },
  pageUpdatedAgo: { ar: 'آخر تحديث {when}', en: 'Updated {when}' },
  pageNeverSaved: { ar: 'لم تُحفظ بعد', en: 'Not saved yet' },
  pageLastUpdated: { ar: 'آخر تحديث', en: 'Last updated' },
  pageViewOnStore: { ar: 'عرض في المتجر', en: 'View on store' },
  pageActions: { ar: 'إجراءات الصفحة', en: 'Page actions' },
  pageDelete: { ar: 'حذف الصفحة', en: 'Delete page' },
  pageDeleteTitle: { ar: 'حذف هذه الصفحة؟', en: 'Delete this page?' },
  pageDeleteBody: { ar: 'تُحذف «{page}» بلغتيها عند الحفظ، ويصبح رابطها غير موجود.', en: '“{page}” is removed in both languages when you save, and its address stops working.' },
  pageClear: { ar: 'مسح المحتوى', en: 'Clear page' },
  pageClearTitle: { ar: 'مسح محتوى هذه الصفحة؟', en: 'Clear this page?' },
  pageClearBody: { ar: 'يُمسح العنوان والمحتوى وإعدادات البحث بلغتيها عند الحفظ، وتختفي الصفحة من المتجر وتذييله.', en: 'Its title, content and search settings are cleared in both languages when you save, and it leaves the store and its footer.' },
  pageTabContent: { ar: 'المحتوى', en: 'Content' },
  pageTabSeo: { ar: 'البحث والمشاركة', en: 'SEO & sharing' },
  pageLanguage: { ar: 'لغة التحرير', en: 'Editing language' },
  pagePublishTitle: { ar: 'النشر', en: 'Publishing' },
  pagePublishedHint: { ar: 'يراها العملاء بكل لغة كتبتها؛ اللغة الفارغة تعرض الأخرى.', en: 'Shoppers see it in every language you wrote; an empty language shows the other.' },
  pageDraftHint: { ar: 'مسودة: لا تظهر في المتجر ولا التذييل ولا خريطة الموقع.', en: 'A draft: not on the store, in the footer or in the sitemap.' },
  pageInFooter: { ar: 'في تذييل المتجر', en: 'In the shop footer' },
  pageInFooterHint: { ar: 'أوقفه لصفحة تصل إليها من رابط أو حملة فقط.', en: 'Turn off for a page reached only from a link or a campaign.' },
  pageOutline: { ar: 'هيكل الصفحة', en: 'Outline' },
  pageOutlineEmpty: { ar: 'أضف عناوين أقسام (##) ليظهر هيكل الصفحة هنا وفهرس «في هذه الصفحة» للعملاء.', en: 'Add section headings (##) to see the outline here — and give shoppers “On this page”.' },
  pageOnThisPage: { ar: 'في هذه الصفحة', en: 'On this page' },
  pagePreviewEmpty: { ar: 'اكتب المحتوى لتراه هنا كما سيظهر للعملاء.', en: 'Write something to see it here as shoppers will.' },
  pageYourStore: { ar: 'متجرك', en: 'your-store' },
  pageTranslateFromArabic: { ar: 'ترجمة من العربية', en: 'Translate from Arabic' },
  pageTranslateFromEnglish: { ar: 'ترجمة من الإنجليزية', en: 'Translate from English' },
  pageTranslating: { ar: 'جارٍ الترجمة…', en: 'Translating…' },
  pageTranslateFailed: { ar: 'تعذّرت الترجمة. تأكد من تفعيل المساعد الذكي.', en: 'Could not translate. Check the AI assistant is switched on.' },
  // The body's editor.
  editorToolbar: { ar: 'تنسيق النص', en: 'Formatting' },
  editorHeading: { ar: 'عنوان قسم', en: 'Section heading' },
  editorSubheading: { ar: 'عنوان فرعي', en: 'Subheading' },
  editorBold: { ar: 'عريض', en: 'Bold' },
  editorBoldPlaceholder: { ar: 'نص عريض', en: 'bold text' },
  editorBullets: { ar: 'قائمة نقطية', en: 'Bulleted list' },
  editorNumbers: { ar: 'قائمة مرقّمة', en: 'Numbered list' },
  editorLink: { ar: 'رابط', en: 'Link' },
  editorLinkTitle: { ar: 'إضافة رابط', en: 'Add a link' },
  editorLinkText: { ar: 'النص الظاهر', en: 'Text to show' },
  editorLinkHref: { ar: 'إلى أين', en: 'Goes to' },
  editorLinkPages: { ar: 'أو اختر صفحة من متجرك', en: 'Or pick a page on your store' },
  editorLinkInsert: { ar: 'إضافة', en: 'Add link' },
  editorLinkInvalid: { ar: 'الرابط يبدأ بـ / لصفحة في المتجر، أو https:// أو mailto: أو tel:.', en: 'Start with / for a page on the store, or https://, mailto: or tel:.' },
  editorUndo: { ar: 'تراجع', en: 'Undo' },
  editorRedo: { ar: 'إعادة', en: 'Redo' },
  editorView: { ar: 'طريقة العرض', en: 'View' },
  editorWrite: { ar: 'كتابة', en: 'Write' },
  editorSplit: { ar: 'جنبًا إلى جنب', en: 'Side by side' },
  editorPreview: { ar: 'معاينة', en: 'Preview' },
  editorPreviewNote: { ar: 'كما سيراها العملاء', en: 'As shoppers will see it' },
  editorFocus: { ar: 'وضع التركيز', en: 'Focus mode' },
  editorExitFocus: { ar: 'إنهاء وضع التركيز (Esc)', en: 'Leave focus mode (Esc)' },
  editorPlaceholder: { ar: 'ابدأ الكتابة… أو الصق من Word أو Google Docs وتبقى العناوين والقوائم والروابط.', en: 'Start writing… or paste from Word or Google Docs — headings, lists and links come with it.' },
  editorWords: { ar: '{count} كلمة', en: '{count} words' },
  editorMinutes: { ar: 'قراءة {count} د', en: '{count} min read' },
  editorSections: { ar: '{count} أقسام', en: '{count} sections' },
  editorLinks: { ar: '{count} روابط', en: '{count} links' },
  editorBrokenLinks: { ar: '{count} رابط لن يعمل', en: '{count} link will not work' },
  editorGuide: { ar: 'دليل التنسيق', en: 'Formatting guide' },
  guideIntro: { ar: 'الأزرار تكتب هذه الرموز، ويمكنك كتابتها بنفسك. لا يُستخدم HTML أبدًا.', en: 'The toolbar writes these marks, and you can type them yourself. HTML is never used.' },
  guideHeading: { ar: 'عنوان قسم — يظهر في «في هذه الصفحة»', en: 'Section heading — listed in “On this page”' },
  guideSubheading: { ar: 'عنوان فرعي — سؤال في صفحة الأسئلة', en: 'Subheading — a question on the FAQ page' },
  guideBold: { ar: 'نص عريض', en: 'Bold text' },
  guideBullets: { ar: 'قائمة نقطية', en: 'Bulleted list' },
  guideNumbers: { ar: 'قائمة مرقّمة', en: 'Numbered list' },
  guideLink: { ar: 'رابط لصفحة أو موقع أو بريد أو هاتف', en: 'A link to a page, a site, an email or a phone' },
  guideParagraph: { ar: 'سطر فارغ يبدأ فقرة جديدة', en: 'A blank line starts a new paragraph' },
  // Search and sharing.
  seoSearchTitle: { ar: 'نتائج البحث', en: 'Search results' },
  seoSearchHint: { ar: 'ما يظهر في Google عند البحث عن متجرك. اتركه فارغًا ليُكتب من عنوان الصفحة وبدايتها.', en: 'What Google shows for this page. Leave blank to use the title and opening words.' },
  seoMetaTitleHint: { ar: 'يُضاف بعده اسم المتجر «{store}» تلقائيًا. الأفضل ٣٠–٦٠ حرفًا والكلمة الأهم في البداية.', en: 'Your store name “{store}” is added after it. 30–60 characters reads best, the key words first.' },
  seoMetaDescriptionHint: { ar: 'جملة أو جملتان تقنع الباحث بالنقر. الأفضل ٧٠–١٦٠ حرفًا.', en: 'One or two sentences that make a searcher click. 70–160 characters reads best.' },
  seoMetaDescriptionAuto: { ar: 'فارغ: تُستخدم بداية الصفحة كما تظهر في الخانة. الوصف المكتوب بعناية أفضل.', en: 'Blank: the opening words are used, as shown in the box. One written for searchers does better.' },
  seoUseOpening: { ar: 'استخدم بداية الصفحة', en: 'Use the opening words' },
  seoMeter: { ar: '{count} حرفًا · الأفضل {min}–{max}', en: '{count} chars · best {min}–{max}' },
  seoPreviewGoogle: { ar: 'معاينة Google', en: 'Google preview' },
  seoEngines: { ar: 'محركات البحث', en: 'Search engines' },
  seoEnginesHint: {
    ar: 'كيف تظهر الصفحة في Google. اتركها فارغة ليُكتب العنوان والوصف من قالب المتجر أو من كلماتها.',
    en: 'How the page shows in Google. Leave it empty and the store’s pattern, or the page’s own words, write it.',
  },
  seoTitleFallbackHint: { ar: 'فارغ: من قالب المتجر، وإلا الاسم', en: 'Empty: the store’s pattern, else the name' },
  seoDescriptionFallbackHint: { ar: 'فارغ: من قالب المتجر، وإلا وصفه', en: 'Empty: the store’s pattern, else its description' },
  seoHideLabel: { ar: 'إخفاء عن محركات البحث', en: 'Hide from search engines' },
  seoHideHint: { ar: 'تبقى الصفحة في المتجر، لكن لا تظهر في نتائج البحث', en: 'It stays on the store, out of search results' },
  seoLanguage: { ar: 'اللغة', en: 'Language' },
  settingsSeo: { ar: 'تحسين محركات البحث (SEO)', en: 'Search engines (SEO)' },
  settingsSeoHint: { ar: 'العناوين والقوالب ورموز التحقق وملف robots', en: 'Titles, patterns, verification codes and robots' },
  seoTitlePattern: { ar: 'قالب العنوان', en: 'Title pattern' },
  seoTitlePatternHint: {
    ar: 'كيف يُؤطَّر عنوان كل صفحة في نتائج البحث وتبويب المتصفح. {title} عنوان الصفحة و{store} اسم المتجر.',
    en: 'How every page’s title is framed in search results and the browser tab. {title} is the page’s, {store} the store’s name.',
  },
  seoExample: { ar: 'مثال:', en: 'For example:' },
  seoHome: { ar: 'الصفحة الرئيسية', en: 'Home page' },
  seoHomeHint: { ar: 'نتيجة البحث لمتجرك نفسه — أول ما يراه من يبحث عن اسمك', en: 'The search result for the store itself — the first thing someone searching your name sees' },
  seoHomeDescriptionFallback: { ar: 'فارغ: من نص البانر الرئيسي', en: 'Empty: the hero’s words' },
  seoStoreShareHint: { ar: 'تظهر عند مشاركة رابط المتجر، ولكل صفحة لا صورة لها', en: 'Shown when the store’s link is shared, and for any page without a picture of its own' },
  seoPatterns: { ar: 'قوالب التعبئة', en: 'Fill-in patterns' },
  seoPatternsHint: {
    ar: 'تكتب عنوان ووصف أي منتج أو قسم أو علامة لم يُكتب لها — وتتغيّر كلها متى غيّرت القالب. ما كُتب للعنصر نفسه يبقى أولًا.',
    en: 'They write the title and description of any product, category or brand that has none — and all follow when a pattern changes. What an item has of its own still comes first.',
  },
  seoPattern_product: { ar: 'المنتجات', en: 'Products' },
  seoPattern_category: { ar: 'الأقسام', en: 'Categories' },
  seoPattern_brand: { ar: 'العلامات التجارية', en: 'Brands' },
  seoTokens: { ar: 'يمكن استخدام:', en: 'You can use:' },
  seoVerification: { ar: 'التحقق من الملكية', en: 'Ownership verification' },
  seoVerificationHint: {
    ar: 'رموز تطلبها أدوات مشرفي المواقع لإثبات أن المتجر لك. الصق الرمز وحده — أو الوسم كاملًا ونأخذ الرمز منه.',
    en: 'Codes webmaster tools ask for to prove the store is yours. Paste the code alone — or the whole tag and the code is taken from it.',
  },
  seoVerify_google: { ar: 'Google Search Console', en: 'Google Search Console' },
  seoVerify_bing: { ar: 'Bing Webmaster', en: 'Bing Webmaster' },
  seoVerify_yandex: { ar: 'Yandex', en: 'Yandex' },
  seoVerify_pinterest: { ar: 'Pinterest', en: 'Pinterest' },
  seoVerify_meta: { ar: 'Meta (فيسبوك وإنستغرام)', en: 'Meta (Facebook and Instagram)' },
  seoVerifyPlaceholder: { ar: 'الرمز', en: 'The code' },
  seoRobots: { ar: 'الزواحف (robots.txt)', en: 'Crawlers (robots.txt)' },
  seoRobotsHint: {
    ar: 'ما يُسمح لمحركات البحث بزيارته. السلة والدفع والحساب مستبعدة دائمًا.',
    en: 'What search engines may visit. The cart, checkout and account are always kept out.',
  },
  seoDisallow: { ar: 'مسارات تُستبعد', en: 'Paths kept out' },
  seoDisallowHint: { ar: 'مسار في كل سطر يبدأ بـ / — مثل /compare', en: 'One per line, starting with / — like /compare' },
  seoBlockAi: { ar: 'منع زواحف تدريب الذكاء الاصطناعي', en: 'Turn away AI training crawlers' },
  seoBlockAiHint: { ar: 'تبقى محركات البحث وبحث الذكاء الاصطناعي مرحّبًا بها. يُمنع: {bots}', en: 'Search engines and AI search stay welcome. Turned away: {bots}' },
  seoSitemap: { ar: 'خريطة الموقع:', en: 'Sitemap:' },
  seoHealthPage: { ar: 'صحة البحث (SEO)', en: 'SEO health' },
  seoHealthPageHint: {
    ar: 'ما ينقص صفحات متجرك لتظهر في البحث — اضغط أي بند لترى العناصر وتصلحها',
    en: 'What your pages are missing to be found — open any item to see what needs fixing',
  },
  seoHealthChecked: { ar: 'فُحص {products} منتج و{categories} قسم و{brands} علامة.', en: 'Checked {products} products, {categories} categories and {brands} brands.' },
  seoHealthAllGood: { ar: 'لا شيء ينقص هنا.', en: 'Nothing missing here.' },
  'seoIssue_product-no-description': { ar: 'منتجات بلا وصف للبحث', en: 'Products with no search description' },
  'seoIssueHint_product-no-description': { ar: 'لا وصف بحث ولا سطر مختصر في لغة على الأقل', en: 'No search description or short line in a language' },
  'seoIssue_product-thin-description': { ar: 'منتجات بوصف قصير', en: 'Products with a thin description' },
  'seoIssueHint_product-thin-description': { ar: 'أقل من ٥٠ كلمة في لغة — صعب أن تظهر في البحث', en: 'Under 50 words in a language — hard to rank' },
  'seoIssue_product-no-alt': { ar: 'صور بلا وصف', en: 'Pictures with no description' },
  'seoIssueHint_product-no-alt': { ar: 'وصف الصورة يقرؤه Google وقارئ الشاشة', en: 'Google and screen readers read a picture’s description' },
  'seoIssue_product-duplicate-title': { ar: 'عناوين مكررة', en: 'Shared titles' },
  'seoIssueHint_product-duplicate-title': { ar: 'منتجات بالعنوان نفسه تتنافس على البحث نفسه', en: 'Products with the same title compete for the same search' },
  'seoIssue_product-hidden': { ar: 'منتجات مخفية عن البحث', en: 'Products hidden from search' },
  'seoIssueHint_product-hidden': { ar: 'للبيع، لكن خارج نتائج البحث — تأكد أن ذلك مقصود', en: 'On sale, out of search results — make sure that is meant' },
  'seoIssue_category-no-description': { ar: 'أقسام بلا وصف', en: 'Categories with no description' },
  'seoIssueHint_category-no-description': { ar: 'فقرة في أعلى القسم تساعد صفحته على الظهور', en: 'A paragraph heading the category helps its page rank' },
  'seoIssue_category-hidden': { ar: 'أقسام مخفية عن البحث', en: 'Categories hidden from search' },
  'seoIssueHint_category-hidden': { ar: 'ظاهرة في المتجر، خارج نتائج البحث', en: 'On the store, out of search results' },
  'seoIssue_brand-no-description': { ar: 'علامات بلا وصف', en: 'Brands with no description' },
  'seoIssueHint_brand-no-description': { ar: 'فقرة عن العلامة تعطي صفحتها ما تقوله', en: 'A paragraph about the brand gives its page something to say' },
  'seoDetail_product-no-description': { ar: '{value}', en: '{value}' },
  'seoDetail_product-thin-description': { ar: '{value} كلمة', en: '{value} words' },
  'seoDetail_product-no-alt': { ar: '{value} صورة', en: '{value} pictures' },
  'seoDetail_product-duplicate-title': { ar: '«{value}»', en: '“{value}”' },
  'seoDetail_product-hidden': { ar: '{value}', en: '{value}' },
  'seoDetail_category-no-description': { ar: '{value}', en: '{value}' },
  'seoDetail_category-hidden': { ar: '{value}', en: '{value}' },
  'seoDetail_brand-no-description': { ar: '{value}', en: '{value}' },
  seoItem: { ar: 'العنصر', en: 'Item' },
  seoItemDetail: { ar: 'التفاصيل', en: 'Detail' },
  seoKind_product: { ar: 'منتج', en: 'Product' },
  seoKind_category: { ar: 'قسم', en: 'Category' },
  seoKind_brand: { ar: 'علامة', en: 'Brand' },
  seoFix: { ar: 'إصلاح', en: 'Fix' },
  // Purchases reported from the server (ADR-100).
  conversionsTitle: { ar: 'تسجيل المشتريات من الخادم', en: 'Purchases reported from the server' },
  conversionsHint: {
    ar: 'مع حقل الخادم في كل بكسل، تصل المشتريات إلى المنصة حتى لو حجبها المتصفح — وتُحسب مرة واحدة. آخر ٧ أيام:',
    en: 'With each pixel’s server field filled, purchases reach the platform even when a browser blocks the pixel — and count once. Last 7 days:',
  },
  conversionsOn: { ar: 'مفعّل', en: 'On' },
  conversionsOff: { ar: 'غير مفعّل', en: 'Off' },
  conversionsWeek: { ar: 'أُرسل {sent} · رُفض {failed}', en: '{sent} sent · {failed} refused' },
  conversionsReportedTo: { ar: 'سُجّل الشراء لدى', en: 'Purchase reported to' },
  conversionPlatform_meta: { ar: 'ميتا', en: 'Meta' },
  conversionPlatform_ga4: { ar: 'Google Analytics', en: 'Google Analytics' },
  conversionPlatform_tiktok: { ar: 'تيك توك', en: 'TikTok' },
  conversionPlatform_snapchat: { ar: 'سناب شات', en: 'Snapchat' },
  // Emails that send themselves (ADR-099).
  automationsPage: { ar: 'الرسائل التلقائية', en: 'Automations' },
  automationsPageHint: { ar: 'رسائل تُرسل وحدها في وقتها — كلٌّ مرة واحدة لموضوعها، ولمن وافق على الرسائل التسويقية', en: 'Mails that go on their own at the right time — each once per subject, and only to those who agreed to marketing' },
  automationsRunNow: { ar: 'شغّلها الآن', en: 'Run now' },
  automationsRan: { ar: 'أُرسلت {count} رسالة.', en: '{count} mails sent.' },
  automationsStatsNote: { ar: 'الأرقام لآخر {days} يومًا. الفتح تقريبي، والنقرات والطلبات دقيقة.', en: 'Numbers for the last {days} days. Opens are approximate; clicks and orders are exact.' },
  automationNumbers: { ar: 'أُرسلت {sent} · فُتحت {opened} · نُقرت {clicked} · {orders} طلب · {revenue}', en: '{sent} sent · {opened} opened · {clicked} clicked · {orders} orders · {revenue}' },
  automationWording: { ar: 'تعديل النص', en: 'Edit the wording' },
  automationInIntegrations: { ar: 'من التكاملات', en: 'In Integrations' },
  automation_review_request: { ar: 'طلب تقييم بعد التسليم', en: 'Review request after delivery' },
  automationHint_review_request: { ar: 'يدعو العميل لتقييم ما اشتراه، كل منتج برابط تقييمه.', en: 'Asks the customer to review what they bought, each product linked to its reviews.' },
  automation_win_back: { ar: 'اشتقنا لك', en: 'Win-back' },
  automationHint_win_back: { ar: 'لعميل توقف عن الشراء — مرة حتى يشتري مجددًا، مع كود له وحده إن أردت.', en: 'To a customer who stopped buying — once until they buy again, with a code for them alone if you like.' },
  automation_repurchase: { ar: 'تذكير بإعادة الشراء', en: 'Repurchase reminder' },
  automationHint_repurchase: { ar: 'لمنتجات تنفد — كريمات العناية والعبوات.', en: 'For things that run out — care creams, refills.' },
  automation_welcome: { ar: 'ترحيب بالمشترك', en: 'Welcome' },
  automationHint_welcome: { ar: 'فور تأكيد الاشتراك في النشرة، مع كود متجرك إن اخترته.', en: 'As a newsletter signup is confirmed, with your coupon if you choose one.' },
  automation_cart_second: { ar: 'تذكير ثانٍ بالسلة', en: 'Second cart reminder' },
  automation_birthday: { ar: 'تهنئة عيد الميلاد', en: 'Birthday greeting' },
  automationHint_birthday: {
    ar: 'صباح عيد ميلاد العميل، مرة في السنة، لمن شارك يومه ووافق على رسائلك — مع كود له وحده ونقاط هدية إن اخترت.',
    en: 'On the morning of a customer’s birthday, once a year, to those who shared the day and agreed to hear from you — with a code for them alone and gift points if you choose.',
  },
  automationBirthdayPoints: { ar: 'نقاط هدية', en: 'Gift points' },
  automationBirthdayPointsHint: { ar: 'تُضاف حين يعمل برنامج النقاط — 0 بلا نقاط', en: 'Added while the points programme runs — 0 for none' },
  automationHint_cart_second: { ar: 'بعد التذكير الأول إن بقيت السلة كما هي — والكود ينتقل إليه.', en: 'After the first if the cart is untouched — the code moves to it.' },
  automationDaysAfterDelivery: { ar: 'بعد التسليم (أيام)', en: 'Days after delivery' },
  automationDaysSinceOrder: { ar: 'بعد آخر طلب (أيام)', en: 'Days since the last order' },
  automationDaysSinceOrderHint: { ar: '١٤ على الأقل', en: '14 at least' },
  automationCodePercent: { ar: 'نسبة الكود ٪', en: 'Code, % off' },
  automationCodePercentHint: { ar: 'فارغ بلا كود — حتى {max}٪', en: 'Empty for none — up to {max}%' },
  automationCodeHours: { ar: 'صلاحية الكود (ساعات)', en: 'Code lasts (hours)' },
  automationRepurchaseHow: { ar: 'حدد على كل منتج «ذكّر بإعادة الشراء بعد» عدد أيام — وتُرسل لمن وافق على الرسائل التسويقية فقط.', en: 'Set "remind to buy again after" on each product, in days — sent only to those who agreed to marketing.' },
  automationWelcomeCoupon: { ar: 'كود الترحيب', en: 'Welcome coupon' },
  automationWelcomeCouponHint: { ar: 'كود من أكواد متجرك بحدوده — اتركه فارغًا لترحيب بلا كود', en: 'One of your coupons, with its own limits — empty for a welcome without one' },
  automationCartSecondHow: { ar: 'يُضبط مع التذكير الأول: «ساعات التذكير الثاني» في تكامل السلال المتروكة.', en: 'Set beside the first reminder: "secondDelayHours" in the abandoned-cart integration.' },
  automationOpenIntegrations: { ar: 'فتح التكاملات', en: 'Open Integrations' },
  trackingTitle: { ar: 'تتبع الرسائل التسويقية', en: 'Tracking marketing mail' },
  trackingHint: { ar: 'على الحملات والرسائل التلقائية فقط — لا تُتتبع رسائل الطلبات أبدًا. روابط المتجر تحمل اسم الحملة دائمًا ليظهر ما جلبته في تقرير المصادر.', en: 'On campaigns and automations only — order mail is never tracked. Store links always carry the campaign’s tag, so what it brought shows in the Sources report.' },
  trackingOpens: { ar: 'عدّ الفتح', en: 'Count opens' },
  trackingOpensHint: { ar: 'صورة صغيرة في الرسالة — تقريبي', en: 'A tiny picture in the mail — approximate' },
  trackingClicks: { ar: 'عدّ النقرات', en: 'Count clicks' },
  trackingClicksHint: { ar: 'الروابط تمر عبر نطاق متجرك', en: 'Links pass through your store’s own domain' },
  subscribersPage: { ar: 'المشتركون', en: 'Subscribers' },
  subscribersPageHint: { ar: 'من اشترك في النشرة دون حساب — المؤكَّد والمنتظر والمنسحب', en: 'Who signed up for the newsletter without an account — confirmed, waiting, withdrawn' },
  subscribersSearch: { ar: 'ابحث بالبريد', en: 'Search by email' },
  subscribersEmpty: { ar: 'لا مشتركين هنا', en: 'No subscribers here' },
  subscribersEmptyHint: { ar: 'يصلون من صندوق النشرة في التذييل، وإتمام الطلب، والنوافذ المنبثقة', en: 'They come from the footer box, the checkout and popups' },
  subscriberStatus_all: { ar: 'الكل', en: 'All' },
  subscriberStatus_confirmed: { ar: 'مؤكَّد', en: 'Confirmed' },
  subscriberStatus_pending: { ar: 'بانتظار التأكيد', en: 'Waiting to confirm' },
  subscriberStatus_withdrawn: { ar: 'منسحب', en: 'Withdrawn' },
  subscriberSource: { ar: 'المصدر', en: 'Source' },
  subscriberSource_FOOTER: { ar: 'تذييل الصفحة', en: 'Footer' },
  subscriberSource_CHECKOUT: { ar: 'إتمام الطلب', en: 'Checkout' },
  subscriberSource_ACCOUNT: { ar: 'الحساب', en: 'Account' },
  subscriberSource_POPUP: { ar: 'نافذة منبثقة', en: 'Popup' },
  subscriberSignedUp: { ar: 'تاريخ الاشتراك', en: 'Signed up' },
  subscriberConfirmedOn: { ar: 'أُكّد {date}', en: 'Confirmed {date}' },
  subscriberHasAccount: { ar: 'لديه حساب', en: 'Has an account' },
  subscriberWithdraw: { ar: 'سحب الموافقة', en: 'Withdraw' },
  subscriberWithdrawTitle: { ar: 'سحب الموافقة؟', en: 'Withdraw consent?' },
  subscriberWithdrawBody: { ar: 'لن تصل {email} أي رسالة تسويقية — ولا لأي حساب بالعنوان نفسه. افعلها بطلب صاحبها.', en: '{email} — and any account with it — will get no more marketing. Do it when the person asks.' },
  repurchaseDaysLabel: { ar: 'ذكّر بإعادة الشراء بعد (أيام)', en: 'Remind to buy again after (days)' },
  repurchaseDaysHint: { ar: 'للمنتجات التي تنفد — فارغ لا يذكّر. يعمل حين تشغّل التذكير في الرسائل التلقائية.', en: 'For things that run out — empty sends none. Works when the reminder is on in Automations.' },
  auditAction_campaign_unscheduled: { ar: 'إلغاء جدولة حملة', en: 'Campaign unscheduled' },
  auditAction_automations_run: { ar: 'تشغيل الرسائل التلقائية يدويًا', en: 'Automations run by hand' },
  auditAction_subscribers_exported: { ar: 'تصدير المشتركين', en: 'Subscribers exported' },
  auditAction_subscriber_withdrawn: { ar: 'سحب موافقة مشترك', en: 'Subscriber withdrawn' },
  auditKind_automation: { ar: 'رسالة تلقائية', en: 'Automation' },
  auditKind_subscriber: { ar: 'مشترك', en: 'Subscriber' },
  auditKind_reportView: { ar: 'عرض تقرير', en: 'Report view' },
  // Popups and the announcement bar (ADR-098).
  popupsPage: { ar: 'النوافذ والشريط', en: 'Popups & bar' },
  popupsPageHint: {
    ar: 'نوافذ تظهر في اللحظة المناسبة — كود ترحيبي مقابل الاشتراك، عرض، إعلان — وشريط فوق كل صفحة',
    en: 'Popups that ask at the right moment — a welcome code for a signup, an offer, a notice — and a bar above every page',
  },
  popupsTab: { ar: 'النوافذ المنبثقة', en: 'Popups' },
  announcementTab: { ar: 'شريط الإعلان', en: 'Announcement bar' },
  popupsListHint: { ar: 'نافذة واحدة على الأكثر لكل زيارة — الأعلى أولوية مما يناسب الصفحة. لا تظهر أبدًا في إتمام الطلب.', en: 'One per visit at most — the highest priority that fits the page. Never on checkout.' },
  popupNew: { ar: 'نافذة جديدة', en: 'New popup' },
  popupEdit: { ar: 'تعديل النافذة', en: 'Edit popup' },
  popupsEmpty: { ar: 'لا نوافذ بعد', en: 'No popups yet' },
  popupsEmptyHint: { ar: 'ابدأ بكود ترحيبي مقابل الاشتراك في النشرة — أكثرها نفعًا', en: 'Start with a welcome code for a newsletter signup — the one that earns most' },
  popupName: { ar: 'الاسم', en: 'Name' },
  popupNameHint: { ar: 'لك أنت، لا يراه الزائر', en: 'For you — visitors never see it' },
  popupWhen: { ar: 'متى وأين', en: 'When and where' },
  popupViews: { ar: 'المشاهدات', en: 'Views' },
  popupResult: { ar: 'النتيجة', en: 'Result' },
  popupSignups: { ar: 'اشتراكات', en: 'signups' },
  popupClicks: { ar: 'نقرات', en: 'clicks' },
  popupOn: { ar: 'مفعّلة', en: 'On' },
  popupOnHint: { ar: 'تظهر للزوار في تواريخها', en: 'Shown to visitors within its dates' },
  popupState_live: { ar: 'تعمل', en: 'Live' },
  popupState_scheduled: { ar: 'مجدولة', en: 'Scheduled' },
  popupState_ended: { ar: 'انتهت', en: 'Ended' },
  popupState_off: { ar: 'متوقفة', en: 'Off' },
  popupKind: { ar: 'نوع النافذة', en: 'Kind' },
  popupKind_NEWSLETTER: { ar: 'اشتراك في النشرة', en: 'Newsletter signup' },
  popupKind_OFFER: { ar: 'عرض بكود', en: 'Offer with a code' },
  popupKind_NOTICE: { ar: 'إعلان', en: 'Notice' },
  popupKindHint_NEWSLETTER: { ar: 'بريد الزائر، ثم كودك له', en: 'Their email, then your code for them' },
  popupKindHint_OFFER: { ar: 'كود يُنسخ ورابط', en: 'A code to copy and a link' },
  popupKindHint_NOTICE: { ar: 'صورة وكلمات ورابط', en: 'A picture, words and a link' },
  popupTitle: { ar: 'العنوان', en: 'Title' },
  popupBody: { ar: 'النص', en: 'Text' },
  popupCta: { ar: 'نص الزر', en: 'Button' },
  popupLink: { ar: 'الرابط', en: 'Link' },
  popupLinkHint: { ar: 'مسار في متجرك يبدأ بـ / أو رابط https', en: 'A store path starting with /, or an https address' },
  popupCoupon: { ar: 'الكود', en: 'Coupon' },
  popupCouponOptional: { ar: 'الكود (اختياري)', en: 'Coupon (optional)' },
  popupCouponHint_NEWSLETTER: { ar: 'يظهر بعد الاشتراك، لا قبله', en: 'Shown once the signup is taken, never before' },
  popupCouponHint_OFFER: { ar: 'يظهر مباشرة مع زر النسخ — حدوده من صفحة الكود', en: 'Shown at once with a copy button — its limits are the coupon’s own' },
  popupCouponHint_NOTICE: { ar: 'لا يظهر في الإعلان', en: 'Not shown on a notice' },
  popupNoCoupon: { ar: 'بلا كود', en: 'No coupon' },
  popupImage: { ar: 'الصورة', en: 'Picture' },
  popupImageHint: { ar: 'عرضية بنسبة ١٦:٩', en: 'Landscape, 16:9' },
  popupPreview: { ar: 'معاينة', en: 'Preview' },
  popupWhenTitle: { ar: 'متى تظهر ولمن', en: 'When, where and to whom' },
  popupWhenHint: { ar: 'نافذة واحدة لكل زيارة، ولا تعود لمن أغلقها قبل مدتها', en: 'One per visit, and not again to someone who closed it before its time' },
  popupTriggerLabel: { ar: 'تظهر', en: 'Shows' },
  popupTriggerName_DELAY: { ar: 'بعد ثوانٍ', en: 'After some seconds' },
  popupTriggerName_SCROLL: { ar: 'بعد تمرير جزء من الصفحة', en: 'After part of the page' },
  popupTriggerName_EXIT: { ar: 'عند المغادرة', en: 'On leaving' },
  popupTrigger_DELAY: { ar: 'بعد {n} ث', en: 'After {n}s' },
  popupTrigger_SCROLL: { ar: 'بعد {n}٪ من الصفحة', en: 'After {n}% of the page' },
  popupTrigger_EXIT: { ar: 'عند المغادرة', en: 'On leaving' },
  popupTriggerSeconds: { ar: 'الثواني', en: 'Seconds' },
  popupTriggerPercent: { ar: 'النسبة من الصفحة', en: 'Percent of the page' },
  popupPages: { ar: 'الصفحات', en: 'Pages' },
  popupPagesHint: { ar: 'لا تظهر أبدًا في إتمام الطلب أو الطلبات أو الحساب', en: 'Never on checkout, orders or the account' },
  popupPage_all: { ar: 'كل الصفحات', en: 'Everywhere' },
  popupPage_home: { ar: 'الرئيسية', en: 'Home' },
  popupPage_product: { ar: 'المنتجات', en: 'Products' },
  popupPage_category: { ar: 'الأقسام', en: 'Categories' },
  popupPage_cart: { ar: 'السلة', en: 'Cart' },
  popupPage_blog: { ar: 'المدونة', en: 'Blog' },
  popupPage_pages: { ar: 'صفحات المتجر', en: 'Store pages' },
  popupAudience: { ar: 'لمن', en: 'Who' },
  popupAudience_all: { ar: 'الجميع', en: 'Everyone' },
  popupAudience_new: { ar: 'الزيارة الأولى', en: 'First visit' },
  popupAudience_returning: { ar: 'الزوار العائدون', en: 'Returning visitors' },
  popupDevices: { ar: 'الأجهزة', en: 'Devices' },
  popupDevices_all: { ar: 'كل الأجهزة', en: 'All devices' },
  popupDevices_mobile: { ar: 'الجوال', en: 'Phones' },
  popupDevices_desktop: { ar: 'الكمبيوتر', en: 'Desktops' },
  popupFrequency: { ar: 'تعود بعد (أيام)', en: 'Again after (days)' },
  popupFrequencyHint: { ar: '٠ = في الزيارة التالية', en: '0 = on the next visit' },
  popupPriority: { ar: 'الأولوية', en: 'Priority' },
  popupPriorityHint: { ar: 'الأعلى يظهر عند تزاحم أكثر من نافذة', en: 'The higher shows when several fit' },
  popupStarts: { ar: 'يبدأ', en: 'Starts' },
  popupEnds: { ar: 'ينتهي', en: 'Ends' },
  popupDeleteTitle: { ar: 'حذف النافذة؟', en: 'Delete this popup?' },
  popupDeleteBody: { ar: 'تُحذف «{name}» وأرقامها. الكود الذي تعرضه يبقى.', en: '“{name}” and its counts go. The coupon it shows stays.' },
  announcementPreviewEmpty: { ar: 'اكتب رسالة لتراها هنا', en: 'Write a message to see it here' },
  announcementMessages: { ar: 'الرسائل', en: 'Messages' },
  announcementMessagesHint: { ar: 'حتى ٣ رسائل تتناوب فوق كل صفحة', en: 'Up to 3, taking turns above every page' },
  announcementMessage: { ar: 'رسالة {n}', en: 'Message {n}' },
  announcementText: { ar: 'النص', en: 'Text' },
  announcementAdd: { ar: 'إضافة رسالة', en: 'Add a message' },
  announcementLook: { ar: 'المظهر', en: 'Look' },
  announcementLookHint: { ar: 'ألوان من هوية متجرك', en: 'Colours from your store’s theme' },
  announcementTone_brand: { ar: 'لون المتجر', en: 'Brand' },
  announcementTone_dark: { ar: 'داكن', en: 'Dark' },
  announcementTone_sale: { ar: 'تخفيضات', en: 'Sale' },
  announcementTone_light: { ar: 'فاتح', en: 'Light' },
  announcementDismissible: { ar: 'يمكن إغلاقه', en: 'Can be closed' },
  announcementDismissibleHint: { ar: 'يبقى مغلقًا حتى تتغير رسائله', en: 'Stays closed until its messages change' },
  announcementCountdown: { ar: 'عدّ تنازلي حتى', en: 'Count down to' },
  announcementCountdownHint: { ar: 'اختياري — لنهاية عرض مثلًا', en: 'Optional — the end of a sale, say' },
  announcementSchedule: { ar: 'متى يظهر', en: 'When it shows' },
  announcementScheduleHint: { ar: 'اتركهما فارغين ليظهر ما دام مفعّلًا', en: 'Leave both empty to show it while it is on' },
  announcementOn: { ar: 'إظهار الشريط', en: 'Show the bar' },
  announcementOnHint: { ar: 'فوق كل صفحة في كل قالب', en: 'Above every page, in every theme' },
  // Where orders come from (ADR-097).
  reportSources: { ar: 'المصادر', en: 'Sources' },
  // ─── Reports v2 (ADR-108) ────────────────────────────────────────────────
  reportFunnel: { ar: 'مسار الشراء', en: 'Funnel' },
  reportFunnelHint: {
    ar: 'من زيارات متجرك نفسه — مجهولة الهوية: كم زيارة وصلت كل خطوة، مقارنةً بالفترة السابقة',
    en: 'From your store’s own visits — anonymous: how many reached each step, against the period before',
  },
  reportFunnelSince: { ar: 'تُحتسب الزيارات منذ {date}؛ لا بيانات قبله.', en: 'Visits are counted since {date}; there is nothing before it.' },
  reportFunnelNone: { ar: 'لا زيارات مسجّلة بعد. تبدأ الأرقام مع أول زائر بعد تفعيل هذه الميزة.', en: 'No visits recorded yet. The figures start with the first visitor after this was switched on.' },
  funnelVisits: { ar: 'الزيارات', en: 'Visits' },
  funnelViewed: { ar: 'شاهدت منتجًا', en: 'Saw a product' },
  funnelAdded: { ar: 'أضافت للسلة', en: 'Added to cart' },
  funnelCheckout: { ar: 'بدأت الدفع', en: 'Began checkout' },
  funnelOrdered: { ar: 'طلبت', en: 'Ordered' },
  funnelOfPrevious: { ar: '{share} من الخطوة السابقة', en: '{share} of the step before' },
  funnelOfVisits: { ar: '{share} من الزيارات', en: '{share} of visits' },
  funnelConversion: { ar: 'نسبة الطلب', en: 'Ordered' },
  funnelBySource: { ar: 'حسب المصدر', en: 'By source' },
  funnelByDevice: { ar: 'حسب الجهاز', en: 'By device' },
  deviceMobile: { ar: 'جوال', en: 'Phone' },
  deviceDesktop: { ar: 'كمبيوتر', en: 'Computer' },
  deviceUnknown: { ar: 'غير معروف', en: 'Unknown' },
  reportPerformance: { ar: 'أداء المنتجات', en: 'Product performance' },
  reportPerformanceHint: {
    ar: 'مشاهدات كل منتج وإضافاته للسلة ومبيعاته، ونسبة من اشتراه ممن شاهده، والمخزون وكم يومًا يكفي',
    en: 'Each product’s views, adds and sales, the share of views that bought, and its stock and how many days it lasts',
  },
  performanceSortRevenue: { ar: 'الأعلى إيرادًا', en: 'Most revenue' },
  performanceSortViews: { ar: 'الأكثر مشاهدة', en: 'Most viewed' },
  performanceSortConversion: { ar: 'الأعلى تحويلًا', en: 'Best converting' },
  performanceSortSlow: { ar: 'الراكدة', en: 'Slow movers' },
  performanceViews: { ar: 'مشاهدات', en: 'Views' },
  performanceAdds: { ar: 'إضافات للسلة', en: 'Added' },
  performanceConversion: { ar: 'طلبات لكل 100 مشاهدة', en: 'Orders per 100 views' },
  performanceStock: { ar: 'المخزون', en: 'Stock' },
  performanceCover: { ar: 'يكفي (أيام)', en: 'Lasts (days)' },
  performanceSlow: { ar: 'راكد', en: 'Slow' },
  performanceShown: { ar: 'يعرض {shown} من {total} منتجًا — التصدير يشملها كلها', en: 'Showing {shown} of {total} products — the export has them all' },
  reportRetention: { ar: 'عودة المشترين', en: 'Retention' },
  reportRetentionHint: {
    ar: 'المشترون حسب شهر أول طلب، ونسبة من عاد واشترى مرة أخرى خلال شهر و2 و3 و6 و12 شهرًا',
    en: 'Buyers by the month of their first order, and the share who bought again within 1, 2, 3, 6 and 12 months',
  },
  retentionBuyers: { ar: 'المشترون في الفترة', en: 'Buyers in the period' },
  retentionRepeatBuyers: { ar: 'اشتروا أكثر من مرة', en: 'Bought more than once' },
  retentionRepeatRate: { ar: 'نسبة التكرار', en: 'Repeat rate' },
  retentionCohort: { ar: 'شهر أول طلب', en: 'First order month' },
  retentionWithin: { ar: 'خلال {months} شهر', en: 'Within {months} mo' },
  retentionNotYet: { ar: 'لم يحن بعد', en: 'Too soon' },
  reportSourcesHint: { ar: 'الطلبات والإيرادات حسب القناة التي جاء منها الزائر', en: 'Orders and revenue by the channel each visit came from' },
  sourceChannel: { ar: 'القناة', en: 'Channel' },
  sourceShare: { ar: 'الحصة', en: 'Share' },
  sourceCampaigns: { ar: 'الحملات', en: 'Campaigns' },
  sourceCampaignsHint: { ar: 'أعلى الروابط الموسومة إيرادًا — المصدر / الوسيط والحملة', en: 'The tagged links that earned most — source / medium and campaign' },
  sourceCampaign: { ar: 'الحملة', en: 'Campaign' },
  sourceCampaignsEmpty: { ar: 'لا طلبات من روابط موسومة في هذه الفترة', en: 'No orders from tagged links in this period' },
  sourceTags: { ar: 'المصدر / الوسيط', en: 'Source / medium' },
  sourceReferrer: { ar: 'الموقع المحيل', en: 'Referring site' },
  sourceLanding: { ar: 'صفحة الوصول', en: 'Landing page' },
  sourceClickId: { ar: 'نقرة إعلان', en: 'Ad click' },
  orderSource: { ar: 'مصدر الطلب', en: 'Source' },
  channel_paid_search: { ar: 'بحث مدفوع', en: 'Paid search' },
  channel_paid_social: { ar: 'إعلانات التواصل', en: 'Paid social' },
  channel_organic_search: { ar: 'بحث طبيعي', en: 'Organic search' },
  channel_social: { ar: 'التواصل الاجتماعي', en: 'Social' },
  channel_email: { ar: 'البريد', en: 'Email' },
  channel_sms: { ar: 'الرسائل النصية', en: 'SMS' },
  channel_affiliate: { ar: 'المسوّقون', en: 'Affiliates' },
  channel_referral: { ar: 'مواقع أخرى', en: 'Referral' },
  channel_other: { ar: 'حملات أخرى', en: 'Other campaigns' },
  channel_direct: { ar: 'مباشر', en: 'Direct' },
  channel_staff: { ar: 'أنشأه فريقك', en: 'Placed by your team' },
  channel_unknown: { ar: 'غير مسجّل', en: 'Not recorded' },
  clickId_gclid: { ar: 'إعلان Google', en: 'Google ad' },
  clickId_gbraid: { ar: 'إعلان Google', en: 'Google ad' },
  clickId_wbraid: { ar: 'إعلان Google', en: 'Google ad' },
  clickId_msclkid: { ar: 'إعلان Bing', en: 'Bing ad' },
  clickId_ttclid: { ar: 'إعلان تيك توك', en: 'TikTok ad' },
  clickId_ScCid: { ar: 'إعلان سناب شات', en: 'Snapchat ad' },
  clickId_fbclid: { ar: 'رابط من فيسبوك أو إنستغرام', en: 'A Facebook or Instagram link' },
  campaignLinks: { ar: 'روابط الحملات', en: 'Campaign links' },
  campaignLinksHint: {
    ar: 'رابط لمتجرك موسوم بمكان نشره، فيُنسب كل طلب يجلبه إلى حملته في تقرير المصادر',
    en: 'A store link tagged for where you post it, so every order it brings is filed under its campaign',
  },
  campaignLinkTo: { ar: 'الرابط إلى', en: 'Link to' },
  campaignLinkToHint: { ar: 'الصفحة التي يصل إليها الزائر، وبأي لغة', en: 'The page the visitor lands on, and in which language' },
  campaignTarget_home: { ar: 'الرئيسية', en: 'Home page' },
  campaignTarget_product: { ar: 'منتج', en: 'A product' },
  campaignTarget_category: { ar: 'قسم', en: 'A category' },
  campaignWhere: { ar: 'أين ستنشره', en: 'Where it goes' },
  campaignWhereHint: { ar: 'يملأ المصدر والوسيط — ويمكنك تعديلهما', en: 'Fills in the source and medium — both can be changed' },
  campaignPreset_instagram: { ar: 'إنستغرام', en: 'Instagram' },
  campaignPreset_snapchat: { ar: 'سناب شات', en: 'Snapchat' },
  campaignPreset_tiktok: { ar: 'تيك توك', en: 'TikTok' },
  campaignPreset_x: { ar: 'إكس', en: 'X' },
  campaignPreset_whatsapp: { ar: 'واتساب', en: 'WhatsApp' },
  campaignPreset_google: { ar: 'إعلانات Google', en: 'Google Ads' },
  campaignPreset_email: { ar: 'البريد', en: 'Email' },
  campaignPreset_sms: { ar: 'رسالة نصية', en: 'SMS' },
  campaignPreset_influencer: { ar: 'مشهور', en: 'Influencer' },
  campaignSource: { ar: 'المصدر (utm_source)', en: 'Source (utm_source)' },
  campaignMedium: { ar: 'الوسيط (utm_medium)', en: 'Medium (utm_medium)' },
  campaignPaid: { ar: 'إعلان مدفوع', en: 'A paid ad' },
  campaignPaidHint: { ar: 'يُحسب في «إعلانات التواصل» أو «بحث مدفوع» لا في المنشورات', en: 'Counted as paid social or paid search, not as a post' },
  campaignLinkName: { ar: 'اسم الحملة', en: 'Campaign name' },
  campaignLinkNameHint: {
    ar: 'اسم واحد لكل روابط الحملة لتُجمع في التقرير — الحروف اللاتينية تُبقي الرابط قصيرًا',
    en: 'One name for all of a campaign’s links, so they add up in the report — Latin letters keep the link short',
  },
  campaignLinkNamePlaceholder: { ar: 'ramadan-sale', en: 'ramadan-sale' },
  campaignContent: { ar: 'تمييز الرابط (اختياري)', en: 'Which post (optional)' },
  campaignContentHint: { ar: 'لتفرق بين منشورين في الحملة نفسها — story-1، bio', en: 'To tell two posts of one campaign apart — story-1, bio' },
  campaignYourLink: { ar: 'رابطك', en: 'Your link' },
  campaignFillIn: { ar: 'اختر الصفحة واكتب اسم الحملة', en: 'Choose the page and name the campaign' },
  campaignNoStoreUrl: { ar: 'عنوان المتجر غير مضبوط في هذه النسخة', en: "The store's address is not set in this build" },
  campaignRecent: { ar: 'حملات آخر ٣٠ يومًا', en: 'Last 30 days' },
  campaignRecentHint: { ar: 'ما جلبته الروابط الموسومة', en: 'What tagged links brought in' },
  // Product feeds (ADR-096).
  feedsPage: { ar: 'كتالوجات المنتجات', en: 'Product feeds' },
  feedsPageHint: {
    ar: 'روابط تقرأ منها Google وميتا وتيك توك وسناب شات منتجاتك يوميًا لإعلانات التسوق والكتالوج',
    en: 'The addresses Google, Meta, TikTok and Snapchat read your products from every day, for shopping and catalogue ads',
  },
  feedsSummary: { ar: 'الكتالوج يحمل {rows} صنفًا من {products} منتج معروض.', en: 'The feed carries {rows} rows from {products} products on sale.' },
  feedChannel_google: { ar: 'Google Merchant Center', en: 'Google Merchant Center' },
  feedChannel_meta: { ar: 'ميتا — فيسبوك وإنستغرام', en: 'Meta — Facebook and Instagram' },
  feedChannel_tiktok: { ar: 'تيك توك', en: 'TikTok' },
  feedChannel_snapchat: { ar: 'سناب شات', en: 'Snapchat' },
  feedChannelHint_google: {
    ar: 'في Merchant Center: المنتجات ← مصادر البيانات ← إضافة ملف برابط، والصق الرابط',
    en: 'In Merchant Center: Products → Data sources → Add a file by link, and paste the address',
  },
  feedChannelHint_meta: {
    ar: 'في مدير التجارة: الكتالوج ← مصادر البيانات ← إضافة عناصر ← موجز بيانات مجدول',
    en: 'In Commerce Manager: Catalogue → Data sources → Add items → Scheduled feed',
  },
  feedChannelHint_tiktok: {
    ar: 'في مدير الكتالوج بتيك توك: إضافة منتجات ← جدولة موجز بيانات',
    en: 'In TikTok Catalog Manager: Add products → Data feed schedule',
  },
  feedChannelHint_snapchat: {
    ar: 'في مدير إعلانات سناب: الكتالوجات ← إنشاء كتالوج ← موجز مجدول',
    en: 'In Snapchat Ads Manager: Catalogs → Create catalog → Scheduled feed',
  },
  feedOn: { ar: 'مفعّل', en: 'On' },
  feedOff: { ar: 'متوقف', en: 'Off' },
  feedLanguage_ar: { ar: 'العربية', en: 'Arabic' },
  feedLanguage_en: { ar: 'الإنجليزية', en: 'English' },
  feedLastRead: { ar: 'آخر قراءة {when}', en: 'Last read {when}' },
  feedNeverRead: { ar: 'لم تُقرأ بعد — الصق الرابط في المنصة', en: 'Not read yet — paste the address into the platform' },
  feedSaveFirst: { ar: 'احفظ ليعمل الرابط', en: 'Save to make the address work' },
  feedsSettings: { ar: 'ما يُرسل', en: 'What is sent' },
  feedsSettingsHint: { ar: 'تنطبق على القنوات كلها وباللغتين', en: 'For every channel, in both languages' },
  feedsOutOfStock: { ar: 'إبقاء النافد في الكتالوج', en: 'Keep out-of-stock products in' },
  feedsOutOfStockHint: {
    ar: 'تُعرض «غير متوفر» — تفضّل المنصات ذلك على منتج يختفي ويعود',
    en: 'Marked out of stock — the platforms prefer that to a product that comes and goes',
  },
  feedsBrandFallback: { ar: 'اسم المتجر علامةً لمنتج بلا علامة', en: "The store's name as the brand of a product with none" },
  feedsBrandFallbackHint: { ar: 'لمتجر يبيع صنعه — العلامة مطلوبة في ميتا وتيك توك', en: 'For a store selling its own goods — Meta and TikTok require a brand' },
  feedsCondition: { ar: 'حالة المنتجات', en: 'Condition' },
  feedsConditionHint: { ar: 'تُرسل لكل منتج', en: 'Sent for every product' },
  feedCondition_new: { ar: 'جديد', en: 'New' },
  feedCondition_refurbished: { ar: 'مجدّد', en: 'Refurbished' },
  feedCondition_used: { ar: 'مستعمل', en: 'Used' },
  feedsLeftOut: { ar: 'خارج الكتالوج', en: 'Left out' },
  feedsLeftOutHint: { ar: 'منتجات معروضة في المتجر لا تُرسل، ولماذا', en: 'Products on the store that are not sent, and why' },
  feedsWarnings: { ar: 'تُرسل لكنها قد تُرفض أو تضعف', en: 'Sent, but may be rejected or do poorly' },
  feedsWarningsHint: { ar: 'ما ستشكو منه المنصات — أصلحه قبل أن تفعل', en: 'What the platforms will complain about — fix it before they do' },
  feedIssue_excluded: { ar: 'استبعدتها أنت', en: 'Left out by you' },
  feedIssueHint_excluded: { ar: 'مفعّل فيها «استبعاد من الكتالوجات»', en: '"Leave out of product feeds" is on' },
  'feedIssue_no-image': { ar: 'بلا صورة', en: 'No picture' },
  'feedIssueHint_no-image': { ar: 'كل المنصات ترفض منتجًا بلا صورة', en: 'Every platform rejects a product without one' },
  'feedIssue_not-a-parcel': { ar: 'منتجات رقمية', en: 'Downloads and codes' },
  'feedIssueHint_not-a-parcel': { ar: 'إعلانات الكتالوج تصف منتجًا يُشحن', en: 'Catalogue ads describe something shipped' },
  'feedIssue_out-of-stock': { ar: 'نافدة', en: 'Out of stock' },
  'feedIssueHint_out-of-stock': { ar: 'تُستبعد لأنك اخترت ذلك أعلاه', en: 'Left out because of your choice above' },
  'feedIssue_no-identifier': { ar: 'بلا باركود ولا رقم قطعة', en: 'No barcode or part number' },
  'feedIssueHint_no-identifier': {
    ar: 'تطابق المنصات المنتج بالباركود (GTIN) أو بالعلامة ورقم القطعة (MPN)',
    en: 'Platforms match a product by its barcode (GTIN), or by brand and part number (MPN)',
  },
  'feedIssue_no-brand': { ar: 'بلا علامة', en: 'No brand' },
  'feedIssueHint_no-brand': { ar: 'مطلوبة في ميتا وتيك توك', en: 'Required by Meta and TikTok' },
  'feedIssue_no-google-category': { ar: 'بلا فئة Google', en: 'No Google category' },
  'feedIssueHint_no-google-category': { ar: 'اختر فئة Google لأقسامك وترثها الأقسام الفرعية', en: 'Set one on your categories; their children inherit it' },
  'feedIssue_no-description': { ar: 'بلا وصف', en: 'No description' },
  'feedIssueHint_no-description': { ar: 'يُرسل الاسم بدلًا منه، وهذا يضعف الظهور', en: 'The name is sent instead, which ranks poorly' },
  feedsExcludeLabel: { ar: 'استبعاد من الكتالوجات', en: 'Leave out of product feeds' },
  feedsExcludeHint: { ar: 'لا يُرسل إلى Google وميتا وتيك توك وسناب شات، ويبقى في المتجر', en: 'Not sent to Google, Meta, TikTok or Snapchat; still on sale' },
  googleCategory: { ar: 'فئة Google للمنتجات', en: 'Google product category' },
  googleCategoryHint: {
    ar: 'رقمها أو مسارها من تصنيف منتجات Google — لكتالوجات الإعلانات، وترثها الأقسام الفرعية',
    en: "Its number or path from Google's product taxonomy — for the product feeds; child categories inherit it",
  },
  seoHideProductHint: { ar: 'يبقى للبيع في المتجر، لكن لا يظهر في نتائج البحث ولا في خريطة الموقع', en: 'Still on sale, out of search results and the sitemap' },
  brands: { ar: 'العلامات التجارية', en: 'Brands' },
  brandsSubtitle: { ar: 'لكل علامة صفحتها في المتجر: اسمها ووصفها وشعارها ونتيجة بحثها', en: 'Each has its own page on the store: its name, a paragraph, a logo, its search result' },
  brandsSearch: { ar: 'ابحث باسم العلامة', en: 'Search by name' },
  brandsEmpty: { ar: 'لا علامات بعد', en: 'No brands yet' },
  brandAdd: { ar: 'إضافة علامة', en: 'Add brand' },
  brandProducts: { ar: 'المنتجات', en: 'Products' },
  brandSeoWritten: { ar: 'عنوان مكتوب', en: 'Own title' },
  brandSeoPattern: { ar: 'من القالب', en: 'From the pattern' },
  brandInactive: { ar: 'موقوفة', en: 'Off' },
  brandEditHint: { ar: 'تغيير الرابط يُبقي الرابط القديم يعمل بتحويل دائم', en: 'Changing its link keeps the old one working with a permanent redirect' },
  brandName: { ar: 'اسم العلامة', en: 'Brand name' },
  brandDescription: { ar: 'وصف العلامة', en: 'Brand description' },
  brandDescriptionHint: { ar: 'فقرة أعلى صفحة العلامة', en: 'A paragraph heading its page' },
  brandLink: { ar: 'رابط الصفحة', en: 'Page link' },
  brandLinkHint: { ar: '/brands/… — حروف لاتينية وأرقام وشرطات', en: '/brands/… — Latin letters, digits and dashes' },
  brandLinkChanged: { ar: 'الرابط القديم سيُحوَّل إلى الجديد', en: 'The old link will redirect to the new one' },
  brandLogo: { ar: 'الشعار', en: 'Logo' },
  brandLogoHint: { ar: 'يظهر في صفحتها، وصورتها عند المشاركة', en: 'Shown on its page, and its picture when shared' },
  brandActive: { ar: 'ظاهرة في المتجر', en: 'On the store' },
  brandActiveHint: { ar: 'الموقوفة تختفي من القوائم وفلاتر المنتجات', en: 'Off, it leaves the lists and the product filters' },
  categoryDescription: { ar: 'وصف القسم', en: 'Category description' },
  categoryDescriptionHint: { ar: 'فقرة أعلى صفحة القسم — تساعد صفحته على الظهور في البحث', en: 'A paragraph heading its page — it helps the page rank' },
  seoPreviewDevice: { ar: 'الجهاز', en: 'Device' },
  seoDesktop: { ar: 'كمبيوتر', en: 'Desktop' },
  seoMobile: { ar: 'جوال', en: 'Mobile' },
  seoPreviewNote: { ar: 'تقريبية: قد يختار Google نصًا آخر من الصفحة.', en: 'Approximate: Google may choose other words from the page.' },
  seoNoDescription: { ar: 'لا وصف بعد — اكتب المحتوى أو الوصف.', en: 'No description yet — write the content or a description.' },
  seoShareTitle: { ar: 'المشاركة', en: 'Sharing' },
  seoShareHint: { ar: 'ما يظهر عند مشاركة الرابط في واتساب وX وغيرها.', en: 'What shows when the link is shared on WhatsApp, X and the like.' },
  seoShareImage: { ar: 'صورة المشاركة', en: 'Share image' },
  seoShareImageHint: { ar: 'أفقية بنسبة ١٫٩١:١ — ١٢٠٠×٦٣٠ بكسل مثالية.', en: 'Landscape at 1.91:1 — 1200×630 pixels is ideal.' },
  seoPreviewSocial: { ar: 'معاينة الرابط', en: 'Link preview' },
  seoNoImage: { ar: 'بلا صورة', en: 'No image' },
  seoEnginesTitle: { ar: 'محركات البحث', en: 'Search engines' },
  seoIndexLabel: { ar: 'تظهر في نتائج البحث', en: 'Show in search results' },
  seoIndexOnHint: { ar: 'تُدرج في خريطة الموقع ويُسمح لمحركات البحث بفهرستها.', en: 'Listed in the sitemap, and search engines may index it.' },
  seoIndexOffHint: { ar: 'مخفية عن محركات البحث (noindex) وخارج خريطة الموقع، ويبقى رابطها يعمل.', en: 'Hidden from search engines (noindex) and left out of the sitemap; its link still works.' },
  seoHealthTitle: { ar: 'جاهزية البحث', en: 'Search health' },
  seoScoreLabel: { ar: 'درجة البحث {score} من ١٠٠', en: 'Search score {score} out of 100' },
  seoShort: { ar: 'SEO {score}', en: 'SEO {score}' },
  seoAllGood: { ar: 'كل شيء جاهز لهذه اللغة.', en: 'All set for this language.' },
  seoToImprove: { ar: '{count} نقاط للتحسين', en: '{count} things to improve' },
  seoImprove: { ar: 'تحسين البحث', en: 'Improve SEO' },
  seoNotWritten: { ar: 'هذه اللغة غير مكتوبة: تحتاج عنوانًا ومحتوى.', en: 'Not written in this language: it needs a title and content.' },
  seoTitleMissing: { ar: 'لا عنوان للبحث.', en: 'No search title.' },
  seoTitleLong: { ar: 'عنوان البحث {count} حرفًا — Google يقطع بعد {max} تقريبًا.', en: 'The search title is {count} characters — Google cuts near {max}.' },
  seoTitleShort: { ar: 'عنوان البحث {count} حرفًا فقط — {min} على الأقل يصف الصفحة أفضل.', en: 'The search title is only {count} characters — {min} or more describes it better.' },
  seoTitleGood: { ar: 'طول عنوان البحث مناسب ({count} حرفًا).', en: 'The search title is a good length ({count}).' },
  seoDescriptionAuto: { ar: 'لا وصف مكتوب للبحث: ستُستخدم بداية الصفحة.', en: 'No search description: the opening words will be used.' },
  seoDescriptionLong: { ar: 'الوصف {count} حرفًا — يُقطع بعد {max} تقريبًا.', en: 'The description is {count} characters — cut near {max}.' },
  seoDescriptionShort: { ar: 'الوصف {count} حرفًا فقط — اكتب {min} على الأقل.', en: 'The description is only {count} characters — aim for {min} or more.' },
  seoDescriptionGood: { ar: 'طول الوصف مناسب ({count} حرفًا).', en: 'The description is a good length ({count}).' },
  seoLengthShort: { ar: 'المحتوى {count} كلمة — الصفحات الأقل من {min} قد تبدو ضعيفة لمحركات البحث.', en: 'The content is {count} words — under {min} can read as thin to search engines.' },
  seoLengthGood: { ar: 'محتوى كافٍ ({count} كلمة).', en: 'Plenty of content ({count} words).' },
  seoSectionsMissing: { ar: 'قسّم النص بعناوين (##) ليسهل قراءته وفهرسته.', en: 'Break the text up with headings (##) — easier to read and to index.' },
  seoSectionsGood: { ar: 'مقسّمة إلى {count} أقسام بعناوين.', en: 'Organised in {count} headed sections.' },
  seoHeadingOrder: { ar: 'تبدأ بعنوان فرعي (###): ابدأ بعنوان قسم (##).', en: 'It opens with a subheading (###): start with a section heading (##).' },
  seoLinksBroken: { ar: 'رابط لن يعمل: {link}', en: 'A link that will not work: {link}' },
  seoLinksMissing: { ar: 'لا روابط لصفحات أخرى في المتجر — الروابط الداخلية تساعد العملاء ومحركات البحث.', en: 'No links to other pages on the store — internal links help shoppers and search engines.' },
  seoLinksGood: { ar: '{count} روابط لصفحات أخرى في المتجر.', en: '{count} links to other pages on the store.' },
  seoArabicMissing: { ar: 'النسخة العربية فارغة: يراها العملاء بالإنجليزية.', en: 'The Arabic version is empty: Arabic shoppers see English.' },
  seoEnglishMissing: { ar: 'النسخة الإنجليزية فارغة: يراها العملاء بالعربية.', en: 'The English version is empty: English shoppers see Arabic.' },
  seoHidden: { ar: 'مخفية عن محركات البحث.', en: 'Hidden from search engines.' },
  seoImageMissing: { ar: 'لا صورة للمشاركة — الروابط المشتركة بصورة تُفتح أكثر.', en: 'No share image — shared links with a picture get more clicks.' },
  seoImageGood: { ar: 'لها صورة مشاركة.', en: 'Has a share image.' },
  editContent: { ar: 'تحرير', en: 'Edit' },
  done: { ar: 'تم', en: 'Done' },
  advanced: { ar: 'إعدادات متقدمة', en: 'Advanced' },
  advancedHint: { ar: 'إعدادات ليس لها نموذج بعد — للمطوّرين.', en: 'Settings without a form yet — for developers.' },
  invalidJson: { ar: 'صيغة غير صالحة — راجع الأقواس والفواصل.', en: 'That is not valid JSON — check the brackets and commas.' },
  ownerOnlySettings: { ar: 'الإعدادات متاحة لمالك المتجر فقط.', en: 'Only the store owner can change settings.' },

  // ─── Product editor tabs (S13) ────────────────────────────────────────────
  tabGeneral: { ar: 'عام', en: 'General' },
  tabSeo: { ar: 'الظهور في البحث', en: 'Search & tags' },
  tabSpecs: { ar: 'المواصفات', en: 'Specifications' },
  tabInputs: { ar: 'خيارات العميل', en: 'Customer inputs' },
  tabShipping: { ar: 'الشحن والأبعاد', en: 'Shipping & size' },
  tabMedia: { ar: 'الوسائط', en: 'Media' },
  seoHint: {
    ar: 'ما يظهر في نتائج البحث. اتركه فارغًا ليُستخدم اسم المنتج ووصفه.',
    en: 'What a search result shows. Leave empty to fall back to the product name and description.',
  },
  metaTitle: { ar: 'عنوان البحث', en: 'Search title' },
  metaTitleHint: { ar: 'نحو ٦٠ حرفًا', en: 'About 60 characters' },
  metaDescription: { ar: 'وصف البحث', en: 'Search description' },
  tags: { ar: 'الوسوم', en: 'Tags' },
  tagsHint: {
    ar: 'مفصولة بفاصلة — تُستخدم للعرض والتجميع، مثل: جديد، هدية',
    en: 'Comma separated, for merchandising — new-in, gift, bestseller',
  },
  brand: { ar: 'العلامة التجارية', en: 'Brand' },
  brandHint: { ar: 'اختياري', en: 'Optional' },
  dragToReorder: { ar: 'اسحب لإعادة الترتيب', en: 'Drag to reorder' },
  previewImage: { ar: 'معاينة الصورة', en: 'Preview image' },
  mainImage: { ar: 'الرئيسية', en: 'Main' },
  moveEarlier: { ar: 'تحريك للخلف', en: 'Move earlier' },
  moveLater: { ar: 'تحريك للأمام', en: 'Move later' },
  imageUploadFailed: { ar: 'تعذّر رفع بعض الصور.', en: 'Some images could not be uploaded.' },
  close: { ar: 'إغلاق', en: 'Close' },
  saveFirst: { ar: 'احفظ المنتج أولًا', en: 'Save the product first' },
  shippingPendingHint: {
    ar: 'الوزن والأبعاد تخص المتغيّر، والمتغيّرات لا تُنشأ إلا بعد الحفظ.',
    en: 'Weight and size belong to a variant, and variants only exist once the product is saved.',
  },
  saveFirstHint: {
    ar: 'المواصفات والصور وخيارات العميل تُربط بمنتج قائم — احفظه ثم عد إلى هنا.',
    en: 'Specifications, images, and customer inputs attach to a saved product — save it, then come back.',
  },
  customerInputs: { ar: 'خيارات يملؤها العميل', en: 'Customer inputs' },
  customerInputsHint: {
    ar: 'أسئلة يجيب عنها المشتري قبل الإضافة للسلة — تُضاف لما يرثه المنتج من قسمه.',
    en: 'Questions the buyer answers before adding to the cart — on top of whatever the category already offers.',
  },
  noCustomerInputs: { ar: 'لا توجد خيارات معرّفة', en: 'No inputs defined yet' },
  noCustomerInputsHint: {
    ar: 'عرّف خيارًا مرة واحدة — نقش، تغليف هدية، تاريخ توصيل — ثم أتِحه هنا.',
    en: 'Define one once — engraving, gift wrapping, a delivery date — then offer it here.',
  },
  requiredAtCheckout: { ar: 'إلزامي', en: 'Required' },
  shippingCardHint: {
    ar: 'لكل متغيّر، لأن المتغيّر هو ما يُشحن فعلًا.',
    en: 'Per variant, because the variant is what actually ships.',
  },
  weightMissingNote: {
    ar: 'متغيّر بلا وزن لا يمكن تسعير شحنه بالوزن لاحقًا.',
    en: 'A variant with no weight cannot be priced by weight when weight-based shipping arrives.',
  },
  noBrand: { ar: 'بدون علامة', en: 'No brand' },

  // ─── Product editor: layout, colours and grouped variants ────────────────
  productBasics: { ar: 'المعلومات الأساسية', en: 'Basic information' },
  productName: { ar: 'الاسم', en: 'Name' },
  productDescription: { ar: 'الوصف', en: 'Description' },
  pricing: { ar: 'التسعير', en: 'Pricing' },
  organization: { ar: 'التصنيف', en: 'Organization' },
  atAGlance: { ar: 'نظرة سريعة', en: 'At a glance' },
  statusDRAFTHint: { ar: 'لا يراه إلا الموظفون', en: 'Only staff can see it' },
  statusACTIVEHint: { ar: 'معروض للبيع في المتجر', en: 'On sale in the store' },
  statusARCHIVEDHint: { ar: 'مخفي، ومحفوظ للطلبات السابقة', en: 'Hidden, and kept for past orders' },
  colours: { ar: 'الألوان', en: 'Colours' },
  coloursHint: {
    ar: 'اسم كل لون بالعربية والإنجليزية، وعيّنته وصوره — ما يختار منه المتسوّق. اسحب لترتيبها',
    en: 'Each colour’s name in both languages, its swatch and its photos — what a shopper picks from. Drag to order them',
  },
  colour: { ar: 'اللون', en: 'Colour' },
  noColour: { ar: 'بلا لون', en: 'No colour' },
  addColour: { ar: 'إضافة لون', en: 'Add colour' },
  colourCode: { ar: 'الرمز', en: 'Code' },
  colourCodeHint: {
    ar: 'بالإنجليزية، تستخدمه السلة والفلاتر — يُشتق من الاسم الإنجليزي',
    en: 'Latin, used by the cart and the filters — made from the English name',
  },
  colourCodeTaken: { ar: 'هذا الرمز لون موجود في المنتج', en: 'That code is already one of this product’s colours' },
  colourStats: { ar: '{variants} متغيّرات · {stock} في المخزون', en: '{variants} variants · {stock} in stock' },
  colourPhotos: { ar: '{count} صور', en: '{count} photos' },
  colourNoPhoto: { ar: 'بلا صورة خاصة — اربط صورة من تبويب الوسائط', en: 'No photo of its own — link one on the Media tab' },
  removeColour: { ar: 'حذف اللون', en: 'Remove colour' },
  removeColourTitle: { ar: 'حذف {name}؟', en: 'Remove {name}?' },
  removeColourBody: {
    ar: 'تُحذف متغيّراته ({count}) من المنتج، وتبقى في الطلبات السابقة.',
    en: 'Its {count} variants are removed from the product; past orders keep them.',
  },
  noColours: { ar: 'لا ألوان لهذا المنتج', en: 'This product has no colours' },
  noColoursHint: {
    ar: 'أضف لونًا لتبيعه بأكثر من لون — لكل لون متغيّراته وصوره وعيّنته',
    en: 'Add a colour to sell it in more than one — each colour gets its own variants, photos and swatch',
  },
  otherOptions: { ar: 'خيارات أخرى', en: 'Other options' },
  otherOptionsHint: { ar: 'كيف يُقرأ باقي كل متغيّر — الوحدة، المقاس', en: 'How the rest of each variant reads — the unit, the size' },
  saveNamesAndColours: { ar: 'حفظ الأسماء والألوان', en: 'Save names and colours' },
  newColourTitle: { ar: 'لون جديد', en: 'A new colour' },
  newColourSubtitle: {
    ar: 'يُباع بنفس طرق بيع اللون الذي تنسخه — كل طريقة متغيّر',
    en: 'Sold the same ways as the colour you copy — each way is a variant',
  },
  sellLike: { ar: 'يُباع مثل', en: 'Sell it like' },
  variantsToCreate: { ar: 'المتغيّرات التي ستُنشأ', en: 'Variants to create' },
  createColour: { ar: 'إضافة اللون و{count} متغيّرات', en: 'Add the colour and {count} variants' },
  newColourStock: { ar: 'تبدأ بمخزون صفر — عدّله بعد الإضافة', en: 'They start with no stock — set it once added' },
  groupByColour: { ar: 'حسب اللون', en: 'By colour' },
  groupNone: { ar: 'قائمة واحدة', en: 'One list' },
  variantsSummary: { ar: '{variants} متغيّرات في {colours} ألوان', en: '{variants} variants in {colours} colours' },
  saveVariantChanges: { ar: 'حفظ {count} تعديلات', en: 'Save {count} changes' },
  editVariant: { ar: 'تعديل المتغيّر', en: 'Edit variant' },
  newVariant: { ar: 'متغيّر جديد', en: 'New variant' },
  variantOptionsLabel: { ar: 'الخيارات', en: 'Options' },
  optionName: { ar: 'الخيار', en: 'Option' },
  optionValue: { ar: 'القيمة', en: 'Value' },
  addOption: { ar: 'إضافة خيار', en: 'Add option' },
  variantSelling: { ar: 'قواعد البيع', en: 'Selling rules' },
  totalStock: { ar: 'إجمالي المخزون', en: 'Total stock' },
  addVariantToColour: { ar: 'متغيّر لهذا اللون', en: 'Variant in this colour' },
  mediaTitle: { ar: 'الصور', en: 'Photos' },
  mediaHint: {
    ar: 'الصورة الأولى تتصدّر القوائم. اربط الصورة بلون فيعرضها المتجر عند اختياره — وتصبح صورة القماش في التنسيقات',
    en: 'The first photo leads every listing. Link a photo to a colour and the store shows it when that colour is chosen — it becomes the fabric’s picture in looks, too',
  },
  imageGeneral: { ar: 'عامة — لكل الألوان', en: 'General — every colour' },
  altText: { ar: 'وصف الصورة', en: 'Alt text' },
  altTextHint: { ar: 'لمن لا يرى الصورة، ولمحركات البحث', en: 'For anyone who cannot see it, and for search engines' },
  dropImages: { ar: 'اسحب الصور هنا، أو اخترها', en: 'Drop photos here, or choose them' },
  builderHint: {
    ar: 'اختر من مكتبة الخيارات ما يأتي به المنتج — ألوانه وطرق بيعه — وكل تركيبة متغيّر',
    en: 'Pick from the options library what the product comes in — its colours, how it is sold — and every combination is a variant',
  },
  optionCode: { ar: 'رمز الخيار', en: 'Option code' },
  optionCodeHint: { ar: 'مثل unit أو size', en: 'Like unit or size' },
  addValue: { ar: 'إضافة قيمة', en: 'Add value' },
  addOtherOption: { ar: 'إضافة خيار آخر', en: 'Add another option' },

  // ─── Specifications (attributes) ──────────────────────────────────────────
  attributes: { ar: 'المواصفات والخصائص', en: 'Specifications' },
  attributesSubtitle: {
    ar: 'حقائق مكتوبة عن المنتج تُعرّف مرة وتُستخدم في كل المنتجات.',
    en: 'Typed facts about a product, defined once and reused across the catalogue.',
  },
  addAttribute: { ar: 'إضافة خاصية', en: 'Add an attribute' },
  editAttribute: { ar: 'تعديل الخاصية', en: 'Edit attribute' },
  attributeEditorHint: {
    ar: 'تصف المنتج ولا تُنشئ خيارات شراء — الخيارات القابلة للشراء تبقى في المتغيّرات.',
    en: 'These describe a product; they do not create purchasable options — variants still do that.',
  },
  noAttributes: { ar: 'لا توجد خصائص بعد', en: 'No attributes yet' },
  noAttributesHint: {
    ar: 'عرّف خاصية مرة، ثم اطلبها من قسم كامل أو من منتج واحد.',
    en: 'Define one once, then ask a whole category for it — or a single product.',
  },
  attributeCode: { ar: 'الرمز', en: 'Code' },
  attributeCodeHint: {
    ar: 'هوية ثابتة تستخدمها عمليات الاستيراد — لا تتغير بعد الحفظ.',
    en: 'A stable identity imports rely on — fixed once saved.',
  },
  attributeType: { ar: 'النوع', en: 'Type' },
  attrTypeTEXT: { ar: 'نص', en: 'Text' },
  attrTypeNUMBER: { ar: 'رقم', en: 'Number' },
  attrTypeBOOLEAN: { ar: 'نعم/لا', en: 'Yes / no' },
  attrTypeSELECT: { ar: 'اختيار واحد', en: 'One choice' },
  attrTypeMULTI_SELECT: { ar: 'اختيار متعدد', en: 'Several choices' },
  nameArabic: { ar: 'الاسم بالعربية', en: 'Arabic name' },
  nameEnglish: { ar: 'الاسم بالإنجليزية', en: 'English name' },
  measurementUnit: { ar: 'الوحدة', en: 'Unit' },
  unitHint: { ar: 'تظهر بعد الرقم: لتر، فولت', en: 'Shown after the number: L, V' },
  filterable: { ar: 'قابلة للتصفية', en: 'Filterable' },
  filterableHint: {
    ar: 'تُعرض كخيار تصفية في صفحة القسم.',
    en: 'Offered as a filter on the listing page.',
  },
  choices: { ar: 'الخيارات', en: 'Choices' },
  choicesHint: {
    ar: 'حذف خيار يحذف إجابات المنتجات التي اختارته.',
    en: 'Removing a choice also removes it from every product that selected it.',
  },
  addChoice: { ar: 'إضافة خيار', en: 'Add a choice' },
  removeChoiceWarning: {
    ar: 'سيُحذف من المنتجات التي اختارته',
    en: 'This also clears it from products that chose it',
  },
  optionsCount: { ar: 'خيارات', en: 'choices' },
  retire: { ar: 'إيقاف', en: 'Retire' },
  retired: { ar: 'موقوفة', en: 'Retired' },
  specificationsSubtitle: {
    ar: 'الأسئلة تأتي من قسم المنتج وأقسامه الأعلى.',
    en: "The questions come from the product's category and the ones above it.",
  },
  noSpecifications: { ar: 'لا مواصفات لهذا المنتج', en: 'No specifications for this product' },
  noSpecificationsHint: {
    ar: 'أسنِد خصائص إلى قسم المنتج لتظهر هنا لكل منتجاته.',
    en: "Assign attributes to the product's category and they appear here for all of its products.",
  },
  required: { ar: 'مطلوبة', en: 'Required' },
  productOnly: { ar: 'خاصة بهذا المنتج', en: 'This product only' },
  yes: { ar: 'نعم', en: 'Yes' },
  no: { ar: 'لا', en: 'No' },

  // ─── Product physical details ─────────────────────────────────────────────
  barcode: { ar: 'الباركود', en: 'Barcode' },
  barcodeHint: { ar: 'EAN/UPC — يمكن البحث به', en: 'EAN/UPC — searchable' },
  methodRules: { ar: 'السعر حسب الوزن وما تحمله الطريقة', en: 'By weight, and what it will carry' },
  methodIncludedKg: { ar: 'وزن يشمله السعر (كجم)', en: 'Kilos the price covers' },
  methodIncludedKgHint: { ar: 'اتركه فارغًا إن لم تُسعّر بالوزن', en: 'Leave empty to ignore weight' },
  methodPerKg: { ar: 'لكل كيلو إضافي (ريال)', en: 'Each extra kilo (SAR)' },
  methodPerKgHint: { ar: 'كل كيلو يبدأ فوق المشمول — لا يُضاف إلى الشحن المجاني', en: 'Each started kilo above — never added to free delivery' },
  methodVolumetricDivisor: { ar: 'معامل الوزن الحجمي', en: 'Volumetric divisor' },
  methodVolumetricDivisorHint: { ar: 'الطول×العرض×الارتفاع بالسم ÷ المعامل؛ 5000 شائع', en: 'L×W×H in cm ÷ divisor; 5000 is common' },
  methodMaxKg: { ar: 'أقصى وزن (كجم)', en: 'Heaviest it takes (kg)' },
  methodMaxKgHint: { ar: 'لا تظهر الطريقة لطلب أثقل', en: 'Not offered for a heavier basket' },
  methodCarrier: { ar: 'شركة الشحن', en: 'Courier' },
  methodCarrierHint: { ar: 'تبدأ بها نافذة الشحن لطلبات هذه الطريقة', en: 'The ship dialog starts on it for this method’s orders' },
  methodCarrierNone: { ar: 'لا شركة محددة', en: 'None set' },
  methodShipWith: { ar: 'الشحن عبر', en: 'Ship with' },
  methodShipWithHint: { ar: 'منصة شحن موصولة في التكاملات تستلم طلبات هذه الطريقة', en: 'A shipping provider connected in Integrations takes this method’s orders' },
  methodShipWithNone: { ar: 'يدويًا — بلا منصة شحن', en: 'By hand — no provider' },
  methodShipWithConnect: { ar: 'صِل منصة شحن (مثل OTO) من التكاملات لتُرسل طلبات هذه الطريقة وتُحجز شحناتها تلقائيًا.', en: 'Connect a shipping provider (such as OTO) in Integrations to send this method’s orders and book them on their own.' },
  methodShipService: { ar: 'شركة الشحن عبر المنصة', en: 'The provider’s courier' },
  methodShipServiceHint: { ar: 'من شركات حسابك في المنصة، بسعرها لك اليوم', en: 'From your account’s couriers on the provider, at what they charge you today' },
  methodShipServiceAny: { ar: 'يختارها المتجر في المنصة لكل طلب', en: 'Chosen per order on the provider' },
  methodShipServicesFailed: { ar: 'تعذّر جلب شركات الشحن من المنصة الآن', en: 'Could not load the provider’s couriers just now' },
  methodShipCost: { ar: 'تكلفة {courier} عليك: نحو {amount} للشحنة — قارنها بسعر هذه الطريقة للعميل.', en: '{courier} costs you about {amount} a parcel — compare it with what this method charges.' },
  methodLivePrice: { ar: 'سعر التوصيل من الشركة مباشرة', en: 'Price live from the courier' },
  methodLivePriceHint: { ar: 'يُحسب السعر للعميل عند الدفع حسب مدينته ووزن الطلب، بإضافتك أدناه. إن لم تُجب الشركة يُستخدم جدول الأسعار، والتوصيل المجاني يبقى مجانيًا.', en: 'Priced at checkout for the customer’s city and the order’s weight, plus what you add below. If the courier does not answer, the price table applies, and free delivery stays free.' },
  methodLiveMarkupPct: { ar: 'زيادة بالنسبة (%)', en: 'Markup (%)' },
  methodLiveMarkupSar: { ar: 'مبلغ يُضاف (ر.س)', en: 'Amount added (SAR)' },
  methodLiveRound: { ar: 'التقريب للأعلى', en: 'Round up to' },
  methodLiveRoundNone: { ar: 'بلا تقريب', en: 'No rounding' },
  methodLiveRound1: { ar: 'أقرب ريال', en: 'Whole riyal' },
  methodLiveRound5: { ar: 'أقرب 5 ريال', en: 'Nearest 5 SAR' },
  methodAutoBook: { ar: 'احجز الشحنة تلقائيًا', en: 'Book automatically' },
  methodAutoBookHint: { ar: 'تُحجز الشحنة مع هذه الشركة وتُصدر البوليصة فور جاهزية الطلب، دون اختيار يدوي.', en: 'The parcel is booked with this courier and its label made as soon as the order is ready — no choosing by hand.' },
  methodExcluded: { ar: 'تصنيفات لا تحملها', en: 'Categories it will not carry' },
  methodExcludedHint: { ar: 'وما تحتها — لا تظهر الطريقة لطلب يحويها', en: 'And those under them — not offered for a basket holding one' },
  methodExcludedAdd: { ar: 'أضف تصنيفًا', en: 'Add a category' },
  methodRulesInvalid: { ar: 'أرقام غير صحيحة في الوزن أو السعر', en: 'A weight or price that is not a number' },
  mpn: { ar: 'رقم قطعة المصنّع', en: 'MPN' },
  gtinValid: { ar: 'GTIN صالح', en: 'Valid GTIN' },
  gtinNot: { ar: 'رمز داخلي — ليس GTIN', en: 'Own code, not a GTIN' },
  weightKg: { ar: 'الوزن (كجم)', en: 'Weight (kg)' },
  weightHint: { ar: 'وزن القطعة كما تُشحن', en: 'As the item ships' },
  lengthCm: { ar: 'الطول (سم)', en: 'Length (cm)' },
  widthCm: { ar: 'العرض (سم)', en: 'Width (cm)' },
  heightCm: { ar: 'الارتفاع (سم)', en: 'Height (cm)' },
  specifications: { ar: 'المواصفات', en: 'Specifications' },

  // ─── Filters ──────────────────────────────────────────────────────────────
  searchLabel: { ar: 'بحث', en: 'Search' },
  filterOrdering: { ar: 'الشراء', en: 'Ordering' },
  filterHasOrders: { ar: 'اشترى من قبل', en: 'Has ordered' },
  filterNeverOrdered: { ar: 'لم يشترِ بعد', en: 'Never ordered' },
  filterJoinedFrom: { ar: 'انضم من', en: 'Joined from' },
  filterJoinedTo: { ar: 'انضم حتى', en: 'Joined until' },
  filterReason: { ar: 'السبب', en: 'Reason' },
  searchReturnsHint: { ar: 'رقم المرتجع أو الطلب أو البريد', en: 'RMA, order number, or email' },
  searchReviewsHint: { ar: 'نص التقييم', en: 'Review text' },
  searchInventoryHint: { ar: 'الرمز أو اسم المنتج', en: 'SKU or product name' },
  minRating: { ar: 'أقل تقييم', en: 'Min rating' },
  maxRating: { ar: 'أعلى تقييم', en: 'Max rating' },
  filterRating: { ar: 'التقييم', en: 'Rating' },
  filterMinRating: { ar: 'من نجمة', en: 'From' },
  filterMaxRating: { ar: 'إلى نجمة', en: 'To' },
  filterStock: { ar: 'المخزون', en: 'Stock' },
  filterInStock: { ar: 'متوفر', en: 'In stock' },
  filterOutOfStock: { ar: 'غير متوفر', en: 'Out of stock' },
  filterMinPrice: { ar: 'أقل سعر', en: 'Min price' },
  filterMaxPrice: { ar: 'أعلى سعر', en: 'Max price' },
  orderMethodCOD: { ar: 'الدفع عند الاستلام', en: 'Cash on delivery' },
  orderMethodCARD: { ar: 'بطاقة', en: 'Card' },
  orderMethodMANUAL: { ar: 'مدفوع خارج المتجر', en: 'Paid outside the store' },
  advancedFilters: { ar: 'تصفية متقدمة', en: 'Filters' },
  resultCount: { ar: '{total} نتيجة', en: '{total} results' },
  showingOf: { ar: '{shown} من {total}', en: '{shown} of {total}' },
  filterStatus: { ar: 'الحالة', en: 'Status' },
  filterPaymentMethod: { ar: 'طريقة الدفع', en: 'Payment method' },
  filterFrom: { ar: 'من تاريخ', en: 'Placed from' },
  filterTo: { ar: 'إلى تاريخ', en: 'Placed until' },
  filterMinTotal: { ar: 'أقل إجمالي', en: 'Min total' },
  filterMaxTotal: { ar: 'أعلى إجمالي', en: 'Max total' },
  anyValue: { ar: 'الكل', en: 'Any' },
  searchOrdersHint: { ar: 'رقم الطلب أو الاسم أو البريد أو الجوال', en: 'Order number, name, email or phone' },

  // ─── Promotions ───────────────────────────────────────────────────────────
  promoStartAgain: { ar: 'تشغيل من جديد', en: 'Start again' },
  promoStopTitle: { ar: 'إيقاف هذا العرض؟', en: 'Stop this automatic discount?' },
  promoStopBody: {
    ar: 'يتوقف خصمه عن سلال العملاء فورًا. الطلبات السابقة تحتفظ بما خُصم لها، ويمكنك تشغيله من جديد متى شئت. لا شيء يُحذف.',
    en: 'It stops coming off shoppers’ baskets immediately. Past orders keep the discount they got, and you can start it again at any time. Nothing is deleted.',
  },
  promoStateScheduled: { ar: 'لم يبدأ بعد', en: 'Not started yet' },
  promoStateEnded: { ar: 'انتهت مدته', en: 'Ended' },
  promoNameHint: {
    ar: 'يراه فريقك في هذه القائمة، ولا يراه العميل إلا إذا تُرك ما يراه العميل فارغًا.',
    en: 'What your team sees in this list. Shoppers see it only where the wording below is left empty.',
  },
  promoColumnName: { ar: 'الاسم', en: 'Name' },
  promoDiscount: { ar: 'الخصم', en: 'Discount' },
  promoAmountOffOrder: { ar: 'الخصم من الطلب', en: 'Amount off the order' },
  promoUpTo: { ar: 'بحد أقصى {amount}', en: 'Up to {amount}' },
  promoMinQuantity: { ar: 'عند شراء {count} فأكثر', en: 'When buying {count} or more' },
  // Buy X get Y, gifts and quantity tiers (ADR-069).
  promoKind: { ar: 'نوع العرض', en: 'What the offer does' },
  promoKindDiscount: { ar: 'خصم', en: 'A discount' },
  promoKindDiscountHint: { ar: 'نسبة أو مبلغ من سعر المنتجات — ويمكن أن يتدرج بالكمية', en: 'A percentage or an amount off — and it can climb with quantity' },
  promoKindBxgy: { ar: 'اشترِ X واحصل على Y', en: 'Buy X, get Y' },
  promoKindBxgyHint: { ar: 'مثل 1+1 مجانًا، أو هدية عند بلوغ مبلغ معين', en: 'Like 1 + 1 free, or a gift for spending enough' },
  promoBuyQuantity: { ar: 'اشترِ', en: 'Buy' },
  promoBuyQuantityHint: { ar: 'عدد القطع المدفوعة — 0 لهدية مقابل قيمة السلة', en: 'Units paid for — 0 for a gift for spending' },
  promoGetQuantity: { ar: 'واحصل على', en: 'Get' },
  promoGetQuantityHint: { ar: 'تُمنح الأرخص منها', en: 'The cheapest units are the reward' },
  promoGetPercent: { ar: 'بخصم (%)', en: 'At (% off)' },
  promoGetPercentHint: { ar: '100 = مجانًا', en: '100 = free' },
  promoGiftMinimumHint: { ar: 'مطلوب للهدية: تُمنح عندما تبلغ السلة هذا المبلغ', en: 'Needed for a gift: it is earned once the basket reaches this' },
  promoRewardScope: { ar: 'المكافأة من', en: 'The reward comes from' },
  promoRewardScopeHint: { ar: 'نفس المنتجات، أو منتجات أخرى مثل «اشترِ حقيبتين واحصل على محفظة»', en: 'The same products, or others — "buy 2 bags, get a wallet"' },
  promoRewardSame: { ar: 'نفس منتجات العرض', en: 'The same products' },
  promoRewardProducts: { ar: 'منتجات المكافأة', en: 'Reward products' },
  promoBuyScope: { ar: 'المنتجات المشتراة', en: 'Products bought' },
  promoBuyScopeHint: { ar: 'ما يجب شراؤه ليُستحق العرض', en: 'What must be bought to earn the reward' },
  promoMaxApplications: { ar: 'يتكرر حتى', en: 'Repeats up to' },
  promoMaxApplicationsHint: { ar: 'مرات في الطلب الواحد — فارغ بلا حد', en: 'Times per order — empty for no limit' },
  promoTiered: { ar: 'خصم متدرج بالكمية', en: 'Quantity tiers' },
  promoTieredHint: { ar: 'كلما زادت الكمية زاد الخصم: قطعتان 10%، ثلاث 15%', en: 'The more units, the bigger the discount: 2 for 10%, 3 for 15%' },
  promoTiers: { ar: 'الشرائح', en: 'Tiers' },
  promoTiersBelow: { ar: 'القيمة في الشرائح أدناه', en: 'The value is set in the tiers below' },
  promoTierFrom: { ar: 'من عدد قطع', en: 'From units' },
  promoAddTier: { ar: 'إضافة شريحة', en: 'Add a tier' },
  promoTiersAscend: { ar: 'كل شريحة بعدد أكبر من التي قبلها', en: 'Each tier needs more units than the one before' },
  promoGiftNeedsMinimum: { ar: 'هدية بلا شراء تحتاج حدًا أدنى للسلة', en: 'A gift with nothing to buy needs a basket minimum' },
  // A set at its own price (ADR-078).
  promoKindBundle: { ar: 'مجموعة بسعر خاص', en: 'A set at its own price' },
  promoKindBundleHint: { ar: 'منتجات تُشترى معًا بسعر أقل — مثل المحفظة وحافظة البطاقات بـ ٢٩٩', en: 'Things bought together for less — the wallet and its card holder for 299' },
  promoSetNeedsTwo: { ar: 'المجموعة تحتاج منتجين مختلفين على الأقل', en: 'A set needs two different things at least' },
  promoSetItems: { ar: 'ما في المجموعة', en: 'What is in the set' },
  promoSetItemsHint: { ar: 'من ٢ إلى ١٠ منتجات، لكلٍّ كميته', en: '2 to 10 things, each with how many' },
  promoSetAddItem: { ar: 'إضافة منتج', en: 'Add a thing' },
  promoSetPick: { ar: 'اختر منتجًا', en: 'Choose a thing' },
  promoSetQuantity: { ar: 'الكمية', en: 'How many' },
  promoSetPrice: { ar: 'سعر المجموعة', en: 'The set’s price' },
  promoSetPriceHint: { ar: 'بالريال، شامل الضريبة — أقل من سعرها منفردة', en: 'In riyals, VAT included — below what its things cost apart' },
  promoSetsPerOrder: { ar: 'مجموعات في الطلب', en: 'Sets per order' },
  promoSetsPerOrderHint: { ar: 'فارغ: بلا حد', en: 'Empty: no limit' },
  promoSetSummary: { ar: '{count} منتجات بـ {price}', en: '{count} things for {price}' },
  promoSetRemove: { ar: 'إزالة', en: 'Remove' },
  promoFree: { ar: 'مجانًا', en: 'free' },
  promoPercentOff: { ar: 'بخصم {percent}', en: 'at {percent} off' },
  promoBxgySummary: { ar: 'اشترِ {buy} واحصل على {get} {reward}', en: 'Buy {buy}, get {get} {reward}' },
  promoGiftSummary: { ar: 'هدية: {count} {reward}', en: 'Gift: {count} {reward}' },
  promoTierSummary: { ar: '{count}+: {value}', en: '{count}+: {value}' },
  promoRewardFrom: { ar: 'المكافأة من: {where}', en: 'Reward from {where}' },
  promoTimesPerOrder: { ar: 'حتى {count} مرات للطلب', en: 'Up to {count} times an order' },
  promoTargetsRequired: { ar: 'اختر واحدًا على الأقل، أو اجعل العرض على كل المنتجات', en: 'Choose at least one, or make it apply to everything' },
  campaignName: { ar: 'اسم الحملة', en: 'Campaign name' },
  campaignFailedCount: { ar: 'فشل {count}', en: '{count} failed' },
  campaignSentOf: { ar: '{sent} من {total}', en: '{sent} of {total}' },
  campaignReached: { ar: 'الوصول', en: 'Reached' },
  campaignWhen: { ar: 'التاريخ', en: 'Date' },
  campaignSendTitle: { ar: 'إرسال الحملة الآن؟', en: 'Send this campaign now?' },
  campaignSendCount: {
    ar: 'ستصل «{name}» إلى {count} ممن وافقوا على تلقي رسائلك ويطابقون الشروط. ما يُرسل لا يمكن استعادته.',
    en: '“{name}” goes to {count} people who agreed to hear from you and match. What goes out cannot be recalled.',
  },
  campaignSendNobody: {
    ar: 'لا أحد ممن وافقوا على تلقي رسائلك يطابق شروط «{name}» الآن، فلن يُرسل شيء.',
    en: 'Nobody who agreed to hear from you matches “{name}” right now, so nothing would go out.',
  },
  campaignSendUnknown: {
    ar: 'ستصل «{name}» إلى كل من وافق على تلقي رسائلك ويطابق الشروط. ما يُرسل لا يمكن استعادته.',
    en: '“{name}” goes to everyone who agreed to hear from you and matches. What goes out cannot be recalled.',
  },
  campaignStopTitle: { ar: 'إيقاف الإرسال؟', en: 'Stop sending?' },
  campaignStopBody: {
    ar: 'ما أُرسل قد وصل ولا يُستعاد. البقية لن تُرسل، ولا يمكن استئناف الحملة بعد إيقافها.',
    en: 'What has gone out stays out. The rest is not sent, and a stopped campaign cannot be resumed.',
  },
  campaignDeleteTitle: { ar: 'حذف الحملة؟', en: 'Delete this campaign?' },
  campaignDeleteDraftBody: { ar: 'تُحذف المسودة نهائيًا. لم يستلمها أحد.', en: 'The draft is deleted for good. Nobody has received it.' },
  campaignDeleteSentBody: {
    ar: 'تختفي من هذه القائمة نهائيًا، وتبقى رسائلها التي أُرسلت في سجل الرسائل.',
    en: 'It leaves this list for good. The messages it sent stay in the message log.',
  },
  campaignDeleted: { ar: 'حُذفت الحملة.', en: 'Campaign deleted.' },
  campaignEdit: { ar: 'تعديل المسودة', en: 'Edit draft' },
  campaignSaved: { ar: 'حُفظت المسودة.', en: 'Draft saved.' },
  campaignChannelFixed: { ar: 'لا تتغير القناة بعد حفظ المسودة', en: 'The channel stays as the draft was saved' },
  campaignNotDraft: { ar: 'بدأ إرسال هذه الحملة، فلم تعد قابلة للتعديل.', en: 'This campaign has started sending, so it can no longer be changed.' },
  campaignTestTitle: { ar: 'جرّبها أولًا', en: 'Try it first' },
  campaignTestTo: { ar: 'أرسل نسخة إلى', en: 'Send a copy to' },
  campaignTestEmailHint: {
    ar: 'تُحفظ تعديلاتك أولًا، ثم تصل نسخة واحدة بالعربية يبدأ عنوانها بـ [TEST].',
    en: 'Your changes are saved first; then one copy arrives, in Arabic, with [TEST] before the subject.',
  },
  campaignTestSmsHint: {
    ar: 'تُحفظ تعديلاتك أولًا، ثم تصل رسالة واحدة بالعربية إلى هذا الرقم.',
    en: 'Your changes are saved first; then one message, in Arabic, goes to this number.',
  },
  campaignTestNeedsDraft: { ar: 'احفظ المسودة، ثم افتحها لترسل نسخة تجريبية إلى نفسك.', en: 'Save the draft, then open it to send yourself a test.' },
  campaignTestSent: { ar: 'أُرسلت نسخة تجريبية إلى {to}.', en: 'A test went to {to}.' },
  campaignTestFailed: { ar: 'لم تُرسل النسخة التجريبية — السبب في سجل الرسائل.', en: 'The test did not go out — the message log says why.' },
  campaignCounting: { ar: 'جارٍ العد…', en: 'Counting…' },
  campaignMinOrders: { ar: 'طلبات مكتملة، على الأقل', en: 'Sold orders, at least' },
  campaignMinOrdersHint: { ar: 'عدد الطلبات — اتركه فارغًا لأي عدد', en: 'Orders — empty for any number' },
  campaignMaxOrders: { ar: 'طلبات مكتملة، على الأكثر', en: 'Sold orders, at most' },
  campaignMaxOrdersHint: { ar: '١ مع «على الأقل ١» = اشترى مرة ولم يعد', en: '1 with “at least 1” = bought once, never again' },
  campaignBoughtProduct: { ar: 'اشترى هذا المنتج', en: 'Bought this product' },
  campaignBoughtCategory: { ar: 'اشترى من هذا القسم', en: 'Bought from this category' },
  campaignBoughtCategoryHint: { ar: 'يشمل الأقسام المتفرعة منه', en: 'Includes the categories nested under it' },
  campaignTagsHint: { ar: 'أي وسم منها — يصل لأصحاب الحسابات فقط', en: 'Any of them — reaches accounts only' },
  campaignAbandonedCart: { ar: 'ترك سلة', en: 'Left a cart behind' },
  campaignAbandonedCartHint: { ar: 'في سلته منتجات ولم يتحرك منذ مهلة السلال المتروكة', en: 'Something in their cart, idle past the abandoned-cart delay' },
  campaignPriceDrop: { ar: 'انخفض سعر منتج في أمنياته', en: 'A wishlist price dropped' },
  campaignPriceDropHint: { ar: 'منتج في قائمة أمنياته أرخص الآن مما حين حفظه', en: 'Something on their wishlist now costs less than when they saved it' },
  mktMaxBelowMin: { ar: 'أقل من الحد الأدنى', en: 'Below the minimum' },
  campaignJoinedSince: { ar: 'انضم منذ', en: 'Joined since' },
  campaignCityHint: { ar: 'كما يكتبها العملاء في عناوينهم', en: 'As customers write it in their addresses' },
  promotions: { ar: 'العروض', en: 'Automatic discounts' },
  promotionsSubtitle: {
    ar: 'خصومات تُطبَّق تلقائيًا — بلا كود يكتبه العميل.',
    en: 'Discounts that apply on their own — no code for the shopper to type.',
  },
  addPromotion: { ar: 'عرض جديد', en: 'New automatic discount' },
  editPromotion: { ar: 'تعديل العرض', en: 'Edit automatic discount' },
  promotionEditorHint: {
    ar: 'يُطبَّق على الأصناف المطابقة فقط، وأفضل عرض لكل صنف — العروض لا تُجمع.',
    en: 'Applies to matching items only, best offer per item — offers never add up.',
  },
  noPromotions: { ar: 'لا توجد عروض', en: 'No automatic discounts yet' },
  noPromotionsHint: {
    ar: 'العرض خصم يُطبَّق وحده للفترة التي تحددها — بلا كود يكتبه العميل.',
    en: 'An automatic discount comes off by itself, for the period you choose — no code to type.',
  },
  running: { ar: 'يعمل الآن', en: 'Running' },
  stopped: { ar: 'موقوف', en: 'Stopped' },
  stopCampaign: { ar: 'إيقاف', en: 'Stop' },
  noCouponStacking: { ar: 'بلا كوبونات', en: 'No coupons' },
  promotionNameInternal: { ar: 'الاسم لدى الفريق', en: 'Name for your team' },
  promotionNameShopper: { ar: 'ما يراه العميل', en: 'What the shopper sees' },
  promotionNameShopperHint: {
    ar: 'يظهر بجانب الخصم في السلة والفاتورة.',
    en: 'Shown beside the discount in the cart and on the receipt.',
  },
  promotionType: { ar: 'نوع الخصم', en: 'Discount type' },
  typePercentage: { ar: 'نسبة مئوية', en: 'Percentage' },
  percentOff: { ar: 'نسبة الخصم %', en: 'Percent off' },
  amountOffPerUnit: { ar: 'الخصم لكل قطعة', en: 'Amount off per unit' },
  perUnit: { ar: 'لكل قطعة', en: 'per unit' },
  promotionScope: { ar: 'النطاق', en: 'Applies to' },
  scopeALL: { ar: 'كل المنتجات', en: 'Everything' },
  scopeORDER: { ar: 'الطلب كاملًا', en: 'The whole order' },
  offTheOrder: { ar: 'على الطلب', en: 'off the order' },
  maxDiscount: { ar: 'أقصى خصم', en: 'Maximum discount' },
  maxDiscountHint: {
    ar: 'سقف للعرض السخي: «٢٠٪ بحد أقصى ٥٠». اتركه فارغًا بلا سقف.',
    en: 'A ceiling on a generous offer: "20% off, up to 50". Empty means no ceiling.',
  },
  minQuantity: { ar: 'أقل كمية', en: 'Minimum quantity' },
  minQuantityHint: {
    ar: 'عدد القطع المطابقة في السلة — «اشترِ ٣ فأكثر».',
    en: 'Matching units in the basket — "buy 3 or more".',
  },
  scopeCATEGORY: { ar: 'أقسام محددة', en: 'Chosen categories' },
  scopePRODUCT: { ar: 'منتجات محددة', en: 'Chosen products' },
  categoryScopeHint: {
    ar: 'القسم يشمل أقسامه الفرعية تلقائيًا.',
    en: 'A category includes everything nested under it.',
  },
  productScopeHint: {
    ar: 'ابحث بالاسم واختر ما ينطبق عليه العرض.',
    en: 'Search by name and pick what the offer applies to.',
  },
  noProductsFound: { ar: 'لا نتائج مطابقة.', en: 'Nothing matches.' },
  priority: { ar: 'الأولوية', en: 'Priority' },
  priorityHint: {
    ar: 'تُرجّح عند تساوي قيمة الخصم على الصنف نفسه.',
    en: 'Breaks a tie when two offers save the same amount on one item.',
  },
  allowCouponStacking: { ar: 'السماح بجمعه مع كوبون', en: 'Allow a coupon alongside this offer' },
  allowCouponStackingHint: {
    ar: 'أوقفه إذا كان العرض عند حد التكلفة ولا يحتمل خصمًا إضافيًا.',
    en: 'Switch off when the offer is already at cost and cannot absorb another discount.',
  },

  // ─── Shipping ─────────────────────────────────────────────────────────────
  shippingZones: { ar: 'مناطق الشحن', en: 'Shipping zones' },
  shippingSubtitle: {
    ar: 'أين تُوصّل، وبكم — هذه الأسعار هي ما يُعرض على العميل عند الدفع.',
    en: 'Where you deliver and for how much — these are the prices quoted at checkout.',
  },
  addZone: { ar: 'إضافة منطقة', en: 'Add a zone' },
  addZoneHint: {
    ar: 'الاسم الإنجليزي للاستخدام الداخلي، والعربي هو ما يقرؤه العميل.',
    en: 'The English name is internal; the Arabic one is what a customer reads.',
  },
  zoneNameInternal: { ar: 'الاسم (داخلي)', en: 'Name (internal)' },
  zoneNameArabic: { ar: 'الاسم بالعربية', en: 'Arabic name' },
  defaultZone: { ar: 'الافتراضية', en: 'Default' },
  defaultZoneHint: {
    ar: 'كل عنوان لا تشمله منطقة أخرى يُشحن من هنا — لذلك لا يمكن إيقافها، بل نقلها لمنطقة أخرى.',
    en: 'Every address no other zone covers ships from here — so it cannot be switched off, only moved.',
  },
  makeDefaultZone: { ar: 'اجعلها الافتراضية', en: 'Make default' },
  activate: { ar: 'تفعيل', en: 'Activate' },
  zoneLocations: { ar: 'المواقع', en: 'Locations' },
  zoneLocationsHint: {
    ar: 'الموقع ينتمي لمنطقة واحدة فقط؛ إن كان في منطقة أخرى فأزِله منها أولًا.',
    en: 'A location belongs to one zone only; if it is in another, remove it there first.',
  },
  defaultZoneLocationsHint: {
    ar: 'المنطقة الافتراضية لا تحتاج مواقع — هي ما يلتقط ما تبقّى.',
    en: 'The default zone needs no locations — it catches everything else.',
  },
  noLocations: { ar: 'لا مواقع بعد.', en: 'No locations yet.' },
  locationKind: { ar: 'النوع', en: 'Kind' },
  memberKindCITY: { ar: 'مدينة', en: 'City' },
  memberKindREGION: { ar: 'منطقة إدارية', en: 'Region' },
  memberKindCOUNTRY: { ar: 'دولة', en: 'Country' },
  country: { ar: 'الدولة', en: 'Country' },
  region: { ar: 'المنطقة', en: 'Region' },
  city: { ar: 'المدينة', en: 'City' },
  searchCity: { ar: 'ابحث عن مدينة', en: 'Search for a city' },
  choose: { ar: 'اختر…', en: 'Choose…' },
  addLocation: { ar: 'إضافة', en: 'Add' },
  addMethod: { ar: 'إضافة طريقة شحن', en: 'Add a delivery method' },
  addMethodHint: {
    ar: 'السعر يُختار حسب مجموع السلة — صفّ واحد يعني سعرًا ثابتًا.',
    en: 'The price is chosen by basket subtotal — a single row means a flat rate.',
  },
  methodNameInternal: { ar: 'اسم الطريقة (داخلي)', en: 'Method name (internal)' },
  methodNameArabic: { ar: 'الاسم بالعربية', en: 'Arabic name' },
  methodInactive: { ar: 'غير مفعّلة — لا تظهر عند الدفع.', en: 'Inactive — not offered at checkout.' },
  deliveryEstimate: { ar: 'مدة التوصيل', en: 'Delivery estimate' },
  deliveryEstimateHint: { ar: 'مثال: ٢–٤ أيام عمل', en: 'For example: 2–4 business days' },
  deliveryDaysFrom: { ar: 'يصل بعد (أيام عمل)', en: 'Arrives after (business days)' },
  deliveryDaysTo: { ar: 'وحتى (أيام عمل)', en: 'And at most (business days)' },
  deliveryDaysHint: {
    ar: 'تُحسب من يوم التجهيز، دون العطلة الأسبوعية والإجازات. صفر = في اليوم نفسه',
    en: 'Counted from dispatch, skipping the weekend and holidays. 0 = the same day',
  },
  cutoffTime: { ar: 'آخر موعد للطلب', en: 'Order cutoff' },
  cutoffTimeHint: {
    ar: 'بتوقيت المتجر — الطلب بعده يُجهَّز في يوم العمل التالي',
    en: "In the store's time zone — an order after it dispatches the next business day",
  },
  pickupMethodHint: {
    ar: 'لا يُشحن: يُجهَّز في المتجر ثم يستلمه العميل بنفسه',
    en: 'Never shipped: made ready at the store, then collected by the customer',
  },
  pickupInstructions: { ar: 'مكان الاستلام ومواعيده', en: 'Where and when to collect' },
  pickupInstructionsHint: {
    ar: 'العنوان وساعات العمل — تظهر عند الدفع وفي رسالة «جاهز للاستلام»',
    en: 'The address and opening hours — shown at checkout and in the "ready to collect" message',
  },
  priceBands: { ar: 'جدول الأسعار', en: 'Price table' },
  bandFrom: { ar: 'من مجموع', en: 'From subtotal' },
  bandTo: { ar: 'إلى', en: 'Up to' },
  bandPrice: { ar: 'سعر الشحن', en: 'Shipping price' },
  bandOpenEnded: { ar: 'وما فوق', en: 'and above' },
  addBand: { ar: 'إضافة شريحة', en: 'Add a tier' },
  bandsHint: {
    ar: 'الشرائح تبدأ من صفر وتتصل دون فجوة، وآخرها مفتوح. اترك «إلى» فارغة للأخيرة.',
    en: 'Tiers start at zero and join without a gap; the last one is open-ended — leave "up to" empty.',
  },
  amountsInRiyals: { ar: 'المبالغ بالريال', en: 'Amounts in riyals' },

  // ─── Customer detail ──────────────────────────────────────────────────────
  customerOrdersCapped: {
    ar: 'آخر {shown} طلبًا، الأحدث أولًا — وتجد البقية بالبحث في صفحة الطلبات.',
    en: 'The latest {shown}, newest first — search the Orders page for the rest.',
  },
  customerReviewsHint: { ar: 'تُراجَع وتُنشر من صفحة التقييمات.', en: 'Moderated on the Reviews page.' },
  backToCustomers: { ar: 'رجوع للعملاء', en: 'Back to customers' },
  customerSince: { ar: 'عميل منذ', en: 'Customer since' },
  emailVerified: { ar: 'بريد موثّق', en: 'Email verified' },
  emailUnverified: { ar: 'بريد غير موثّق', en: 'Email not verified' },
  excludesCancelled: { ar: 'بدون الطلبات الملغاة', en: 'Excludes cancelled orders' },
  firstOrder: { ar: 'أول طلب', en: 'First order' },
  customerOrdersHint: { ar: 'الأحدث أولًا.', en: 'Newest first.' },
  customerReturnsHint: {
    ar: 'تشمل ما طُلب قبل إنشاء الحساب — المطابقة بالبريد أيضًا.',
    en: 'Includes returns from before they signed up — matched by email too.',
  },
  addresses: { ar: 'العناوين', en: 'Addresses' },
  noAddresses: { ar: 'لا توجد عناوين محفوظة', en: 'No saved addresses' },
  defaultAddress: { ar: 'الافتراضي', en: 'Default' },
  notes: { ar: 'ملاحظات الفريق', en: 'Staff notes' },
  notesHint: {
    ar: 'تُضاف ولا تُعدَّل، باسم كاتبها — ليعرف زميلك ما دار.',
    en: 'Appended and attributed, never edited — so the next colleague knows what happened.',
  },
  notePlaceholder: {
    ar: 'ما الذي ينبغي أن يعرفه من يتحدث مع هذا العميل لاحقًا؟',
    en: 'What should the next person speaking to this customer know?',
  },
  addNote: { ar: 'إضافة ملاحظة', en: 'Add note' },
  customersAccounts: { ar: 'الحسابات', en: 'Accounts' },
  customersGuests: { ar: 'المشترون الزوار', en: 'Guest buyers' },
  customersGuestsHint: {
    ar: 'اشتروا دون حساب — نعرفهم بالبريد على طلباتهم',
    en: 'Bought without an account — known by the email on their orders',
  },
  searchGuestsHint: { ar: 'ابحث بالبريد أو الاسم أو الجوال', en: 'Search by email, name or phone' },
  noGuests: { ar: 'لا مشترين زوار', en: 'No guest buyers' },
  viewTheirOrders: { ar: 'عرض طلباته', en: 'View their orders' },
  editCustomer: { ar: 'تعديل', en: 'Edit' },
  editCustomerTitle: { ar: 'تعديل بيانات العميل', en: 'Edit customer' },
  customerPhoneHint: {
    ar: 'رقم يُكتب هنا لا يُعدّ موثّقًا — يُستخدم للدخول برمز إن كان مفعّلًا',
    en: 'A number typed here is not verified — it is the sign-in number if sign-in by code is on',
  },
  lastOrderPhone: { ar: 'الجوال (من آخر طلب)', en: 'Phone (from their last order)' },
  blockCustomer: { ar: 'حظر', en: 'Block' },
  unblockCustomer: { ar: 'إلغاء الحظر', en: 'Unblock' },
  blockCustomerTitle: { ar: 'حظر هذا العميل؟', en: 'Block this customer?' },
  blockCustomerBody: {
    ar: 'لن يستطيع تسجيل الدخول، وتنتهي كل جلساته الآن. تبقى طلباته كما هي.',
    en: 'They cannot sign in, and every session they have ends now. Their orders stay as they are.',
  },
  unblockCustomerTitle: { ar: 'إلغاء حظر هذا العميل؟', en: 'Unblock this customer?' },
  unblockCustomerBody: { ar: 'يستطيع تسجيل الدخول مجددًا.', en: 'They can sign in again.' },
  customerBlocked: { ar: 'محظور', en: 'Blocked' },

  // ─── Reports ──────────────────────────────────────────────────────────────
  reports: { ar: 'التقارير', en: 'Reports' },
  reportsSubtitle: {
    ar: 'ما بيع، ومن اشترى، وما الذي يُرفع للمحاسب — عن فترة تختارها.',
    en: 'What sold, who bought it, and what goes to the accountant — for a period you choose.',
  },
  // ─── Dashboard: suggestions, goal, live (ADR-121) ────────────────────────
  insightsTitle: { ar: 'اقتراحات لك', en: 'Suggestions' },
  insightsHint: { ar: 'من أرقام متجرك — كلٌّ معه ما تفعله', en: 'From your store’s own numbers — each with what to do' },
  insightsNone: { ar: 'لا شيء يحتاج انتباهك الآن.', en: 'Nothing needs your attention right now.' },
  insightSeverity_high: { ar: 'عاجل', en: 'Urgent' },
  insightSeverity_medium: { ar: 'مهم', en: 'Worth a look' },
  insightSeverity_low: { ar: 'فرصة', en: 'Opportunity' },
  insight_restock: { ar: 'بقي في «{product}» مخزون نحو {days} أيام بوتيرة الشهر الماضي — {count} من الأكثر مبيعًا ينفد.', en: '“{product}” has about {days} days of stock left at last month’s pace — {count} best sellers are running low.' },
  insight_behind_target: { ar: 'بهذه الوتيرة ينتهي الشهر عند {projected} دون هدف {target}. كل يوم باقٍ يحتاج {neededPerDay}.', en: 'At this pace the month ends at {projected}, short of {target}. Each day left needs {neededPerDay}.' },
  insight_coupon_loss: { ar: 'الكوبون {code} خصم {given} على {orders} طلبات ربحها الإجمالي {profit} فقط.', en: 'Coupon {code} gave away {given} on {orders} orders that earned only {profit} in gross profit.' },
  insight_at_risk_customers: { ar: '{count} عميلًا اشتروا أكثر من مرة صاروا معرضين للفقد — أنفقوا معًا {value} ولا أحد يتابعهم.', en: '{count} customers who bought more than once are at risk — together they spent {value}, and nobody is following them up.' },
  insight_city_cod_cancellations: { ar: '{share} من طلبات الدفع عند الاستلام إلى {city} أُلغيت ({orders} طلبًا في ٩٠ يومًا). فكّر في طلب الدفع المسبق هناك.', en: '{share} of cash orders to {city} were cancelled ({orders} in 90 days). Consider asking for payment up front there.' },
  insight_conversion_drop: { ar: 'هذا الأسبوع انتهت {now} من الزيارات بطلب، نزولًا من {before} الأسبوع الماضي.', en: 'This week {now} of visits ended in an order, down from {before} the week before.' },
  insight_carts_without_reminders: { ar: 'تُركت {count} سلة بقيمة {value} هذا الشهر، وتذكير السلات متوقف.', en: '{count} carts worth {value} were left this month, and cart reminders are off.' },
  insight_slow_stock: { ar: '{count} منتجات لم تُبع منذ ٦٠ يومًا — {atPrice} على الرف بسعر البيع.', en: '{count} products haven’t sold in 60 days — {atPrice} on the shelf at price.' },
  insight_viewed_not_bought: { ar: 'شوهد «{product}» {views} مرة هذا الشهر دون بيع ({count} منتجات مثله).', en: '“{product}” was viewed {views} times this month without a sale ({count} products like it).' },
  insight_returned_often: { ar: 'أُرجع «{product}» {returned} مرات في ٩٠ يومًا — {share} مما بيع.', en: '“{product}” came back {returned} times in 90 days — {share} of what sold.' },
  insight_missing_costs: { ar: '{count} أصناف بيعت هذا الشهر بلا تكلفة، فلا يظهر ربحها.', en: '{count} variants sold this month have no cost, so their profit can’t be shown.' },
  insightAction_restock: { ar: 'غطاء المخزون', en: 'See stock cover' },
  insightAction_behind_target: { ar: 'أطلق حملة', en: 'Start a campaign' },
  insightAction_coupon_loss: { ar: 'راجع الكوبون', en: 'Review the coupon' },
  insightAction_at_risk_customers: { ar: 'استعِدهم', en: 'Win them back' },
  insightAction_city_cod_cancellations: { ar: 'المدن', en: 'See cities' },
  insightAction_conversion_drop: { ar: 'مسار الشراء', en: 'See the funnel' },
  insightAction_carts_without_reminders: { ar: 'فعّل التذكير', en: 'Turn reminders on' },
  insightAction_slow_stock: { ar: 'أطلق عرضًا', en: 'Run an offer' },
  insightAction_viewed_not_bought: { ar: 'أداء المنتجات', en: 'See products' },
  insightAction_returned_often: { ar: 'المرتجعات', en: 'See returns' },
  insightAction_missing_costs: { ar: 'أضف التكاليف', en: 'Add costs' },
  targetTitle: { ar: 'هدف الشهر', en: 'This month’s goal' },
  targetHint: { ar: 'المبيعات والطلبات مقابل الهدف، والوتيرة المطلوبة', en: 'Sales and orders against the goal, and the pace it needs' },
  targetDay: { ar: 'اليوم {day} من {days}', en: 'Day {day} of {days}' },
  targetSet: { ar: 'حدّد هدفًا', en: 'Set a goal' },
  targetChange: { ar: 'تعديل', en: 'Change' },
  targetRevenue: { ar: 'المبيعات (ر.س)', en: 'Sales (SAR)' },
  targetOrders: { ar: 'الطلبات', en: 'Orders' },
  targetNone: { ar: 'لم يُحدَّد هدف لهذا الشهر.', en: 'No goal set for this month.' },
  targetNoneOwner: { ar: 'حدّد هدف مبيعات شهريًا لترى الوتيرة المطلوبة كل يوم.', en: 'Set a monthly sales goal to see the pace each day needs.' },
  targetPace: { ar: 'بهذه الوتيرة: {projected} · المطلوب يوميًا: {needed}', en: 'At this pace: {projected} · Needed a day: {needed}' },
  liveTitle: { ar: 'الآن', en: 'Live' },
  liveHint: { ar: 'آخر ٥ دقائق — يتحدّث كل ١٥ ثانية', en: 'The last 5 minutes — refreshed every 15 seconds' },
  liveVisitors: { ar: 'زوار الآن', en: 'Visitors now' },
  liveInCart: { ar: 'في السلة', en: 'In a cart' },
  liveAtCheckout: { ar: 'في الدفع', en: 'At checkout' },
  liveOrdersToday: { ar: 'طلبات اليوم', en: 'Orders today' },
  liveRevenueToday: { ar: 'مبيعات اليوم', en: 'Sales today' },
  liveVisitsToday: { ar: 'زيارات اليوم', en: 'Visits today' },
  liveYesterday: { ar: 'أمس حتى الآن: {value}', en: 'Yesterday by now: {value}' },
  liveBySource: { ar: 'من أين جاؤوا', en: 'Where they came from' },
  liveLatestOrders: { ar: 'آخر الطلبات', en: 'Latest orders' },
  liveDevice_mobile: { ar: 'جوال', en: 'Mobile' },
  liveDevice_desktop: { ar: 'كمبيوتر', en: 'Desktop' },
  liveDevice_unknown: { ar: 'غير معروف', en: 'Unknown' },
  // ─── Reports v3 (ADR-118 – ADR-122) ──────────────────────────────────────
  reportGroup_sales: { ar: 'المبيعات والأرباح', en: 'Sales and profit' },
  reportGroup_customers: { ar: 'العملاء', en: 'Customers' },
  reportGroup_operations: { ar: 'العمليات', en: 'Operations' },
  reportGroup_visitors: { ar: 'الزوار', en: 'Visitors' },
  reportGroup_custom: { ar: 'تقرير مخصص', en: 'Custom' },
  reportTab_orders: { ar: 'الطلبات', en: 'Orders' },
  reportTab_customers: { ar: 'العملاء', en: 'Customers' },
  reportTab_finance: { ar: 'المالية', en: 'Finance' },
  reportTab_products: { ar: 'الأكثر مبيعًا', en: 'Best sellers' },
  reportTab_discounts: { ar: 'الخصومات', en: 'Discounts' },
  reportTab_sources: { ar: 'المصادر', en: 'Sources' },
  reportTab_funnel: { ar: 'مسار الشراء', en: 'Funnel' },
  reportTab_performance: { ar: 'أداء المنتجات', en: 'Product performance' },
  reportTab_retention: { ar: 'عودة المشترين', en: 'Retention' },
  reportTab_value: { ar: 'قيمة العملاء', en: 'Customer value' },
  reportTab_profit: { ar: 'الأرباح', en: 'Profit' },
  reportTab_stock: { ar: 'قيمة المخزون', en: 'Stock value' },
  reportTab_geography: { ar: 'المدن والمناطق', en: 'Cities and regions' },
  reportTab_shipping: { ar: 'الشحن والتوصيل', en: 'Shipping and delivery' },
  reportTab_returns: { ar: 'المرتجعات', en: 'Returns' },
  reportTab_carts: { ar: 'السلات المتروكة', en: 'Abandoned carts' },
  reportTab_staff: { ar: 'الفريق', en: 'Team' },
  reportTab_custom: { ar: 'تقرير مخصص', en: 'Custom report' },
  // Filters (ADR-119).
  reportAddFilter: { ar: '+ إضافة فلتر', en: '+ Add a filter' },
  reportClearFilters: { ar: 'مسح الفلاتر', en: 'Clear filters' },
  reportFiltersHint: { ar: 'ضيّق التقرير بالمدينة أو الدفع أو المنتج أو العميل…', en: 'Narrow the report by city, payment, product, customer…' },
  reportApplyFilter: { ar: 'تطبيق', en: 'Apply' },
  reportSearchValue: { ar: 'ابحث…', en: 'Search…' },
  reportNobody: { ar: 'غير مُسند', en: 'Nobody' },
  reportMinTotal: { ar: 'من (ر.س)', en: 'From (SAR)' },
  reportMaxTotal: { ar: 'إلى (ر.س)', en: 'To (SAR)' },
  reportFilter_status: { ar: 'الحالة', en: 'Status' },
  reportFilter_payment: { ar: 'الدفع', en: 'Payment' },
  reportFilter_channel: { ar: 'المصدر', en: 'Channel' },
  reportFilter_region: { ar: 'المنطقة', en: 'Region' },
  reportFilter_city: { ar: 'المدينة', en: 'City' },
  reportFilter_category: { ar: 'التصنيف', en: 'Category' },
  reportFilter_brand: { ar: 'الماركة', en: 'Brand' },
  reportFilter_product: { ar: 'المنتج', en: 'Product' },
  reportFilter_customer: { ar: 'نوع العميل', en: 'Customer' },
  reportFilter_stage: { ar: 'مرحلة العميل', en: 'Customer stage' },
  reportFilter_group: { ar: 'مجموعة العملاء', en: 'Customer group' },
  reportFilter_coupon: { ar: 'الكوبون', en: 'Coupon' },
  reportFilter_shippingMethod: { ar: 'طريقة الشحن', en: 'Shipping method' },
  reportFilter_carrier: { ar: 'شركة الشحن', en: 'Carrier' },
  reportFilter_assignee: { ar: 'المسؤول', en: 'Assigned to' },
  reportFilter_fulfilment: { ar: 'الاستلام', en: 'Pickup or delivery' },
  reportFilter_total: { ar: 'قيمة الطلب', en: 'Order total' },
  reportCustomer_guest: { ar: 'زائر بلا حساب', en: 'Guest' },
  reportCustomer_first: { ar: 'أول طلب', en: 'First order' },
  reportCustomer_returning: { ar: 'عميل عائد', en: 'Returning' },
  reportFulfilment_pickup: { ar: 'استلام من الفرع', en: 'Pickup' },
  reportFulfilment_delivery: { ar: 'توصيل', en: 'Delivery' },
  orderMethodWALLET: { ar: 'رصيد المتجر', en: 'Store credit' },
  // Saved views.
  reportViews: { ar: 'العروض المحفوظة', en: 'Saved views' },
  reportNoViews: { ar: 'لا عروض محفوظة بعد — اضبط التقرير ثم احفظه.', en: 'No saved views yet — set up a report, then save it.' },
  reportSaveView: { ar: 'احفظ هذا العرض', en: 'Save this view' },
  reportViewName: { ar: 'اسم العرض', en: 'View name' },
  reportShareView: { ar: 'مشاركة مع الفريق', en: 'Share with the team' },
  reportShared: { ar: 'مشترك', en: 'Shared' },
  // Profit and stock (ADR-118).
  profitRevenue: { ar: 'المبيعات بدون ضريبة', en: 'Sales without VAT' },
  profitCost: { ar: 'تكلفة البضاعة', en: 'Cost of goods' },
  grossProfit: { ar: 'إجمالي الربح', en: 'Gross profit' },
  profitMargin: { ar: 'هامش الربح', en: 'Margin' },
  profitMarginHint: { ar: 'على البضاعة فقط، بلا شحن أو رسوم', en: 'On the goods alone — no shipping or fees' },
  profitWithoutCost: { ar: '{lines} سطرًا بيعت قبل إدخال تكلفتها ({revenue}) — خارج الربح أعلاه.', en: '{lines} lines sold before their cost was entered ({revenue}) — left out of the profit above.' },
  profitBackfill: { ar: 'املأها بالتكلفة الحالية', en: 'Fill them with today’s costs' },
  profitBackfillBody: { ar: 'كل سطر قديم بلا تكلفة يأخذ تكلفة منتجه الحالية. يُفعل مرة عند إدخال التكاليف أول مرة.', en: 'Every past line without a cost takes its product’s current cost. Done once, when costs are first entered.' },
  profitBackfilled: { ar: 'مُلئت تكلفة {lines} سطرًا.', en: '{lines} lines now have a cost.' },
  profitBreakdown: { ar: 'الربح حسب', en: 'Profit by' },
  profitBreakdownHint: { ar: 'المرتجعات المستردة مخصومة من سطورها', en: 'Refunded returns come off their lines' },
  profitBy_product: { ar: 'المنتج', en: 'Product' },
  profitBy_category: { ar: 'التصنيف', en: 'Category' },
  profitBy_brand: { ar: 'الماركة', en: 'Brand' },
  profitBy_day: { ar: 'اليوم', en: 'Day' },
  profitNone: { ar: 'بلا', en: 'None' },
  profitUnitsWithoutCost: { ar: '{units} بلا تكلفة', en: '{units} without cost' },
  costOfGoods: { ar: 'تكلفة البضاعة المباعة', en: 'Cost of goods sold' },
  stockUnits: { ar: 'الوحدات', en: 'Units' },
  stockAtCost: { ar: 'بالتكلفة', en: 'At cost' },
  stockAtPrice: { ar: 'بسعر البيع', en: 'At price' },
  stockAtPriceHint: { ar: 'بدون ضريبة', en: 'Without VAT' },
  stockPotential: { ar: 'ربح ينتظر البيع', en: 'Profit waiting to sell' },
  stockWithoutCost: { ar: '{variants} صنفًا في المخزون بلا تكلفة — أدخلها من محرر المنتجات الجماعي.', en: '{variants} variants in stock have no cost — add them in the bulk product editor.' },
  stockBreakdown: { ar: 'المخزون حسب', en: 'Stock by' },
  stockBreakdownHint: { ar: 'ما على الرفوف الآن', en: 'What the shelves hold now' },
  stockBy_product: { ar: 'المنتج', en: 'Product' },
  stockBy_category: { ar: 'التصنيف', en: 'Category' },
  stockBy_location: { ar: 'الموقع', en: 'Location' },
  stockEmpty: { ar: 'لا مخزون', en: 'No stock' },
  // Operations (ADR-120).
  geographyTitle: { ar: 'المبيعات حسب المكان', en: 'Sales by place' },
  geographyHint: { ar: 'المدن التي ترفض الدفع عند الاستلام كثيرًا مميزة بالأحمر', en: 'Cities that often cancel cash orders show in red' },
  geographyBuyers: { ar: 'المشترون', en: 'Buyers' },
  geographyCodShare: { ar: 'نسبة الدفع عند الاستلام', en: 'Cash share' },
  geographyCancelled: { ar: 'نسبة الإلغاء', en: 'Cancelled' },
  geographyCodCancelled: { ar: 'إلغاء الدفع عند الاستلام', en: 'Cash cancelled' },
  geographyDeliveryDays: { ar: 'أيام التوصيل', en: 'Days to deliver' },
  shippingShipments: { ar: 'الشحنات', en: 'Shipments' },
  shippingHoursToShip: { ar: 'ساعات حتى الشحن', en: 'Hours to dispatch' },
  shippingHoursHint: { ar: 'من الطلب إلى تسليمه لشركة الشحن', en: 'From the order to the courier' },
  shippingDaysInTransit: { ar: 'أيام في الطريق', en: 'Days in transit' },
  shippingFreeOrders: { ar: '{count} طلب بشحن مجاني', en: '{count} with free shipping' },
  shippingByCarrier: { ar: 'حسب شركة الشحن', en: 'By carrier' },
  shippingByMethod: { ar: 'حسب طريقة الشحن', en: 'By shipping method' },
  shippingCarrier: { ar: 'شركة الشحن', en: 'Carrier' },
  shippingDelivered: { ar: 'سُلّمت', en: 'Delivered' },
  shippingFailed: { ar: 'تعذّرت', en: 'Failed' },
  shippingInTransit: { ar: 'في الطريق', en: 'In transit' },
  shippingMethod: { ar: 'الطريقة', en: 'Method' },
  shippingAverage: { ar: 'متوسط الرسوم', en: 'Average charged' },
  shippingFree: { ar: 'مجاني', en: 'Free' },
  returnsRate: { ar: 'نسبة الإرجاع', en: 'Return rate' },
  returnsRateHint: { ar: '{returned} من {sold} وحدة', en: '{returned} of {sold} units' },
  returnsCount: { ar: 'المرتجعات', en: 'Returns' },
  returnsRefunded: { ar: 'المسترد', en: 'Refunded' },
  returnsDaysToRefund: { ar: 'أيام حتى الاسترداد', en: 'Days to refund' },
  returnsRestocked: { ar: '{units} وحدة عادت للمخزون', en: '{units} units back in stock' },
  returnsByReason: { ar: 'حسب السبب', en: 'By reason' },
  returnsByResolution: { ar: 'حسب الحل', en: 'By resolution' },
  returnsByProduct: { ar: 'الأكثر إرجاعًا', en: 'Returned most' },
  returnsByProductHint: { ar: 'نسبة ١٠٪ فأكثر مميزة بالأحمر', en: '10% or more shows in red' },
  returnsReason: { ar: 'السبب', en: 'Reason' },
  returnsResolution: { ar: 'الحل', en: 'Resolution' },
  returnsUnits: { ar: 'وحدات مرتجعة', en: 'Units returned' },
  returnsTopReason: { ar: 'أكثر سبب', en: 'Top reason' },
  returnsNone: { ar: 'لا مرتجعات في هذه الفترة', en: 'No returns in this period' },
  cartsLeft: { ar: 'سلات متروكة', en: 'Carts left' },
  cartsRecovered: { ar: 'استُعيدت', en: 'Recovered' },
  cartsRecoveredRevenue: { ar: 'إيراد مستعاد', en: 'Recovered revenue' },
  cartsHoursToRecover: { ar: 'ساعات حتى العودة', en: 'Hours to come back' },
  cartsByReminder: { ar: 'حسب التذكير', en: 'By reminder' },
  cartsByReminderHint: { ar: 'هل التذكير يعيد العملاء؟', en: 'Do reminders bring shoppers back?' },
  cartsReminders: { ar: 'التذكيرات', en: 'Reminders' },
  cartsRate: { ar: 'نسبة الاستعادة', en: 'Recovery rate' },
  cartsValueLeft: { ar: 'قيمة متروكة', en: 'Value left' },
  cartsReminder_none: { ar: 'بلا تذكير', en: 'No reminder' },
  cartsReminder_first: { ar: 'تذكير واحد', en: 'One reminder' },
  cartsReminder_second: { ar: 'تذكيران', en: 'Two reminders' },
  staffTitle: { ar: 'عمل الفريق', en: 'The team’s work' },
  staffHint: { ar: 'في الفترة المختارة', en: 'In the period chosen' },
  staffMember: { ar: 'الموظف', en: 'Person' },
  staffMoved: { ar: 'طلبات حرّكها', en: 'Orders moved on' },
  staffShipped: { ar: 'شحنها', en: 'Shipped' },
  staffAssigned: { ar: 'أُسندت إليه', en: 'Assigned' },
  staffReplies: { ar: 'ردود التذاكر', en: 'Ticket replies' },
  staffFirstReply: { ar: 'أول رد', en: 'First reply' },
  staffFollowUps: { ar: 'متابعات أنجزها', en: 'Follow-ups done' },
  staffNotes: { ar: 'ملاحظات', en: 'Notes' },
  staffMinutes: { ar: '{minutes} دقيقة', en: '{minutes} min' },
  byDay: { ar: 'حسب اليوم', en: 'By day' },
  date: { ar: 'التاريخ', en: 'Date' },
  // Custom reports (ADR-122).
  customTitle: { ar: 'ابنِ تقريرك', en: 'Build your report' },
  customHint: { ar: 'اختر البيانات والأعمدة والتجميع — والفلاتر أعلاه تنطبق عليه', en: 'Choose the data, the columns and the grouping — the filters above apply' },
  customDataset: { ar: 'البيانات', en: 'Data' },
  customDataset_orders: { ar: 'الطلبات', en: 'Orders' },
  customDataset_lines: { ar: 'المنتجات المباعة', en: 'Products sold' },
  customDataset_customers: { ar: 'المشترون', en: 'Buyers' },
  customGroupBy: { ar: 'جمّع حسب', en: 'Group by' },
  customThenBy: { ar: 'ثم حسب', en: 'Then by' },
  customNoGrouping: { ar: 'بلا تجميع (كل صف)', en: 'No grouping (every row)' },
  customSortBy: { ar: 'رتّب حسب', en: 'Sort by' },
  customColumns: { ar: 'الأعمدة — المتقطعة أرقام تُجمع', en: 'Columns — dashed ones are figures that add up' },
  customRun: { ar: 'اعرض التقرير', en: 'Run the report' },
  customResult: { ar: 'النتيجة', en: 'Result' },
  customTruncated: { ar: 'تظهر أول ١٬٠٠٠ صف — التصدير يحمل الكل', en: 'Showing the first 1,000 rows — the export has them all' },
  customColumn_day: { ar: 'اليوم', en: 'Day' },
  customColumn_week: { ar: 'الأسبوع', en: 'Week' },
  customColumn_month: { ar: 'الشهر', en: 'Month' },
  customColumn_number: { ar: 'رقم الطلب', en: 'Order' },
  customColumn_status: { ar: 'الحالة', en: 'Status' },
  customColumn_payment: { ar: 'الدفع', en: 'Payment' },
  customColumn_channel: { ar: 'المصدر', en: 'Channel' },
  customColumn_region: { ar: 'المنطقة', en: 'Region' },
  customColumn_city: { ar: 'المدينة', en: 'City' },
  customColumn_coupon: { ar: 'الكوبون', en: 'Coupon' },
  customColumn_shippingMethod: { ar: 'طريقة الشحن', en: 'Shipping method' },
  customColumn_assignee: { ar: 'المسؤول', en: 'Assigned to' },
  customColumn_customerType: { ar: 'نوع العميل', en: 'Customer' },
  customColumn_stage: { ar: 'المرحلة', en: 'Stage' },
  customColumn_email: { ar: 'البريد', en: 'Email' },
  customColumn_name: { ar: 'الاسم', en: 'Name' },
  customColumn_product: { ar: 'المنتج', en: 'Product' },
  customColumn_sku: { ar: 'رمز المنتج', en: 'SKU' },
  customColumn_category: { ar: 'التصنيف', en: 'Category' },
  customColumn_brand: { ar: 'الماركة', en: 'Brand' },
  customColumn_firstOrder: { ar: 'أول طلب', en: 'First order' },
  customColumn_lastOrder: { ar: 'آخر طلب', en: 'Last order' },
  customColumn_orders: { ar: 'الطلبات', en: 'Orders' },
  customColumn_units: { ar: 'الوحدات', en: 'Units' },
  customColumn_revenue: { ar: 'الإيراد', en: 'Revenue' },
  customColumn_netRevenue: { ar: 'الإيراد بدون ضريبة', en: 'Revenue without VAT' },
  customColumn_averageOrder: { ar: 'متوسط الطلب', en: 'Average order' },
  customColumn_discounts: { ar: 'الخصومات', en: 'Discounts' },
  customColumn_shipping: { ar: 'الشحن', en: 'Shipping' },
  customColumn_vat: { ar: 'الضريبة', en: 'VAT' },
  customColumn_cost: { ar: 'التكلفة', en: 'Cost' },
  customColumn_profit: { ar: 'إجمالي الربح', en: 'Gross profit' },
  customColumn_margin: { ar: 'الهامش', en: 'Margin' },
  customColumn_buyers: { ar: 'المشترون', en: 'Buyers' },
  reportOrders: { ar: 'الطلبات', en: 'Orders' },
  reportProducts: { ar: 'الأكثر مبيعًا', en: 'Best sellers' },
  reportProductsHint: {
    ar: 'من بيانات الطلب وقت الشراء، فالمنتج المحذوف أو المُعاد تسميته يظهر كما بيع.',
    en: 'From the order snapshots, so a renamed or deleted product still reports as it sold.',
  },
  reportCustomers: { ar: 'العملاء', en: 'Customers' },
  reportFinance: { ar: 'المالية', en: 'Finance' },
  reportDiscounts: { ar: 'الخصومات', en: 'Discounts' },
  reportDiscountsHint: {
    ar: 'كم استُخدم كل كود، وكم كلّف المتجر.',
    en: 'How often each code was used, and what it cost the store.',
  },
  from: { ar: 'من', en: 'From' },
  to: { ar: 'إلى', en: 'To' },
  thisMonth: { ar: 'هذا الشهر', en: 'This month' },
  exportCsv: { ar: 'تصدير CSV', en: 'Export CSV' },
  noDataForRange: { ar: 'لا توجد بيانات في هذه الفترة.', en: 'Nothing in this period.' },
  unitsSold: { ar: 'القطع المباعة', en: 'Units sold' },
  revenuePerDay: { ar: 'الإيراد اليومي', en: 'Revenue per day' },
  revenuePerDayHint: {
    ar: 'الأيام بلا مبيعات تظهر فارغة لا مفقودة.',
    en: 'A day with no sales shows as empty, not as missing.',
  },
  byStatus: { ar: 'حسب الحالة', en: 'By status' },
  byPaymentMethod: { ar: 'حسب طريقة الدفع', en: 'By payment method' },
  byMonth: { ar: 'شهرًا بشهر', en: 'Month by month' },
  month: { ar: 'الشهر', en: 'Month' },
  newAccounts: { ar: 'حسابات جديدة', en: 'New accounts' },
  firstTimeBuyers: { ar: 'اشتروا لأول مرة', en: 'First-time buyers' },
  returningBuyers: { ar: 'عملاء عائدون', en: 'Returning buyers' },
  returningBuyersHint: {
    ar: 'يُحتسب بالبريد، فالزائر الذي طلب مرتين بلا حساب يُحتسب أيضًا.',
    en: 'Counted by email, so a guest who ordered twice counts too.',
  },
  guestOrders: { ar: 'طلبات بلا حساب', en: 'Guest orders' },
  topSpenders: { ar: 'الأعلى إنفاقًا', en: 'Top spenders' },
  financeSummary: { ar: 'ملخص مالي', en: 'Finance summary' },
  financeSummaryHint: {
    ar: 'الضريبة مضمّنة في الإجماليات لا مضافة إليها؛ المرتجعات مؤرخة بيوم ردّ المبلغ.',
    en: 'VAT is contained in the totals, not added; refunds are dated by when the money moved.',
  },
  grossSales: { ar: 'المبيعات قبل الخصم', en: 'Gross sales' },
  campaignsGiven: { ar: 'خصومات العروض', en: 'Campaign discounts' },
  discountsGiven: { ar: 'خصومات الكوبونات', en: 'Coupon discounts' },
  shippingCharged: { ar: 'الشحن المحصّل', en: 'Shipping charged' },
  paymentFees: { ar: 'رسوم الدفع عند الاستلام', en: 'Cash-on-delivery fees' },
  codCollected: { ar: 'نقد مُحصَّل (الدفع عند الاستلام)', en: 'Cash collected (COD)' },
  codOutstanding: { ar: 'نقد لم يُحصَّل بعد (الدفع عند الاستلام)', en: 'Cash still to collect (COD)' },
  netSales: { ar: 'صافي المبيعات', en: 'Net sales' },
  vatCollected: { ar: 'الضريبة المضمّنة', en: 'VAT included' },
  refunds: { ar: 'المبالغ المستردة', en: 'Refunds' },
  netAfterRefunds: { ar: 'الصافي بعد الاسترداد', en: 'Net after refunds' },
  uses: { ar: 'مرات الاستخدام', en: 'Uses' },
  discountGiven: { ar: 'قيمة الخصم', en: 'Discount given' },
  type: { ar: 'النوع', en: 'Type' },

  // ─── Email templates ──────────────────────────────────────────────────────
  emailTemplates: { ar: 'رسائل البريد', en: 'Email templates' },
  emailTemplatesSubtitle: {
    ar: 'ما يصل عميلك في كل مرحلة من مراحل الطلب — بلغتيه.',
    en: 'What your customer reads at each stage of an order, in both languages.',
  },
  emailBody: { ar: 'نص الرسالة', en: 'Message body' },
  emailEnabled: { ar: 'إرسال هذه الرسالة', en: 'Send this message' },
  emailEnabledHint: {
    ar: 'إيقافها لا يحذف ما كتبته — يبقى محفوظًا حتى تُعيد تشغيلها.',
    en: 'Switching it off keeps your wording; it simply stops going out.',
  },
  emailEdited: { ar: 'معدّلة', en: 'Edited' },
  emailOff: { ar: 'موقوفة', en: 'Off' },
  restoreDefault: { ar: 'استعادة النص الافتراضي', en: 'Restore the default wording' },
  placeholders: { ar: 'الحقول المتاحة', en: 'Available fields' },
  placeholdersHint: {
    ar: 'اضغط على أي حقل لإدراجه عند موضع المؤشر؛ يُستبدل بقيمته عند الإرسال.',
    en: 'Click one to insert it where the cursor is; it is replaced with the real value when the email is sent.',
  },
  emailPreview: { ar: 'معاينة الرسالة', en: 'Preview' },
  emailPreviewHint: {
    ar: 'ببيانات طلب تجريبي — لا تُعرض بيانات عميل حقيقي هنا.',
    en: 'Rendered with a sample order — never a real customer.',
  },
  sendTest: { ar: 'إرسال نسخة تجريبية', en: 'Send a test' },
  sendTestHint: {
    ar: 'الطريقة الوحيدة للتأكد من شكل الرسالة داخل بريد حقيقي.',
    en: 'The only way to see how it really looks in an inbox.',
  },
  testSent: { ar: 'أُرسلت إلى', en: 'Sent to' },
  send: { ar: 'إرسال', en: 'Send' },
  subject: { ar: 'عنوان الرسالة', en: 'Subject' },
  arabic: { ar: 'عربي', en: 'Arabic' },
  english: { ar: 'إنجليزي', en: 'English' },

  // ─── Audit ────────────────────────────────────────────────────────────────
  // ─── Team: staff and permission groups ─────────────────────────────────────
  groupCreatedOn: { ar: 'أُنشئت في {date}', en: 'Created {date}' },
  team: { ar: 'الفريق', en: 'Team' },
  supportTickets: { ar: 'الدعم', en: 'Support' },
  supportSubtitle: { ar: 'رسائل العملاء: من يردّ عليها ومتى', en: "Customers' messages: who answers, and when" },
  searchTicketsHint: { ar: 'رقم التذكرة أو الموضوع أو العميل', en: 'Ticket number, subject, or customer' },
  ticket: { ar: 'التذكرة', en: 'Ticket' },
  ticketStatusOPEN: { ar: 'مفتوحة', en: 'Open' },
  ticketStatusPENDING_CUSTOMER: { ar: 'بانتظار العميل', en: 'Waiting on the customer' },
  ticketStatusRESOLVED: { ar: 'مُعالجة', en: 'Resolved' },
  ticketStatusCLOSED: { ar: 'مغلقة', en: 'Closed' },
  priorityLOW: { ar: 'منخفضة', en: 'Low' },
  priorityNORMAL: { ar: 'عادية', en: 'Normal' },
  priorityHIGH: { ar: 'عالية', en: 'High' },
  priorityURGENT: { ar: 'عاجلة', en: 'Urgent' },
  assignedTo: { ar: 'المسؤول', en: 'Assigned to' },
  unassigned: { ar: 'لم يستلمها أحد', en: 'Nobody yet' },
  ticketGuest: { ar: 'زائر', en: 'Guest' },
  ticketFiles: { ar: 'الملفات المرفقة', en: 'Files sent with it' },
  priceLists: { ar: 'قوائم الأسعار', en: 'Price lists' },
  priceListsSubtitle: {
    ar: 'ما يدفعه حساب التاجر: نسبة من سعر التجزئة، وأسعار محددة حيث تضعها',
    en: 'What a trade account pays: a percentage off retail, and exact prices where you set them',
  },
  newPriceList: { ar: 'قائمة أسعار جديدة', en: 'New price list' },
  editPriceList: { ar: 'تعديل قائمة الأسعار', en: 'Edit price list' },
  searchPriceLists: { ar: 'ابحث في القوائم', en: 'Search price lists' },
  noPriceLists: { ar: 'لا قوائم أسعار بعد', en: 'No price lists yet' },
  noPriceListsHint: {
    ar: 'أنشئ قائمة، ثم اجعل عميلًا حساب تاجر من صفحته',
    en: 'Create a list, then make a customer a trade account from their page',
  },
  priceListName: { ar: 'الاسم', en: 'Name' },
  priceListDiscount: { ar: 'النسبة من التجزئة', en: 'Off retail' },
  priceListDiscountHint: {
    ar: 'نسبة مئوية من ٠ إلى ٩٩٫٩٩ — مثل ١٠ أو ١٢٫٥',
    en: 'A percentage from 0 to 99.99 — like 10 or 12.5',
  },
  priceListExact: { ar: 'أسعار محددة', en: 'Exact prices' },
  priceListAccounts: { ar: 'الحسابات', en: 'Accounts' },
  priceListWithOffers: { ar: 'تسري العروض أيضًا', en: 'Offers apply too' },
  priceListWithOffersHint: {
    ar: 'تُطبّق عروض المتجر التلقائية فوق سعر التاجر',
    en: "The store's automatic offers apply on top of the trade price",
  },
  priceListWithCodes: { ar: 'تسري أكواد الخصم أيضًا', en: 'Promo codes apply too' },
  priceListWithCodesHint: { ar: 'يمكن استخدام كود خصم فوق سعر التاجر', en: 'A promo code can be used on top of the trade price' },
  priceListIsTheDeal: { ar: 'القائمة هي العرض كاملًا', en: 'The list is the whole deal' },
  priceListActive: { ar: 'مفعّلة', en: 'Active' },
  priceListActiveHint: {
    ar: 'القائمة المتوقفة لا تسعّر أحدًا: يشتري عملاؤها بسعر التجزئة حتى تُفعّل',
    en: 'An inactive list prices nobody: its accounts buy at retail until it is active again',
  },
  priceListDialogHint: {
    ar: 'تُطبّق على الطلب التالي لكل حساب يشتري منها',
    en: 'Takes effect on the next price each of its accounts sees',
  },
  priceListPrices: { ar: 'الأسعار', en: 'Prices' },
  priceListPricesHint: {
    ar: 'كل صنف بخصم {percent}٪ ما لم تضع له سعرًا محددًا',
    en: 'Every variant at {percent}% off, unless you set an exact price',
  },
  priceListPricesHintNone: {
    ar: 'بلا نسبة: سعر التجزئة ما لم تضع سعرًا محددًا',
    en: 'No percentage: retail, unless you set an exact price',
  },
  priceListExactOnly: { ar: 'الأسعار المحددة فقط', en: 'Exact prices only' },
  priceListPrice: { ar: 'سعر التاجر', en: 'Trade price' },
  priceListExactPrice: { ar: 'سعر محدد', en: 'Exact price' },
  retailPrice: { ar: 'سعر التجزئة', en: 'Retail' },
  tradeAccount: { ar: 'حساب تاجر', en: 'Trade account' },
  tradeAccountHint: {
    ar: 'الشركة التي يشتري لها، وقائمة الأسعار التي يشتري منها',
    en: 'The business they buy for, and the price list they buy from',
  },
  makeTradeAccount: { ar: 'اجعله حساب تاجر', en: 'Make trade account' },
  notTradeAccount: { ar: 'يشتري بسعر التجزئة — ليس حساب تاجر', en: 'Buys at retail — not a trade account' },
  tradeStatus: { ar: 'الحالة', en: 'Status' },
  tradePricingOn: { ar: 'بأسعار التاجر', en: 'On trade prices' },
  tradeRetail: { ar: 'بسعر التجزئة', en: 'At retail' },
  tradeSuspended: { ar: 'موقوف', en: 'Suspended' },
  tradeSuspendedHint: {
    ar: 'يشتري بسعر التجزئة حتى تعيده',
    en: 'Buys at retail until you reinstate the account',
  },
  legalName: { ar: 'الاسم النظامي', en: 'Legal name' },
  tradeName: { ar: 'الاسم التجاري', en: 'Trade name' },
  crNumber: { ar: 'السجل التجاري', en: 'CR number' },
  crNumberHint: { ar: 'عشرة أرقام', en: 'Ten digits' },
  vatNumberHint: { ar: '١٥ رقمًا تبدأ وتنتهي بـ٣', en: 'Fifteen digits, starting and ending with 3' },
  tradeAddress: { ar: 'العنوان', en: 'Address' },
  priceList: { ar: 'قائمة الأسعار', en: 'Price list' },
  orderPricedFromList: {
    ar: 'سُعّر هذا الطلب من قائمة أسعار الجملة لهذا العميل، لا من أسعار المتجر.',
    en: 'This order was priced from the customer’s trade list, not the shop’s prices.',
  },
  priceListChoiceHint: {
    ar: 'بلا قائمة يشتري بسعر التجزئة',
    en: 'With no list, the account buys at retail',
  },
  // ─── Looks (R17) ───────────────────────────────────────────────────────────
  looks: { ar: 'التنسيقات', en: 'Looks' },
  looksSubtitle: {
    ar: 'منتجات تُنسّق معًا بكمياتها — تظهر في صفحات منتجاتها وفي الرئيسية، وتُضاف للسلة بخطوة واحدة',
    en: 'Products that go together, with how much of each — on their pages and the home page, added to the basket in one step',
  },
  newLook: { ar: 'تنسيق جديد', en: 'New look' },
  editLook: { ar: 'تعديل التنسيق', en: 'Edit look' },
  searchLooks: { ar: 'ابحث بالعنوان', en: 'Search by title' },
  noLooks: { ar: 'لا تنسيقات بعد', en: 'No looks yet' },
  noLooksHint: {
    ar: 'اجمع منتجات تُلبس أو تُستخدم معًا، وحدّد كمية كل منها. فعّل «التنسيقات» في الإعدادات ليراها المتسوّق',
    en: 'Put together things worn or used together, with how much of each. Switch looks on in Settings for shoppers to see them',
  },
  look: { ar: 'التنسيق', en: 'Look' },
  lookItems: { ar: 'ما فيه', en: 'What is in it' },
  lookItemsEmpty: { ar: 'أضف منتجين أو أكثر', en: 'Add two or more products' },
  lookTotal: { ar: 'مجموعه اليوم', en: 'Its total today' },
  lookDialogHint: {
    ar: 'لا سعر خاص للتنسيق: مجموعه أسعار أسطره اليوم. يُخفى ما دام أحد منتجاته خارج البيع',
    en: 'A look has no price of its own: its total is its lines at today’s prices. It hides while any of its products is off sale',
  },
  deleteLookTitle: { ar: 'حذف التنسيق؟', en: 'Delete this look?' },
  deleteLookBody: { ar: 'تبقى منتجاته كما هي.', en: 'Its products stay as they are.' },
  titleEn: { ar: 'العنوان بالإنجليزية', en: 'Title in English' },
  titleAr: { ar: 'العنوان بالعربية', en: 'Title in Arabic' },
  lookBlurbEn: { ar: 'النبذة بالإنجليزية', en: 'Blurb in English' },
  lookBlurbAr: { ar: 'النبذة بالعربية', en: 'Blurb in Arabic' },
  lookCaptionEn: { ar: 'تعليق الصورة القريبة بالإنجليزية', en: 'Detail caption in English' },
  lookCaptionAr: { ar: 'تعليق الصورة القريبة بالعربية', en: 'Detail caption in Arabic' },
  lookOrder: { ar: 'الترتيب', en: 'Order' },
  lookOrderHint: { ar: 'الأصغر أولًا', en: 'Lowest first' },
  lookPortrait: { ar: 'الصورة الطولية', en: 'Portrait' },
  lookDetail: { ar: 'الصورة القريبة', en: 'Detail' },
  lookActive: { ar: 'ظاهر', en: 'Shown' },
  lookActiveHint: { ar: 'المخفي لا يظهر في أي مكان', en: 'A hidden look shows nowhere' },
  variant: { ar: 'الصنف', en: 'Variant' },
  addToLook: { ar: 'إضافة', en: 'Add' },
  lookModelPhoto: { ar: 'صور العارضة', en: 'Model photos' },
  lookModelPhotoHint: {
    ar: 'حتى ثماني صور للتنسيق ملبوسًا — الأولى غلافه في البطاقات، وتظهر كلها في المعاينة',
    en: 'Up to eight of the look worn — the first is its cover on cards, and all of them show in its preview',
  },
  lookDropPhoto: { ar: 'اسحب الصور هنا، أو اخترها', en: 'Drop photos here, or choose them' },
  lookPhotoFormats: { ar: 'JPG أو PNG أو WebP، طولية ٣:٤', en: 'JPG, PNG or WebP, portrait 3:4' },
  replacePhoto: { ar: 'استبدال', en: 'Replace' },
  lookCover: { ar: 'الغلاف', en: 'Cover' },
  lookMakeCover: { ar: 'اجعلها الغلاف', en: 'Make cover' },
  lookAddPhotos: { ar: 'إضافة صور', en: 'Add photos' },
  lookPhotosCount: { ar: '{count} من {max}', en: '{count} of {max}' },
  lookPhotosFull: { ar: 'يتسع التنسيق لثماني صور فقط', en: 'A look holds eight photos at most' },
  lookProducts: { ar: 'منتجات التنسيق', en: 'Products in this look' },
  lookProductsHint: {
    ar: 'حتى اثني عشر — لكل منها القماش والكمية',
    en: 'Up to twelve — each a fabric and how much of it',
  },
  lookAddProducts: { ar: 'إضافة منتجات', en: 'Add products' },
  lookContent: { ar: 'العنوان والوصف', en: 'Title and description' },
  lookTitle: { ar: 'العنوان', en: 'Title' },
  lookBlurb: { ar: 'الوصف', en: 'Description' },
  lookVisibility: { ar: 'الظهور', en: 'Visibility' },
  lookOrganization: { ar: 'التصنيف والترتيب', en: 'Filing and order' },
  lookSummary: { ar: 'ملخص', en: 'Summary' },
  lookPieces: { ar: '{count} قطع', en: '{count} pieces' },
  lookTotalHint: { ar: 'بأسعار اليوم، كما تسعّرها السلة', en: 'At today’s prices, as the basket prices them' },
  lookTotalAfterSave: { ar: 'يُحدَّث المجموع بعد الحفظ', en: 'The total updates when you save' },
  lookTitleRequired: { ar: 'اكتب العنوان بالإنجليزية — هو المطلوب', en: 'Write the English title — it is the required one' },
  lookNeedsItems: { ar: 'أضف منتجًا واحدًا على الأقل', en: 'Add at least one product' },
  fabric: { ar: 'القماش', en: 'Fabric' },
  lookAmount: { ar: '= {amount} {unit}', en: '= {amount} {unit}' },
  eachPrice: { ar: '{price} للوحدة', en: '{price} each' },
  pickerTitle: { ar: 'إضافة منتجات للتنسيق', en: 'Add products to the look' },
  pickerSubtitle: {
    ar: 'اختر منتجًا، ثم القماش من صوره — يمكنك الجمع من أكثر من منتج',
    en: 'Choose a product, then the fabric from its photos — pick from as many products as you like',
  },
  pickerSearch: { ar: 'ابحث عن منتج', en: 'Search products' },
  pickerChooseProduct: { ar: 'اختر منتجًا لترى أقمشته', en: 'Choose a product to see its fabrics' },
  pickerNoProducts: { ar: 'لا منتجات منشورة بهذا الاسم', en: 'No published product by that name' },
  pickerChooseFabric: { ar: 'اختر القماش', en: 'Choose the fabric' },
  pickerChooseFabricHint: {
    ar: 'واحدًا أو أكثر — كل قماش سطر في التنسيق',
    en: 'One or more — each fabric is a line in the look',
  },
  pickerPhotos: { ar: 'صور المنتج', en: 'Its photos' },
  pickerPhotosHint: {
    ar: 'الصورة المربوطة بلون تختار قماشه. اربط الصور بالألوان من تبويب «الوسائط» في المنتج',
    en: 'A photo linked to a colour picks that fabric. Link photos to colours on the product’s Media tab',
  },
  pickerPhotoUnlinked: { ar: 'صورة عامة — غير مربوطة بلون', en: 'A general photo — not linked to a colour' },
  pickerSoldAs: { ar: 'يُباع', en: 'Sold as' },
  pickerSelected: { ar: 'تم اختيار {count}', en: '{count} selected' },
  pickerAdd: { ar: 'إضافة للتنسيق', en: 'Add to the look' },
  pickerInLook: { ar: 'في التنسيق', en: 'In the look' },
  pickerFull: { ar: 'يتسع التنسيق لاثني عشر فقط', en: 'A look holds twelve at most' },
  pickerOnlySamples: { ar: 'عيّنات فقط — لا تدخل التنسيق', en: 'Samples only — cannot go in a look' },

  // ─── What goes with it, and how much (R15) ──────────────────────────────
  goesWith: { ar: 'قد يعجبك أيضًا', en: 'You may also love' },
  goesWithHint: {
    ar: 'منتجات تختارها لتظهر مع هذا المنتج بالترتيب، قبل منتجات قسمه — في صفحته وفي السلة',
    en: 'Products you pick to show with this one, in order, before its category’s own — on its page and in the basket',
  },
  goesWithPicked: { ar: 'المنتجات المختارة (حتى ١٢)', en: 'Picked products (up to 12)' },
  showsQuantityGuide: { ar: 'عرض «كم أحتاج؟»', en: 'Show “How much do I need?”' },
  guideUnit: { ar: 'الوحدة', en: 'Unit' },
  sampleVariant: { ar: 'عيّنة مجانية', en: 'Free sample' },
  sampleVariantHint: {
    ar: 'تُحسب ضمن الحد الأقصى للعيّنات في الطلب، ولا تكون سعر المنتج «ابتداءً من»',
    en: 'Counts toward the samples-per-order cap, and is never the product’s “from” price',
  },
  guideAmount: { ar: 'الكمية', en: 'Amount' },
  guideAddRow: { ar: 'إضافة سطر', en: 'Add a row' },
  guideNoteArabic: { ar: 'ملاحظة بالعربية', en: 'Note in Arabic' },
  guideNoteEnglish: { ar: 'ملاحظة بالإنجليزية', en: 'Note in English' },
  showsQuantityGuideHint: {
    ar: 'يعرض جدول الكميات من إعدادات عرض المنتجات، إن كان بوحدة هذا المنتج',
    en: 'Shows the quantity table from the product listing settings, when it is in this product’s unit',
  },
  // ─── Trade applications (ADR-046) ─────────────────────────────────────────
  tradeApplications: { ar: 'طلبات حسابات التجّار', en: 'Trade applications' },
  tradeApplicationsSubtitle: {
    ar: 'منشآت تطلب الشراء بأسعار التجّار — اقبلها على قائمة أسعار، أو أعدها للتعديل، أو ارفضها بملاحظة',
    en: 'Businesses asking to buy at trade prices — approve onto a price list, send back for changes, or decline with a note',
  },
  tradeStatusSUBMITTED: { ar: 'بانتظار المراجعة', en: 'Waiting' },
  tradeStatusCHANGES_REQUESTED: { ar: 'أعيد للتعديل', en: 'Changes requested' },
  tradeStatusAPPROVED: { ar: 'مقبول', en: 'Approved' },
  tradeStatusREJECTED: { ar: 'مرفوض', en: 'Declined' },
  searchApplications: { ar: 'ابحث بالرقم أو المنشأة أو السجل أو البريد', en: 'Search by number, company, CR or email' },
  noApplications: { ar: 'لا طلبات هنا', en: 'No applications here' },
  noApplicationsHint: {
    ar: 'تظهر الطلبات حين يتقدّم العملاء من صفحة حسابهم التجاري، والتقديم مفعّل في الإعدادات',
    en: "Applications arrive from customers' business account pages, once applications are on in Settings",
  },
  application: { ar: 'الطلب', en: 'Application' },
  businessType: { ar: 'نوع النشاط', en: 'Business type' },
  received: { ar: 'وصل', en: 'Received' },
  company: { ar: 'المنشأة', en: 'Company' },
  contact: { ar: 'مسؤول التواصل', en: 'Contact' },
  nationalAddress: { ar: 'العنوان الوطني', en: 'National address' },
  jobTitle: { ar: 'المسمى الوظيفي', en: 'Job title' },
  mobile: { ar: 'الجوال', en: 'Mobile' },
  contactMobile: { ar: 'جوال التواصل', en: 'Contact mobile' },
  workEmail: { ar: 'بريد العمل', en: 'Work email' },
  whatTheyBuy: { ar: 'ما يشترونه', en: 'What they buy' },
  monthlyVolume: { ar: 'الحجم الشهري', en: 'Monthly volume' },
  interests: { ar: 'الأقسام', en: 'Interested in' },
  applicantNotes: { ar: 'ملاحظاتهم', en: 'Their notes' },
  documents: { ar: 'المستندات', en: 'Documents' },
  documentLinksExpire: {
    ar: 'الروابط صالحة عشر دقائق — أعد فتح الطلب لروابط جديدة',
    en: 'Links work for ten minutes — reopen the application for fresh ones',
  },
  decision: { ar: 'القرار', en: 'Decision' },
  approveApplication: { ar: 'قبول', en: 'Approve' },
  requestChanges: { ar: 'طلب تعديل', en: 'Ask for changes' },
  declineApplication: { ar: 'رفض', en: 'Decline' },
  approveOnto: { ar: 'على قائمة الأسعار', en: 'Onto the price list' },
  approveOntoHint: {
    ar: 'يُنشأ حسابه التجاري من بيانات الطلب ويشتري من هذه القائمة',
    en: 'Their trade account is made from the application, buying from this list',
  },
  choosePriceList: { ar: 'اختر قائمة أسعار', en: 'Choose a price list' },
  noteToApplicant: { ar: 'ملاحظة لمقدّم الطلب', en: 'A note to the applicant' },
  noteToApplicantOptional: { ar: 'ملاحظة لمقدّم الطلب (اختيارية)', en: 'A note to the applicant (optional)' },
  noteToApplicantHint: { ar: 'تصله مع القرار بالبريد', en: 'Mailed to them with the decision' },
  latestApplication: { ar: 'آخر طلب', en: 'Latest application' },
  openApplication: { ar: 'فتح الطلب', en: 'Open the application' },
  searchVariants: { ar: 'ابحث بالرمز أو اسم المنتج', en: 'Search by SKU or product name' },
  deletePriceListTitle: { ar: 'حذف قائمة الأسعار؟', en: 'Delete this price list?' },
  deletePriceListBody: {
    ar: 'تُحذف مع أسعارها المحددة. لا تُحذف قائمة يشتري منها حساب.',
    en: 'It goes with its exact prices. A list any account buys from cannot be deleted.',
  },
  ticketGuestHint: {
    ar: 'كتب بلا حساب: يصله ردّك بالبريد مع رابط المحادثة، والملاحظات الداخلية لا تُرسل.',
    en: 'Wrote without an account: your reply is emailed to them with a link to the thread. Internal notes are never sent.',
  },
  unassignedOnly: { ar: 'غير المستلمة فقط', en: 'Unassigned only' },
  anyAgent: { ar: 'أي موظف', en: 'Anyone' },
  pickedUp: { ar: 'الاستلام', en: 'Picked up' },
  lastReply: { ar: 'آخر رد', en: 'Last reply' },
  noTickets: { ar: 'لا توجد تذاكر', en: 'No tickets' },
  noTicketsHint: { ar: 'إما أن العملاء سعداء، أو أن الاستقبال مغلق في الإعدادات.', en: 'Either the customers are happy, or the form is switched off in settings.' },
  internalNote: { ar: 'ملاحظة داخلية', en: 'Internal note' },
  internalNoteHint: { ar: 'لا يراها العميل ولا تُغيّر حالة التذكرة', en: 'The customer never sees it, and it changes nothing' },
  internalNotePlaceholder: { ar: 'ملاحظة للزملاء…', en: 'A note for colleagues…' },
  replyPlaceholder: { ar: 'اكتب ردك للعميل…', en: 'Write your reply…' },
  sendReply: { ar: 'إرسال الرد', en: 'Send reply' },
  ticketReadOnly: {
    ar: 'يمكنك قراءة هذه التذكرة. الرد عليها وتحويلها يحتاجان صلاحية «الرد على العملاء».',
    en: 'You can read this ticket. Replying and handing it on need “Answer customers”.',
  },
  ticketClosedNote: { ar: 'التذكرة مغلقة. أعد فتحها بتغيير الحالة إن لزم.', en: 'This ticket is closed. Change its status to reopen it.' },
  branding: { ar: 'الهوية البصرية', en: 'Branding' },
  navigationMenu: { ar: 'قائمة التنقّل', en: 'Header menu' },
  navigationSubtitle: { ar: 'روابط الترويسة كما تريدها — أقسام وصفحات وعروض.', en: 'The header links, arranged the way you want them — categories, pages, offers.' },
  addMenuItem: { ar: 'إضافة رابط', en: 'Add link' },
  addSubItem: { ar: 'إضافة رابط فرعي', en: 'Add sub-link' },
  removeItem: { ar: 'حذف', en: 'Remove' },
  menuEmpty: { ar: 'لا توجد قائمة مخصّصة', en: 'No custom menu' },
  menuEmptyHint: { ar: 'تُعرض شجرة الأقسام كما هي الآن. أضف رابطًا لتستبدلها.', en: 'The category tree is shown as it is today. Add a link to replace it.' },
  labelEnglish: { ar: 'النص (إنجليزي)', en: 'Label (English)' },
  labelArabic: { ar: 'النص (عربي)', en: 'Label (Arabic)' },
  linkTo: { ar: 'يشير إلى', en: 'Points at' },
  target: { ar: 'الوجهة', en: 'Target' },
  menuKindproducts: { ar: 'كل المنتجات', en: 'All products' },
  menuKindcategory: { ar: 'قسم', en: 'A category' },
  menuKindpage: { ar: 'صفحة محتوى', en: 'A content page' },
  menuKindlink: { ar: 'رابط', en: 'A link' },
  chooseCategory: { ar: 'اختر قسمًا', en: 'Choose a category' },
  integrations: { ar: 'التكاملات', en: 'Integrations' },
  apiKeys: { ar: 'مفاتيح الـAPI', en: 'API keys' },
  // ─── The store for an AI assistant (ADR-112) ─────────────────────────────
  mcpTitle: { ar: 'ربط مساعد ذكاء اصطناعي (MCP)', en: 'Connect an AI assistant (MCP)' },
  mcpHint: {
    ar: 'اسأل Claude أو مساعدك عن متجرك — «كم بعنا هذا الأسبوع؟»، «ما الذي سينفد؟» — بمفتاح من هنا. قراءة فقط: لا يغيّر المساعد شيئًا في المتجر.',
    en: 'Ask Claude or your own assistant about your store — “how were sales this week?”, “what is running out?” — with a key from here. Read-only: the assistant changes nothing.',
  },
  mcpAddress: { ar: 'عنوان الخادم', en: 'Server address' },
  mcpClaudeCode: { ar: 'في Claude Code (ضع مفتاحك مكان sk_live_…)', en: 'In Claude Code (put your key in place of sk_live_…)' },
  mcpTools: {
    ar: 'يرى المساعد ما تسمح به صلاحيات المفتاح: الأرقام والتقارير (عرض لوحة التحكم)، والطلبات (قراءة الطلبات)، والعملاء (قراءة العملاء)، والمخزون المنخفض (إدارة المخزون).',
    en: 'The assistant sees what the key’s permissions allow: figures and reports (dashboard), orders (read orders), customers (read customers), low stock (manage inventory).',
  },
  webhooks: { ar: 'الويبهوك وتنبيهات المحادثات', en: 'Webhooks and chat alerts' },
  messageBody: { ar: 'النص', en: 'Message' },
  campaigns: { ar: 'الحملات', en: 'Campaigns' },
  campaignsSubtitle: { ar: 'رسالة واحدة إلى العملاء الذين وافقوا على سماعنا. الموافقة شرط دائمًا — لا توجد طريقة للإرسال للجميع.', en: 'One message to the customers who agreed to hear from you. Consent is always required — there is no way to send to everyone.' },
  newCampaign: { ar: 'حملة جديدة', en: 'New campaign' },
  noCampaigns: { ar: 'لا توجد حملات', en: 'No campaigns' },
  noCampaignsHint: { ar: 'اكتب واحدة، وشاهد كم شخصًا ستصل قبل أن ترسلها.', en: 'Write one, and see how many people it reaches before you send it.' },
  campaignNameHint: { ar: 'لك أنت، لا يراه العميل', en: 'For you — the customer never sees it' },
  campaignBodyHint: { ar: '{name} يُملأ باسم كل مستقبل. رابط إلغاء الاشتراك يُضاف تلقائيًا.', en: '{name} is filled per recipient. The unsubscribe link is added for you.' },
  campaignStatusDRAFT: { ar: 'مسودة', en: 'Draft' },
  campaignStatusSENDING: { ar: 'قيد الإرسال', en: 'Sending' },
  campaignStatusTESTING: { ar: 'يُختبر العنوان', en: 'Testing the subject' },
  // ─── A/B test of a campaign's subject (ADR-113) ──────────────────────────
  abTestTitle: { ar: 'اختبار عنوانين (A/B)', en: 'Test two subjects (A/B)' },
  abTestHint: {
    ar: 'يُرسل العنوانان إلى شريحة من الجمهور، نصفها لكلٍّ منهما، ثم يذهب العنوان الذي نُقر أكثر إلى الباقين بعد المدة التي تحددها.',
    en: 'Both subjects go to a slice of the audience, half each; then the one with more clicks goes to everyone else after the wait you choose.',
  },
  abTestSubjectB: { ar: 'العنوان الثاني (B)', en: 'Second subject (B)' },
  abTestShare: { ar: 'حجم الاختبار', en: 'Test share' },
  abTestShareHint: { ar: '{each}٪ يصلهم A و{each}٪ يصلهم B، والباقون ينتظرون الفائز', en: '{each}% get A, {each}% get B, the rest wait for the winner' },
  abTestWait: { ar: 'مدة الانتظار', en: 'Wait' },
  abTestWaitHint: { ar: 'بعد آخر رسالة في الاختبار؛ الأكثر نقرًا يفوز، ثم الأكثر فتحًا', en: 'After the test’s last mail; more clicks wins, then more opens' },
  abTestHours: { ar: '{hours} ساعة', en: '{hours} h' },
  abTestLine: { ar: '{variant}: {sent} أُرسلت · فُتحت {opened} · نُقرت {clicked}', en: '{variant}: {sent} sent · {opened} opened · {clicked} clicked' },
  abTestWinner: { ar: 'الفائز', en: 'winner' },
  abTestEndsAt: { ar: 'يُختار الفائز {when}', en: 'The winner is chosen {when}' },
  abTestSendNow: { ar: 'أرسل {variant} للباقين الآن', en: 'Send {variant} to the rest now' },
  abTestPicked: { ar: 'يذهب العنوان {variant} إلى باقي الجمهور الآن.', en: 'Subject {variant} is going to the rest of the audience now.' },
  campaignStatusSENT: { ar: 'أُرسلت', en: 'Sent' },
  campaignStatusSCHEDULED: { ar: 'مجدولة', en: 'Scheduled' },
  campaignSchedule: { ar: 'جدولة الإرسال', en: 'Schedule' },
  campaignScheduleWhen: { ar: 'موعد الإرسال', en: 'Send at' },
  campaignScheduleHint: { ar: 'بتوقيت جهازك. يُعاد عدّ الجمهور عند الإرسال، فيدخل من وافق بعد اليوم ويخرج من انسحب.', en: 'In your own clock. Its audience is counted again when it goes: those who agree meanwhile are in, those who withdraw are out.' },
  campaignScheduleNext: { ar: 'متابعة', en: 'Continue' },
  campaignScheduleCount: { ar: 'تُرسل «{name}» في {when} إلى نحو {count} — العدد اليوم.', en: '“{name}” goes at {when} to about {count} — today’s count.' },
  campaignScheduledFor: { ar: 'مجدولة: {when}', en: 'Scheduled: {when}' },
  campaignScheduledNotice: { ar: 'جُدولت الحملة.', en: 'The campaign is scheduled.' },
  campaignUnschedule: { ar: 'إلغاء الجدولة', en: 'Unschedule' },
  campaignUnscheduledNotice: { ar: 'عادت الحملة مسودة ولن تُرسل.', en: 'Back to a draft — it will not go.' },
  campaignResults: { ar: 'فُتحت {opened} · نُقرت {clicked} · {orders} طلب · {revenue}', en: 'Opened {opened} · clicked {clicked} · {orders} orders · {revenue}' },
  campaignOpensHint: { ar: 'الفتح تقريبي — بعض تطبيقات البريد تحمّل الصور تلقائيًا. النقرات والطلبات دقيقة.', en: 'Opens are approximate — some mail apps load every picture. Clicks and orders are exact.' },
  campaignStatusCANCELLED: { ar: 'أُوقفت', en: 'Stopped' },
  sendCampaign: { ar: 'إرسال الحملة', en: 'Send it' },
  sendCampaignBody: { ar: 'ستذهب إلى كل من وافق ويطابق الشروط. ما يخرج لا يُستعاد. الحملة:', en: 'This goes to everyone consenting who matches. What goes out cannot be recalled. Campaign:' },
  campaignSending: { ar: 'بدأ الإرسال — السجل يعرض كل مستقبل.', en: 'Sending — the message log shows every recipient.' },
  campaignStopped: { ar: 'أُوقفت. ما خرج قد خرج.', en: 'Stopped. What went out has gone out.' },
  campaignDrafted: { ar: 'حُفظت كمسودة.', en: 'Saved as a draft.' },
  campaignFailuresHint: { ar: 'الإخفاقات في سجل الرسائل، ويمكن إعادة إرسال كل واحدة منها.', en: 'The failures are in the message log, and each one can be sent again.' },
  audience: { ar: 'الجمهور', en: 'Audience' },
  audienceConsentHint: { ar: 'الموافقة مُضافة دائمًا: لا يُرسل إلا لمن اختار أن يسمع منك.', en: 'Consent is always applied: only customers who chose to hear from you.' },
  neverOrdered: { ar: 'لم يطلب من قبل', en: 'Has never ordered' },
  willReach: { ar: 'ستصل إلى', en: 'This reaches' },
  saveDraft: { ar: 'حفظ كمسودة', en: 'Save as draft' },
  stop: { ar: 'إيقاف', en: 'Stop' },
  exportExcel: { ar: 'تصدير Excel', en: 'Export Excel' },
  exportPdf: { ar: 'تصدير PDF', en: 'Export PDF' },
  messageLog: { ar: 'سجل الرسائل', en: 'Message log' },
  messageLogSubtitle: { ar: 'كل بريد ورسالة نصية أرسلناها أو حاولنا. السؤال الذي بُني له: هل وصلت العميل؟', en: 'Every email and text we sent, or tried to. Built for one question: did the customer get it?' },
  messageFailuresToday: { ar: 'إخفاقات في آخر 24 ساعة', en: 'Failed in the last 24 hours' },
  showThem: { ar: 'اعرضها', en: 'Show them' },
  channel: { ar: 'القناة', en: 'Channel' },
  sms: { ar: 'رسالة نصية', en: 'SMS' },
  sent: { ar: 'أُرسلت', en: 'Sent' },
  recipient: { ar: 'المستقبل', en: 'Recipient' },
  recipientSearchHint: { ar: 'بريد أو رقم', en: 'An address or a number' },
  noMessages: { ar: 'لا توجد رسائل', en: 'No messages' },
  noMessagesHint: { ar: 'لم يُرسل شيء بعد بهذه المعايير.', en: 'Nothing has been sent that matches this.' },
  sendAgain: { ar: 'إرسال مرة أخرى', en: 'Send again' },
  resendQueued: { ar: 'أُعيد الإرسال — راجع السجل.', en: 'Sent again — check the history.' },
  resendWorked: { ar: 'أُرسلت هذه المرة.', en: 'It went out this time.' },
  resendFailedAgain: { ar: 'فشلت مجددًا — السبب في الصف الجديد.', en: 'It failed again — the new row says why.' },
  aiKeyLabel: { ar: 'مفتاح الـAPI', en: 'API key' },
  aiFailed: { ar: 'فشل', en: 'failed' },
  aiAssistant: { ar: 'المساعد الذكي', en: 'AI assistant' },
  aiSubtitle: { ar: 'يكتب مسودات فقط: اسم منتج، ردّ دعم، رأي في مراجعة. لا يحفظ ولا يرسل شيئًا — القرار لك.', en: 'It drafts, and only drafts: a product name, a support reply, an opinion on a review. It never saves and never sends — the decision stays yours.' },
  aiModel: { ar: 'النموذج', en: 'The model' },
  aiModelHint: { ar: 'أي خدمة متوافقة مع OpenAI: OpenAI نفسها، أو نموذج محلي على جهازك، أو أي وسيط. لا قائمة موردين — عنوان ونموذج.', en: 'Any OpenAI-compatible service: OpenAI itself, a model running on your own machine, or a proxy. Not a vendor list — an address and a model name.' },
  aiBaseUrl: { ar: 'العنوان', en: 'Base URL' },
  aiBaseUrlHint: { ar: 'https للبعيد؛ http مسموح للمحلي فقط', en: 'https for anything remote; http only for localhost' },
  aiModelName: { ar: 'اسم النموذج', en: 'Model' },
  aiModelNameHint: { ar: 'كما يسمّيه المزود بالضبط', en: 'Exactly as the provider names it' },
  aiKeyStored: { ar: 'محفوظ:', en: 'Stored:' },
  aiKeyOptional: { ar: 'اختياري — النموذج المحلي لا يحتاج مفتاحًا', en: 'Optional — a local model needs no key' },
  aiClearKey: { ar: 'إزالة المفتاح', en: 'Remove the key' },
  aiTimeout: { ar: 'المهلة (ثانية)', en: 'Timeout (seconds)' },
  aiTimeoutHint: { ar: 'من 5 إلى 120', en: 'Between 5 and 120' },
  aiTest: { ar: 'اختبار الاتصال', en: 'Test the connection' },
  aiTestOk: { ar: 'ردّ النموذج', en: 'The model answered' },
  aiTasks: { ar: 'ما الذي يُسمح له به', en: 'What it may do' },
  aiTasksHint: { ar: 'كل مهمة على حدة. تشغيل المساعد ليس تشغيل كل ما يستطيع فعله.', en: 'One task at a time. Switching the assistant on is not switching on everything it could do.' },
  aiUsage: { ar: 'الاستخدام', en: 'Usage' },
  aiRecentCalls: { ar: 'آخر الطلبات', en: 'Recent calls' },
  aiSucceeded: { ar: 'ناجحة', en: 'Succeeded' },
  aiTokens: { ar: 'الرموز', en: 'Tokens' },
  aiUsageHint: { ar: 'الرموز تظهر حين يبلّغ عنها المزود؛ النموذج المحلي غالبًا لا يفعل.', en: 'Tokens appear where the provider reports them — a local model usually does not.' },
  aiTask: { ar: 'المهمة', en: 'Task' },
  aiHistoryHint: { ar: 'كل طلب، نجح أو فشل، وبكلمات المزود نفسه حين يرفض.', en: 'Every call, worked or not, in the provider’s own words when it refused.' },
  aiNoHistory: { ar: 'لم يُطلب من النموذج شيء بعد.', en: 'Nothing has been asked of the model yet.' },
  aiOk: { ar: 'تم', en: 'ok' },
  aiOffHint: { ar: 'المساعد مطفأ — لن يظهر زر مسودة في أي شاشة.', en: 'The assistant is off — no draft button appears on any screen.' },
  aiDraft: { ar: 'مسودة بالذكاء', en: 'Draft with AI' },
  aiDrafting: { ar: 'يكتب…', en: 'Drafting…' },
  aiDraftInserted: { ar: 'مسودة — راجعها قبل الإرسال.', en: 'A draft — read it before you send.' },
  webhooksSubtitle: { ar: 'نُخبر أنظمة أخرى بما يحدث في متجرك، ونحتفظ بسجل ما أُرسل.', en: 'We tell other systems what happens in your store, and keep a record of what was sent.' },
  addWebhook: { ar: 'إضافة وجهة', en: 'Add a destination' },
  noWebhooks: { ar: 'لا توجد وجهات', en: 'No endpoints' },
  noWebhooksHint: { ar: 'أضف نظامًا يستقبل أحداث متجرك، أو قناة Slack أو مجموعة Telegram لفريقك.', en: 'Add a system to receive your store’s events, or a Slack channel or Telegram group for your team.' },
  webhookNameHint: { ar: 'اسم يذكّرك بمن يستقبلها', en: 'A name that says who receives it' },
  webhookUrlHint: { ar: 'https فقط — البيانات تمر على الشبكة', en: 'https only — this carries order data' },
  subscribedEvents: { ar: 'الأحداث', en: 'Events' },
  subscribedEventsHint: { ar: 'القائمة من الخادم: لا يمكن الاشتراك في حدث لا نُطلقه.', en: 'The list comes from the server: you cannot subscribe to an event we never emit.' },
  testQueued: { ar: 'أُرسلت تجربة — راجع السجل.', en: 'Test queued — check the history.' },
  webhookCreated: { ar: 'أُضيفت الوجهة.', en: 'Endpoint added.' },
  // ─── Store events to Slack and Telegram (ADR-110) ─────────────────────────
  webhookKind: { ar: 'النوع', en: 'Kind' },
  webhookKind_JSON: { ar: 'ويبهوك', en: 'Webhook' },
  webhookKind_SLACK: { ar: 'Slack', en: 'Slack' },
  webhookKind_TELEGRAM: { ar: 'Telegram', en: 'Telegram' },
  webhookKindHint_JSON: { ar: 'جسم JSON موقَّع إلى نظامك — ERP أو مستودع أو برنامج محاسبة', en: 'A signed JSON body to your own system — an ERP, a warehouse, accounting' },
  webhookKindHint_SLACK: { ar: 'سطر مقروء لكل حدث في قناة Slack لفريقك', en: 'One readable line per event in your team’s Slack channel' },
  webhookKindHint_TELEGRAM: { ar: 'سطر مقروء لكل حدث من بوت متجرك إلى مجموعة أو شخص', en: 'One readable line per event from your store’s bot to a group or a person' },
  webhookUpdated: { ar: 'حُفظت الوجهة.', en: 'Destination saved.' },
  editWebhook: { ar: 'تعديل الوجهة', en: 'Edit the destination' },
  slackUrl: { ar: 'عنوان الويبهوك الوارد', en: 'Incoming webhook address' },
  slackUrlHint: {
    ar: 'في Slack: التطبيقات ← Incoming Webhooks ← أضف إلى قناة، ثم انسخ العنوان (يبدأ https://hooks.slack.com/services/). يُحفظ مشفّرًا.',
    en: 'In Slack: Apps → Incoming Webhooks → Add to a channel, then copy the address (it starts https://hooks.slack.com/services/). Kept encrypted.',
  },
  telegramToken: { ar: 'رمز البوت', en: 'Bot token' },
  telegramTokenHint: {
    ar: 'في Telegram افتح BotFather وأنشئ بوتًا (newbot)، ثم انسخ الرمز. يُحفظ مشفّرًا.',
    en: 'From @BotFather in Telegram: /newbot, then copy the token. Kept encrypted.',
  },
  telegramChat: { ar: 'المحادثة', en: 'Chat' },
  telegramChatHint: {
    ar: 'أضف البوت إلى مجموعة فريقك واكتب فيها رسالة، ثم اضغط «ابحث عن محادثاته» واختر المجموعة.',
    en: 'Add the bot to your team’s group and write a message there, then press “Find its chats” and pick the group.',
  },
  telegramFindChats: { ar: 'ابحث عن محادثاته', en: 'Find its chats' },
  telegramNoChats: { ar: 'لم يرَ البوت أي محادثة بعد — أضفه إلى المجموعة واكتب فيها، ثم ابحث مجددًا.', en: 'The bot has seen no chat yet — add it to the group and write there, then look again.' },
  keepCredentialHint: { ar: 'اتركه فارغًا لإبقاء المحفوظ.', en: 'Leave empty to keep the one saved.' },
  webhookLanguage: { ar: 'لغة الرسائل', en: 'Language of the lines' },
  webhookEventGroup_order: { ar: 'الطلبات', en: 'Orders' },
  webhookEventGroup_return: { ar: 'المرتجعات', en: 'Returns' },
  webhookEventGroup_product: { ar: 'المنتجات', en: 'Products' },
  webhookEventGroup_inventory: { ar: 'المخزون', en: 'Stock' },
  webhookEventGroup_customer: { ar: 'العملاء', en: 'Customers' },
  webhookEventGroup_cart: { ar: 'السلال', en: 'Carts' },
  webhookEventGroup_ticket: { ar: 'خدمة العملاء', en: 'Support' },
  webhookEventGroup_review: { ar: 'التقييمات', en: 'Reviews' },
  webhookEventGroup_question: { ar: 'الأسئلة', en: 'Questions' },
  webhookEvent_order_placed: { ar: 'طلب جديد', en: 'Order placed' },
  webhookEvent_order_paid: { ar: 'دُفع طلب', en: 'Order paid' },
  webhookEvent_order_processing: { ar: 'بدأ تجهيز طلب', en: 'Order being prepared' },
  webhookEvent_order_shipped: { ar: 'شُحن طلب', en: 'Order shipped' },
  webhookEvent_order_ready_for_pickup: { ar: 'طلب جاهز للاستلام', en: 'Order ready for pickup' },
  webhookEvent_order_delivered: { ar: 'سُلِّم طلب', en: 'Order delivered' },
  webhookEvent_order_cancelled: { ar: 'أُلغي طلب', en: 'Order cancelled' },
  webhookEvent_order_refunded: { ar: 'استُرد طلب', en: 'Order refunded' },
  webhookEvent_order_payment_failed: { ar: 'تعذّر الدفع', en: 'Payment failed' },
  webhookEvent_return_requested: { ar: 'طلب إرجاع جديد', en: 'Return requested' },
  webhookEvent_return_approved: { ar: 'قُبل إرجاع', en: 'Return approved' },
  webhookEvent_return_rejected: { ar: 'رُفض إرجاع', en: 'Return rejected' },
  webhookEvent_return_received: { ar: 'وصلت قطع مرتجعة', en: 'Return received' },
  webhookEvent_return_refunded: { ar: 'استُرد مبلغ إرجاع', en: 'Return refunded' },
  webhookEvent_return_cancelled: { ar: 'ألغى العميل إرجاعًا', en: 'Return cancelled' },
  webhookEvent_product_created: { ar: 'أُضيف منتج', en: 'Product added' },
  webhookEvent_product_updated: { ar: 'عُدّل منتج', en: 'Product changed' },
  webhookEvent_inventory_low_stock: { ar: 'مخزون منخفض', en: 'Low stock' },
  webhookEvent_inventory_out_of_stock: { ar: 'نفد المخزون', en: 'Out of stock' },
  webhookEvent_customer_registered: { ar: 'سجّل عميل جديد', en: 'Customer signed up' },
  webhookEvent_customer_stage_changed: { ar: 'تغيّرت مرحلة عميل', en: 'Customer changed stage' },
  webhookEvent_cart_abandoned: { ar: 'سلة متروكة', en: 'Cart left' },
  webhookEvent_cart_recovered: { ar: 'عادت سلة متروكة طلبًا', en: 'Left cart came back as an order' },
  webhookEvent_ticket_opened: { ar: 'محادثة دعم جديدة', en: 'Support conversation opened' },
  webhookEvent_ticket_customer_replied: { ar: 'ردّ عميل في محادثة', en: 'Customer replied' },
  webhookEvent_review_submitted: { ar: 'تقييم جديد', en: 'Review submitted' },
  webhookEvent_question_asked: { ar: 'سؤال جديد عن منتج', en: 'Product question asked' },
  webhookEvent_webhook_test: { ar: 'رسالة تجربة', en: 'Test' },
  signingSecret: { ar: 'مفتاح التوقيع', en: 'Signing secret' },
  signingSecretHint: { ar: 'يتحقق به المستقبل من أن الطلب منّا: HMAC-SHA256 على «الطابع الزمني.الجسم».', en: 'The receiver verifies us with it: HMAC-SHA256 over "timestamp.body".' },
  pausedAfterFailures: { ar: 'موقوف بعد فشل متكرر', en: 'Paused after repeated failures' },
  recentFailures: { ar: 'إخفاقات متتالية', en: 'Failures in a row' },
  noDeliveries: { ar: 'لم يُرسل شيء بعد.', en: 'Nothing sent yet.' },
  attempt: { ar: 'محاولة', en: 'attempt' },
  noResponse: { ar: 'بلا رد', en: 'no response' },
  deleteWebhook: { ar: 'حذف الوجهة', en: 'Delete this endpoint' },
  deleteWebhookBody: { ar: 'تُحذف مع سجلّها. لا يوجد ما يُبطل — نتوقف ببساطة عن الإرسال إليها.', en: 'It goes with its history. There is nothing to revoke — we simply stop calling it.' },
  apiKeysSubtitle: { ar: 'مفاتيح يستخدمها من يتكامل مع متجرك، وما يُسمح لكل مفتاح بفعله.', en: 'Keys other systems use to reach your store, and what each is allowed to do.' },
  createApiKey: { ar: 'إنشاء مفتاح', en: 'Create a key' },
  apiKeyNameHint: { ar: 'اسم يذكّرك بمن يستخدمه — «نظام المستودع»', en: 'A name that says who uses it — "Warehouse system"' },
  apiKeyPermissionsHint: { ar: 'نفس صلاحيات الفريق: المفتاح لا يملك ما لا يملكه اسم.', en: 'The same permissions the team uses: a key cannot hold a power without a name.' },
  noApiKeys: { ar: 'لا توجد مفاتيح', en: 'No keys yet' },
  noApiKeysHint: { ar: 'أنشئ مفتاحًا ليتمكن نظام آخر من التعامل مع متجرك.', en: 'Create one so another system can work with your store.' },
  keyCreated: { ar: 'أُنشئ المفتاح', en: 'Key created' },
  keyShownOnce: { ar: 'انسخه الآن — لن يُعرض مرة أخرى، ولا يمكن استرجاعه.', en: 'Copy it now. It is never shown again and cannot be recovered.' },
  copy: { ar: 'نسخ', en: 'Copy' },
  lastUsed: { ar: 'آخر استخدام', en: 'Last used' },
  neverUsed: { ar: 'لم يُستخدم', en: 'Never' },
  calls: { ar: 'الطلبات', en: 'Calls' },
  history: { ar: 'السجل', en: 'History' },
  noCallsYet: { ar: 'لم يستدعِ هذا المفتاح شيئًا بعد.', en: 'This key has not called anything yet.' },
  endpoint: { ar: 'الوجهة', en: 'Endpoint' },
  result: { ar: 'النتيجة', en: 'Result' },
  revoke: { ar: 'إبطال', en: 'Revoke' },
  revoked: { ar: 'مُبطل', en: 'Revoked' },
  revokeKey: { ar: 'إبطال المفتاح', en: 'Revoke this key' },
  revokeKeyBody: { ar: 'يتوقف فورًا ولا يمكن إعادته. يبقى سجله للمراجعة.', en: 'It stops at once and cannot be brought back. Its history stays.' },
  expiresAt: { ar: 'ينتهي في', en: 'Expires' },
  messaging: { ar: 'الرسائل النصية', en: 'Text messages' },
  messagingSubtitle: { ar: 'ماذا نرسل، وبأي صيغة، وقواعد رمز الدخول — قبل اختيار المزوّد.', en: 'What goes out, in what words, and the one-time-code rules — before a provider is chosen.' },
  smsDelivery: { ar: 'الإرسال', en: 'Delivery' },
  smsDeliveryHint: { ar: 'حتى تختار مزوّدًا، تُطبع الرسائل في السجل بدل إرسالها.', en: 'Until a provider is signed, messages are printed to the log instead of sent.' },
  smsConsoleDriver: { ar: 'تُطبع في السجل', en: 'Logged, not sent' },
  senderName: { ar: 'اسم المُرسِل', en: 'Sender name' },
  senderNameHint: { ar: 'يظهر للعميل مكان رقم المرسل، حيث يدعمه المزوّد', en: 'What the message arrives from, where the provider supports it' },
  otpSignIn: { ar: 'الدخول برمز', en: 'Sign in with a code' },
  otpSignInHint: { ar: 'القواعد مطبَّقة الآن: الطول، المدة، إعادة الإرسال، الحد لكل رقم.', en: 'The rules are enforced now: length, expiry, resend, and the cap per number.' },
  otpNeedsSms: { ar: 'الدخول برمز يحتاج تفعيل الإرسال أيضًا.', en: 'Sign-in by code also needs delivery switched on.' },
  otplength: { ar: 'عدد الأرقام', en: 'Digits' },
  otpttlSeconds: { ar: 'مدة الصلاحية (ثانية)', en: 'Expires after (seconds)' },
  otpresendSeconds: { ar: 'إعادة الإرسال بعد (ثانية)', en: 'Resend after (seconds)' },
  otpmaxPerHour: { ar: 'حد الرموز في الساعة', en: 'Codes per hour' },
  otpmaxAttempts: { ar: 'محاولات خاطئة', en: 'Wrong attempts' },
  smsEvents: { ar: 'المناسبات', en: 'When we text' },
  smsEventsHint: { ar: 'القائمة ثابتة في الكود، والصياغة يملكها المتجر.', en: 'The catalogue is code; the wording belongs to the store.' },
  customised: { ar: 'صياغة مخصّصة', en: 'Your wording' },
  alwaysSent: { ar: 'يُرسل دائمًا عند تفعيل الدخول برمز', en: 'Always sent when code sign-in is on' },
  integrationsSubtitle: { ar: 'التحليلات والرسائل وشركات الشحن — فعّل ما تحتاجه والصق مفاتيحه.', en: 'Analytics, messaging and couriers — switch on what you use and paste its keys.' },
  integrationCategoryAnalytics: { ar: 'التحليلات والإعلانات', en: 'Analytics and advertising' },
  integrationCategoryMessaging: { ar: 'الرسائل', en: 'Messaging' },
  integrationCategoryShipping: { ar: 'شركات الشحن', en: 'Couriers' },
  integrationCategoryMarketing: { ar: 'التسويق', en: 'Marketing' },
  integrationCategoryCapability: { ar: 'قدرات غير مبنية بعد', en: 'Not built yet' },
  integrationsAll: { ar: 'الكل', en: 'All' },
  integrationConnected: { ar: 'متصل', en: 'Connected' },
  integrationNotConnected: { ar: 'غير متصل', en: 'Not connected' },
  configure: { ar: 'إعداد', en: 'Configure' },
  integrationDocs: { ar: 'التوثيق', en: 'Documentation' },
  searchIntegrations: { ar: 'ابحث في التطبيقات', en: 'Search integrations' },
  integrationsNone: { ar: 'لا شيء بهذا الاسم هنا', en: 'Nothing by that name here' },
  integrationSwitch: { ar: 'مفعّل في المتجر', en: 'Switched on for the store' },
  integrationSwitchHint: {
    ar: 'لا يُفعَّل ما دام ينقصه مفتاح مطلوب',
    en: 'It cannot be switched on while a required key is missing',
  },
  integrationUnbuiltHint: {
    ar: 'معروض لتعرف أنه قادم — لا يمكن تفعيله حتى تُبنى له وحدة تشغيل',
    en: 'Shown so you know it is coming — it cannot be switched on until the platform acts on it',
  },
  integrationKeys: { ar: 'المفاتيح', en: 'Keys' },
  brandingSubtitle: { ar: 'ألوان المتجر وشعاره وخطه — تظهر مباشرة دون إعادة نشر.', en: "The store's colours, logo and type — live, with no redeploy." },
  brandColor: { ar: 'اللون الأساسي', en: 'Brand colour' },
  brandColorHint: { ar: 'لون واحد، وتُشتق منه عشر درجات متناسقة.', en: 'One colour in; the ten shades below are derived from it.' },
  pickAColor: { ar: 'اختر لونًا', en: 'Pick' },
  hexValue: { ar: 'القيمة', en: 'Hex' },
  logoAndFavicon: { ar: 'الشعار والأيقونة', en: 'Logo and favicon' },
  logoHint: {
    ar: 'يظهر الشعار في ترويسة المتجر، وفي قائمة لوحة التحكم وصفحة دخولها؛ والأيقونة في تبويب المتصفح.',
    en: "The logo sits in the store's header, and in the admin's menu and sign-in page; the favicon in the browser tab.",
  },
  storeLogo: { ar: 'شعار المتجر', en: 'Store logo' },
  favicon: { ar: 'أيقونة المتصفح', en: 'Favicon' },
  noLogoYet: { ar: 'يُعرض اسم المتجر بدلًا منه', en: "The store's name is shown instead" },
  noFaviconYet: { ar: 'لا توجد أيقونة', en: 'No favicon' },
  removeImage: { ar: 'إزالة', en: 'Remove' },
  typeAndShape: { ar: 'الخط والحواف', en: 'Type and shape' },
  typeAndShapeHint: { ar: 'الخط من قائمة مختارة تدعم العربية جيدًا.', en: 'A chosen set of faces, each of which renders Arabic well.' },
  fontFamily: { ar: 'الخط', en: 'Typeface' },
  fontsystem: { ar: 'خط النظام', en: 'System' },
  fontcairo: { ar: 'القاهرة', en: 'Cairo' },
  fonttajawal: { ar: 'تجوال', en: 'Tajawal' },
  fontibmplex: { ar: 'IBM Plex Arabic', en: 'IBM Plex Arabic' },
  cornerStyle: { ar: 'الحواف', en: 'Corners' },
  radiussharp: { ar: 'حادة', en: 'Sharp' },
  radiussoft: { ar: 'ناعمة', en: 'Soft' },
  radiusround: { ar: 'دائرية', en: 'Round' },
  brandingPreview: { ar: 'معاينة', en: 'Preview' },
  adminColors: { ar: 'ألوان لوحة التحكم', en: 'Admin colours' },
  adminColorsHint: {
    ar: 'ما يراه فريقك في لوحة التحكم هذه. شعارك يظهر فيها في الحالتين.',
    en: 'What your team sees in this back office. Your logo shows here either way.',
  },
  adminThemePlatform: { ar: 'ألوان المنصة', en: 'Platform palette' },
  adminThemePlatformHint: { ar: 'الزمردي والأخضر الداكن — مصممة لساعات العمل الطويلة.', en: 'Emerald and forest — made for long working days.' },
  adminThemeStore: { ar: 'لون متجري', en: "My store's colour" },
  adminThemeStoreHint: { ar: 'تأخذ الأزرار والتمييز والقائمة لون علامتك.', en: 'Buttons, highlights and the menu take your brand colour.' },
  adminThemeNeedsColor: {
    ar: 'احفظ لون العلامة أولًا — وحتى ذلك تبقى لوحة التحكم بألوان المنصة.',
    en: 'Save a brand colour first — until then the admin keeps the platform palette.',
  },
  adminPreview: { ar: 'في لوحة التحكم', en: 'In the admin' },
  storefrontPreview: { ar: 'في المتجر', en: 'In the store' },
  previewHint: { ar: 'بنفس المتغيّرات التي يستلمها المتجر.', en: 'Built from the same variables the storefront gets.' },
  addToCartPreview: { ar: 'أضف إلى السلة', en: 'Add to cart' },
  promoBadgePreview: { ar: 'خصم 20٪ اليوم', en: '20% off today' },
  secondaryPreview: { ar: 'إجراء ثانوي', en: 'Secondary action' },
  paymentProviders: { ar: 'مزوّدو الدفع', en: 'Payment providers' },
  paymentProvidersSubtitle: { ar: 'فعّل ما تعاقدت معه والصق المفاتيح — بدون إعادة نشر.', en: 'Switch on whoever you signed with and paste the keys — no redeploy.' },
  paymentKindOffline: { ar: 'دفع عند الاستلام', en: 'Paid on delivery' },
  paymentKindCard: { ar: 'بطاقات', en: 'Cards' },
  paymentKindBnpl: { ar: 'اشترِ الآن وادفع لاحقًا', en: 'Buy now, pay later' },
  enabled: { ar: 'مفعّل', en: 'On' },
  enable: { ar: 'تفعيل', en: 'Enable' },
  testMode: { ar: 'وضع الاختبار', en: 'Test mode' },
  notShippedYet: { ar: 'لم يُنفّذ بعد', en: 'Not built yet' },
  supportsRefunds: { ar: 'يدعم الاسترداد', en: 'Refunds' },
  supportsWebhooks: { ar: 'يدعم الويبهوك', en: 'Webhooks' },
  redirectsShopper: { ar: 'يحوّل المشتري لصفحته', en: 'Redirects the shopper' },
  missingCredentials: { ar: 'ينقصه', en: 'Still needs' },
  paymentMethodsOffered: { ar: 'طرق الدفع المعروضة للعميل', en: 'Ways to pay offered at checkout' },
  paymentMethodsOfferedHint: { ar: 'تظهر في صفحة الدفع بهذا الترتيب. واحدة على الأقل.', en: 'Shown at checkout in this order. At least one.' },
  noCredentialsNeeded: { ar: 'لا يحتاج أي مفاتيح.', en: 'Needs no keys at all.' },
  stored: { ar: 'المحفوظ', en: 'Stored' },
  leaveBlankToKeep: { ar: 'اتركه فارغًا للإبقاء على الحالي', en: 'Leave blank to keep the current one' },
  saveKeys: { ar: 'حفظ المفاتيح', en: 'Save keys' },
  repeatPurchases: { ar: 'المشتريات المتكررة', en: 'Bought more than once' },
  repeatPurchasesHint: { ar: 'ما اشتراه في أكثر من طلب — أقوى دليل على ما سيشتريه لاحقًا.', en: 'What they bought in more than one order — the best signal about what they buy next.' },
  noRepeatPurchases: { ar: 'لم يكرّر شراء أي منتج بعد.', en: 'Nothing bought twice yet.' },
  ordersWithIt: { ar: 'عدد الطلبات', en: 'Orders' },
  unitsBought: { ar: 'القطع', en: 'Units' },
  spent: { ar: 'الإنفاق', en: 'Spent' },
  favouriteCategory: { ar: 'القسم المفضّل', en: 'Where the money goes' },
  currentCart: { ar: 'السلة الحالية', en: 'Cart right now' },
  cartEmpty: { ar: 'السلة فارغة.', en: 'Nothing in the cart.' },
  updated: { ar: 'آخر تحديث', en: 'Updated' },
  wishlist: { ar: 'المفضلة', en: 'Wishlist' },
  wishlistHint: { ar: 'ما نواه لاحقًا.', en: 'What they meant to come back for.' },
  noWishlist: { ar: 'لا توجد منتجات محفوظة.', en: 'Nothing saved.' },
  compareList: { ar: 'قائمة المقارنة', en: 'Comparing' },
  compareListHint: { ar: 'ما يوازن بينه الآن.', en: 'What they are deciding between.' },
  noCompare: { ar: 'لا شيء في المقارنة.', en: 'Nothing being compared.' },
  noResults: { ar: 'لا توجد نتائج', en: 'No matches' },
  addItem: { ar: 'إضافة…', en: 'Add…' },
  editEntry: { ar: 'تعديل', en: 'Edit' },
  deleteEntry: { ar: 'حذف', en: 'Delete' },
  fullName: { ar: 'الاسم', en: 'Name' },
  teamSubtitle: { ar: 'من يعمل هنا، وماذا يستطيع أن يفعل', en: 'Who works here, and what they can do' },
  people: { ar: 'الأشخاص', en: 'People' },
  permissionGroups: { ar: 'المجموعات', en: 'Groups' },
  addStaff: { ar: 'إضافة موظف', en: 'Add staff' },
  editStaff: { ar: 'تعديل الموظف', en: 'Edit staff member' },
  staffPassword: { ar: 'كلمة المرور', en: 'Password' },
  staffPasswordHint: { ar: '12 حرفًا على الأقل. يغيّرها الموظف بعد أول دخول.', en: 'At least 12 characters. They change it after signing in.' },
  setStaffPassword: { ar: 'تعيين كلمة مرور', en: 'Set password' },
  setStaffPasswordBody: { ar: 'للزميل الذي لا يستطيع الدخول. جلساته الأخرى تبقى كما هي.', en: 'For the colleague who cannot sign in. Their other sessions are left alone.' },
  role: { ar: 'الدور', en: 'Role' },
  roleOWNER: { ar: 'مالك', en: 'Owner' },
  roleSTAFF: { ar: 'موظف', en: 'Staff' },
  ownerBypassNote: { ar: 'المالك يتجاوز كل الصلاحيات — لا حاجة لتحديد شيء له.', en: 'An owner bypasses every permission check, so nothing needs ticking.' },
  staffGroup: { ar: 'المجموعة', en: 'Group' },
  noStaffGroup: { ar: 'بدون مجموعة', en: 'No group' },
  extraPermissions: { ar: 'صلاحيات إضافية', en: 'Extra permissions' },
  extraPermissionsHint: { ar: 'فوق ما تمنحه المجموعة — للاستثناء الذي يخص شخصًا واحدًا.', en: 'On top of the group — for the exception one person needs.' },
  effectivePermissionCount: { ar: '{count} صلاحية', en: '{count} permissions' },
  fromGroup: { ar: 'من المجموعة', en: 'From the group' },
  deactivateStaff: { ar: 'إيقاف الحساب', en: 'Deactivate' },
  activateStaff: { ar: 'تفعيل الحساب', en: 'Activate' },
  deactivated: { ar: 'موقوف', en: 'Deactivated' },
  searchTeamHint: { ar: 'الاسم أو البريد', en: 'Name or email' },
  filterRole: { ar: 'الدور', en: 'Role' },
  allRoles: { ar: 'كل الأدوار', en: 'All roles' },
  allGroups: { ar: 'كل المجموعات', en: 'All groups' },
  anyState: { ar: 'الكل', en: 'Any' },
  onlyActive: { ar: 'النشطون', en: 'Active' },
  onlyInactive: { ar: 'الموقوفون', en: 'Deactivated' },
  noStaff: { ar: 'لا يوجد موظفون', en: 'Nobody here yet' },
  noStaffHint: { ar: 'أضف زميلًا وأسنده إلى مجموعة.', en: 'Add a colleague and put them in a group.' },
  addGroup: { ar: 'إضافة مجموعة', en: 'Add group' },
  editGroup: { ar: 'تعديل المجموعة', en: 'Edit group' },
  groupNameEn: { ar: 'الاسم (إنجليزي)', en: 'Name (English)' },
  groupNameAr: { ar: 'الاسم (عربي)', en: 'Name (Arabic)' },
  permissions: { ar: 'الصلاحيات', en: 'Permissions' },
  members: { ar: 'الأعضاء', en: 'Members' },
  systemGroup: { ar: 'مدمجة', en: 'Built in' },
  deleteGroup: { ar: 'حذف المجموعة', en: 'Delete group' },
  deleteGroupBody: { ar: 'تُحذف نهائيًا. المجموعات التي بها أعضاء أو المدمجة تُوقف بدل الحذف.', en: 'Deleted for good. A group with members, or a built-in one, is deactivated instead.' },
  noGroups: { ar: 'لا توجد مجموعات', en: 'No groups' },
  noGroupsHint: { ar: 'المجموعة تمنح الصلاحيات حسب الوظيفة بدل تحديدها لكل شخص.', en: 'A group grants access by job instead of person by person.' },
  selectAll: { ar: 'تحديد الكل', en: 'Select all' },
  clearAll: { ar: 'إلغاء الكل', en: 'Clear all' },
  permAreaCatalog: { ar: 'المنتجات', en: 'Catalog' },
  permAreaOrders: { ar: 'الطلبات والمال', en: 'Orders & money' },
  permAreaReturns: { ar: 'المرتجعات', en: 'Returns' },
  permAreaCustomers: { ar: 'العملاء والتسويق', en: 'Customers & marketing' },
  permAreaConfig: { ar: 'المحتوى والإعداد', en: 'Content & config' },
  permAreaData: { ar: 'البيانات', en: 'Data' },
  permAreaTeam: { ar: 'الفريق', en: 'Team' },
  permProductsRead: { ar: 'عرض المنتجات', en: 'View products' },
  permProductsManage: { ar: 'إدارة المنتجات', en: 'Manage products' },
  permCategoriesManage: { ar: 'إدارة الأقسام', en: 'Manage categories' },
  permInventoryManage: { ar: 'إدارة المخزون', en: 'Manage inventory' },
  permOrdersRead: { ar: 'عرض الطلبات', en: 'View orders' },
  permOrdersFulfill: { ar: 'تجهيز الطلبات', en: 'Fulfil orders' },
  permOrdersAdmin: { ar: 'تعديل حالة الطلب', en: 'Override order status' },
  permRefundsManage: { ar: 'تنفيذ الاستردادات', en: 'Issue refunds' },
  permReturnsRead: { ar: 'عرض المرتجعات', en: 'View returns' },
  permReturnsManage: { ar: 'إدارة المرتجعات', en: 'Manage returns' },
  permCustomersRead: { ar: 'عرض العملاء', en: 'View customers' },
  permSupportManage: { ar: 'الرد على العملاء وكتابة الملاحظات', en: 'Answer customers and write notes' },
  permDiscountsManage: { ar: 'إدارة الخصومات', en: 'Manage discounts' },
  permReviewsModerate: { ar: 'مراجعة التقييمات', en: 'Moderate reviews' },
  permContentManage: { ar: 'إدارة المحتوى', en: 'Manage content' },
  permSettingsManage: { ar: 'إدارة الإعدادات', en: 'Manage settings' },
  permShippingManage: { ar: 'إدارة الشحن', en: 'Manage shipping' },
  permGeographyManage: { ar: 'إدارة المناطق', en: 'Manage locations' },
  permImportRun: { ar: 'تشغيل الاستيراد', en: 'Run imports' },
  permDashboardRead: { ar: 'عرض التقارير', en: 'View the dashboard' },
  permAuditRead: { ar: 'قراءة سجل التدقيق', en: 'Read the audit log' },
  permStaffManage: { ar: 'إدارة الفريق', en: 'Manage the team' },
  // ─── Audit trail ───────────────────────────────────────────────────────────
  auditKindFilter: { ar: 'نوع السجل', en: 'Kind of record' },
  auditAnyKind: { ar: 'كل الأنواع', en: 'Any kind' },
  auditAnyone: { ar: 'أي شخص', en: 'Anyone' },
  auditFilterByActor: { ar: 'اعرض كل ما فعله هذا الشخص', en: 'Show everything this person did' },
  auditOpenRecord: { ar: 'افتح هذا السجل', en: 'Open this record' },
  auditActionCode: { ar: 'رمز الإجراء', en: 'Action code' },
  auditAccountGone: { ar: 'حساب لم يعد موجودًا', en: 'An account that no longer exists' },
  // What a record is (lib/audit.ts, `subjectKind`).
  auditKind_order: { ar: 'طلب', en: 'Order' },
  auditKind_return: { ar: 'مرتجع', en: 'Return' },
  auditKind_product: { ar: 'منتج', en: 'Product' },
  auditKind_variant: { ar: 'صنف', en: 'Variant' },
  auditKind_category: { ar: 'قسم', en: 'Category' },
  auditKind_attribute: { ar: 'مواصفة', en: 'Specification' },
  auditKind_option: { ar: 'خيار', en: 'Option' },
  auditKind_option_value: { ar: 'قيمة خيار', en: 'Option value' },
  auditKind_productField: { ar: 'حقل منتج', en: 'Product field' },
  auditKind_brand: { ar: 'علامة تجارية', en: 'Brand' },
  auditKind_popup: { ar: 'نافذة منبثقة', en: 'Popup' },
  auditKind_look: { ar: 'تنسيق', en: 'Look' },
  auditKind_image: { ar: 'صورة', en: 'Image' },
  auditKind_video: { ar: 'فيديو', en: 'Video' },
  auditKind_file: { ar: 'ملف', en: 'File' },
  auditKind_upload_batch: { ar: 'دفعة استيراد', en: 'Import batch' },
  auditKind_mapping_profile: { ar: 'مطابقة أعمدة', en: 'Column mapping' },
  auditKind_customer: { ar: 'عميل', en: 'Customer' },
  auditKind_user: { ar: 'حساب', en: 'Account' },
  auditKind_address: { ar: 'عنوان', en: 'Address' },
  auditKind_trade_application: { ar: 'طلب حساب تاجر', en: 'Trade application' },
  auditKind_price_list: { ar: 'قائمة أسعار', en: 'Price list' },
  auditKind_discount: { ar: 'كود خصم', en: 'Discount code' },
  auditKind_promotion: { ar: 'عرض تلقائي', en: 'Automatic discount' },
  auditKind_campaign: { ar: 'حملة رسائل', en: 'Message campaign' },
  auditKind_review: { ar: 'تقييم', en: 'Review' },
  auditKind_question: { ar: 'سؤال', en: 'Question' },
  auditKind_ticket: { ar: 'تذكرة دعم', en: 'Support ticket' },
  auditKind_message: { ar: 'رسالة', en: 'Message' },
  auditKind_emailTemplate: { ar: 'رسالة بريد', en: 'Email template' },
  auditKind_setting: { ar: 'إعداد', en: 'Setting' },
  auditKind_shippingZone: { ar: 'منطقة شحن', en: 'Shipping zone' },
  auditKind_shippingMethod: { ar: 'طريقة توصيل', en: 'Delivery option' },
  auditKind_paymentProvider: { ar: 'بوابة دفع', en: 'Payment gateway' },
  auditKind_courier: { ar: 'منصة شحن', en: 'Shipping platform' },
  auditKind_integration: { ar: 'تكامل', en: 'Integration' },
  auditKind_webhook: { ar: 'ويبهوك', en: 'Webhook' },
  auditKind_apiKey: { ar: 'مفتاح API', en: 'API key' },
  auditKind_staffGroup: { ar: 'مجموعة صلاحيات', en: 'Permission group' },
  auditKind_cart: { ar: 'سلة', en: 'Cart' },
  auditKind_invoice: { ar: 'فاتورة', en: 'Invoice' },
  auditKind_size_chart: { ar: 'جدول مقاسات', en: 'Size chart' },
  auditKind_blog_post: { ar: 'تدوينة', en: 'Blog post' },
  auditAction_blog_post_create: { ar: 'كتابة تدوينة', en: 'Blog post written' },
  auditAction_blog_post_update: { ar: 'تعديل تدوينة', en: 'Blog post edited' },
  auditAction_blog_post_delete: { ar: 'حذف تدوينة', en: 'Blog post deleted' },
  auditAction_wallet_adjust: { ar: 'تعديل رصيد عميل', en: 'Store credit changed' },
  auditAction_loyalty_adjust: { ar: 'تعديل نقاط عميل', en: 'Loyalty points changed' },
  auditAction_giftcard_issue: { ar: 'إصدار بطاقات هدية', en: 'Gift cards issued' },
  auditAction_giftcard_void: { ar: 'إلغاء بطاقة هدية', en: 'Gift card voided' },
  auditAction_giftcard_resend: { ar: 'إعادة إرسال بطاقة هدية', en: 'Gift card sent again' },
  auditKind_giftcard: { ar: 'بطاقة هدية', en: 'Gift card' },
  auditKind_affiliate: { ar: 'مسوّق', en: 'Affiliate' },
  auditKind_customer_group: { ar: 'مجموعة عملاء', en: 'Customer group' },
  auditAction_customer_group_create: { ar: 'إضافة مجموعة عملاء', en: 'Customer group added' },
  auditAction_customer_group_update: { ar: 'تعديل مجموعة عملاء', en: 'Customer group changed' },
  auditAction_customer_group_delete: { ar: 'حذف مجموعة عملاء', en: 'Customer group deleted' },
  auditKind_follow_up: { ar: 'متابعة', en: 'Follow-up' },
  auditAction_whatsapp_templates_submitted: { ar: 'إرسال قوالب واتساب إلى Meta', en: 'WhatsApp templates sent to Meta' },
  auditAction_whatsapp_test_sent: { ar: 'رسالة واتساب تجريبية', en: 'WhatsApp test message sent' },
  auditAction_campaign_winner_picked: { ar: 'اختيار العنوان الفائز في حملة', en: 'Campaign subject winner picked' },
  auditAction_preferences_update: { ar: 'حفظ تخصيص الصفحة الرئيسية أو ملخص البريد', en: 'Overview or summary email changed' },
  auditAction_follow_up_create: { ar: 'إضافة متابعة', en: 'Follow-up added' },
  auditAction_follow_up_update: { ar: 'تعديل متابعة', en: 'Follow-up changed' },
  auditAction_follow_up_done: { ar: 'إنجاز متابعة', en: 'Follow-up done' },
  auditAction_follow_up_reopen: { ar: 'إعادة فتح متابعة', en: 'Follow-up reopened' },
  auditAction_follow_up_delete: { ar: 'حذف متابعة', en: 'Follow-up deleted' },
  auditKind_stock_location: { ar: 'موقع مخزون', en: 'Stock location' },
  auditAction_stock_location_create: { ar: 'إضافة موقع مخزون', en: 'Stock location added' },
  auditAction_stock_location_update: { ar: 'تعديل موقع مخزون', en: 'Stock location changed' },
  auditAction_stock_location_delete: { ar: 'حذف موقع مخزون', en: 'Stock location deleted' },
  auditAction_affiliate_create: { ar: 'إضافة مسوّق', en: 'Affiliate added' },
  auditAction_affiliate_update: { ar: 'تعديل مسوّق', en: 'Affiliate changed' },
  auditAction_affiliate_pay: { ar: 'تسجيل دفعة لمسوّق', en: 'Affiliate paid' },
  // The store's blog (ADR-080).
  blog: { ar: 'المدونة', en: 'Blog' },
  blogSubtitle: { ar: 'مقالات بكلماتك — تُكتب كالصفحات وتحمل منتجاتك', en: 'Articles in your own words — written as pages are, carrying your products' },
  blogWrite: { ar: 'كتابة تدوينة', en: 'Write a post' },
  blogStatus: { ar: 'الحالة', en: 'Status' },
  blogAll: { ar: 'الكل', en: 'All' },
  blogPublished: { ar: 'المنشورة', en: 'Published' },
  blogDrafts: { ar: 'المسودات', en: 'Drafts' },
  blogSearch: { ar: 'ابحث في العناوين', en: 'Search titles' },
  blogEmpty: { ar: 'لا تدوينات بعد', en: 'No posts yet' },
  blogEmptyHint: { ar: 'اكتب عن العناية بمنتجاتك وصنعتها — وأضف رابط /blog إلى القائمة.', en: 'Write about caring for what you sell and how it is made — and add /blog to the menu.' },
  blogPostTitle: { ar: 'العنوان', en: 'Title' },
  blogDate: { ar: 'تاريخ النشر', en: 'Published on' },
  blogDateHint: { ar: 'تاريخ قادم يجدول النشر؛ فارغ مع النشر: الآن', en: 'A date ahead schedules it; empty when publishing: now' },
  blogAuthor: { ar: 'الكاتب', en: 'Author' },
  blogState_draft: { ar: 'مسودة', en: 'Draft' },
  blogState_live: { ar: 'منشورة', en: 'Published' },
  blogState_scheduled: { ar: 'مجدولة', en: 'Scheduled' },
  blogWords: { ar: 'النص', en: 'The words' },
  blogLanguage: { ar: 'اللغة', en: 'Language' },
  blogExcerpt: { ar: 'مقتطف', en: 'Excerpt' },
  blogExcerptHint: { ar: 'جملة أو اثنتان تظهر في قائمة التدوينات', en: 'A sentence or two for the list of posts' },
  blogBody: { ar: 'نص التدوينة', en: 'The post' },
  blogSearchResult: { ar: 'في نتائج البحث', en: 'In search results' },
  blogSearchResultHint: { ar: 'فارغ: العنوان والمقتطف', en: 'Empty: the title and the excerpt' },
  blogProducts: { ar: 'منتجات في التدوينة', en: 'Products in the post' },
  blogProductsHint: { ar: 'حتى ٨، تظهر أسفلها وتُضاف للسلة مباشرة', en: 'Up to 8, shown under it, ready for the basket' },
  blogPublishing: { ar: 'النشر', en: 'Publishing' },
  blogDraftHint: { ar: 'لا يراها إلا الفريق', en: 'Only the team sees it' },
  blogPublish: { ar: 'نشر', en: 'Publish' },
  blogPublishHint: { ar: 'تظهر للزوار عند تاريخها', en: 'Visitors see it from its date' },
  blogSlugHint: { ar: 'عنوان الصفحة: /blog/…', en: 'Its address: /blog/…' },
  blogCover: { ar: 'صورة الغلاف', en: 'Cover' },
  blogCoverHint: { ar: 'تظهر في القائمة وعند مشاركة الرابط', en: 'Shown in the list and when the link is shared' },
  blogDeleteTitle: { ar: 'حذف التدوينة؟', en: 'Delete this post?' },
  blogDeleteBody: { ar: 'يتوقف عنوانها عن العمل — أضف تحويلًا إن كان قد شورك.', en: 'Its address stops working — add a redirect if it was shared.' },
  // What happened (lib/audit.ts, `action` with its dots as underscores).
  auditAction_account_address_created: { ar: 'إضافة عنوان إلى حساب', en: 'Address added to an account' },
  auditAction_account_address_deleted: { ar: 'حذف عنوان من حساب', en: 'Address removed from an account' },
  auditAction_account_address_updated: { ar: 'تعديل عنوان في حساب', en: 'Account address edited' },
  auditAction_account_erased: { ar: 'حذف العميل لحسابه', en: 'Account erased by its owner' },
  auditAction_account_exported: { ar: 'تنزيل العميل لبياناته', en: 'Account data downloaded by its owner' },
  auditAction_account_password_changed: { ar: 'تغيير كلمة مرور الحساب', en: 'Account password changed' },
  auditAction_account_profile_updated: { ar: 'تعديل بيانات الحساب', en: 'Account details edited' },
  auditAction_ai_configured: { ar: 'ضبط المساعد الذكي', en: 'AI assistant set up' },
  auditAction_api_key_created: { ar: 'إنشاء مفتاح API', en: 'API key created' },
  auditAction_api_key_revoked: { ar: 'إلغاء مفتاح API', en: 'API key revoked' },
  auditAction_api_key_updated: { ar: 'تعديل مفتاح API', en: 'API key edited' },
  auditAction_attribute_created: { ar: 'إنشاء مواصفة', en: 'Specification created' },
  auditAction_attribute_deactivated: { ar: 'إيقاف مواصفة', en: 'Specification switched off' },
  auditAction_attribute_updated: { ar: 'تعديل مواصفة', en: 'Specification edited' },
  auditAction_brand_created: { ar: 'إنشاء علامة تجارية', en: 'Brand created' },
  auditAction_branding_updated: { ar: 'تعديل الهوية البصرية', en: 'Branding changed' },
  auditAction_campaign_cancelled: { ar: 'إلغاء حملة رسائل', en: 'Message campaign cancelled' },
  auditAction_campaign_created: { ar: 'إنشاء حملة رسائل', en: 'Message campaign created' },
  auditAction_campaign_deleted: { ar: 'حذف حملة رسائل', en: 'Message campaign deleted' },
  auditAction_campaign_sent: { ar: 'إرسال حملة رسائل', en: 'Message campaign sent' },
  auditAction_campaign_updated: { ar: 'تعديل حملة رسائل', en: 'Message campaign edited' },
  auditAction_category_attributes_set: { ar: 'تحديد مواصفات قسم', en: 'Category specifications set' },
  auditAction_category_create: { ar: 'إنشاء قسم', en: 'Category created' },
  auditAction_category_deactivate: { ar: 'إيقاف قسم', en: 'Category switched off' },
  auditAction_category_order_set: { ar: 'إعادة ترتيب الأقسام', en: 'Categories reordered' },
  auditAction_category_productFields_set: { ar: 'تحديد حقول منتجات قسم', en: 'Category product fields set' },
  auditAction_category_update: { ar: 'تعديل قسم', en: 'Category edited' },
  auditAction_customer_erased: { ar: 'حذف بيانات عميل', en: 'Customer erased' },
  auditAction_customer_export: { ar: 'تصدير قائمة العملاء', en: 'Customer list exported' },
  auditAction_cart_reminder_sent: { ar: 'تذكير عميل بسلته', en: 'Cart reminder sent' },
  auditAction_customer_exported: { ar: 'تصدير بيانات عميل', en: 'Customer data exported' },
  auditAction_customer_note_added: { ar: 'إضافة ملاحظة على عميل', en: 'Customer note added' },
  auditAction_customer_updated: { ar: 'تعديل بيانات عميل', en: 'Customer updated' },
  auditAction_discount_created: { ar: 'إنشاء كود خصم', en: 'Discount code created' },
  auditAction_discount_disabled: { ar: 'إيقاف كود خصم', en: 'Discount code switched off' },
  auditAction_discount_updated: { ar: 'تعديل كود خصم', en: 'Discount code edited' },
  auditAction_email_template_reset: { ar: 'استعادة النص الافتراضي لرسالة بريد', en: 'Email template reset to default' },
  auditAction_email_template_tested: { ar: 'إرسال نسخة تجريبية من رسالة بريد', en: 'Test email sent' },
  auditAction_email_template_updated: { ar: 'تعديل رسالة بريد', en: 'Email template edited' },
  auditAction_ingestion_cancel: { ar: 'إلغاء استيراد', en: 'Import cancelled' },
  auditAction_ingestion_profile_create: { ar: 'حفظ مطابقة أعمدة', en: 'Column mapping saved' },
  auditAction_ingestion_profile_delete: { ar: 'حذف مطابقة أعمدة', en: 'Column mapping deleted' },
  auditAction_ingestion_profile_update: { ar: 'تعديل مطابقة أعمدة', en: 'Column mapping edited' },
  auditAction_ingestion_run: { ar: 'تشغيل استيراد', en: 'Import run' },
  auditAction_ingestion_upload: { ar: 'رفع ملف استيراد', en: 'Import file uploaded' },
  auditAction_ingestion_validate: { ar: 'فحص ملف استيراد', en: 'Import file checked' },
  auditAction_integrations_cart_recovery_run: { ar: 'تشغيل تذكير السلال المتروكة', en: 'Abandoned-cart reminders run' },
  auditAction_integrations_price_drop_run: { ar: 'تشغيل رسائل انخفاض الأسعار', en: 'Price-drop mail run' },
  auditAction_digital_file_add: { ar: 'رفع ملف منتج رقمي', en: 'Digital file uploaded' },
  auditAction_digital_file_rename: { ar: 'إعادة تسمية ملف رقمي', en: 'Digital file renamed' },
  auditAction_digital_file_remove: { ar: 'حذف ملف رقمي', en: 'Digital file deleted' },
  auditAction_digital_codes_add: { ar: 'إضافة أكواد', en: 'Codes added' },
  auditAction_digital_code_remove: { ar: 'حذف كود', en: 'Code deleted' },
  auditAction_order_digital_delivered: { ar: 'تسليم الملفات والأكواد', en: 'Files and codes delivered' },
  auditAction_integrations_updated: { ar: 'تعديل تكامل', en: 'Integration changed' },
  auditAction_inventory_adjusted: { ar: 'تعديل المخزون', en: 'Stock adjusted' },
  auditAction_product_grid_saved: { ar: 'تعديل جماعي للمنتجات', en: 'Products edited in the grid' },
  auditAction_brand_update: { ar: 'تعديل علامة تجارية', en: 'Brand changed' },
  auditAction_popup_created: { ar: 'إنشاء نافذة منبثقة', en: 'Popup created' },
  auditAction_popup_updated: { ar: 'تعديل نافذة منبثقة', en: 'Popup changed' },
  auditAction_popup_deleted: { ar: 'حذف نافذة منبثقة', en: 'Popup deleted' },
  auditAction_inventory_transferred: { ar: 'نقل مخزون بين المواقع', en: 'Stock moved between locations' },
  auditAction_invoice_download: { ar: 'تنزيل فاتورة', en: 'Invoice downloaded' },
  auditAction_invoice_export: { ar: 'تصدير سجل الفواتير', en: 'Invoice register exported' },
  auditAction_size_chart_create: { ar: 'إضافة جدول مقاسات', en: 'Size chart added' },
  auditAction_size_chart_update: { ar: 'تعديل جدول مقاسات', en: 'Size chart changed' },
  auditAction_size_chart_delete: { ar: 'حذف جدول مقاسات', en: 'Size chart deleted' },
  auditAction_look_create: { ar: 'إنشاء تنسيق', en: 'Look created' },
  auditAction_look_delete: { ar: 'حذف تنسيق', en: 'Look deleted' },
  auditAction_look_update: { ar: 'تعديل تنسيق', en: 'Look edited' },
  auditAction_message_resent: { ar: 'إعادة إرسال رسالة', en: 'Message resent' },
  auditAction_navigation_updated: { ar: 'تعديل قائمة التنقّل', en: 'Header menu changed' },
  auditAction_option_created: { ar: 'إنشاء خيار', en: 'Option created' },
  auditAction_option_updated: { ar: 'تعديل خيار', en: 'Option edited' },
  auditAction_option_value_created: { ar: 'إضافة قيمة إلى خيار', en: 'Option value added' },
  auditAction_option_value_updated: { ar: 'تعديل قيمة خيار', en: 'Option value edited' },
  auditAction_order_address_change: { ar: 'تصحيح عنوان طلب', en: 'Order address corrected' },
  auditAction_order_checkout: { ar: 'إتمام طلب', en: 'Order placed' },
  auditAction_order_created: { ar: 'طلب أنشأه الفريق', en: 'Order placed by the team' },
  auditAction_order_export: { ar: 'تصدير الطلبات', en: 'Orders exported' },
  auditAction_order_label_printed: { ar: 'طباعة ملصق شحن', en: 'Shipping label printed' },
  auditAction_order_items_changed: { ar: 'تعديل منتجات طلب', en: 'Order items changed' },
  auditAction_order_note: { ar: 'ملاحظة على طلب', en: 'Order note added' },
  auditAction_order_notification_resent: { ar: 'إعادة إرسال إشعار طلب', en: 'Order message resent' },
  auditAction_order_refunded: { ar: 'استرداد مبلغ على طلب', en: 'Order refunded' },
  auditAction_order_status_change: { ar: 'تغيير حالة طلب', en: 'Order status changed' },
  auditAction_order_updated: { ar: 'تعديل بيانات طلب', en: 'Order details changed' },
  auditAction_payments_provider_updated: { ar: 'تعديل بوابة دفع', en: 'Payment gateway changed' },
  auditAction_payments_webhook_received: { ar: 'إشعار من بوابة الدفع', en: 'Payment gateway notification received' },
  auditAction_payments_checked: { ar: 'سؤال بوابة الدفع عن نتيجة دفع', en: 'Payment gateway asked for a result' },
  auditAction_payments_retried: { ar: 'إعادة محاولة دفع ببطاقة مرفوضة', en: 'Declined card payment retried' },
  auditAction_couriers_webhook_received: { ar: 'تحديث من منصة الشحن', en: 'Shipping platform update received' },
  auditAction_return_pickup_booked: { ar: 'حجز استلام مرتجع', en: 'Return pickup booked' },
  auditAction_price_list_created: { ar: 'إنشاء قائمة أسعار', en: 'Price list created' },
  auditAction_price_list_deleted: { ar: 'حذف قائمة أسعار', en: 'Price list deleted' },
  auditAction_price_list_item_removed: { ar: 'إزالة سعر من قائمة أسعار', en: 'Price removed from a list' },
  auditAction_price_list_item_set: { ar: 'تحديد سعر في قائمة أسعار', en: 'Price set on a list' },
  auditAction_price_list_updated: { ar: 'تعديل قائمة أسعار', en: 'Price list edited' },
  auditAction_product_archive: { ar: 'أرشفة منتج', en: 'Product archived' },
  auditAction_product_attributes_set: { ar: 'تحديد مواصفات منتج', en: 'Product specifications set' },
  auditAction_product_create: { ar: 'إنشاء منتج', en: 'Product created' },
  auditAction_product_duplicated: { ar: 'نسخ منتج', en: 'Product duplicated' },
  auditAction_product_image_add: { ar: 'إضافة صورة إلى منتج', en: 'Product photo added' },
  auditAction_product_image_delete: { ar: 'حذف صورة منتج', en: 'Product photo removed' },
  auditAction_product_image_update: { ar: 'تعديل صورة منتج', en: 'Product photo edited' },
  auditAction_product_images_reorder: { ar: 'إعادة ترتيب صور منتج', en: 'Product photos reordered' },
  auditAction_product_option_values_set: { ar: 'تحديد خيارات منتج', en: 'Product options set' },
  auditAction_product_productFields_set: { ar: 'تحديد حقول منتج', en: 'Product fields set' },
  auditAction_product_update: { ar: 'تعديل منتج', en: 'Product edited' },
  auditAction_product_variant_add: { ar: 'إضافة صنف إلى منتج', en: 'Variant added' },
  auditAction_product_variant_delete: { ar: 'حذف صنف', en: 'Variant removed' },
  auditAction_product_variant_update: { ar: 'تعديل صنف', en: 'Variant edited' },
  auditAction_product_video_add: { ar: 'إضافة فيديو إلى منتج', en: 'Product video added' },
  auditAction_product_video_delete: { ar: 'حذف فيديو منتج', en: 'Product video removed' },
  auditAction_productField_created: { ar: 'إنشاء حقل منتج', en: 'Product field created' },
  auditAction_productField_deactivated: { ar: 'إيقاف حقل منتج', en: 'Product field switched off' },
  auditAction_productField_updated: { ar: 'تعديل حقل منتج', en: 'Product field edited' },
  auditAction_promotion_created: { ar: 'إنشاء عرض تلقائي', en: 'Automatic discount created' },
  auditAction_promotion_deactivated: { ar: 'إيقاف عرض تلقائي', en: 'Automatic discount switched off' },
  auditAction_promotion_updated: { ar: 'تعديل عرض تلقائي', en: 'Automatic discount edited' },
  auditAction_question_answered: { ar: 'الإجابة عن سؤال', en: 'Question answered' },
  auditAction_question_asked: { ar: 'سؤال من عميل', en: 'Question asked' },
  auditAction_question_moderated: { ar: 'مراجعة سؤال', en: 'Question moderated' },
  auditAction_return_approved: { ar: 'قبول مرتجع', en: 'Return approved' },
  auditAction_return_cancelled: { ar: 'إلغاء مرتجع', en: 'Return cancelled' },
  auditAction_return_received: { ar: 'استلام مرتجع', en: 'Return received' },
  auditAction_return_refunded: { ar: 'ردّ مبلغ مرتجع', en: 'Return refunded' },
  auditAction_return_rejected: { ar: 'رفض مرتجع', en: 'Return rejected' },
  auditAction_return_requested: { ar: 'طلب إرجاع', en: 'Return requested' },
  auditAction_review_created: { ar: 'تقييم جديد', en: 'Review written' },
  auditAction_review_moderated: { ar: 'مراجعة تقييم', en: 'Review moderated' },
  auditAction_review_replied: { ar: 'الرد على تقييم', en: 'Review replied to' },
  auditAction_review_reply_removed: { ar: 'حذف الرد على تقييم', en: 'Review reply removed' },
  auditAction_saved_list_cleared: { ar: 'إفراغ قائمة المحفوظات', en: 'Saved list cleared' },
  auditAction_saved_product_added: { ar: 'حفظ منتج في المحفوظات', en: 'Product saved' },
  auditAction_saved_product_removed: { ar: 'إزالة منتج من المحفوظات', en: 'Product unsaved' },
  auditAction_search_reindexed: { ar: 'إعادة فهرسة البحث', en: 'Search reindexed' },
  auditAction_settings_sms_updated: { ar: 'تعديل إعدادات الرسائل النصية', en: 'Text message settings changed' },
  auditAction_settings_updated: { ar: 'تعديل الإعدادات', en: 'Settings changed' },
  auditAction_shipping_method_created: { ar: 'إنشاء طريقة توصيل', en: 'Delivery option created' },
  auditAction_shipping_method_deactivated: { ar: 'إيقاف طريقة توصيل', en: 'Delivery option switched off' },
  auditAction_shipping_method_updated: { ar: 'تعديل طريقة توصيل', en: 'Delivery option edited' },
  auditAction_shipping_zone_created: { ar: 'إنشاء منطقة شحن', en: 'Shipping zone created' },
  auditAction_shipping_zone_member_added: { ar: 'إضافة موقع إلى منطقة شحن', en: 'Place added to a shipping zone' },
  auditAction_shipping_zone_member_removed: { ar: 'إزالة موقع من منطقة شحن', en: 'Place removed from a shipping zone' },
  auditAction_shipping_zone_updated: { ar: 'تعديل منطقة شحن', en: 'Shipping zone edited' },
  auditAction_shopper_file_uploaded: { ar: 'رفع ملف من عميل', en: 'File uploaded by a shopper' },
  auditAction_staff_created: { ar: 'إضافة موظف', en: 'Staff member added' },
  auditAction_staff_group_created: { ar: 'إنشاء مجموعة صلاحيات', en: 'Permission group created' },
  auditAction_staff_group_deleted: { ar: 'حذف مجموعة صلاحيات', en: 'Permission group deleted' },
  auditAction_staff_group_updated: { ar: 'تعديل مجموعة صلاحيات', en: 'Permission group edited' },
  auditAction_staff_password_set: { ar: 'تعيين كلمة مرور موظف', en: 'Staff password set' },
  auditAction_staff_two_factor_reset: { ar: 'إعادة ضبط التحقق بخطوتين لموظف', en: 'Staff two-step sign-in reset' },
  auditAction_reports_costs_backfilled: { ar: 'ملء تكاليف الطلبات القديمة', en: 'Past order costs filled' },
  auditAction_report_view_saved: { ar: 'حفظ عرض تقرير', en: 'Report view saved' },
  auditAction_report_view_updated: { ar: 'تعديل عرض تقرير', en: 'Report view changed' },
  auditAction_report_view_deleted: { ar: 'حذف عرض تقرير', en: 'Report view removed' },
  auditAction_staff_updated: { ar: 'تعديل موظف', en: 'Staff member edited' },
  auditAction_stock_alert_requested: { ar: 'طلب تنبيه بتوفّر منتج', en: 'Back-in-stock alert requested' },
  auditAction_support_customer_replied: { ar: 'ردّ العميل على تذكرة', en: 'Customer replied to a ticket' },
  auditAction_support_guest_replied: { ar: 'ردّ زائر على تذكرة', en: 'Guest replied to a ticket' },
  auditAction_support_guest_ticket_opened: { ar: 'فتح زائر لتذكرة', en: 'Ticket opened by a guest' },
  auditAction_support_staff_replied: { ar: 'ردّ الفريق على تذكرة', en: 'Staff replied to a ticket' },
  auditAction_support_ticket_opened: { ar: 'فتح تذكرة', en: 'Ticket opened' },
  auditAction_support_ticket_updated: { ar: 'تعديل تذكرة', en: 'Ticket updated' },
  auditAction_trade_account_saved: { ar: 'حفظ حساب تاجر', en: 'Trade account saved' },
  auditAction_trade_application_approved: { ar: 'قبول طلب حساب تاجر', en: 'Trade application approved' },
  auditAction_trade_application_changes_requested: { ar: 'طلب تعديلات على طلب حساب تاجر', en: 'Changes asked for on a trade application' },
  auditAction_trade_application_rejected: { ar: 'رفض طلب حساب تاجر', en: 'Trade application rejected' },
  auditAction_trade_application_submitted: { ar: 'تقديم طلب حساب تاجر', en: 'Trade application submitted' },
  auditAction_upload_image: { ar: 'رفع صورة', en: 'Image uploaded' },
  auditAction_upload_video: { ar: 'رفع فيديو', en: 'Video uploaded' },
  auditAction_user_login: { ar: 'تسجيل دخول', en: 'Signed in' },
  auditAction_user_logout: { ar: 'تسجيل خروج', en: 'Signed out' },
  auditAction_user_otp_login: { ar: 'تسجيل دخول برمز تحقق', en: 'Signed in with a code' },
  auditAction_user_login_two_factor: { ar: 'تسجيل دخول بالتحقق بخطوتين', en: 'Signed in with two-step' },
  auditAction_user_social_login: { ar: 'تسجيل دخول بحساب Google أو Apple', en: 'Signed in with Google or Apple' },
  auditAction_two_factor_enabled: { ar: 'تفعيل التحقق بخطوتين', en: 'Two-step sign-in turned on' },
  auditAction_two_factor_disabled: { ar: 'إيقاف التحقق بخطوتين', en: 'Two-step sign-in turned off' },
  auditAction_two_factor_recovery_renewed: { ar: 'رموز استرداد جديدة', en: 'Recovery codes renewed' },
  auditAction_user_otp_requested: { ar: 'طلب رمز تحقق', en: 'Sign-in code requested' },
  auditAction_user_register: { ar: 'إنشاء حساب', en: 'Account created' },
  auditAction_webhook_created: { ar: 'إنشاء ويبهوك', en: 'Webhook created' },
  auditAction_webhook_deleted: { ar: 'حذف ويبهوك', en: 'Webhook deleted' },
  auditAction_webhook_resent: { ar: 'إعادة إرسال ويبهوك', en: 'Webhook delivery resent' },
  auditAction_webhook_tested: { ar: 'اختبار ويبهوك', en: 'Webhook tested' },
  auditAction_webhook_updated: { ar: 'تعديل ويبهوك', en: 'Webhook edited' },
  searchAuditHint: { ar: 'الإجراء أو المسار أو الشخص', en: 'Action, route, or person' },
  auditAction: { ar: 'الإجراء', en: 'Action' },
  auditSubject: { ar: 'السجل المتأثر', en: 'Record' },
  subjectId: { ar: 'معرّف السجل', en: 'Record id' },
  subjectIdHint: { ar: 'الصق معرّفًا لتتبّع سجل واحد', en: 'Paste an id to follow one record' },
  filterBySubject: { ar: 'اعرض كل ما حدث لهذا السجل', en: 'Show everything that happened to this record' },
  when: { ar: 'متى', en: 'When' },
  actor: { ar: 'من', en: 'Who' },
  systemActor: { ar: 'النظام', en: 'System' },
  route: { ar: 'المسار', en: 'Route' },
  ipAddress: { ar: 'عنوان IP', en: 'IP address' },
  requestId: { ar: 'معرّف الطلب', en: 'Request id' },
  details: { ar: 'التفاصيل', en: 'Details' },
  hideDetails: { ar: 'إخفاء', en: 'Hide' },
  field: { ar: 'الحقل', en: 'Field' },
  beforeValue: { ar: 'قبل', en: 'Before' },
  afterValue: { ar: 'بعد', en: 'After' },
  noFieldChanges: { ar: 'لا توجد قيم مسجّلة قبل وبعد لهذا الإجراء.', en: 'This action recorded no before/after values.' },
  noAuditEntries: { ar: 'لا توجد إدخالات', en: 'No entries' },
  noAuditEntriesHint: { ar: 'جرّب توسيع المدة أو إزالة بعض عوامل التصفية.', en: 'Widen the dates, or drop a filter.' },
  auditSubtitle: { ar: 'من غيّر ماذا ومتى', en: 'Who changed what, and when' },

  // ─── Import ───────────────────────────────────────────────────────────────
  // The fields a column can be read as (PRODUCT_IMPORT_FIELDS); a field the API adds later shows its code.
  // Customer import (ADR-070).
  importTarget: { ar: 'ماذا يحوي الملف؟', en: 'What does the file hold?' },
  importTargetProducts: { ar: 'منتجات', en: 'Products' },
  importTargetProductsHint: { ar: 'المنتجات وأصنافها وأسعارها ومخزونها', en: 'Products, their variants, prices and stock' },
  importTargetCustomers: { ar: 'عملاء', en: 'Customers' },
  importTargetCustomersHint: { ar: 'مثل تصدير العملاء من سلة — لا يُستبدل ما لدى المتجر', en: "Like a customer export from Salla — nothing the store has is overwritten" },
  chooseCustomersFileHint: {
    ar: 'CSV أو XLSX — بريد أو جوال لكل عميل، والاسم والوسوم والموافقة على التسويق إن وُجدت',
    en: 'CSV or XLSX — an email or mobile for each customer, with name, tags and marketing consent where you have them',
  },
  mapCustomerColumnsHint: {
    ar: 'اربط عمود البريد أو الجوال على الأقل. العميل الموجود يحتفظ ببياناته، وتُضاف وسومه فقط.',
    en: 'Map the email or the mobile at least. A customer you already have keeps their details; only tags are added.',
  },
  importField_email: { ar: 'البريد الإلكتروني', en: 'Email' },
  importField_phone: { ar: 'رقم الجوال', en: 'Mobile' },
  importField_name: { ar: 'الاسم', en: 'Name' },
  importField_first_name: { ar: 'الاسم الأول', en: 'First name' },
  importField_last_name: { ar: 'اسم العائلة', en: 'Last name' },
  importField_tags: { ar: 'الوسوم', en: 'Tags' },
  importField_accepts_marketing: { ar: 'يقبل التسويق', en: 'Accepts marketing' },
  importField_joined_at: { ar: 'تاريخ التسجيل', en: 'Joined' },
  importField_sku: { ar: 'رمز المنتج (SKU)', en: 'SKU' },
  importField_name_en: { ar: 'الاسم بالإنجليزية', en: 'Name in English' },
  importField_name_ar: { ar: 'الاسم بالعربية', en: 'Name in Arabic' },
  importField_description_en: { ar: 'الوصف بالإنجليزية', en: 'Description in English' },
  importField_description_ar: { ar: 'الوصف بالعربية', en: 'Description in Arabic' },
  importField_slug: { ar: 'المعرّف في الرابط', en: 'URL slug' },
  importField_category_slug: { ar: 'معرّف القسم', en: 'Category slug' },
  importField_base_price: { ar: 'السعر الأساسي', en: 'Base price' },
  importField_price: { ar: 'سعر المتغيّر', en: 'Variant price' },
  importField_stock: { ar: 'المخزون', en: 'Stock' },
  importField_status: { ar: 'الحالة', en: 'Status' },
  importField_options: { ar: 'الخيارات (اللون، المقاس…)', en: 'Options (colour, size…)' },
  importField_barcode: { ar: 'الباركود', en: 'Barcode' },
  importField_mpn: { ar: 'رقم القطعة (MPN)', en: 'MPN' },
  importField_weight_grams: { ar: 'الوزن بالجرام', en: 'Weight in grams' },
  importField_cost: { ar: 'سعر التكلفة', en: 'Cost price' },
  gridCostPrice: { ar: 'التكلفة (بدون ضريبة)', en: 'Cost (no VAT)' },
  variantCostPrice: { ar: 'سعر التكلفة (بدون ضريبة)', en: 'Cost price (without VAT)' },
  variantCostPriceHint: { ar: 'ما دفعته للوحدة — لا يراه العملاء، ويُحسب منه الربح', en: 'What one unit cost you — never shown to shoppers; profit is read from it' },
  importField_weight_kg: { ar: 'الوزن بالكيلو', en: 'Weight in kilos' },
  importField_brand: { ar: 'الماركة', en: 'Brand' },
  importSource_shopify: { ar: 'Shopify', en: 'Shopify' },
  importSource_woocommerce: { ar: 'WooCommerce', en: 'WooCommerce' },
  importSource_salla: { ar: 'سلة', en: 'Salla' },
  importReadAs: {
    ar: 'قُرئ الملف كتصدير من {source}: صف لكل نوع، والخيارات مجمّعة، والتعيين أدناه جاهز — راجعه ثم تابع.',
    en: 'Read as a {source} export: a row for each variant, options gathered, and the mapping below filled in — check it, then go on.',
  },
  deleteMapping: { ar: 'حذف المطابقة', en: 'Delete mapping' },
  deleteSavedMapping: { ar: 'حذف هذه المطابقة المحفوظة', en: 'Delete this saved mapping' },
  deleteSavedMappingTitle: { ar: 'حذف المطابقة «{name}»؟', en: 'Delete the saved mapping “{name}”?' },
  deleteSavedMappingBody: {
    ar: 'تُحذف المطابقة المحفوظة وحدها. الأعمدة المختارة الآن تبقى كما هي، وما استُورد سابقًا لا يتغيّر.',
    en: 'Only the saved mapping goes. The columns chosen now stay as they are, and nothing already imported changes.',
  },
  importRowErrors: { ar: 'تقرير الأخطاء', en: 'Error report' },
  downloadErrorsFailed: { ar: 'تعذّر تنزيل تقرير الأخطاء.', en: 'Could not download the error report.' },
  import: { ar: 'استيراد', en: 'Import' },
  importSubtitle: { ar: 'أضف منتجات أو عملاء دفعة واحدة من ملف CSV أو Excel', en: 'Add products or customers in bulk from a CSV or Excel file' },
  importCustomers: { ar: 'استيراد', en: 'Import' },
  importCustomersHint: { ar: 'استورد العملاء من ملف — مثل تصدير سلة', en: 'Bring customers in from a file — a Salla export, say' },
  stepUpload: { ar: 'رفع الملف', en: 'Upload' },
  stepMap: { ar: 'مطابقة الأعمدة', en: 'Map columns' },
  stepReview: { ar: 'مراجعة', en: 'Review' },
  stepImport: { ar: 'الاستيراد', en: 'Import' },
  chooseFile: { ar: 'اختر ملفًا', en: 'Choose a file' },
  chooseFileHint: { ar: 'CSV أو Excel. لن يُضاف شيء للمتجر قبل مراجعتك.', en: 'CSV or Excel. Nothing reaches the store before you review it.' },
  mapColumns: { ar: 'مطابقة الأعمدة', en: 'Map the columns' },
  mapColumnsHint: { ar: 'اقترحنا مطابقة من عناوين الملف — صحّحها إن لزم.', en: 'We suggested a mapping from the file headers — correct it if needed.' },
  ignoreColumn: { ar: '— تجاهل —', en: '— Ignore —' },
  savedMapping: { ar: 'مطابقة محفوظة', en: 'Saved mapping' },
  saveMapping: { ar: 'حفظ هذه المطابقة', en: 'Save this mapping' },
  saveMappingHint: { ar: 'لإعادة استخدامها مع نفس المورّد', en: 'To reuse with the same supplier' },
  runDryRun: { ar: 'فحص الملف', en: 'Check the file' },
  dryRunResult: { ar: 'نتيجة الفحص', en: 'Check result' },
  dryRunHint: { ar: 'لم يُكتب شيء بعد — هذه الصفوف التي ستنجح والتي ستفشل.', en: 'Nothing has been written — this is what would succeed and what would fail.' },
  rowsTotal: { ar: 'إجمالي الصفوف', en: 'Rows in file' },
  rowsOk: { ar: 'صفوف سليمة', en: 'Will import' },
  rowsFailed: { ar: 'صفوف بها أخطاء', en: 'Would fail' },
  errorSample: { ar: 'أمثلة على الأخطاء', en: 'Sample of the errors' },
  preview: { ar: 'معاينة أول الصفوف', en: 'How the first rows were read' },
  row: { ar: 'صف', en: 'Row' },
  downloadErrors: { ar: 'تنزيل تقرير الأخطاء', en: 'Download error report' },
  backToMapping: { ar: 'رجوع للمطابقة', en: 'Back to mapping' },
  importRows: { ar: 'استيراد {count} صف', en: 'Import {count} rows' },
  importing: { ar: 'جارٍ الاستيراد', en: 'Importing' },
  importFinished: { ar: 'انتهى الاستيراد', en: 'Import finished' },
  importAnother: { ar: 'استيراد ملف آخر', en: 'Import another file' },
  startOver: { ar: 'البدء من جديد', en: 'Start over' },
  importHistory: { ar: 'عمليات سابقة', en: 'Recent imports' },
  noImports: { ar: 'لا توجد عمليات استيراد بعد', en: 'No imports yet' },

  // ─── Auth ─────────────────────────────────────────────────────────────────
  signIn: { ar: 'تسجيل الدخول', en: 'Sign in' },
  signInWelcomeTitle: { ar: 'كل ما يحتاجه متجرك، في مكان واحد.', en: 'Everything your store runs on, in one place.' },
  signInWelcomeBody: {
    ar: 'الطلبات والمنتجات والعملاء والتقارير — يديرها فريقك بالعربية والإنجليزية.',
    en: 'Orders, products, customers and reports — run by your team, in Arabic and English.',
  },
  signInFeatureOrders: { ar: 'الطلبات والتجهيز', en: 'Orders and fulfilment' },
  signInFeatureCatalog: { ar: 'المنتجات والمخزون', en: 'Products and inventory' },
  signInFeatureInsights: { ar: 'التقارير والتحليلات', en: 'Reports and insights' },
  signInSecure: { ar: 'اتصال آمن ومشفّر', en: 'Secure, encrypted connection' },
  signInHint: { ar: 'للموظفين ومالك المتجر فقط.', en: 'Staff and owner accounts only.' },
  email: { ar: 'البريد الإلكتروني', en: 'Email' },
  password: { ar: 'كلمة المرور', en: 'Password' },
  signingIn: { ar: 'جارٍ تسجيل الدخول…', en: 'Signing in…' },
  // Two-step sign-in (ADR-116).
  twoFactorTitle: { ar: 'التحقق بخطوتين', en: 'Two-step sign-in' },
  twoFactorSetupTitle: { ar: 'إعداد التحقق بخطوتين', en: 'Set up two-step sign-in' },
  twoFactorCodesTitle: { ar: 'رموز الاسترداد', en: 'Recovery codes' },
  twoFactorVerifyHint: { ar: 'أدخل الرمز المكوّن من 6 أرقام من تطبيق المصادقة.', en: 'Enter the 6-digit code from your authenticator app.' },
  twoFactorRequiredHint: { ar: 'متجرك يطلب التحقق بخطوتين من كل موظف. أعدّه الآن للدخول.', en: 'Your store requires two-step sign-in for all staff. Set it up now to continue.' },
  twoFactorCodesKeep: { ar: 'احفظها في مكان آمن قبل المتابعة.', en: 'Keep them somewhere safe before you continue.' },
  twoFactorCode: { ar: 'الرمز', en: 'Code' },
  twoFactorVerify: { ar: 'تحقّق', en: 'Verify' },
  twoFactorBack: { ar: 'رجوع إلى كلمة المرور', en: 'Back to password' },
  twoFactorFirstCode: { ar: 'الرمز الظاهر في التطبيق', en: 'The code the app shows' },
  twoFactorTurnOn: { ar: 'تفعيل', en: 'Turn on' },
  twoFactorTurnOff: { ar: 'إيقاف التحقق بخطوتين', en: 'Turn off two-step sign-in' },
  twoFactorStart: { ar: 'إعداد بتطبيق المصادقة', en: 'Set up with an authenticator app' },
  twoFactorCodesSaved: { ar: 'حفظتها — متابعة', en: 'I’ve saved them — continue' },
  twoFactorScan: {
    ar: 'امسح الرمز بتطبيق مصادقة مثل Google Authenticator أو Microsoft Authenticator أو 1Password.',
    en: 'Scan this with an authenticator app such as Google Authenticator, Microsoft Authenticator or 1Password.',
  },
  twoFactorQrAlt: { ar: 'رمز QR لتطبيق المصادقة', en: 'QR code for your authenticator app' },
  twoFactorKeyHint: { ar: 'لا يمكنك المسح؟ أدخل هذا المفتاح يدويًا:', en: 'Can’t scan? Enter this key by hand:' },
  twoFactorRecoveryHint: { ar: 'فقدت هاتفك؟ أدخل أحد رموز الاسترداد بدلًا من الرمز.', en: 'Lost your phone? Enter one of your recovery codes instead.' },
  twoFactorCodesHint: {
    ar: 'كل رمز يعمل مرة واحدة إن فقدت هاتفك. لن تظهر مرة أخرى.',
    en: 'Each works once if you lose your phone. You won’t see them again.',
  },
  twoFactorCopyCodes: { ar: 'نسخ الرموز', en: 'Copy codes' },
  twoFactorDownloadCodes: { ar: 'تنزيلها ملفًّا', en: 'Download as a file' },
  twoFactorOn: { ar: 'مفعّل', en: 'On' },
  twoFactorOff: { ar: 'متوقف', en: 'Off' },
  twoFactorBadge: { ar: 'بخطوتين', en: '2-step' },
  twoFactorPolicyOff: { ar: 'أوقفه متجرك لجميع الموظفين.', en: 'Your store has turned it off for all staff.' },
  twoFactorPolicyOptional: { ar: 'اختياري — يحمي حسابك إن تسرّبت كلمة المرور.', en: 'Your choice — it protects your account if your password leaks.' },
  twoFactorPolicyRequired: { ar: 'متجرك يطلبه من كل موظف.', en: 'Your store requires it for all staff.' },
  twoFactorWhy: {
    ar: 'بعد كلمة المرور يُطلب رمز من تطبيق على هاتفك، فلا تكفي كلمة المرور وحدها لدخول حسابك.',
    en: 'After your password you’ll be asked for a code from an app on your phone, so a password alone can’t get into your account.',
  },
  twoFactorUnavailable: {
    ar: 'التحقق بخطوتين يحتاج ضبط CREDENTIALS_SECRET على الخادم. اطلب ذلك ممن يدير الاستضافة.',
    en: 'Two-step sign-in needs CREDENTIALS_SECRET set on the server. Ask whoever runs your hosting.',
  },
  twoFactorOnSince: { ar: 'مفعّل منذ {date}', en: 'On since {date}' },
  twoFactorCodesLeft: { ar: 'رموز استرداد متبقية: {count}', en: '{count} recovery codes left' },
  twoFactorRenewCodes: { ar: 'رموز استرداد جديدة', en: 'New recovery codes' },
  twoFactorReset: { ar: 'إعادة ضبط التحقق بخطوتين', en: 'Reset two-step sign-in' },
  twoFactorResetBody: {
    ar: 'يتوقف تطبيق {name} ورموز استرداده عن العمل. إن كان متجرك يطلبه، يعدّه من جديد عند دخوله القادم.',
    en: '{name}’s app and recovery codes stop working. Where your store requires it, they set it up again at their next sign-in.',
  },
  securityTitle: { ar: 'أمان الدخول', en: 'Sign-in security' },
  securityHint: { ar: 'كيف تحمي دخولك إلى لوحة التحكم', en: 'How your own sign-in to the admin is protected' },
  customerSignsInWith: { ar: 'يدخل بحساب {providers}', en: 'Signs in with {providers}' },
  noAdminAccess: { ar: 'هذا الحساب لا يملك صلاحية الدخول للوحة التحكم.', en: 'This account does not have access to the admin.' },

  // ─── Page migration: sales and customers ──────────────────────────────────
  orderShippingAddress: { ar: 'عنوان الشحن', en: 'Shipping address' },
  orderPayment: { ar: 'الدفع', en: 'Payment' },
  returnView_all: { ar: 'الكل', en: 'All' },
  returnView_closed: { ar: 'مغلقة', en: 'Closed' },

  // ─── Page migration: catalog ───────────────────────────────────────────────
  productsViewAll: { ar: 'الكل', en: 'All' },
  stockAtMost: { ar: '{count} أو أقل', en: '{count} or fewer' },
  importStatus_PENDING: { ar: 'بانتظار المعالجة', en: 'Pending' },
  importStatus_VALIDATING: { ar: 'قيد الفحص', en: 'Checking' },
  importStatus_READY: { ar: 'جاهز للاستيراد', en: 'Ready to import' },
  importStatus_RUNNING: { ar: 'قيد الاستيراد', en: 'Importing' },
  importStatus_DONE: { ar: 'مكتمل', en: 'Done' },
  importStatus_FAILED: { ar: 'فشل', en: 'Failed' },
  importStatus_CANCELLED: { ar: 'ملغى', en: 'Cancelled' },

  // ─── Page migration: care, marketing, analytics, home ─────────────────────
  // Support's thread pane before a ticket is chosen from the queue beside it.
  supportPickTicket: { ar: 'اختر تذكرة من القائمة لقراءتها والرد عليها', en: 'Pick a ticket from the queue to read and answer it' },
  // The questions queue's "every status" tab.
  allQuestions: { ar: 'الكل', en: 'All' },

  // ─── Page migration: settings area and online store ───────────────────────
  paymentGateways: { ar: 'بوابات الدفع', en: 'Gateways' },
  shippingZonesAndRates: { ar: 'المناطق والأسعار', en: 'Zones and rates' },
  shippingDeliveryOptions: { ar: 'خيارات التوصيل', en: 'Delivery options' },
  noShippingZones: { ar: 'لا توجد مناطق شحن بعد', en: 'No shipping zones yet' },
  notificationTemplates: { ar: 'القوالب', en: 'Templates' },
  notificationSender: { ar: 'المرسِل', en: 'Sender' },

  // ─── Options library (ADR-052) ─────────────────────────────────────────────
  optionsLibrary: { ar: 'الخيارات', en: 'Options' },
  // Size charts (ADR-072).
  sizeCharts: { ar: 'جداول المقاسات', en: 'Size charts' },
  sizeChartsSubtitle: { ar: 'جداول تكتبها مرة وتعلّقها على قسم أو منتج — تظهر «دليل المقاسات» بجانب زر الشراء', en: 'Tables written once and hung on a category or a product — shown as "Size guide" beside the buy button' },
  addSizeChart: { ar: 'جدول جديد', en: 'New chart' },
  editSizeChart: { ar: 'تعديل الجدول', en: 'Edit chart' },
  searchSizeCharts: { ar: 'ابحث باسم الجدول', en: 'Search by name' },
  noSizeCharts: { ar: 'لا جداول مقاسات بعد', en: 'No size charts yet' },
  noSizeChartsHint: { ar: 'اكتب جدولًا لكل نوع — العبايات، الأحزمة — ثم علّقه على قسمه', en: 'Write one chart per kind — abayas, belts — then hang it on its category' },
  sizeChartShape: { ar: 'الحجم', en: 'Shape' },
  sizeChartShapeValue: { ar: '{columns} أعمدة × {rows} صفوف', en: '{columns} columns × {rows} rows' },
  sizeChartHangsOn: { ar: 'معلّق على', en: 'Hangs on' },
  sizeChartUsage: { ar: '{categories} أقسام، {products} منتجات', en: '{categories} categories, {products} products' },
  sizeChartUnused: { ar: 'لا شيء بعد', en: 'Nothing yet' },
  sizeChartEditorHint: { ar: 'العناوين بالعربية والإنجليزية؛ الخلايا كما تُكتب — أرقام أو كلمات', en: 'Headings in Arabic and English; cells as written — numbers or words' },
  sizeChartName: { ar: 'الاسم لدى الفريق', en: 'Name for the team' },
  sizeChartNameHint: { ar: 'يظهر في القائمة وعند الاختيار، لا يراه العميل', en: 'In the list and the pickers; shoppers never see it' },
  sizeChartTitle: { ar: 'العنوان للعميل', en: 'Title for shoppers' },
  sizeChartGrid: { ar: 'الجدول', en: 'The table' },
  sizeChartGridHint: { ar: 'العمود الأول يبقى ظاهرًا عند التمرير على الجوال — اجعله المقاس', en: 'The first column stays in view when scrolling on a phone — make it the size' },
  sizeChartHeadingAr: { ar: 'عنوان العمود {number} بالعربية', en: 'Column {number} heading in Arabic' },
  sizeChartHeadingEn: { ar: 'عنوان العمود {number} بالإنجليزية', en: 'Column {number} heading in English' },
  sizeChartCell: { ar: 'الصف {row}، العمود {column}', en: 'Row {row}, column {column}' },
  sizeChartRemoveColumn: { ar: 'حذف العمود', en: 'Remove column' },
  sizeChartRemoveRow: { ar: 'حذف الصف', en: 'Remove row' },
  sizeChartAddRow: { ar: 'إضافة صف', en: 'Add a row' },
  sizeChartAddColumn: { ar: 'إضافة عمود', en: 'Add a column' },
  sizeChartNote: { ar: 'ملاحظة أسفل الجدول', en: 'Note under the table' },
  sizeChartNoteHint: { ar: 'مثل «القياسات بالسنتيمتر، والقطعة مفرودة»', en: 'Like "Measured flat, in centimetres"' },
  deleteSizeChartTitle: { ar: 'حذف هذا الجدول؟', en: 'Delete this chart?' },
  deleteSizeChartBody: { ar: 'يُزال من كل قسم ومنتج عُلّق عليه.', en: 'It comes off every category and product it hangs on.' },
  sizeChart: { ar: 'جدول المقاسات', en: 'Size chart' },
  sizeChartFromCategory: { ar: 'جدول القسم (إن وُجد)', en: "Its category's (if any)" },
  productSizeChartHint: { ar: 'اتركه لجدول القسم، أو اختر جدولًا خاصًا بهذا المنتج', en: "Leave it for the category's chart, or pick one for this product alone" },
  noSizeChart: { ar: 'بلا جدول', en: 'No chart' },
  categorySizeChartHint: { ar: 'تعرضه منتجات هذا القسم وما تحته، ما لم يكن لمنتج جدوله', en: 'Shown by every product beneath, unless one carries its own' },
  name: { ar: 'الاسم', en: 'Name' },
  updatedAt: { ar: 'آخر تعديل', en: 'Last changed' },
  delete: { ar: 'حذف', en: 'Delete' },
  productOptions: { ar: 'الخيارات', en: 'Options' },
  productOptionsHint: {
    ar: 'الطرق التي يأتي بها المنتج، من مكتبة المتجر. إضافة قيمة تُنشئ متغيّراتها',
    en: 'The ways this product comes, from the store’s library. Adding a value creates its variants',
  },
  manageLibrary: { ar: 'إدارة مكتبة الخيارات', en: 'Manage the options library' },
  noProductOptions: { ar: 'يأتي هذا المنتج بطريقة واحدة', en: 'This product comes one way' },
  noProductOptionsHint: {
    ar: 'أضف خيارًا — لونًا أو طولًا — لتبيعه بأكثر من طريقة',
    en: 'Add an option — a colour, a length — to sell it more than one way',
  },
  optionValueCount: { ar: '{count} قيم', en: '{count} values' },
  ownNameOnProduct: { ar: 'باسم خاص في هذا المنتج', en: 'Named its own way on this product' },
  libraryNameOnProduct: { ar: 'بالاسم المعتمد في المكتبة', en: 'Named as in the library' },
  addOptionHint: {
    ar: 'اختر خيارًا من المكتبة — تأخذ كل المتغيّرات قيمته الأولى وتُنسخ لكل قيمة أخرى',
    en: 'Pick an option from the library — every variant takes its first value and is copied for each of the others',
  },
  addColoursTitle: { ar: 'إضافة ألوان', en: 'Add colours' },
  addValuesTitle: { ar: 'إضافة إلى {option}', en: 'Add to {option}' },
  pickFromLibraryHint: { ar: 'اختر من مكتبة المتجر، أو أضف جديدًا', en: 'Pick from the store’s library, or add a new one' },
  reviewVariantsHint: { ar: 'ستُنشأ هذه المتغيّرات — راجع الرموز والأسعار', en: 'These variants will be created — check the SKUs and prices' },
  reviewVariants: { ar: 'مراجعة المتغيّرات', en: 'Review variants' },
  createVariants: { ar: 'إنشاء {count} متغيّرات', en: 'Create {count} variants' },
  optionAddedToAll: {
    ar: 'تأخذ المتغيّرات الحالية ({count}) القيمة «{value}»',
    en: 'The existing variants ({count}) take {value}',
  },
  nothingNewToCreate: { ar: 'لا جديد — هذه التركيبات موجودة', en: 'Nothing new — these combinations already exist' },
  searchLibrary: { ar: 'ابحث في المكتبة', en: 'Search the library' },
  newColourInLibrary: { ar: 'لون جديد', en: 'New colour' },
  newValueInLibrary: { ar: 'قيمة جديدة', en: 'New value' },
  libraryNothingFound: { ar: 'لا شيء بهذا الاسم في المكتبة — أضفه', en: 'Nothing by that name in the library — add it' },
  alreadyOnProduct: { ar: 'موجود في المنتج', en: 'Already on this product' },
  noFamily: { ar: 'بلا عائلة', en: 'No family' },
  holdsAmount: { ar: 'تحوي {amount}', en: 'Holds {amount}' },
  colourFamily: { ar: 'عائلة اللون', en: 'Colour family' },
  colourFamilyHint: { ar: 'ما يصنّفه تحته فلتر الألوان', en: 'What the colour filter files it under' },
  familySuggestion: { ar: 'مقترح من العيّنة: {family}', en: 'Suggested from the swatch: {family}' },
  holdsLabel: { ar: 'الكمية في الواحدة', en: 'Holds' },
  holdsHint: { ar: 'بوحدة المنتج: 1 للمتر، 10 للفّة ١٠ م', en: 'In the product’s unit: 1 for a metre, 10 for a 10 m roll' },
  addToLibrary: { ar: 'إضافة إلى المكتبة', en: 'Add to library' },
  ownNameTitle: { ar: 'اسمه في هذا المنتج', en: 'Its name on this product' },
  ownNameHint: {
    ar: 'اتركه فارغًا ليُستخدم اسم المكتبة. الفلتر يعتمد المكتبة دائمًا',
    en: 'Leave it empty to use the library’s name. The filter always uses the library',
  },
  saveOwnName: { ar: 'حفظ الاسم', en: 'Save the name' },
  useLibraryName: { ar: 'استخدام اسم المكتبة', en: 'Use the library’s name' },
  linkPhotosOnMedia: { ar: 'اربط الصور من تبويب الوسائط', en: 'Link photos on the Media tab' },
  setPrice: { ar: 'تعيين السعر', en: 'Set price' },
  setStock: { ar: 'تعيين المخزون', en: 'Set stock' },
  clearSelection: { ar: 'إلغاء التحديد', en: 'Clear selection' },
  selectGroup: { ar: 'تحديد كل {name}', en: 'Select all of {name}' },
  removeSelectedTitle: { ar: 'حذف {count} متغيّرات؟', en: 'Remove {count} variants?' },
  removeSelectedBody: {
    ar: 'تُحذف من المنتج وتبقى في الطلبات السابقة. يبقى للمنتج متغيّر واحد على الأقل.',
    en: 'They are removed from the product; past orders keep them. A product keeps at least one variant.',
  },
  builderCount: { ar: 'سيُنشأ {count} متغيّرات', en: '{count} variants will be created' },
  duplicate: { ar: 'نسخ', en: 'Duplicate' },
  urlHandle: { ar: 'عنوان الصفحة', en: 'URL handle' },
  urlHandleHint: { ar: 'بالإنجليزية وشرطات — يُشتق من الاسم إن تُرك فارغًا', en: 'Latin and dashes — made from the name when left empty' },
  urlHandleChanged: {
    ar: 'تغيير العنوان بعد النشر يكسر الروابط القديمة إليه',
    en: 'Changing the address after it is published breaks links to the old one',
  },
  searchPreview: { ar: 'في نتائج البحث', en: 'In search results' },
  searchPreviewHint: { ar: 'كيف يظهر المنتج في محركات البحث', en: 'How the product reads in a search engine' },
  searchLengths: {
    ar: 'العنوان {title} حرفًا (نحو ٦٠) · الوصف {description} حرفًا (نحو ١٦٠)',
    en: 'Title {title} characters (about 60) · description {description} (about 160)',
  },
  readyTitle: { ar: 'جاهز للبيع', en: 'Ready to sell' },
  readyCount: { ar: '{done} من {total}', en: '{done} of {total}' },
  readyNames: { ar: 'الاسم بالعربية والإنجليزية', en: 'Name in Arabic and English' },
  readyDescriptions: { ar: 'الوصف باللغتين', en: 'Description in both languages' },
  readyPhoto: { ar: 'صورة واحدة على الأقل', en: 'At least one photo' },
  readyColourPhotos: { ar: 'صورة لكل لون', en: 'A photo for every colour' },
  readyCategory: { ar: 'قسم أساسي', en: 'A main category' },
  readyStock: { ar: 'مخزون متاح', en: 'Something in stock' },
  readyPrices: { ar: 'كل متغيّر له سعر', en: 'Every variant has a price' },
  readyRequiredSpecs: { ar: 'المواصفات المطلوبة مكتملة', en: 'Required specifications answered' },
  readyRequiredSpecsMissing: { ar: '{count} مواصفات مطلوبة بلا إجابة', en: '{count} required specifications unanswered' },
  readySearch: { ar: 'وصف لمحركات البحث', en: 'A search description' },
  removeValueWithVariants: { ar: 'حذفه مع متغيّراته ({count})', en: 'Remove it and its {count} variants' },

  // ─── Options library screen ───────────────────────────────────────────────
  optionsLibrarySubtitle: {
    ar: 'يُسمّى كل خيار وقيمه مرة واحدة للمتجر كله، وتختار المنتجات منها هنا — وتُجمَع الألوان في فلتر المتجر حسب عائلتها',
    en: 'Each option and its values are named once for the whole store, and products pick from them here — colours are grouped in the store’s filter by family',
  },
  optNewOption: { ar: 'خيار جديد', en: 'New option' },
  optNewOptionHint: {
    ar: 'مثل اللون أو المقاس أو طريقة البيع — تختار المنتجات قيمها منه',
    en: 'Like colour, size or how it is sold — products pick their values from it',
  },
  optSettings: { ar: 'إعدادات الخيار', en: 'Option settings' },
  optSettingsHint: { ar: 'يصل التغيير إلى كل منتج يستخدم هذا الخيار', en: 'A change reaches every product that uses this option' },
  optOptionCodeHint: {
    ar: 'بالإنجليزية، تُعرَف به المتغيّرات — يُشتق من الاسم الإنجليزي ولا يتغيّر بعد الإنشاء',
    en: 'Latin, what variants key on — made from the English name, fixed once created',
  },
  optValueCodeHint: {
    ar: 'بالإنجليزية، تحمله المتغيّرات والسلة — يُشتق من الاسم الإنجليزي ولا يتغيّر بعد الإنشاء',
    en: 'Latin, what variants and the cart hold — made from the English name, fixed once created',
  },
  optCodeFixed: { ar: 'لا يتغيّر الرمز بعد الإنشاء — تحمله المتغيّرات', en: 'A code is fixed once created — variants hold it' },
  optCodeInvalid: { ar: 'الرمز يحتاج حرفًا أو رقمًا لاتينيًا', en: 'A code needs at least one Latin letter or digit' },
  optDisplay: { ar: 'طريقة الاختيار', en: 'How shoppers pick it' },
  optDisplay_SWATCH: { ar: 'عيّنات لون', en: 'Swatches' },
  optDisplay_BUTTONS: { ar: 'أزرار', en: 'Buttons' },
  optDisplay_LIST: { ar: 'قائمة', en: 'List' },
  optDisplayHint: {
    ar: 'العيّنات للألوان: لكل قيمة لونها وعائلتها التي يقرؤها الفلتر',
    en: 'Swatches are for colours: each value keeps its colour and the family a filter reads',
  },
  optActive: { ar: 'مفعّل', en: 'Active' },
  optStatus: { ar: 'الحالة', en: 'Status' },
  optNoOptions: { ar: 'لا خيارات بعد', en: 'No options yet' },
  optNoOptionsHint: { ar: 'أنشئ خيارًا مثل اللون أو المقاس، ثم أضف قيمه', en: 'Create one such as colour or size, then add its values' },
  optSearchValues: { ar: 'ابحث بالاسم أو الرمز', en: 'Search by name or code' },
  optFamily: { ar: 'عائلة اللون', en: 'Colour family' },
  optAllFamilies: { ar: 'الكل', en: 'All' },
  optNoFamily: { ar: 'بلا عائلة', en: 'No family' },
  optChooseFamily: { ar: 'اختر عائلة', en: 'Choose a family' },
  optNeedsName: { ar: 'بحاجة إلى اسم', en: 'Needs a name' },
  optNeedsNameCount: { ar: '{count} بحاجة إلى اسم', en: '{count} need a name' },
  optUsedByNone: { ar: 'غير مستخدم بعد', en: 'Not used yet' },
  optUsedByOne: { ar: 'في منتج واحد', en: 'Used by 1 product' },
  optUsedBy: { ar: 'في {count} من المنتجات', en: 'Used by {count} products' },
  optNoValues: { ar: 'لا قيم لهذا الخيار بعد', en: 'This option has no values yet' },
  optNoValuesHint: {
    ar: 'أضف قيمة هنا — وتنضم أيضًا القيم التي تأتي بها المنتجات والاستيراد',
    en: 'Add one here — values that products and imports bring join on their own too',
  },
  optNoMatches: { ar: 'لا قيم مطابقة', en: 'No matching values' },
  optNoMatchesHint: { ar: 'جرّب كلمة أخرى أو عائلة أخرى', en: 'Try another word or family' },
  optNewValue: { ar: 'قيمة جديدة', en: 'New value' },
  optNewValueHint: { ar: 'تنضم إلى المكتبة، وتختارها المنتجات من هنا', en: 'It joins the library, and products pick it from here' },
  optEditValue: { ar: 'تعديل القيمة', en: 'Edit value' },
  optValueSheetHint: {
    ar: 'يصل التعديل إلى كل منتج يستخدمها، إلا منتجًا سمّاها باسم خاص به',
    en: 'Reaches every product that uses it, except one that calls it something of its own',
  },
  optSwatchPick: { ar: 'اختر اللون', en: 'Pick the colour' },
  optSwatchInvalid: { ar: 'لون بصيغة سداسية، مثل #796727', en: 'A hex colour, such as #796727' },
  optSuggestFrom: { ar: 'اقتراح من العيّنة: {family}', en: 'Suggest from the swatch: {family}' },
  optHolds: { ar: 'الكمية', en: 'Holds' },
  optHoldsHint: {
    ar: 'ما يحمله المتغيّر الجديد بهذه القيمة، بوحدة المنتج: 1 للمتر، 10 للفّة عشرة أمتار، 0.5 لنصف متر',
    en: 'What a new variant with it holds, in the product’s unit: 1 for a metre, 10 for a 10 m roll, 0.5 for half a metre',
  },
  optHoldsInvalid: { ar: 'رقم أكبر من صفر، بثلاث خانات عشرية على الأكثر', en: 'A number above zero, with up to three decimals' },
  optSample: { ar: 'عيّنة', en: 'Sample' },
  optSampleHint: { ar: 'المتغيّر الجديد بهذه القيمة يُباع عيّنةً', en: 'A new variant with it is a sample' },
  optValueActiveHint: {
    ar: 'الموقوفة لا تُعرض للاختيار في المنتجات، وتبقى على ما يستخدمها',
    en: 'Switched off, it is not offered to products; the ones using it keep it',
  },
  optSuggestFamilies: { ar: 'اقتراح العائلات', en: 'Suggest families' },
  optSuggestFamiliesHint: {
    ar: 'ألوان بلا عائلة، ولكلٍّ عائلة مقترحة من عيّنتها. راجعها ثم طبّقها ليجمعها فلتر الألوان في المتجر',
    en: 'Colours without a family, each with one suggested from its swatch. Review, then apply, and the store’s colour filter groups them',
  },
  optSuggestNone: { ar: 'كل لون له عيّنة مصنّف في عائلة', en: 'Every colour with a swatch has a family' },
  optSuggestSkipped: { ar: '{count} بلا عيّنة — صنّفها من بطاقتها', en: '{count} without a swatch — file those from their own card' },
  optInclude: { ar: 'تضمين {name}', en: 'Include {name}' },
  optApplyCount: { ar: 'تطبيق {count}', en: 'Apply {count}' },
  optApplying: { ar: 'جارٍ التطبيق… {done} من {total}', en: 'Applying… {done} of {total}' },

  // ─── Specifications per product and category templates ────────────────────
  specsCardSubtitle: {
    ar: 'يقترح القسم بعضها، وتضيف ما تشاء من المكتبة.',
    en: 'Its category suggests some; add any other from the library.',
  },
  specsFromCategory: { ar: 'من القسم', en: 'From the category' },
  specsAddedHere: { ar: 'مضافة لهذا المنتج', en: 'Added to this product' },
  specsAddedHint: {
    ar: 'تُحفظ متى كانت لها قيمة — الفارغة تسقط عند الحفظ.',
    en: 'Kept once it has a value — an empty one is dropped on save.',
  },
  specsEmptyHint: {
    ar: 'لا يقترح قسمه مواصفات بعد. أضف من المكتبة ما يذكره هذا المنتج — الخامة، المقاس، الضمان.',
    en: 'Its category suggests none yet. Add what this product states from the library — material, size, warranty.',
  },
  specsChooseCategoryHint: {
    ar: 'اختر قسمًا لترى ما يقترحه، أو أضف مواصفات من المكتبة.',
    en: 'Choose a category to see what it suggests, or add specifications from the library.',
  },
  specRetiredClear: { ar: 'موقوفة — احذفها', en: 'Retired — clear it' },
  addSpecification: { ar: 'إضافة مواصفة', en: 'Add specification' },
  specsReload: { ar: 'إعادة التحميل من القسم', en: 'Reload from the category' },
  saveSpecifications: { ar: 'حفظ المواصفات', en: 'Save specifications' },
  removeSpecification: { ar: 'إزالة من هذا المنتج', en: 'Remove from this product' },
  specsPickerHint: {
    ar: 'اختر من المكتبة؛ تُضاف فارغة لتملأها.',
    en: 'Pick from the library; each is added empty for you to fill in.',
  },
  specsSearch: { ar: 'ابحث بالاسم أو الرمز', en: 'Search by name or code' },
  specsNoMatch: { ar: 'لا مواصفة بهذا الاسم', en: 'No specification matches' },
  specsAllUsed: { ar: 'كل مواصفات المكتبة مضافة هنا بالفعل.', en: 'Every specification in the library is already here.' },
  addCount: { ar: 'إضافة {count}', en: 'Add {count}' },
  newSpecification: { ar: 'مواصفة جديدة', en: 'New specification' },
  newSpecificationHint: {
    ar: 'تُضاف إلى المكتبة لكل المنتجات، وإلى هنا مباشرة.',
    en: 'Added to the library for every product, and here straight away.',
  },
  specChoicesHint: {
    ar: 'ما يُختار منه — خيار واحد على الأقل باسمه الإنجليزي.',
    en: 'What a product picks from — at least one, with its English name.',
  },
  createAndAdd: { ar: 'إنشاء وإضافة', en: 'Create and add' },
  templateHint: {
    ar: 'ما يُسأل عنه كل منتج في هذا القسم والأقسام التي تحته. ويبقى لكل منتج أن يضيف غيرها.',
    en: 'What every product in this category — and in the ones under it — is asked. A product can still add others.',
  },
  templateInherited: { ar: 'موروثة من الأقسام الأعلى', en: 'Inherited from parent categories' },
  templateInheritedHint: { ar: 'تُعدّل في القسم الذي يطلبها.', en: 'Edited on the category that asks for them.' },
  templateOwn: { ar: 'يطلبها هذا القسم', en: 'Asked by this category' },
  templateEmpty: { ar: 'لا يطلب هذا القسم مواصفات بعد.', en: 'This category asks for no specifications yet.' },
  templateOwnerOnly: {
    ar: 'تعديل مواصفات الأقسام متاح لمالك المتجر فقط.',
    en: "Only the store owner can change a category's specifications.",
  },
  removeFromTemplate: { ar: 'إزالة من القسم', en: 'Remove from this category' },
  templatePickerHint: {
    ar: 'تُسأل عنها منتجات هذا القسم بعد الحفظ.',
    en: "This category's products are asked for them once saved.",
  },

  // ─── Product description blocks (ADR-055) ─────────────────────────────────
  tabDescription: { ar: 'الوصف', en: 'Description' },
  shortDescription: { ar: 'وصف مختصر', en: 'Short description' },
  shortDescriptionHint: {
    ar: 'جملة أو جملتان بجانب السعر — أول ما يقرؤه المتسوّق، وما تعرضه نتائج البحث إن لم يُكتب وصف البحث.',
    en: 'One or two sentences beside the price — the first words a shopper reads, and what search results show when there is no search description.',
  },
  charCount: { ar: '{count} / {max}', en: '{count} / {max}' },
  charCountLabel: { ar: 'الأحرف: {count} من {max}', en: '{count} of {max} characters' },
  readyShortDescription: { ar: 'الوصف المختصر باللغتين', en: 'Short description in both languages' },
  descStandardTitle: { ar: 'القالب يرتّب هذه الصفحة', en: 'The theme lays this page out' },
  descStandardBody: {
    ar: 'ما لم ترتّبها بنفسك، يبني القالب صفحة المنتج بطريقته من الوصف أدناه وصوره ومواصفاته.',
    en: 'Until you lay it out, the theme builds this product’s page its own way, from the description below, its photos and its specifications.',
  },
  descLayOut: { ar: 'رتّب هذه الصفحة', en: 'Lay out this page' },
  descLayOutHint: {
    ar: 'تبدأ بهذا الوصف في مكوّن نصّي، ثم تضيف الصور والمزايا والقوائم بالترتيب الذي تريده.',
    en: 'Starts with this description as a text block; then add photos, highlights and lists in the order you want.',
  },
  descBlocksTitle: { ar: 'الوصف التفصيلي', en: 'Long description' },
  descBlocksSubtitle: {
    ar: 'تظهر في صفحة المنتج بهذا الترتيب — المكوّنات: {count} من {max}',
    en: 'The product page shows these blocks in this order — {count} of {max}',
  },
  descPreview: { ar: 'معاينة', en: 'Preview' },
  descMoreActions: { ar: 'إجراءات أخرى', en: 'More actions' },
  descUseStandard: { ar: 'استخدم ترتيب القالب الافتراضي', en: 'Use the theme’s standard layout' },
  descUseStandardTitle: { ar: 'العودة إلى ترتيب القالب؟', en: 'Go back to the theme’s layout?' },
  descUseStandardBody: {
    ar: 'سيرتّب القالب الصفحة بنفسه من جديد. تبقى كلمات هذه المكوّنات وصفًا عاديًا، أما الصور والمزايا وترتيبها فتُحذف عند الحفظ.',
    en: 'The theme lays the page out itself again. The words of these blocks stay as the plain description; the photos, highlights and their order go when you save.',
  },
  descUseStandardConfirm: { ar: 'استخدم ترتيب القالب', en: 'Use the theme’s layout' },
  descNoBlocks: { ar: 'لا مكوّنات في الصفحة', en: 'No blocks on the page' },
  descNoBlocksHint: { ar: 'أضف أول مكوّن من الأسفل.', en: 'Add the first one below.' },
  blockActions: { ar: 'إجراءات المكوّن', en: 'Block actions' },
  blockRemoved: { ar: 'حُذف مكوّن «{type}».', en: '{type} block removed.' },
  undo: { ar: 'تراجع', en: 'Undo' },
  blockNothingYet: { ar: 'لا شيء هنا بعد', en: 'Nothing here yet' },
  blockPhotosCount: { ar: 'الصور: {count}', en: 'Photos: {count}' },
  blockPointsCount: { ar: 'النقاط: {count}', en: 'Points: {count}' },
  addBlock: { ar: 'أضف مكوّنًا', en: 'Add a block' },
  addBlockOnce: { ar: 'موجود في الصفحة — مرة واحدة فقط', en: 'Already on the page — once only' },
  addBlockFull: { ar: 'الحد الأقصى للمكوّنات: {max}', en: 'A page holds {max} blocks at most' },
  blockType_text: { ar: 'نص', en: 'Text' },
  blockType_photos: { ar: 'صور', en: 'Photos' },
  blockType_highlights: { ar: 'مزايا', en: 'Highlights' },
  blockType_list: { ar: 'قائمة', en: 'List' },
  blockType_looks: { ar: 'التنسيقات', en: 'Looks' },
  blockType_categories: { ar: 'الأقسام', en: 'Categories' },
  blockTypeHint_text: { ar: 'عنوان وفقرات', en: 'A heading and paragraphs' },
  blockTypeHint_photos: { ar: 'من صورة إلى ثلاث من صوره، مع تعليق', en: 'One to three of its photos, with captions' },
  blockTypeHint_highlights: { ar: 'حتى أربع بطاقات، لكلٍّ أيقونة ومعلومة', en: 'Up to four cards, each an icon and a fact' },
  blockTypeHint_list: { ar: 'نقاط مرقّمة أو منقّطة', en: 'Numbered or bulleted points' },
  blockTypeHint_looks: { ar: 'التنسيقات التي يظهر فيها المنتج', en: 'The looks that wear this product' },
  blockTypeHint_categories: { ar: 'الأقسام الأخرى التي يظهر فيها', en: 'The other categories it is listed in' },
  blockTypeUnknown: { ar: 'مكوّن غير معروف', en: 'Unknown block' },
  blockTypeUnknownHint: { ar: 'لا يُعدّل هنا، ويبقى كما هو.', en: 'It cannot be edited here, and is kept as it is.' },
  blockHeading: { ar: 'العنوان (اختياري)', en: 'Heading (optional)' },
  blockBody: { ar: 'النص', en: 'Text' },
  blockBodyHint: { ar: 'السطر الفارغ يبدأ فقرة جديدة.', en: 'A blank line starts a new paragraph.' },
  blockPhotosHint: {
    ar: 'الصورة الواحدة تظهر بعرض الصفحة، والصورتان أو الثلاث متجاورة.',
    en: 'One photo shows wide; two or three show side by side.',
  },
  blockChoosePhotos: { ar: 'اختر الصور', en: 'Choose photos' },
  blockChangePhotos: { ar: 'غيّر الصور', en: 'Change photos' },
  blockPhotoCaption: { ar: 'التعليق (اختياري)', en: 'Caption (optional)' },
  blockCloseUp: { ar: 'صورة مقرّبة', en: 'Close-up' },
  blockCloseUpHint: { ar: 'تعرض وسط الصورة مكبّرًا — النسيج عن قرب.', en: 'Shows the centre of the photo, zoomed in — the weave up close.' },
  blockPhotoMissing: { ar: 'لم تعد هذه الصورة من صور المنتج، فلن تظهر.', en: 'This photo is no longer one of the product’s, so it will not show.' },
  blockPhotosSaveFirst: { ar: 'الصور من صور المنتج نفسه', en: 'Photos come from the product’s own' },
  blockPhotosSaveFirstHint: {
    ar: 'أضف الصور في تبويب الوسائط واحفظ المنتج، ثم اختر منها هنا.',
    en: 'Add photos on the Media tab and save the product, then choose from them here.',
  },
  blockPhotosNone: { ar: 'لا صور لهذا المنتج بعد', en: 'This product has no photos yet' },
  blockPhotosNoneHint: { ar: 'أضفها من تبويب الوسائط، ثم اختر منها هنا.', en: 'Add them on the Media tab, then choose from them here.' },
  goToMedia: { ar: 'انتقل إلى الوسائط', en: 'Go to Media' },
  photoPickerHint: { ar: 'حتى {max}، بالترتيب الذي تختارها به', en: 'Up to {max}, in the order you pick them' },
  photoPickerCount: { ar: 'المختارة: {count} من {max}', en: '{count} of {max} chosen' },
  photoPickerApply: { ar: 'استخدم المختارة', en: 'Use these' },
  photoPickerFull: {
    ar: 'اكتمل العدد — ألغِ اختيار صورة لتختار غيرها',
    en: 'That is the most — unpick one to choose another',
  },
  blockHighlightN: { ar: 'البطاقة {n}', en: 'Card {n}' },
  blockHighlightIcon: { ar: 'الأيقونة', en: 'Icon' },
  blockNoIcon: { ar: 'بلا أيقونة', en: 'No icon' },
  blockHighlightSource: { ar: 'تعرض', en: 'Shows' },
  blockWrittenHere: { ar: 'نصًّا يُكتب هنا', en: 'Written here' },
  blockFromSpec: { ar: 'من المواصفات', en: 'From a specification' },
  blockSpecValue: { ar: 'القيمة من المواصفة', en: 'Value from the specification' },
  blockSpecValueHint: {
    ar: 'تُقرأ من إجابة المنتج فلا تختلف عنها أبدًا.',
    en: 'Read from the product’s answer, so the two never drift apart.',
  },
  blockSpecGone: {
    ar: 'لم تعد لهذه المواصفة قيمة في المنتج، فلن تظهر البطاقة.',
    en: 'The product no longer answers this specification, so the card will not show.',
  },
  blockLabelOverride: { ar: 'العنوان (اختياري)', en: 'Label (optional)' },
  blockLabelOverrideHint: { ar: 'إن تُرك فارغًا يظهر اسم المواصفة.', en: 'Empty shows the specification’s name.' },
  blockHighlightLabel: { ar: 'العنوان', en: 'Label' },
  blockHighlightValue: { ar: 'القيمة', en: 'Value' },
  blockSpecsSaveFirst: {
    ar: 'بعد حفظ المنتج وتعبئة مواصفاته، يمكن للبطاقة أن تقرأ إحداها.',
    en: 'Once the product is saved and its specifications answered, a card can read one.',
  },
  blockSpecsNone: { ar: 'املأ مواصفات المنتج لتصنع بطاقات تقرؤها.', en: 'Answer the product’s specifications to make cards that read them.' },
  goToSpecs: { ar: 'انتقل إلى المواصفات', en: 'Go to Specifications' },
  addHighlight: { ar: 'أضف بطاقة', en: 'Add a card' },
  highlightsFull: { ar: 'الحد الأقصى للبطاقات: {max}', en: '{max} cards at most' },
  highlightIcon_drape: { ar: 'الانسدال', en: 'Drape' },
  highlightIcon_season: { ar: 'الموسم', en: 'Season' },
  highlightIcon_width: { ar: 'العرض', en: 'Width' },
  highlightIcon_weight: { ar: 'الوزن', en: 'Weight' },
  highlightIcon_composition: { ar: 'التركيب', en: 'Composition' },
  highlightIcon_care: { ar: 'العناية', en: 'Care' },
  highlightIcon_origin: { ar: 'المنشأ', en: 'Origin' },
  highlightIcon_quality: { ar: 'الجودة', en: 'Quality' },
  highlightIcon_natural: { ar: 'طبيعي', en: 'Natural' },
  highlightIcon_durable: { ar: 'متين', en: 'Durable' },
  blockListStyle: { ar: 'شكل القائمة', en: 'List style' },
  listBulleted: { ar: 'نقاط', en: 'Bulleted' },
  listNumbered: { ar: 'أرقام', en: 'Numbered' },
  blockPoints: { ar: 'النقاط', en: 'Points' },
  blockPointN: { ar: 'النقطة {n}', en: 'Point {n}' },
  blockPointsHint: { ar: 'مفتاح Enter يضيف نقطة بعدها.', en: 'Enter adds a point after it.' },
  addPoint: { ar: 'أضف نقطة', en: 'Add a point' },
  pointsFull: { ar: 'الحد الأقصى للنقاط: {max}', en: '{max} points at most' },
  blockHeadingTheme: { ar: 'فارغ = عنوان القالب', en: 'Empty uses the theme’s heading' },
  blockLooksShows: {
    ar: 'يعرض تلقائيًا التنسيقات التي يظهر فيها هذا المنتج، وتُدار من شاشة التنسيقات.',
    en: 'Shows the looks that wear this product, on its own. Looks are managed on the Looks screen.',
  },
  blockCategoriesShows: {
    ar: 'يعرض تلقائيًا الأقسام الأخرى التي يظهر فيها المنتج:',
    en: 'Shows the other categories the product is listed in, on its own:',
  },
  blockCategoriesNone: {
    ar: 'لا يظهر المنتج في أقسام أخرى بعد — أضفها من بطاقة «التصنيف».',
    en: 'It is not listed in any other category yet — add some under Organization.',
  },
  previewSubtitle: { ar: 'كما يقرؤها المتسوّق', en: 'As a shopper reads it' },
  previewEmpty: { ar: 'لا شيء في الصفحة بعد', en: 'Nothing on the page yet' },
  previewFallback: {
    ar: 'نصوص لم تُكتب بهذه اللغة فتظهر بالأخرى: {count}',
    en: 'Missing in this language, shown in the other: {count}',
  },
  previewSpecNote: {
    ar: 'تظهر قيم المواصفات هنا بلغة لوحة التحكم، وفي المتجر يقرؤها كل متسوّق بلغته.',
    en: 'Specification values show here in the admin’s language; in the store each shopper reads their own.',
  },
  previewThemeHeading: { ar: 'عنوان القالب', en: 'The theme’s heading' },
  previewLooks: { ar: 'تظهر هنا التنسيقات التي يظهر فيها المنتج.', en: 'The looks that wear this product appear here.' },
  langNameAr: { ar: 'العربية', en: 'العربية' },
  langNameEn: { ar: 'English', en: 'English' },

  // ─── Shared ───────────────────────────────────────────────────────────────
  cancel: { ar: 'إلغاء', en: 'Cancel' },
  remove: { ar: 'حذف', en: 'Remove' },
  clearSearch: { ar: 'مسح البحث', en: 'Clear search' },
  confirm: { ar: 'تأكيد', en: 'Confirm' },
  save: { ar: 'حفظ', en: 'Save' },
  saving: { ar: 'جارٍ الحفظ…', en: 'Saving…' },
  saved: { ar: 'تم الحفظ', en: 'Saved' },
  loading: { ar: 'جارٍ التحميل…', en: 'Loading…' },
  retry: { ar: 'إعادة المحاولة', en: 'Try again' },
  genericError: { ar: 'تعذّر إتمام العملية. حاول مرة أخرى.', en: 'Something went wrong. Please try again.' },
  notFound: { ar: 'غير موجود', en: 'Not found' },
  comingSoon: { ar: 'قيد الإنشاء', en: 'Coming soon' },
  comingSoonHint: { ar: 'هذه الشاشة هي التالية في خطة البناء.', en: 'This screen is next in the build order.' },
  dash: { ar: '—', en: '—' },
} as const satisfies Record<string, BilingualLabel>;

export type MessageKey = keyof typeof M;

export const MESSAGES = M;

/** `t('revenue')` in the active language, with `{placeholder}` interpolation. */
export function translate(
  key: MessageKey,
  locale: AdminLocale,
  values?: Record<string, string | number>,
): string {
  // Composed keys — `status${value}`, `type${value}` — are only as safe as the
  // enum they came from. An API that adds a value must not white-screen the
  // admin, so a missing key renders as itself: visible, and not fatal.
  const entry = M[key];
  if (!entry) return String(key);

  const template = entry[locale];
  if (!values) return template;

  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in values ? String(values[name]) : match,
  );
}

/** Order status names, kept beside the rest of the copy. */
export const ORDER_STATUS_LABELS: Record<string, BilingualLabel> = {
  PENDING_PAYMENT: { ar: 'بانتظار الدفع', en: 'Awaiting payment' },
  AWAITING_COLLECTION: { ar: 'مؤكد', en: 'Confirmed' },
  PAID: { ar: 'مدفوع', en: 'Paid' },
  PROCESSING: { ar: 'قيد التجهيز', en: 'Processing' },
  SHIPPED: { ar: 'تم الشحن', en: 'Shipped' },
  READY_FOR_PICKUP: { ar: 'جاهز للاستلام', en: 'Ready for pickup' },
  DELIVERED: { ar: 'تم التوصيل', en: 'Delivered' },
  CANCELLED: { ar: 'ملغي', en: 'Cancelled' },
  REFUNDED: { ar: 'مسترجع', en: 'Refunded' },
};
